import React from 'react';
import PropTypes from 'prop-types';
import {withStyles} from '@material-ui/core/styles';
import DashboardTemplate from "../../Common/DashboardTemplate";
import {Checkbox, CircularProgress, FormControl, InputLabel, MenuItem, Button, Select, InputAdornment, Dialog, DialogContent, DialogActions, Tabs, Tab, Box, Typography, Tooltip, FormControlLabel} from "@material-ui/core";
import Zoom from "@material-ui/core/Zoom";
import axios from 'axios';
import {ServiceLocator} from "../../../Services/ServiceLocator";
import TextField from "@material-ui/core/TextField";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faTrash, faCheck, faGripVertical, faPeopleCarry} from "@fortawesome/free-solid-svg-icons";
import { v4 as uuid } from 'uuid';
import Autocomplete from "@material-ui/lab/Autocomplete";
import DrawerMenuRight from "../../Common/DrawerMenuRight";
import PrintIcon from '@material-ui/icons/Print';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { AuthenticationService } from "../../../Services/AuthenticationService";
import LzString from "lz-string";
import {getNumberInputDisplayValue, parseNumberInputValue} from '../../../utils/numberInputUtils';
import PartAutocompleteOption from '../../Common/PartAutocompleteOption';
import VehicleFitmentPanel from './VehicleFitmentPanel';
import {formatUtcForDisplay} from '../../../utils/dateUtils';
import { DatePicker } from '@material-ui/pickers';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { addDays } from 'date-fns';
import {buildBomIndex, containerHasTarget, buildContributionChains, formatBomQty, chainMultiplier} from '../../../utils/bomContributions';
import {
    formatBomExtendedCostDisplay,
    formatBomUnitCostDisplay,
    getBomLineExtendedCost,
    getBomLineUnitCost,
    isBomLineImpliedPart,
} from '../../../helpers/bomCost';
import { groupItemsForBomTable as groupBomLinesByOrder } from '../../../helpers/bomLineOrder';

// Status normalization function to ensure consistent casing
const normalizeStatus = (status) => {
    if (!status) return '-';
    
    const normalized = status.toString().toLowerCase();
    if (normalized === 'fulfilled' || normalized === 'fulfill') {
        return 'Fulfilled';
    } else if (normalized === 'pending') {
        return 'Pending';
    } else if (normalized === 'cancelled' || normalized === 'canceled') {
        return 'Cancelled';
    } else if (normalized === 'shipped') {
        return 'Shipped';
    }
    
    // For unknown statuses, capitalize first letter and lowercase the rest
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

const getBomNotesFieldRows = (notes) => {
    const text = (notes || '').trim();
    if (!text) {
        return 1;
    }
    const explicitLines = text.split(/\r?\n/).length;
    const wrappedLines = Math.ceil(text.length / 100);
    return Math.min(8, Math.max(1, explicitLines, wrappedLines));
};

const styles = theme => ({
    wrapper: {
        margin: -5,
    },
    printButton: {
        marginLeft: theme.spacing(1),
    },
});

let assemblies;
let subAssemblies;
let discountStrategies;
let assemblyFamilies;
let editingAssembly;
let editingSubAssembly;

// Helper functions for determining currently selling assembly
const getCurrentlySellingAssemblyId = (assemblyId, assemblies) => {
    // Get all assemblies with the same assemblyId that don't have a finalOrderNumber
    const assembliesWithoutFinalOrder = assemblies.filter(assembly => 
        assembly.assembly.assemblyId === assemblyId && 
        !assembly.assembly.finalOrderNumber
    );
    
    // If only one assembly without finalOrderNumber, it's the currently selling one
    if (assembliesWithoutFinalOrder.length === 1) {
        return assembliesWithoutFinalOrder[0].assembly.id;
    }
    
    // If multiple assemblies without finalOrderNumber, find the one with the lowest revision
    if (assembliesWithoutFinalOrder.length > 1) {
        // Sort by revision (ascending) and return the lowest
        const sortedAssemblies = assembliesWithoutFinalOrder.sort((a, b) => {
            const revisionA = a.assembly.revision || '';
            const revisionB = b.assembly.revision || '';
            return revisionA.localeCompare(revisionB, undefined, { numeric: true, sensitivity: 'base' });
        });
        return sortedAssemblies[0].assembly.id;
    }
    
    // If no assemblies without finalOrderNumber, return null
    return null;
};

const isAssemblyCurrentlySelling = (assembly, assemblies) => {
    if (!assembly || assembly.finalOrderNumber) {
        return false;
    }
    
    const currentlySellingId = getCurrentlySellingAssemblyId(assembly.assemblyId, assemblies);
    return currentlySellingId === assembly.id;
};

class EditAssembly extends React.Component {
    state = {
        loading: true,
        user: {},
        users: [],
        adminUsers: [],
        showAdvancedInfo: false,
        assemblyMenuOpen: false,
        newAssemblyIsFG: true,
        newConnectionQuantity: 1,
        newConnectionBomRequired: true,
        newFamilyName: '',
        newFamilyDescription: '',
        partFamilies: [], // Add part families to state
        imageModalOpen: false,
        selectedImageUrl: null,
        isDraggingOverAssemblyMain: false,
        isDraggingOverAssemblyZone: null,
        isDraggingOverSubAssembly: false,
        isDraggingOverAssemblyFilesMain: false,
        isDraggingOverAssemblyFilesZone: null,
        isDraggingOverSubAssemblyFiles: false,
        isAddingBomItem: false,
        newBomItemFrom: null,
        newBomItemQuantity: 1,
        newBomItemBomRequired: true,
        newBomItemIsImpliedPart: false,
        // Inline editing state
        editingBomItem: null,
        editingBomItemType: null,
        editingBomItemPart: null,
        editingBomItemQuantity: 1,
        editingBomItemBomRequired: true,
        editingBomItemIsImpliedPart: false,
        // Clone state
        showCloneDialog: false,
        inventoryData: null, // Store inventory analytics data
        clonedAssemblyData: null,
        clonedSubAssemblyData: null,
        isCloningAssembly: false,
        cloneImagesAndFiles: true,
        // Bulk BOM editing state
        isBulkEditingBom: false,
        bulkBomItems: [],
        newBulkBomItems: [],
        profitTiersExpanded: false,
        customDiscount: 50,
        // Change detection state
        originalAssembly: null, // Store original assembly data for change detection
        originalSubAssembly: null, // Store original subassembly data for change detection
        // Input tracking for freeSolo dropdowns
        assemblyTypeInputValue: '', // Track input value for assembly primary category filtering
        manufacturerForInputValue: '', // Track input value for assembly secondary category filtering
        tertiaryTypeInputValue: '', // Track input value for assembly tertiary category filtering
        brandInputValue: '', // Track input value for assembly brand filtering
        subAssemblyTypeInputValue: '', // Track input value for sub-assembly primary category filtering
        subManufacturerForInputValue: '', // Track input value for sub-assembly secondary category filtering
        subTertiaryTypeInputValue: '', // Track input value for sub-assembly tertiary category filtering
        subBrandInputValue: '', // Track input value for sub-assembly brand filtering
        // Calculation contributions state
        calculationContributionsTab: 0, // Active tab index
        showParentChildOptionalColumns: false, // Parent/Child: show hidden detail columns
        showParentChildImpliedParts: false, // Parent/Child: include implied parts in the list
        calculationContributionsData: {
            packageLogs: null,
            salesLogs: null
        },
        loadingCalculationContributions: false,
        useCustomStoreDescriptionAssembly: false,
        useCustomStoreDescriptionSub: false,
    };

    constructor(props) {
        super(props);
        globalProps = props;

        const jwtToken = AuthenticationService.getInstance().getToken();
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + jwtToken;
    }

    // Function to check if the assembly/subassembly has been modified
    checkForChanges = () => {
        if (this.state.editingAssembly && this.state.originalAssembly) {
            const original = this.state.originalAssembly;
            const current = editingAssembly;
            return JSON.stringify(original) !== JSON.stringify(current);
        } else if (this.state.editingSubAssembly && this.state.originalSubAssembly) {
            const original = this.state.originalSubAssembly;
            const current = editingSubAssembly;
            return JSON.stringify(original) !== JSON.stringify(current);
        }
        return false;
    };

    componentDidMount() {
        this.loadData();
    }

    componentDidUpdate(prevProps, prevState) {
        // Reload data if the route parameter changed (same component, different assembly/subassembly)
        const currentAssemblyId = this.props.match?.params?.assemblyId;
        const prevAssemblyId = prevProps.match?.params?.assemblyId;
        
        if (currentAssemblyId && prevAssemblyId !== currentAssemblyId) {
            // Reset global variables immediately
            editingAssembly = null;
            editingSubAssembly = null;
            
            // Force a complete reset and reload
            this.setState({
                loading: true,
                calculationContributionsData: {
                    packageLogs: null,
                    salesLogs: null
                },
                calculationContributionsTab: 0,
                editingAssembly: null,
                editingSubAssembly: null
            }, () => {
                // Load data after state is reset
                this.loadData();
            });
        }
        
        // Update document title when assembly/subassembly data changes
        if (editingAssembly && editingAssembly.assembly && (!prevState.editingAssembly || prevState.editingAssembly.assembly.id !== editingAssembly.assembly.id)) {
            document.title = `${editingAssembly.assembly.assemblyId} rev ${editingAssembly.assembly.revision}`;
        } else if (editingSubAssembly && editingSubAssembly.subAssembly && (!prevState.editingSubAssembly || prevState.editingSubAssembly.subAssembly.id !== editingSubAssembly.subAssembly.id)) {
            document.title = `${editingSubAssembly.subAssembly.subAssemblyId} rev ${editingSubAssembly.subAssembly.revision}`;
        }
    }

    loadData = () => {
        const self = this;
        const user = AuthenticationService.getInstance().getUser();
        if (!user || !user.userPermissions || !user.userPermissions.includes('Workshop')) {
            globalProps.history.push('/login');
            return;
        }

        self.setState({loading: true});
        Promise.all([
            self.loadAssemblies(), 
            self.loadSubAssemblies(), 
            self.loadDiscountStrategies(), 
            self.loadParts(), 
            self.loadAssemblyFamilies(),
            self.loadPartFamilies(), // Add loading part families
            self.loadAdminUsers()
        ]).then(() => {
            const assemblyId = globalProps.match.params.assemblyId;
            editingSubAssembly = subAssemblies.find(x => x.subAssembly.id === assemblyId);
            if (editingSubAssembly) {
                editingSubAssembly.subAssembly.imageURLs = editingSubAssembly.subAssembly.imageURL ? editingSubAssembly.subAssembly.imageURL.split('~|~') : [''];
                // FileURLs are stored as "filename|url" entries separated by ~|~
                // Filter out empty entries when loading
                const subFileURLsValue = editingSubAssembly.subAssembly.fileURLs;
                console.log('Loading SubAssembly fileURLs from database:', subFileURLsValue, 'type:', typeof subFileURLsValue);
                if (subFileURLsValue && typeof subFileURLsValue === 'string' && subFileURLsValue.trim() !== '') {
                    editingSubAssembly.subAssembly.fileURLs = subFileURLsValue.split('~|~').filter(x => x && x.trim() !== '');
                } else {
                    editingSubAssembly.subAssembly.fileURLs = [];
                }
                console.log('Loaded SubAssembly fileURLs (converted to array):', editingSubAssembly.subAssembly.fileURLs);
            }
            editingAssembly = assemblies.find(x => x.assembly.id === assemblyId);
            let matchingDiscountStrategy = null;
            let matchingFamily = null;
            if (editingAssembly) {
                const assembly = editingAssembly.assembly;
                editingAssembly.assembly.imageURLs = editingAssembly.assembly.imageURL.split('~|~');
                // FileURLs are stored as "filename|url" entries separated by ~|~
                // Filter out empty entries when loading
                const fileURLsValue = editingAssembly.assembly.fileURLs;
                console.log('Loading Assembly fileURLs from database:', fileURLsValue, 'type:', typeof fileURLsValue);
                if (fileURLsValue && typeof fileURLsValue === 'string' && fileURLsValue.trim() !== '') {
                    editingAssembly.assembly.fileURLs = fileURLsValue.split('~|~').filter(x => x && x.trim() !== '');
                } else {
                    editingAssembly.assembly.fileURLs = [];
                }
                console.log('Loaded Assembly fileURLs (converted to array):', editingAssembly.assembly.fileURLs);
                matchingDiscountStrategy = discountStrategies.find(x => x.discountTier1 === assembly.discountTier1 && x.discountTier2 === assembly.discountTier2 && x.discountTier3 === assembly.discountTier3);
                matchingFamily = assemblyFamilies.find(x => x.id === assembly.assemblyFamilyId);
                
                // Store original assembly data for change detection
                const originalAssembly = JSON.parse(JSON.stringify(editingAssembly));
                self.setState({
                    loading: false, 
                    user, 
                    permissions: user.userPermissions, 
                    editingAssembly, 
                    editingSubAssembly, 
                    matchingDiscountStrategy, 
                    matchingFamily, 
                    discountStrategy: matchingDiscountStrategy ? matchingDiscountStrategy.id : null, 
                    savingConnection: false,
                    originalAssembly,
                    useCustomStoreDescriptionAssembly: !!(assembly.customerSiteDescription || '').trim(),
                });
                // Update document title
                document.title = `${editingAssembly.assembly.assemblyId} rev ${editingAssembly.assembly.revision}`;
                // Fetch inventory data after main data is loaded
                self.fetchInventoryData();
                self.prefetchCalculationContributionsTableData();
            } else if (editingSubAssembly) {
                // Store original subassembly data for change detection
                const originalSubAssembly = JSON.parse(JSON.stringify(editingSubAssembly));
                self.setState({
                    loading: false, 
                    user, 
                    permissions: user.userPermissions, 
                    editingAssembly, 
                    editingSubAssembly, 
                    matchingDiscountStrategy, 
                    matchingFamily, 
                    discountStrategy: matchingDiscountStrategy ? matchingDiscountStrategy.id : null, 
                    savingConnection: false,
                    originalSubAssembly,
                    useCustomStoreDescriptionSub: !!(editingSubAssembly.subAssembly.customerSiteDescription || '').trim(),
                });
                // Update document title
                document.title = `${editingSubAssembly.subAssembly.subAssemblyId} rev ${editingSubAssembly.subAssembly.revision}`;
                // Fetch inventory data after main data is loaded
                self.fetchInventoryData();
                self.prefetchCalculationContributionsTableData();
            } else {
                self.setState({
                    loading: false, 
                    user, 
                    permissions: user.userPermissions, 
                    editingAssembly, 
                    editingSubAssembly, 
                    matchingDiscountStrategy, 
                    matchingFamily, 
                    discountStrategy: matchingDiscountStrategy ? matchingDiscountStrategy.id : null, 
                    savingConnection: false
                });
            }
        });
    };

    // Fetch inventory analytics data for the current assembly/subassembly
    fetchInventoryData = async () => {
        if (!editingAssembly && !editingSubAssembly) return;
        
        try {
            const assemblyService = ServiceLocator.Instance.getAssemblyService();
            const [allAssembliesExpanded, allSubAssembliesExpanded] = await Promise.all([
                assemblyService.getAllAssembliesExpanded(),
                assemblyService.getAllSubAssembliesExpanded()
            ]);
            
            // Find the current assembly/subassembly in the expanded data
            let assemblyExpanded = null;
            let isAssembly = false;
            
            if (editingAssembly) {
                assemblyExpanded = allAssembliesExpanded.find(a => a.assembly.id === editingAssembly.assembly.id);
                isAssembly = true;
            } else if (editingSubAssembly) {
                assemblyExpanded = allSubAssembliesExpanded.find(s => s.subAssembly.id === editingSubAssembly.subAssembly.id);
                isAssembly = false;
            }
            
            if (assemblyExpanded) {
                this.setState({
                    inventoryData: {
                        isAssembly: isAssembly,
                        totalSoldWithRev: assemblyExpanded.totalSoldWithRev || 0,
                        totalAssembledWithRev: assemblyExpanded.totalAssembledWithRev || 0,
                        totalAssembledOnHandWithRev: assemblyExpanded.totalAssembledOnHandWithRev || 0,
                        subATotalOnHandFGWithRev: !isAssembly && assemblyExpanded.subATotalOnHandFGWithRev ? assemblyExpanded.subATotalOnHandFGWithRev : 0,
                        fourWeekSalesPerWeekWithRev: assemblyExpanded.fourWeekSalesPerWeekWithRev || 0,
                        twentyFourWeekSalesPerWeekWithRev: assemblyExpanded.twentyFourWeekSalesPerWeekWithRev || 0
                    }
                });
            }
        } catch (error) {
            console.error('Error fetching inventory data:', error);
            this.setState({ inventoryData: null });
        }
    };

    loadAssemblies = () => {
        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();

        return assemblyService.getAllAssembliesExpanded().then((assembliesResult) => {
            const driveshaftCarriersAssemblies = assembliesResult.filter(x => x.assembly.assemblyType === 'Driveshaft Carriers').sort(function (a, b) {
                // Sort by manufacturerFor first
                if (a.assembly.manufacturerFor !== b.assembly.manufacturerFor) {
                    if (a.assembly.manufacturerFor < b.assembly.manufacturerFor) return -1;
                    if (a.assembly.manufacturerFor > b.assembly.manufacturerFor) return 1;
                }

                // Then by tertiaryType (empty/null first)
                const aTertiary = a.assembly.tertiaryType || '';
                const bTertiary = b.assembly.tertiaryType || '';
                if (aTertiary !== bTertiary) {
                    if (aTertiary === '' && bTertiary !== '') return -1;
                    if (aTertiary !== '' && bTertiary === '') return 1;
                    if (aTertiary < bTertiary) return -1;
                    if (aTertiary > bTertiary) return 1;
                }

                // Then by assemblyId
                if (a.assembly.assemblyId !== b.assembly.assemblyId) {
                    if (a.assembly.assemblyId < b.assembly.assemblyId) return -1;
                    if (a.assembly.assemblyId > b.assembly.assemblyId) return 1;
                }

                // Finally by revision (descending)
                if (a.assembly.revision !== b.assembly.revision) {
                    if (a.assembly.revision > b.assembly.revision) return -1;
                    if (a.assembly.revision < b.assembly.revision) return 1;
                }

                return 0;
            });
            const otherAssemblies = assembliesResult.filter(x => x.assembly.assemblyType !== 'Driveshaft Carriers').sort(function (a, b) {
                // Sort by assemblyType first
                if (a.assembly.assemblyType !== b.assembly.assemblyType) {
                    if (a.assembly.assemblyType < b.assembly.assemblyType) return -1;
                    if (a.assembly.assemblyType > b.assembly.assemblyType) return 1;
                }

                // Then by manufacturerFor
                if (a.assembly.manufacturerFor !== b.assembly.manufacturerFor) {
                    if (a.assembly.manufacturerFor < b.assembly.manufacturerFor) return -1;
                    if (a.assembly.manufacturerFor > b.assembly.manufacturerFor) return 1;
                }

                // Then by tertiaryType (empty/null first)
                const aTertiary = a.assembly.tertiaryType || '';
                const bTertiary = b.assembly.tertiaryType || '';
                if (aTertiary !== bTertiary) {
                    if (aTertiary === '' && bTertiary !== '') return -1;
                    if (aTertiary !== '' && bTertiary === '') return 1;
                    if (aTertiary < bTertiary) return -1;
                    if (aTertiary > bTertiary) return 1;
                }

                // Then by assemblyId
                if (a.assembly.assemblyId !== b.assembly.assemblyId) {
                    if (a.assembly.assemblyId < b.assembly.assemblyId) return -1;
                    if (a.assembly.assemblyId > b.assembly.assemblyId) return 1;
                }

                // Finally by revision (descending)
                if (a.assembly.revision !== b.assembly.revision) {
                    if (a.assembly.revision > b.assembly.revision) return -1;
                    if (a.assembly.revision < b.assembly.revision) return 1;
                }

                return 0;
            });
            assemblies = driveshaftCarriersAssemblies.concat(otherAssemblies);
            self.setState({assemblies});
        });
    };

    loadSubAssemblies = () => {
        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();

        return assemblyService.getAllSubAssembliesExpanded().then((subAssembliesResult) => {
            subAssemblies = subAssembliesResult.sort(function (a, b) {
                // Sort by assemblyType first (if available)
                if (a.subAssembly.assemblyType !== b.subAssembly.assemblyType) {
                    if (a.subAssembly.assemblyType < b.subAssembly.assemblyType) return -1;
                    if (a.subAssembly.assemblyType > b.subAssembly.assemblyType) return 1;
                }

                // Then by manufacturerFor (if available)
                if (a.subAssembly.manufacturerFor !== b.subAssembly.manufacturerFor) {
                    if (a.subAssembly.manufacturerFor < b.subAssembly.manufacturerFor) return -1;
                    if (a.subAssembly.manufacturerFor > b.subAssembly.manufacturerFor) return 1;
                }

                // Then by tertiaryType (if available)
                const aTertiary = a.subAssembly.tertiaryType || '';
                const bTertiary = b.subAssembly.tertiaryType || '';
                if (aTertiary !== bTertiary) {
                    if (aTertiary === '' && bTertiary !== '') return -1;
                    if (aTertiary !== '' && bTertiary === '') return 1;
                    if (aTertiary < bTertiary) return -1;
                    if (aTertiary > bTertiary) return 1;
                }

                // Then by subAssemblyId
                if (a.subAssembly.subAssemblyId !== b.subAssembly.subAssemblyId) {
                    if (a.subAssembly.subAssemblyId < b.subAssembly.subAssemblyId) return -1;
                    if (a.subAssembly.subAssemblyId > b.subAssembly.subAssemblyId) return 1;
                }

                // Finally by revision (descending)
                if (a.subAssembly.revision !== b.subAssembly.revision) {
                    if (a.subAssembly.revision > b.subAssembly.revision) return -1;
                    if (a.subAssembly.revision < b.subAssembly.revision) return 1;
                }

                return 0;
            });
            self.setState({subAssemblies});
        });
    };

    loadAdminUsers = () => {
        const userService = ServiceLocator.Instance.getUserService();
        return userService.getAdminUsers().then((adminUsers) => {
            this.setState({adminUsers});
        });
    };

    loadParts = () => {
        const self = this;
        self.setState({loading: true});
        const partService = ServiceLocator.Instance.getPartService();

        return partService.getAllPartsExpanded().then((parts) => {
            parts = parts.sort(function (a, b) {
                if (a.part.partId < b.part.partId) return -1;
                if (a.part.partId > b.part.partId) return 1;
                return 0;
            });
            self.setState({parts});
        });
    };

    getAllPartsExpanded = () => {
        return this.state.parts || [];
    };

    getBomCostDisplayOptions = (bomLine) => ({
        bomChanges: this.state.bomChanges,
        lineId: bomLine?.id,
    });

    formatBomItemUnitCost = (bomLine, expandedPart) =>
        formatBomUnitCostDisplay(bomLine, expandedPart, this.getBomCostDisplayOptions(bomLine));

    formatBomItemExtendedCost = (bomLine, expandedPart, quantity) =>
        formatBomExtendedCostDisplay(bomLine, expandedPart, quantity, this.getBomCostDisplayOptions(bomLine));

    loadDiscountStrategies = () => {
        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();

        return assemblyService.getDiscountStrategies().then((discountStrategiesResult) => {
            discountStrategies = discountStrategiesResult;
            self.setState({discountStrategies});
        });
    };

    loadAssemblyFamilies = () => {
        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();

        return assemblyService.getAssemblyFamilies().then((assemblyFamiliesResult) => {
            assemblyFamilies = assemblyFamiliesResult;
            self.setState({assemblyFamilies});
        });
    };

    loadPartFamilies = () => {
        const self = this;
        const partService = ServiceLocator.Instance.getPartService();
        return partService.getPartFamilies().then((partFamilies) => {
            self.setState({partFamilies});
        });
    };

    changeNewFamilyName = (event) => {
        this.setState({newFamilyName: event.target.value});
    };

    changeNewFamilyDescription = (event) => {
        this.setState({newFamilyDescription: event.target.value});
    };

    removeAssemblyFamily = () => {
        if (editingAssembly?.assembly) {
            editingAssembly.assembly.assemblyFamilyId = null;
            editingAssembly.assembly.assemblyFamily = null;
        }
        this.setState({
            editingAssembly: {
                ...this.state.editingAssembly,
                assembly: {
                    ...this.state.editingAssembly.assembly,
                    assemblyFamilyId: null,
                    assemblyFamily: null
                }
            },
            matchingFamily: null,
            assemblyFamily: null
        });
    };

    handleAssemblyFamilyChange = (event, newValue) => {
        if (!newValue) this.setState({assemblyFamily: null});
        else {
            const matchingFamily = this.state.assemblyFamilies.find(x => x.id === newValue.value);
            if (!matchingFamily) {
                this.setState({assemblyFamily: 'New'});
            } else {
                editingAssembly.assembly.assemblyFamilyId = matchingFamily.id;
                editingAssembly.assembly.assemblyFamily = matchingFamily;
                this.setState({editingAssembly, matchingFamily, assemblyFamily: newValue.value});
            }
        }
    };

    changeAssemblyTypeSelection = (event, newValue) => {
        if (!newValue || newValue === 'New...') {
            editingAssembly.assembly.assemblyType = !newValue ? '' : newValue;
            this.setState({editingAssembly, newAssemblyType: true});
        } else {
            editingAssembly.assembly.assemblyType = newValue;
            this.setState({editingAssembly, newAssemblyType: false});
        }
    };

    changeAssemblyType = (event) => {
        editingAssembly.assembly.assemblyType = event.target.value;
        this.setState({editingAssembly});
    };

    changeManufacturerForSelection = (event, newValue) => {
        if (!newValue || newValue.subType === 'New...') {
            editingAssembly.assembly.manufacturerFor = !newValue ? '' : newValue.subType;
            this.setState({editingAssembly, newManufacturerFor: true});
        } else {
            editingAssembly.assembly.manufacturerFor = newValue.subType;
            this.setState({editingAssembly, newManufacturerFor: false});
        }
    };

    changeSpareBushingsQuantity = (event) => {
        editingAssembly.assembly.spareBushingsSetQuantity = event.target.value;
        this.setState({editingAssembly});
    };

    changeSpareHardwareQuantity = (event) => {
        editingAssembly.assembly.spareHardwareSetQuantity = event.target.value;
        this.setState({editingAssembly});
    };

    changeManufacturerFor = (event) => {
        editingAssembly.assembly.manufacturerFor = event.target.value;
        this.setState({editingAssembly});
    };

    changeAssemblyId = (event) => {
        editingAssembly.assembly.assemblyId = event.target.value;
        this.setState({editingAssembly});
    };

    changeRevision = (event) => {
        editingAssembly.assembly.revision = event.target.value;
        this.setState({editingAssembly});
    };

    changeRevisionNotes = (event) => {
        editingAssembly.assembly.revisionNotes = event.target.value;
        this.setState({editingAssembly});
    };

    changeBomNotes = (event) => {
        editingAssembly.assembly.bomNotes = event.target.value;
        this.setState({editingAssembly});
    };

    changeDescription = (event) => {
        editingAssembly.assembly.description = event.target.value;
        this.setState({editingAssembly});
    };

    toggleUseCustomStoreDescriptionAssembly = (event) => {
        const checked = event.target.checked;
        if (!checked) {
            editingAssembly.assembly.customerSiteDescription = '';
        }
        this.setState({ useCustomStoreDescriptionAssembly: checked, editingAssembly });
    };

    changeCustomerSiteDescriptionAssembly = (event) => {
        editingAssembly.assembly.customerSiteDescription = event.target.value;
        this.setState({ editingAssembly });
    };

    addNewImage = () => {
        editingAssembly.assembly.imageURLs.push('');
        this.setState({editingAssembly});
    };

    changeImageURL = (index, event) => {
        editingAssembly.assembly.imageURLs[index] = event.target.value;
        this.setState({editingAssembly});
    };

    removeImage = (imageURL) => {
        editingAssembly.assembly.imageURLs.splice(editingAssembly.assembly.imageURLs.indexOf(imageURL), 1);
        this.setState({editingAssembly});
    };

    openImageModal = (imageUrl) => {
        this.setState({
            imageModalOpen: true,
            selectedImageUrl: imageUrl
        });
    };

    closeImageModal = () => {
        this.setState({
            imageModalOpen: false,
            selectedImageUrl: null
        });
    };

    processAssemblyImageFiles = async (files, index) => {
        if (!files || files.length === 0) return;

        // Validate all files
        const invalidFiles = files.filter(file => !file.type.startsWith('image/'));
        if (invalidFiles.length > 0) {
            window.$alertError("Please select valid image files only.");
            return;
        }

        const oversizedFiles = files.filter(file => file.size > 10 * 1024 * 1024);
        if (oversizedFiles.length > 0) {
            window.$alertError("All files must be less than 10MB.");
            return;
        }

        // Set uploading state for the index
        this.setState({[`uploadingAssemblyImage${index}`]: true});

        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        const uploadedUrls = [];
        let successCount = 0;
        let errorCount = 0;

        try {
            // Upload all files sequentially
            for (let i = 0; i < files.length; i++) {
                try {
                    const response = await assemblyService.uploadAssemblyImage(editingAssembly.assembly.id, files[i]);
                    uploadedUrls.push(response.imageUrl);
                    successCount++;
                } catch (error) {
                    console.error(`Error uploading assembly image ${i + 1}:`, error);
                    errorCount++;
                }
            }

            if (uploadedUrls.length > 0) {
                // Find blank slots (empty strings) to replace first
                let blankIndex = editingAssembly.assembly.imageURLs.findIndex(url => !url || url.trim() === '');
                
                // Replace blank slots first, then append remaining files
                uploadedUrls.forEach((url, i) => {
                    if (blankIndex !== -1 && blankIndex < editingAssembly.assembly.imageURLs.length) {
                        // Replace the blank slot
                        editingAssembly.assembly.imageURLs[blankIndex] = url;
                        // Find next blank slot
                        blankIndex = editingAssembly.assembly.imageURLs.findIndex((url, idx) => idx > blankIndex && (!url || url.trim() === ''));
                    } else {
                        // No more blank slots, append as new
                        editingAssembly.assembly.imageURLs.push(url);
                    }
                });
                
                this.setState({editingAssembly, [`uploadingAssemblyImage${index}`]: false});
                
                if (successCount === files.length) {
                    window.$alertSuccess(`${successCount} image${successCount > 1 ? 's' : ''} uploaded successfully!`);
                } else {
                    window.$alertSuccess(`${successCount} image${successCount > 1 ? 's' : ''} uploaded successfully. ${errorCount} failed.`);
                }
            } else {
                this.setState({[`uploadingAssemblyImage${index}`]: false});
                window.$alertError("Failed to upload images. Please try again.");
            }
        } catch (error) {
            console.error("Error uploading assembly images:", error);
            window.$alertError("Failed to upload images. Please try again.");
            this.setState({[`uploadingAssemblyImage${index}`]: false});
        }
    };

    handleAssemblyImageUpload = async (index, event) => {
        const files = Array.from(event.target.files);
        await this.processAssemblyImageFiles(files, index);
        // Clear the file input
        event.target.value = '';
    };

    processSubAssemblyImageFiles = async (files, index) => {
        if (!files || files.length === 0) return;

        // Validate all files
        const invalidFiles = files.filter(file => !file.type.startsWith('image/'));
        if (invalidFiles.length > 0) {
            window.$alertError("Please select valid image files only.");
            return;
        }

        const oversizedFiles = files.filter(file => file.size > 10 * 1024 * 1024);
        if (oversizedFiles.length > 0) {
            window.$alertError("All files must be less than 10MB.");
            return;
        }

        // Initialize imageURLs if it doesn't exist
        if (!editingSubAssembly.subAssembly.imageURLs) {
            editingSubAssembly.subAssembly.imageURLs = editingSubAssembly.subAssembly.imageURL 
                ? editingSubAssembly.subAssembly.imageURL.split('~|~') 
                : [''];
        }

        // Set uploading state
        this.setState({uploadingSubAssemblyImage: true});

        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        const uploadedUrls = [];
        let successCount = 0;
        let errorCount = 0;

        try {
            // Upload all files sequentially
            for (let i = 0; i < files.length; i++) {
                try {
                    const response = await assemblyService.uploadSubAssemblyImage(editingSubAssembly.subAssembly.id, files[i]);
                    uploadedUrls.push(response.imageUrl);
                    successCount++;
                } catch (error) {
                    console.error(`Error uploading subassembly image ${i + 1}:`, error);
                    errorCount++;
                }
            }

            if (uploadedUrls.length > 0) {
                // Find blank slots (empty strings) to replace first
                let blankIndex = editingSubAssembly.subAssembly.imageURLs.findIndex(url => !url || url.trim() === '');
                
                // Replace blank slots first, then append remaining files
                uploadedUrls.forEach((url, i) => {
                    if (blankIndex !== -1 && blankIndex < editingSubAssembly.subAssembly.imageURLs.length) {
                        // Replace the blank slot
                        editingSubAssembly.subAssembly.imageURLs[blankIndex] = url;
                        // Find next blank slot
                        blankIndex = editingSubAssembly.subAssembly.imageURLs.findIndex((url, idx) => idx > blankIndex && (!url || url.trim() === ''));
                    } else {
                        // No more blank slots, append as new
                        editingSubAssembly.subAssembly.imageURLs.push(url);
                    }
                });
                
                this.setState({editingSubAssembly, uploadingSubAssemblyImage: false});
                
                if (successCount === files.length) {
                    window.$alertSuccess(`${successCount} image${successCount > 1 ? 's' : ''} uploaded successfully!`);
                } else {
                    window.$alertSuccess(`${successCount} image${successCount > 1 ? 's' : ''} uploaded successfully. ${errorCount} failed.`);
                }
            } else {
                this.setState({uploadingSubAssemblyImage: false});
                window.$alertError("Failed to upload images. Please try again.");
            }
        } catch (error) {
            console.error("Error uploading subassembly images:", error);
            window.$alertError("Failed to upload images. Please try again.");
            this.setState({uploadingSubAssemblyImage: false});
        }
    };

    handleSubAssemblyImageUpload = async (index, event) => {
        const files = Array.from(event.target.files);
        await this.processSubAssemblyImageFiles(files, index);
        // Clear the file input
        event.target.value = '';
    };

    addNewSubAssemblyImage = () => {
        if (!editingSubAssembly.subAssembly.imageURLs) {
            editingSubAssembly.subAssembly.imageURLs = editingSubAssembly.subAssembly.imageURL 
                ? editingSubAssembly.subAssembly.imageURL.split('~|~') 
                : [''];
        }
        editingSubAssembly.subAssembly.imageURLs.push('');
        this.setState({editingSubAssembly});
    };

    changeSubAssemblyImageURL = (index, event) => {
        if (!editingSubAssembly.subAssembly.imageURLs) {
            editingSubAssembly.subAssembly.imageURLs = editingSubAssembly.subAssembly.imageURL 
                ? editingSubAssembly.subAssembly.imageURL.split('~|~') 
                : [''];
        }
        editingSubAssembly.subAssembly.imageURLs[index] = event.target.value;
        this.setState({editingSubAssembly});
    };

    removeSubAssemblyImage = (imageURL) => {
        if (!editingSubAssembly.subAssembly.imageURLs) {
            editingSubAssembly.subAssembly.imageURLs = editingSubAssembly.subAssembly.imageURL 
                ? editingSubAssembly.subAssembly.imageURL.split('~|~') 
                : [''];
        }
        editingSubAssembly.subAssembly.imageURLs.splice(editingSubAssembly.subAssembly.imageURLs.indexOf(imageURL), 1);
        this.setState({editingSubAssembly});
    };

    handleDragOverAssemblyMain = (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.setState({ isDraggingOverAssemblyMain: true });
    };

    handleDragLeaveAssemblyMain = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!event.currentTarget.contains(event.relatedTarget)) {
            this.setState({ isDraggingOverAssemblyMain: false });
        }
    };

    handleDragOverAssemblyZone = (event, index) => {
        event.preventDefault();
        event.stopPropagation();
        this.setState({ isDraggingOverAssemblyZone: index });
    };

    handleDragLeaveAssemblyZone = (event, index) => {
        event.preventDefault();
        event.stopPropagation();
        if (!event.currentTarget.contains(event.relatedTarget)) {
            this.setState({ isDraggingOverAssemblyZone: null });
        }
    };

    handleAssemblyDrop = async (event, index) => {
        event.preventDefault();
        event.stopPropagation();
        this.setState({ isDraggingOverAssemblyMain: false, isDraggingOverAssemblyZone: null });

        const files = Array.from(event.dataTransfer.files);
        if (files.length > 0) {
            await this.processAssemblyImageFiles(files, index);
        }
    };

    handleDragOverSubAssembly = (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.setState({ isDraggingOverSubAssembly: true });
    };

    handleDragLeaveSubAssembly = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!event.currentTarget.contains(event.relatedTarget)) {
            this.setState({ isDraggingOverSubAssembly: false });
        }
    };

    handleSubAssemblyDrop = async (event, index) => {
        event.preventDefault();
        event.stopPropagation();
        this.setState({ isDraggingOverSubAssembly: false });

        const files = Array.from(event.dataTransfer.files);
        if (files.length > 0) {
            await this.processSubAssemblyImageFiles(files, index);
        }
    };

    processAssemblyFileFiles = async (files, index) => {
        if (!files || files.length === 0) return;

        // Validate file size (50MB limit)
        const oversizedFiles = files.filter(file => file.size > 50 * 1024 * 1024);
        if (oversizedFiles.length > 0) {
            window.$alertError("All files must be less than 50MB.");
            return;
        }

        // Set uploading state for the index
        this.setState({[`uploadingAssemblyFile${index}`]: true});

        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        const uploadedUrls = [];
        let successCount = 0;
        let errorCount = 0;

        try {
            // Upload all files sequentially
            const uploadedFiles = [];
            for (let i = 0; i < files.length; i++) {
                try {
                    const response = await assemblyService.uploadAssemblyFile(editingAssembly.assembly.id, files[i]);
                    // Store as "filename|url" format
                    // API returns { fileUrl, fileName }
                    const fileName = response.fileName || files[i].name || `file_${i + 1}`;
                    const fileUrl = response.fileUrl;
                    if (!fileUrl) {
                        console.error('No fileUrl in response:', response);
                        throw new Error('No fileUrl in upload response');
                    }
                    console.log('Uploaded assembly file:', { fileName, fileUrl, originalFileName: files[i].name, response });
                    uploadedFiles.push({ fileName, fileUrl });
                    successCount++;
                } catch (error) {
                    console.error(`Error uploading assembly file ${i + 1}:`, error);
                    errorCount++;
                }
            }

            if (uploadedFiles.length > 0) {
                // Initialize fileURLs if it doesn't exist, ensure it's an array
                if (!editingAssembly.assembly.fileURLs) {
                    editingAssembly.assembly.fileURLs = [];
                } else if (typeof editingAssembly.assembly.fileURLs === 'string') {
                    // Convert string to array if needed
                    editingAssembly.assembly.fileURLs = editingAssembly.assembly.fileURLs.split('~|~').filter(x => x && x.trim() !== '');
                } else if (!Array.isArray(editingAssembly.assembly.fileURLs)) {
                    editingAssembly.assembly.fileURLs = [];
                }
                
                // Find blank slots (empty strings) to replace first
                let blankIndex = editingAssembly.assembly.fileURLs.findIndex(url => !url || url.trim() === '');
                
                // Replace blank slots first, then append remaining files
                uploadedFiles.forEach((file, i) => {
                    // Store as "filename|url" format
                    const fileEntry = `${file.fileName}|${file.fileUrl}`;
                    console.log('Storing Assembly file entry:', fileEntry, 'from file:', file);
                    if (blankIndex !== -1 && blankIndex < editingAssembly.assembly.fileURLs.length) {
                        // Replace the blank slot
                        editingAssembly.assembly.fileURLs[blankIndex] = fileEntry;
                        // Find next blank slot
                        blankIndex = editingAssembly.assembly.fileURLs.findIndex((url, idx) => idx > blankIndex && (!url || url.trim() === ''));
                    } else {
                        // No more blank slots, append as new
                        editingAssembly.assembly.fileURLs.push(fileEntry);
                    }
                });
                console.log('After upload, Assembly fileURLs array:', editingAssembly.assembly.fileURLs);
                
                this.setState({editingAssembly, [`uploadingAssemblyFile${index}`]: false});
                
                if (successCount === files.length) {
                    window.$alertSuccess(`${successCount} file${successCount > 1 ? 's' : ''} uploaded successfully!`);
                } else {
                    window.$alertSuccess(`${successCount} file${successCount > 1 ? 's' : ''} uploaded successfully. ${errorCount} failed.`);
                }
            } else {
                this.setState({[`uploadingAssemblyFile${index}`]: false});
                window.$alertError("Failed to upload files. Please try again.");
            }
        } catch (error) {
            console.error("Error uploading assembly files:", error);
            window.$alertError("Failed to upload files. Please try again.");
            this.setState({[`uploadingAssemblyFile${index}`]: false});
        }
    };

    handleAssemblyFileUpload = async (index, event) => {
        const files = Array.from(event.target.files);
        await this.processAssemblyFileFiles(files, index);
        // Clear the file input
        event.target.value = '';
    };

    handleDragOverAssemblyFilesMain = (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.setState({ isDraggingOverAssemblyFilesMain: true });
    };

    handleDragLeaveAssemblyFilesMain = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!event.currentTarget.contains(event.relatedTarget)) {
            this.setState({ isDraggingOverAssemblyFilesMain: false });
        }
    };

    handleDragOverAssemblyFilesZone = (event, index) => {
        event.preventDefault();
        event.stopPropagation();
        this.setState({ isDraggingOverAssemblyFilesZone: index });
    };

    handleDragLeaveAssemblyFilesZone = (event, index) => {
        event.preventDefault();
        event.stopPropagation();
        if (!event.currentTarget.contains(event.relatedTarget)) {
            this.setState({ isDraggingOverAssemblyFilesZone: null });
        }
    };

    handleAssemblyFilesDrop = async (event, index) => {
        event.preventDefault();
        event.stopPropagation();
        this.setState({ isDraggingOverAssemblyFilesMain: false, isDraggingOverAssemblyFilesZone: null });

        const files = Array.from(event.dataTransfer.files);
        if (files.length > 0) {
            await this.processAssemblyFileFiles(files, index);
        }
    };

    addNewAssemblyFile = () => {
        if (!editingAssembly.assembly.fileURLs) {
            editingAssembly.assembly.fileURLs = [''];
        }
        editingAssembly.assembly.fileURLs.push('');
        this.setState({editingAssembly});
    };

    changeAssemblyFileURL = (index, event) => {
        if (!editingAssembly.assembly.fileURLs) {
            editingAssembly.assembly.fileURLs = [''];
        }
        editingAssembly.assembly.fileURLs[index] = event.target.value;
        this.setState({editingAssembly});
    };

    removeAssemblyFile = (fileEntry) => {
        // Ensure fileURLs is an array
        if (!editingAssembly.assembly.fileURLs) {
            editingAssembly.assembly.fileURLs = [];
        } else if (typeof editingAssembly.assembly.fileURLs === 'string') {
            // Convert string to array if needed
            editingAssembly.assembly.fileURLs = editingAssembly.assembly.fileURLs.split('~|~').filter(x => x && x.trim() !== '');
        } else if (!Array.isArray(editingAssembly.assembly.fileURLs)) {
            editingAssembly.assembly.fileURLs = [];
        }
        
        const index = editingAssembly.assembly.fileURLs.indexOf(fileEntry);
        if (index !== -1) {
            editingAssembly.assembly.fileURLs.splice(index, 1);
        }
        this.setState({editingAssembly});
    };

    processSubAssemblyFileFiles = async (files, index) => {
        if (!files || files.length === 0) return;

        // Validate file size (50MB limit)
        const oversizedFiles = files.filter(file => file.size > 50 * 1024 * 1024);
        if (oversizedFiles.length > 0) {
            window.$alertError("All files must be less than 50MB.");
            return;
        }

        // Initialize fileURLs if it doesn't exist
        if (!editingSubAssembly.subAssembly.fileURLs) {
            editingSubAssembly.subAssembly.fileURLs = [];
        }

        // Set uploading state
        this.setState({uploadingSubAssemblyFile: true});

        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        const uploadedUrls = [];
        let successCount = 0;
        let errorCount = 0;

        try {
            // Upload all files sequentially
            const uploadedFiles = [];
            for (let i = 0; i < files.length; i++) {
                try {
                    const response = await assemblyService.uploadSubAssemblyFile(editingSubAssembly.subAssembly.id, files[i]);
                    // Store as "filename|url" format
                    // API returns { fileUrl, fileName }
                    const fileName = response.fileName || files[i].name || `file_${i + 1}`;
                    const fileUrl = response.fileUrl;
                    if (!fileUrl) {
                        console.error('No fileUrl in response:', response);
                        throw new Error('No fileUrl in upload response');
                    }
                    console.log('Uploaded subassembly file:', { fileName, fileUrl, originalFileName: files[i].name, response });
                    uploadedFiles.push({ fileName, fileUrl });
                    successCount++;
                } catch (error) {
                    console.error(`Error uploading subassembly file ${i + 1}:`, error);
                    errorCount++;
                }
            }

            if (uploadedFiles.length > 0) {
                // Initialize fileURLs if it doesn't exist, ensure it's an array
                if (!editingSubAssembly.subAssembly.fileURLs) {
                    editingSubAssembly.subAssembly.fileURLs = [];
                } else if (typeof editingSubAssembly.subAssembly.fileURLs === 'string') {
                    // Convert string to array if needed
                    editingSubAssembly.subAssembly.fileURLs = editingSubAssembly.subAssembly.fileURLs.split('~|~').filter(x => x && x.trim() !== '');
                } else if (!Array.isArray(editingSubAssembly.subAssembly.fileURLs)) {
                    editingSubAssembly.subAssembly.fileURLs = [];
                }
                
                // Find blank slots (empty strings) to replace first
                let blankIndex = editingSubAssembly.subAssembly.fileURLs.findIndex(url => !url || url.trim() === '');
                
                // Replace blank slots first, then append remaining files
                uploadedFiles.forEach((file, i) => {
                    // Store as "filename|url" format
                    const fileEntry = `${file.fileName}|${file.fileUrl}`;
                    console.log('Storing SubAssembly file entry:', fileEntry, 'from file:', file);
                    if (blankIndex !== -1 && blankIndex < editingSubAssembly.subAssembly.fileURLs.length) {
                        // Replace the blank slot
                        editingSubAssembly.subAssembly.fileURLs[blankIndex] = fileEntry;
                        // Find next blank slot
                        blankIndex = editingSubAssembly.subAssembly.fileURLs.findIndex((url, idx) => idx > blankIndex && (!url || url.trim() === ''));
                    } else {
                        // No more blank slots, append as new
                        editingSubAssembly.subAssembly.fileURLs.push(fileEntry);
                    }
                });
                console.log('After upload, SubAssembly fileURLs array:', editingSubAssembly.subAssembly.fileURLs);
                
                this.setState({editingSubAssembly, uploadingSubAssemblyFile: false});
                
                if (successCount === files.length) {
                    window.$alertSuccess(`${successCount} file${successCount > 1 ? 's' : ''} uploaded successfully!`);
                } else {
                    window.$alertSuccess(`${successCount} file${successCount > 1 ? 's' : ''} uploaded successfully. ${errorCount} failed.`);
                }
            } else {
                this.setState({uploadingSubAssemblyFile: false});
                window.$alertError("Failed to upload files. Please try again.");
            }
        } catch (error) {
            console.error("Error uploading subassembly files:", error);
            window.$alertError("Failed to upload files. Please try again.");
            this.setState({uploadingSubAssemblyFile: false});
        }
    };

    handleSubAssemblyFileUpload = async (index, event) => {
        const files = Array.from(event.target.files);
        await this.processSubAssemblyFileFiles(files, index);
        // Clear the file input
        event.target.value = '';
    };

    handleDragOverSubAssemblyFiles = (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.setState({ isDraggingOverSubAssemblyFiles: true });
    };

    handleDragLeaveSubAssemblyFiles = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!event.currentTarget.contains(event.relatedTarget)) {
            this.setState({ isDraggingOverSubAssemblyFiles: false });
        }
    };

    handleSubAssemblyFilesDrop = async (event, index) => {
        event.preventDefault();
        event.stopPropagation();
        this.setState({ isDraggingOverSubAssemblyFiles: false });

        const files = Array.from(event.dataTransfer.files);
        if (files.length > 0) {
            await this.processSubAssemblyFileFiles(files, index);
        }
    };

    addNewSubAssemblyFile = () => {
        if (!editingSubAssembly.subAssembly.fileURLs) {
            editingSubAssembly.subAssembly.fileURLs = [''];
        }
        editingSubAssembly.subAssembly.fileURLs.push('');
        this.setState({editingSubAssembly});
    };

    changeSubAssemblyFileURL = (index, event) => {
        if (!editingSubAssembly.subAssembly.fileURLs) {
            editingSubAssembly.subAssembly.fileURLs = [''];
        }
        editingSubAssembly.subAssembly.fileURLs[index] = event.target.value;
        this.setState({editingSubAssembly});
    };

    removeSubAssemblyFile = (fileEntry) => {
        // Ensure fileURLs is an array
        if (!editingSubAssembly.subAssembly.fileURLs) {
            editingSubAssembly.subAssembly.fileURLs = [];
        } else if (typeof editingSubAssembly.subAssembly.fileURLs === 'string') {
            // Convert string to array if needed
            editingSubAssembly.subAssembly.fileURLs = editingSubAssembly.subAssembly.fileURLs.split('~|~').filter(x => x && x.trim() !== '');
        } else if (!Array.isArray(editingSubAssembly.subAssembly.fileURLs)) {
            editingSubAssembly.subAssembly.fileURLs = [];
        }
        
        const index = editingSubAssembly.subAssembly.fileURLs.indexOf(fileEntry);
        if (index !== -1) {
            editingSubAssembly.subAssembly.fileURLs.splice(index, 1);
        }
        this.setState({editingSubAssembly});
    };

    getFileNameFromUrl = (fileEntry) => {
        if (!fileEntry) return 'File';
        
        console.log('getFileNameFromUrl called with:', fileEntry);
        
        // Check if it's in "filename|url" format
        if (fileEntry.includes('|')) {
            const parts = fileEntry.split('|');
            console.log('Split parts:', parts);
            if (parts.length >= 2) {
                const filename = parts[0].trim();
                // Return the filename part if it's not empty
                if (filename && filename.length > 0 && filename !== '') {
                    console.log('Returning filename:', filename);
                    return filename;
                }
                // If filename part is empty, fall through
                console.log('Filename part is empty, falling through');
            }
        }
        
        // Legacy format: just URL - these are old files without filename stored
        // Try to extract something meaningful from the URL, otherwise return "File"
        try {
            const urlObj = new URL(fileEntry);
            const pathname = urlObj.pathname;
            const lastPart = pathname.split('/').pop();
            // If it's a GUID (has dashes and is long), return "File"
            if (lastPart && lastPart.includes('-') && lastPart.length > 30) {
                console.log('Detected GUID, returning File');
                return 'File';
            }
            // Otherwise try to use the last part of the path
            console.log('Returning lastPart:', lastPart);
            return lastPart || 'File';
        } catch (e) {
            console.log('URL parsing failed, returning File');
            return 'File';
        }
        
        // Fallback: try to extract extension and create a generic name
        const urlMatch = fileEntry.match(/\.([a-zA-Z0-9]+)(\?|$)/);
        if (urlMatch) {
            return `file.${urlMatch[1]}`;
        }
        
        return 'file';
    };

    getFileUrlFromEntry = (fileEntry) => {
        if (!fileEntry) return '';
        
        // Check if it's in "filename|url" format
        if (fileEntry.includes('|')) {
            const parts = fileEntry.split('|');
            if (parts.length >= 2) {
                return parts.slice(1).join('|'); // Return everything after first | as URL (in case URL contains |)
            }
        }
        
        // Legacy format: just URL
        return fileEntry;
    };

    getAssemblyFileURLsArray = () => {
        // Ensure fileURLs is always an array
        if (!editingAssembly || !editingAssembly.assembly) return [];
        
        const fileURLs = editingAssembly.assembly.fileURLs;
        if (!fileURLs) return [];
        
        // If it's already an array, return it
        if (Array.isArray(fileURLs)) {
            return fileURLs;
        }
        
        // If it's a string, split it
        if (typeof fileURLs === 'string') {
            return fileURLs.split('~|~').filter(x => x && x.trim() !== '');
        }
        
        return [];
    };

    getSubAssemblyFileURLsArray = () => {
        // Ensure fileURLs is always an array
        if (!editingSubAssembly || !editingSubAssembly.subAssembly) return [];
        
        const fileURLs = editingSubAssembly.subAssembly.fileURLs;
        if (!fileURLs) return [];
        
        // If it's already an array, return it
        if (Array.isArray(fileURLs)) {
            return fileURLs;
        }
        
        // If it's a string, split it
        if (typeof fileURLs === 'string') {
            return fileURLs.split('~|~').filter(x => x && x.trim() !== '');
        }
        
        return [];
    };

    downloadAssemblyFile = (fileEntry, index) => {
        const url = this.getFileUrlFromEntry(fileEntry);
        if (!url) return;
        
        // Use XMLHttpRequest to download the file properly with the correct filename
        const xhr = new XMLHttpRequest();
        xhr.responseType = 'blob';
        
        xhr.onload = function () {
            if (xhr.status === 200) {
                const blob = xhr.response;
                const hyperlinkElement = document.createElement("a");
                const objectUrl = URL.createObjectURL(blob);
                hyperlinkElement.href = objectUrl;
                
                // Try to extract filename from file entry or use a default name
                let filename = this.getFileNameFromUrl(fileEntry);
                
                // If we still have a generic name, add index
                if (filename === 'file' || filename === 'File') {
                    filename = `file_${index + 1}`;
                    // Try to get extension from blob content type
                    const contentType = blob.type;
                    if (contentType) {
                        const extension = contentType.split('/')[1];
                        if (extension && extension !== 'octet-stream') {
                            filename = `${filename}.${extension}`;
                        }
                    }
                }
                
                // Use assembly ID in filename if available
                if (editingAssembly && editingAssembly.assembly && editingAssembly.assembly.assemblyId) {
                    const assemblyId = editingAssembly.assembly.assemblyId.replace(/[^a-zA-Z0-9]/g, '_');
                    filename = `${assemblyId}_${filename}`;
                }
                
                hyperlinkElement.setAttribute("download", filename);
                document.body.appendChild(hyperlinkElement);
                hyperlinkElement.click();
                document.body.removeChild(hyperlinkElement);
                // Clean up the object URL after a delay
                setTimeout(() => URL.revokeObjectURL(objectUrl), 100);
            } else {
                // Fallback: open in new tab if download fails
                window.open(url, '_blank');
            }
        }.bind(this);
        
        xhr.onerror = function () {
            // Fallback: open in new tab if download fails
            window.open(url, '_blank');
        };
        
        xhr.open('GET', url);
        xhr.send();
    };

    downloadSubAssemblyFile = (fileEntry, index) => {
        const url = this.getFileUrlFromEntry(fileEntry);
        if (!url) return;
        
        // Use XMLHttpRequest to download the file properly with the correct filename
        const xhr = new XMLHttpRequest();
        xhr.responseType = 'blob';
        
        xhr.onload = function () {
            if (xhr.status === 200) {
                const blob = xhr.response;
                const hyperlinkElement = document.createElement("a");
                const objectUrl = URL.createObjectURL(blob);
                hyperlinkElement.href = objectUrl;
                
                // Try to extract filename from file entry or use a default name
                let filename = this.getFileNameFromUrl(fileEntry);
                
                // If we still have a generic name, add index
                if (filename === 'file' || filename === 'File') {
                    filename = `file_${index + 1}`;
                    // Try to get extension from blob content type
                    const contentType = blob.type;
                    if (contentType) {
                        const extension = contentType.split('/')[1];
                        if (extension && extension !== 'octet-stream') {
                            filename = `${filename}.${extension}`;
                        }
                    }
                    // Use subassembly ID in filename if available
                    if (editingSubAssembly && editingSubAssembly.subAssembly && editingSubAssembly.subAssembly.subAssemblyId) {
                        const subAssemblyId = editingSubAssembly.subAssembly.subAssemblyId.replace(/[^a-zA-Z0-9]/g, '_');
                        filename = `${subAssemblyId}_${filename}`;
                    }
                }
                
                hyperlinkElement.setAttribute("download", filename);
                document.body.appendChild(hyperlinkElement);
                hyperlinkElement.click();
                document.body.removeChild(hyperlinkElement);
                // Clean up the object URL after a delay
                setTimeout(() => URL.revokeObjectURL(objectUrl), 100);
            } else {
                // Fallback: open in new tab if download fails
                window.open(url, '_blank');
            }
        }.bind(this);
        
        xhr.onerror = function () {
            // Fallback: open in new tab if download fails
            window.open(url, '_blank');
        };
        
        xhr.open('GET', url);
        xhr.send();
    };

    printSubAssemblyLabelSmall = () => {
        if (!this.state.editingSubAssembly || !this.state.editingSubAssembly.subAssembly) return;
        
        const subAssembly = this.state.editingSubAssembly.subAssembly;
        
        // Create HTML for the label
        let containerHtml = '<div class=\'top-gutter\'></div><div class=\'left-gutter\'></div>';
        
        // New 4" x 2" small label design (using only left 3" for content, right 1" is scrap)
        let partNumberFontSize;
        if (subAssembly.subAssemblyId.length >= 18) {
            partNumberFontSize = '20px';
        } else if (subAssembly.subAssemblyId.length >= 16) {
            partNumberFontSize = '24px';
        } else if (subAssembly.subAssemblyId.length >= 14) {
            partNumberFontSize = '28px';
        } else if (subAssembly.subAssemblyId.length >= 12) {
            partNumberFontSize = '32px';
        } else {
            partNumberFontSize = '36px';
        }
        containerHtml += `<div class="label-small-4x2">
            <div class="label-content-area">
                <div class="label-text-area">
                    <div class="part-number" style="font-size: ${partNumberFontSize};">${subAssembly.subAssemblyId}</div>
                    <div class="revision">rev ${subAssembly.revision}</div>
                    <div class="description">${subAssembly.description}</div>
                </div>
                <div class="label-qr-area">
                    <div class="qr-left">
                        <img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(window.location.origin + '/search/' + subAssembly.id)}" style="width: 70px; height: 70px;"/>
                        <div class="qr-label">Homepage</div>
                    </div>
                    <div class="qr-right">
                        <img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${subAssembly.id}" style="width: 70px; height: 70px;"/>
                        <div class="qr-label">BOM Check</div>
                    </div>
                </div>
                <div class="cut-line-top"></div>
                <div class="cut-line-bottom"></div>
            </div>
            <div class="scrap-area"></div>
        </div>`;
        
        containerHtml += '<div class=\'right-gutter\'></div><div class=\'bottom-gutter\'></div>';
        
        // Open a new window and print the label
        const win = window.open("", "Title", `toolbar=no,location=no,directories=no,status=no,menubar=no,scrollbars=yes,resizable=no,width=384,height=1100`);
        win.document.body.innerHTML = `
            <html>
                <head>
                    <style>
                        @media print { @page { margin: 0 !important; } body, html { margin: 0 !important; padding: 0 !important; } }
                        .labels_page {
                            font-family: Montserrat, sans-serif;
                            width: 4in;
                            position: absolute;
                            top: 0;
                            left: 0;
                        }
                        .label-small-4x2 {
                            width: 4in;
                            height: 2in;
                            position: relative;
                            border: 0px solid #000;
                            font-weight: 800;
                            display: flex;
                        }
                        .label-content-area {
                            width: 3in;
                            height: 2in;
                            position: relative;
                            border-right: 1px dashed #666;
                        }
                        .scrap-area {
                            width: 1in;
                            height: 2in;
                            background-color: #f0f0f0;
                        }
                        .label-text-area {
                            width: 100%;
                            height: 0.75in;
                            display: flex;
                            flex-direction: column;
                            justify-content: center;
                            align-items: center;
                            padding: 1px;
                            box-sizing: border-box;
                        }
                        .part-number {
                            font-weight: 900;
                            line-height: 1.0;
                            margin-bottom: 0px;
                        }
                        .revision {
                            font-size: 8px;
                            font-weight: 600;
                            line-height: 1.0;
                            margin-bottom: 0px;
                        }
                        .description {
                            font-size: 7px;
                            font-weight: 500;
                            text-align: center;
                            word-break: break-word;
                            max-width: 100%;
                            line-height: 1.0;
                        }
                        .label-qr-area {
                            width: 100%;
                            height: 1in;
                            display: flex;
                            justify-content: space-around;
                            align-items: center;
                            padding: 1px;
                            box-sizing: border-box;
                        }
                        .qr-left, .qr-right {
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                        }
                        .qr-label {
                            font-size: 10px;
                            margin-top: 1px;
                            font-weight: 600;
                        }
                        .cut-line-top {
                            position: absolute;
                            top: 0.75in;
                            left: 0;
                            right: 0;
                            height: 1px;
                            border-top: 1px dashed #666;
                        }
                        .cut-line-bottom {
                            position: absolute;
                            bottom: 0.25in;
                            left: 0;
                            right: 0;
                            height: 1px;
                            border-top: 1px dashed #666;
                        }
                    </style>
                </head>
                <body>
                    <div id="container" class="labels_page">${containerHtml}</div>
                </body>
            </html>`;
        
        setTimeout(() => {
            win.print();
        }, 2000);
    };

    printSubAssemblyLabelLarge = () => {
        if (!this.state.editingSubAssembly || !this.state.editingSubAssembly.subAssembly) return;
        
        const subAssembly = this.state.editingSubAssembly.subAssembly;
        
        // Create HTML for the label
        let containerHtml = '<div class=\'top-gutter\'></div><div class=\'left-gutter\'></div>';
        
        containerHtml += `<div class="label-standard">
            <div class="label-left">
                <div style="text-align: center;">
                    <img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(window.location.origin + '/search/' + subAssembly.id)}" style="width: 100px; height: 100px;"/>
                    <div style="font-size: 11px; margin-top: 10px;">Homepage</div>
                </div>
            </div>
            <div class="label-center">
                <div style="font-weight: 700; font-size: 19px;">${subAssembly.subAssemblyId}<br />rev ${subAssembly.revision}</div>
            </div>
            <div class="label-right">
                <div style="text-align: center;">
                    <img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${subAssembly.id}" style="width: 100px; height: 100px;"/>
                    <div style="font-size: 11px; margin-top: 10px;">BOM Check</div>
                </div>
            </div>
        </div>`;
        
        containerHtml += '<div class=\'right-gutter\'></div><div class=\'bottom-gutter\'></div>';
        
        // Open a new window and print the label
        const win = window.open("", "Title", "toolbar=no,location=no,directories=no,status=no,menubar=no,scrollbars=yes,resizable=no,width=384,height=1100");
        win.document.body.innerHTML = `
            <html>
                <head>
                    <style>
                        @media print { @page { margin: 0 !important; } body, html { margin: 0 !important; padding: 0 !important; } }
                        .label-left, .label-right {
                            width: 30%;
                            display: inline-block;
                            text-align: center;
                        }
                        .label-center {
                            width: 40%;
                            display: inline-block;
                            text-align: center;
                        }
                        .labels_page {
                            font-family: Montserrat, sans-serif;
                            width: 4in;
                            position: absolute;
                            top: 0;
                            left: 0;
                        }
                        .label-standard {
                            height: 2in;
                            text-align: center;
                            display: flex;
                            align-items: center;
                            position: relative;
                            padding: 0 10px;
                        }
                    </style>
                </head>
                <body>
                    <div id="container" class="labels_page">${containerHtml}</div>
                </body>
            </html>`;
        
        setTimeout(() => {
            win.print();
        }, 2000);
    };

    openImageModal = (imageUrl) => {
        this.setState({
            imageModalOpen: true,
            selectedImageUrl: imageUrl
        });
    };

    closeImageModal = () => {
        this.setState({
            imageModalOpen: false,
            selectedImageUrl: null
        });
    };

    getFirstImageUrl = (imageURLs) => {
        if (!imageURLs || imageURLs.trim() === '') return null;
        const urls = imageURLs.split('~|~');
        return urls[0] || null;
    };

    handleAssemblyStatusChange = (status) => {
        editingAssembly.assembly.status = status;
        this.setState({editingAssembly});
    };

    changeShippingCost = (event) => {
        let v = (event.target.value || '').replace(/[^0-9.]/g, '');
        const parts = v.split('.');
        if (parts.length > 2) v = parts[0] + '.' + parts.slice(1).join('');
        editingAssembly.assembly.shippingCost = v;
        this.setState({editingAssembly});
    };

    changeMSRP = (event) => {
        let v = (event.target.value || '').replace(/[^0-9.]/g, '');
        const parts = v.split('.');
        if (parts.length > 2) v = parts[0] + '.' + parts.slice(1).join('');
        editingAssembly.assembly.msrp = v;
        this.setState({editingAssembly});
    };

    changeWeightPounds = (event) => {
        const pounds = parseInt(event.target.value) || 0;
        editingAssembly.assembly.weightPounds = pounds;
        this.setState({editingAssembly});
    };

    changeWeightOunces = (event) => {
        let ounces = parseInt(event.target.value) || 0;
        
        // Auto-convert ounces to pounds if >= 16
        if (ounces >= 16) {
            const additionalPounds = Math.floor(ounces / 16);
            const remainingOunces = ounces % 16;
            
            editingAssembly.assembly.weightPounds = (editingAssembly.assembly.weightPounds || 0) + additionalPounds;
            editingAssembly.assembly.weightOunces = remainingOunces;
        } else {
            editingAssembly.assembly.weightOunces = ounces;
        }
        
        this.setState({editingAssembly});
    };

    changeInstallVideoURL = (event) => {
        editingAssembly.assembly.installVideoURL = event.target.value;
        this.setState({editingAssembly});
    };

    handleDiscountStrategyChange = (event, newValue) => {
        if (!newValue) this.setState({discountStrategy: null});
        else {
            const matchingDiscountStrategy = this.state.discountStrategies.find(x => x.id === newValue.value);
            editingAssembly.assembly.discountTier1 = matchingDiscountStrategy.discountTier1;
            editingAssembly.assembly.discountTier2 = matchingDiscountStrategy.discountTier2;
            editingAssembly.assembly.discountTier3 = matchingDiscountStrategy.discountTier3;
            this.setState({editingAssembly, matchingDiscountStrategy, discountStrategy: newValue.value});
        }
    };

    changeDiscountTier1 = (event) => {
        editingAssembly.assembly.discountTier1 = event.target.value > 1.00 ? event.target.value / 100 : event.target.value;
        const matchingDiscountStrategy = discountStrategies.find(x => x.discountTier1 === editingAssembly.assembly.discountTier1 && x.discountTier2 === editingAssembly.assembly.discountTier2 && x.discountTier3 === editingAssembly.assembly.discountTier3);
        this.setState({matchingDiscountStrategy, editingAssembly});
    };

    changeDiscountTier2 = (event) => {
        editingAssembly.assembly.discountTier2 = event.target.value > 1.00 ? event.target.value / 100 : event.target.value;
        const matchingDiscountStrategy = discountStrategies.find(x => x.discountTier1 === editingAssembly.assembly.discountTier1 && x.discountTier2 === editingAssembly.assembly.discountTier2 && x.discountTier3 === editingAssembly.assembly.discountTier3);
        this.setState({matchingDiscountStrategy, editingAssembly});
    };

    changeDiscountTier3 = (event) => {
        editingAssembly.assembly.discountTier3 = event.target.value > 1.00 ? event.target.value / 100 : event.target.value;
        const matchingDiscountStrategy = discountStrategies.find(x => x.discountTier1 === editingAssembly.assembly.discountTier1 && x.discountTier2 === editingAssembly.assembly.discountTier2 && x.discountTier3 === editingAssembly.assembly.discountTier3);
        this.setState({matchingDiscountStrategy, editingAssembly});
    };

    changeSpareBushings = (event, newValue) => {
        if (!newValue) {
            editingAssembly.assembly.spareBushingsSetId = null;
            this.setState({editingAssembly});
        } else {
            editingAssembly.assembly.spareBushingsSetId = newValue.id;
            this.setState({editingAssembly});
        }
    };

    changeSpareHardware = (event, newValue) => {
        if (!newValue) {
            editingAssembly.assembly.spareHardwareSetId = null;
            this.setState({editingAssembly});
        } else {
            editingAssembly.assembly.spareHardwareSetId = newValue.id;
            this.setState({editingAssembly});
        }
    };

    saveAssembly = () => {
        const self = this;
        self.setState({savingAssembly: true, chassisInputAssembly: ''});
        const assemblyService = ServiceLocator.Instance.getAssemblyService();

        editingAssembly.assembly.imageURL = editingAssembly.assembly.imageURLs.filter(x => x !== '').join('~|~');
        // Ensure fileURLs is always a string (empty string if no files)
        // First ensure it's an array
        let fileURLsArray = editingAssembly.assembly.fileURLs;
        if (!fileURLsArray) {
            fileURLsArray = [];
        } else if (typeof fileURLsArray === 'string') {
            fileURLsArray = fileURLsArray.split('~|~').filter(x => x && x.trim() !== '');
        } else if (!Array.isArray(fileURLsArray)) {
            fileURLsArray = [];
        }
        // Filter out empty entries and convert to string
        const filteredFileURLs = fileURLsArray.filter(x => x !== '' && x != null && x.trim() !== '');
        editingAssembly.assembly.fileURLs = filteredFileURLs.length > 0 ? filteredFileURLs.join('~|~') : '';
        console.log('Saving Assembly fileURLs:', editingAssembly.assembly.fileURLs, 'from array:', fileURLsArray, 'filtered:', filteredFileURLs);

        if (!this.state.useCustomStoreDescriptionAssembly) {
            editingAssembly.assembly.customerSiteDescription = null;
        } else if (!(editingAssembly.assembly.customerSiteDescription || '').trim()) {
            editingAssembly.assembly.customerSiteDescription = null;
        }

        // Check for duplicate assemblyId/revision combination
        const existingAssembly = assemblies.find(x => 
            x.assembly.assemblyId.toLowerCase().trim() === editingAssembly.assembly.assemblyId.toLowerCase().trim() && 
            x.assembly.revision.toLowerCase().trim() === editingAssembly.assembly.revision.toLowerCase().trim() &&
            x.assembly.id !== editingAssembly.assembly.id // Exclude current assembly when updating
        );
        
        const existingSubAssembly = subAssemblies.find(x => 
            x.subAssembly.subAssemblyId.toLowerCase().trim() === editingAssembly.assembly.assemblyId.toLowerCase().trim() && 
            x.subAssembly.revision.toLowerCase().trim() === editingAssembly.assembly.revision.toLowerCase().trim()
        );
        
        if (existingAssembly || existingSubAssembly) {
            self.setState({savingAssembly: false});
            return window.$alertWarning("An assembly already exists with that Assembly Number and Revision!");
        }

        // Convert fileURLs to string if it's an array
        let fileURLsString = '';
        if (Array.isArray(editingAssembly.assembly.fileURLs)) {
            fileURLsString = editingAssembly.assembly.fileURLs.filter(x => x !== '' && x != null && x.trim() !== '').join('~|~');
        } else if (typeof editingAssembly.assembly.fileURLs === 'string') {
            fileURLsString = editingAssembly.assembly.fileURLs;
        }
        
        if (this.state.newFamilyName !== '') {
            const familyPayload = {
                id: uuid(),
                assemblyFamilyId: this.state.newFamilyName,
                description: this.state.newFamilyDescription
            };
            assemblyService.createAssemblyFamily(familyPayload).then((newFamily) => {
                editingAssembly.assembly.assemblyFamilyId = newFamily.id;
                editingAssembly.assembly.assemblyFamily = newFamily;
                if (editingAssembly.assembly.id === '') {
                    editingAssembly.assembly.id = uuid();
                    const assemblyToSave = {
                        ...editingAssembly.assembly,
                        fileURLs: fileURLsString
                    };
                    assemblyService.createAssembly(assemblyToSave).then((part) => {
                        window.$alertSuccess("Assembly created");
                        self.setState({editAssemblyOpen: false, savingAssembly: false, assemblyFamily: newFamily.id});
                        self.loadData();
                    });
                } else {
                    const assemblyToUpdate = {
                        ...editingAssembly.assembly,
                        fileURLs: fileURLsString,
                        partsToAssemblies: [],
                        subAssembliesToAssemblies: []
                    };
                    assemblyService.updateAssembly(assemblyToUpdate).then((part) => {
                        window.$alertSuccess("Assembly updated");
                        self.setState({editAssemblyOpen: false, savingAssembly: false, assemblyFamily: newFamily.id});
                        self.loadData();
                    });
                }
            });
        } else {
            if (editingAssembly.assembly.id === '') {
                editingAssembly.assembly.id = uuid();
                const assemblyToSave = {
                    ...editingAssembly.assembly,
                    fileURLs: fileURLsString
                };
                assemblyService.createAssembly(assemblyToSave).then((part) => {
                    window.$alertSuccess("Assembly created");
                    self.setState({editAssemblyOpen: false, savingAssembly: false});
                    self.loadData();
                });
            } else {
                const assemblyToUpdate = {
                    ...this.state.editingAssembly.assembly,
                    fileURLs: fileURLsString,
                    partsToAssemblies: [],
                    subAssembliesToAssemblies: []
                };
                assemblyService.updateAssembly(assemblyToUpdate).then((part) => {
                    window.$alertSuccess("Assembly updated");
                    self.setState({editAssemblyOpen: false, savingAssembly: false});
                    self.loadData();
                });
            }
        }
    };

    deactivateAssembly = () => {
        const confirm = window.confirm("Are you sure you want to deactivate this assembly?");
        if (!confirm) return;

        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        editingAssembly.assembly.isActive = false;
        
        // Convert fileURLs to string if it's an array
        let fileURLsString = '';
        if (Array.isArray(editingAssembly.assembly.fileURLs)) {
            fileURLsString = editingAssembly.assembly.fileURLs.filter(x => x !== '' && x != null && x.trim() !== '').join('~|~');
        } else if (typeof editingAssembly.assembly.fileURLs === 'string') {
            fileURLsString = editingAssembly.assembly.fileURLs;
        }
        
        const assemblyToUpdate = {
            ...editingAssembly.assembly,
            fileURLs: fileURLsString
        };
        
        assemblyService.updateAssembly(assemblyToUpdate).then(() => {
            window.$alertSuccess("Assembly deleted");
            self.setState({editAssemblyOpen: false, savingAssembly: false});
            globalProps.history.push('/admin/assemblies');
        });
    };

    reactivateAssembly = () => {
        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        editingAssembly.assembly.isActive = true;
        
        // Convert fileURLs to string if it's an array
        let fileURLsString = '';
        if (Array.isArray(editingAssembly.assembly.fileURLs)) {
            fileURLsString = editingAssembly.assembly.fileURLs.filter(x => x !== '' && x != null && x.trim() !== '').join('~|~');
        } else if (typeof editingAssembly.assembly.fileURLs === 'string') {
            fileURLsString = editingAssembly.assembly.fileURLs;
        }
        
        const assemblyToUpdate = {
            ...editingAssembly.assembly,
            fileURLs: fileURLsString
        };
        
        assemblyService.updateAssembly(assemblyToUpdate).then(() => {
            window.$alertSuccess("Assembly reactivated");
            self.setState({editingAssembly, editAssemblyOpen: false, savingAssembly: false});
        });
    };

    handleDiscontinueAssembly = () => {
        const confirm = window.confirm("Are you sure you want to discontinue this assembly? This will mark all revisions with the same Assembly ID as discontinued.");
        if (!confirm) return;

        const self = this;
        const apiUrl = process.env.REACT_APP_API_URL || '';
        const jwtToken = AuthenticationService.getInstance().getToken();
        
        axios.put(`${apiUrl}assemblies/assembly/${editingAssembly.assembly.id}/discontinue`, 
            { isDiscontinued: true },
            { headers: { 'Authorization': `Bearer ${jwtToken}` } }
        ).then(() => {
            window.$alertSuccess("Assembly discontinued");
            self.loadData();
        }).catch(error => {
            console.error('Error discontinuing assembly:', error);
            window.$alertWarning("Error discontinuing assembly");
        });
    };

    handleActivateAssembly = () => {
        const confirm = window.confirm("Are you sure you want to activate this assembly? This will mark all revisions with the same Assembly ID as active.");
        if (!confirm) return;

        const self = this;
        const apiUrl = process.env.REACT_APP_API_URL || '';
        const jwtToken = AuthenticationService.getInstance().getToken();
        
        axios.put(`${apiUrl}assemblies/assembly/${editingAssembly.assembly.id}/discontinue`, 
            { isDiscontinued: false },
            { headers: { 'Authorization': `Bearer ${jwtToken}` } }
        ).then(() => {
            window.$alertSuccess("Assembly activated");
            self.loadData();
        }).catch(error => {
            console.error('Error activating assembly:', error);
            window.$alertWarning("Error activating assembly");
        });
    };

    deleteAssembly = () => {
        // Check revision-specific sales data, not total sales across all revisions
        const totalSoldWithRev = this.state.editingAssembly?.totalSoldWithRev ?? this.state.inventoryData?.totalSoldWithRev ?? 0;
        const totalAssembledWithRev = this.state.editingAssembly?.totalAssembledWithRev ?? this.state.inventoryData?.totalAssembledWithRev ?? 0;
        
        if (this.state.editingAssembly && (totalSoldWithRev > 0 || totalAssembledWithRev > 0)) {
            return window.$alertWarning("You cannot delete this assembly revision. It has sales or assembly data tied to it.");
        }

        // Check for dependencies before deletion
        const dependencies = [];
        const currentAssembly = this.state.editingAssembly || editingAssembly;
        


        // Check if this assembly has parts (following the pattern from EditPart.js)
        const partsInAssembly = currentAssembly.assembly.partsToAssemblies || [];
        if (partsInAssembly.length > 0) {
            dependencies.push(`${partsInAssembly.length} parts`);
        }

        // Check if this assembly has subassemblies
        const subAssembliesInAssembly = currentAssembly.assembly.subAssembliesToAssemblies || [];
        if (subAssembliesInAssembly.length > 0) {
            dependencies.push(`${subAssembliesInAssembly.length} subassemblies`);
        }

        // If there are dependencies, show detailed error message
        if (dependencies.length > 0) {
            const dependencyList = dependencies.join(", ");
            return window.$alertError(`Cannot delete assembly '${currentAssembly.assembly.assemblyId}' because of its dependencies. Clear the BOM before deleting the assembly.`);
        }

        const confirm = window.confirm("Are you sure you want to permanently delete this assembly? This cannot be undone.");
        if (!confirm) return;

        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        assemblyService.deleteAssembly(currentAssembly.assembly.id).then(() => {
            window.$alertSuccess("Assembly deleted");
            self.setState({editAssemblyOpen: false, savingAssembly: false});
            globalProps.history.push('/admin/assemblies');
        }).catch((error) => {
            console.error('Error deleting assembly:', error);
            window.$alertError('Failed to delete assembly. Please try again.');
        });
    };

    changeSubAssemblyTypeSelection = (event, newValue) => {
        if (!newValue || newValue === 'New...') {
            editingSubAssembly.subAssembly.assemblyType = !newValue ? '' : newValue;
            this.setState({editingSubAssembly, newAssemblyType: true});
        } else {
            editingSubAssembly.subAssembly.assemblyType = newValue;
            this.setState({editingSubAssembly, newAssemblyType: false});
        }
    };

    changeSubAssemblyType = (event) => {
        editingSubAssembly.subAssembly.assemblyType = event.target.value;
        this.setState({editingSubAssembly});
    };

    changeSubManufacturerForSelection = (event, newValue) => {
        if (!newValue || newValue.subType === 'New...') {
            editingSubAssembly.subAssembly.manufacturerFor = !newValue ? '' : newValue.subType;
            this.setState({editingSubAssembly, newManufacturerFor: true});
        } else {
            editingSubAssembly.subAssembly.manufacturerFor = newValue.subType;
            this.setState({editingSubAssembly, newManufacturerFor: false});
        }
    };

    changeSubManufacturerFor = (event) => {
        editingSubAssembly.subAssembly.manufacturerFor = event.target.value;
        this.setState({editingSubAssembly});
    };

    changeSubAssemblyId = (event) => {
        editingSubAssembly.subAssembly.subAssemblyId = event.target.value;
        this.setState({editingSubAssembly});
    };

    changeSubRevision = (event) => {
        editingSubAssembly.subAssembly.revision = event.target.value;
        this.setState({editingSubAssembly});
    };

    changeSubRevisionNotes = (event) => {
        editingSubAssembly.subAssembly.revisionNotes = event.target.value;
        this.setState({editingSubAssembly});
    };

    changeSubWeightPounds = (event) => {
        const pounds = parseInt(event.target.value) || 0;
        editingSubAssembly.subAssembly.weightPounds = pounds;
        this.setState({editingSubAssembly});
    };

    changeSubWeightOunces = (event) => {
        let ounces = parseInt(event.target.value) || 0;

        if (ounces >= 16) {
            const additionalPounds = Math.floor(ounces / 16);
            const remainingOunces = ounces % 16;

            editingSubAssembly.subAssembly.weightPounds = (editingSubAssembly.subAssembly.weightPounds || 0) + additionalPounds;
            editingSubAssembly.subAssembly.weightOunces = remainingOunces;
        } else {
            editingSubAssembly.subAssembly.weightOunces = ounces;
        }

        this.setState({editingSubAssembly});
    };

    changeSubBomNotes = (event) => {
        editingSubAssembly.subAssembly.bomNotes = event.target.value;
        this.setState({editingSubAssembly});
    };

    changeSubDescription = (event) => {
        editingSubAssembly.subAssembly.description = event.target.value;
        this.setState({editingSubAssembly});
    };

    toggleUseCustomStoreDescriptionSub = (event) => {
        const checked = event.target.checked;
        if (!checked) {
            editingSubAssembly.subAssembly.customerSiteDescription = '';
        }
        this.setState({ useCustomStoreDescriptionSub: checked, editingSubAssembly });
    };

    changeCustomerSiteDescriptionSub = (event) => {
        editingSubAssembly.subAssembly.customerSiteDescription = event.target.value;
        this.setState({ editingSubAssembly });
    };

    changeSubImageURL = (event) => {
        editingSubAssembly.subAssembly.imageURL = event.target.value;
        this.setState({editingSubAssembly});
    };

    saveSubAssembly = () => {
        const self = this;
        self.setState({savingSubAssembly: true, chassisInputSubAssembly: ''});
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        
        // Convert imageURLs array back to imageURL string
        if (editingSubAssembly.subAssembly.imageURLs) {
            editingSubAssembly.subAssembly.imageURL = editingSubAssembly.subAssembly.imageURLs.filter(x => x !== '').join('~|~');
        }
        // Convert fileURLs array back to fileURLs string
        // Ensure fileURLs is always a string (empty string if no files)
        // First ensure it's an array
        let fileURLsArray = editingSubAssembly.subAssembly.fileURLs;
        if (!fileURLsArray) {
            fileURLsArray = [];
        } else if (typeof fileURLsArray === 'string') {
            fileURLsArray = fileURLsArray.split('~|~').filter(x => x && x.trim() !== '');
        } else if (!Array.isArray(fileURLsArray)) {
            fileURLsArray = [];
        }
        // Filter out empty entries and convert to string
        const filteredSubFileURLs = fileURLsArray.filter(x => x !== '' && x != null && x.trim() !== '');
        editingSubAssembly.subAssembly.fileURLs = filteredSubFileURLs.length > 0 ? filteredSubFileURLs.join('~|~') : '';
        console.log('Saving SubAssembly fileURLs:', editingSubAssembly.subAssembly.fileURLs, 'from array:', fileURLsArray, 'filtered:', filteredSubFileURLs);

        if (!this.state.useCustomStoreDescriptionSub) {
            editingSubAssembly.subAssembly.customerSiteDescription = null;
        } else if (!(editingSubAssembly.subAssembly.customerSiteDescription || '').trim()) {
            editingSubAssembly.subAssembly.customerSiteDescription = null;
        }
        
        // Check for duplicate subAssemblyId/revision combination
        const existingSubAssembly = subAssemblies.find(x => 
            x.subAssembly.subAssemblyId.toLowerCase().trim() === editingSubAssembly.subAssembly.subAssemblyId.toLowerCase().trim() && 
            x.subAssembly.revision.toLowerCase().trim() === editingSubAssembly.subAssembly.revision.toLowerCase().trim() &&
            x.subAssembly.id !== editingSubAssembly.subAssembly.id // Exclude current subassembly when updating
        );
        
        const existingAssembly = assemblies.find(x => 
            x.assembly.assemblyId.toLowerCase().trim() === editingSubAssembly.subAssembly.subAssemblyId.toLowerCase().trim() && 
            x.assembly.revision.toLowerCase().trim() === editingSubAssembly.subAssembly.revision.toLowerCase().trim()
        );
        
        if (existingSubAssembly || existingAssembly) {
            self.setState({savingSubAssembly: false});
            return window.$alertWarning("An assembly already exists with that SubAssembly Number and Revision!");
        }
        
        // Convert fileURLs to string if it's an array
        let fileURLsString = '';
        if (Array.isArray(editingSubAssembly.subAssembly.fileURLs)) {
            fileURLsString = editingSubAssembly.subAssembly.fileURLs.filter(x => x !== '' && x != null && x.trim() !== '').join('~|~');
        } else if (typeof editingSubAssembly.subAssembly.fileURLs === 'string') {
            fileURLsString = editingSubAssembly.subAssembly.fileURLs;
        }
        
        if (editingSubAssembly.subAssembly.id === '') {
            editingSubAssembly.subAssembly.id = uuid();
            const subAssemblyToSave = {
                ...editingSubAssembly.subAssembly,
                fileURLs: fileURLsString
            };
            assemblyService.createSubAssembly(subAssemblyToSave).then((assembly) => {
                window.$alertSuccess("Assembly created");
                self.setState({editSubAssemblyOpen: false, savingSubAssembly: false});
                self.loadData();
            });
        } else {
            const subAssemblyToUpdate = {
                ...this.state.editingSubAssembly.subAssembly,
                fileURLs: fileURLsString,
                partsToSubAssemblies: [],
                subAssembliesToSubAssemblies: []
            };
            assemblyService.updateSubAssembly(subAssemblyToUpdate).then((subAssembly) => {
                window.$alertSuccess("Assembly updated");
                self.setState({editSubAssemblyOpen: false, savingSubAssembly: false});
                self.loadData();
            });
        }
    };

    deactivateSubAssembly = () => {
        const confirm = window.confirm("Are you sure you want to deactivate this subassembly?");
        if (!confirm) return;

        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        editingSubAssembly.subAssembly.isActive = false;
        
        // Convert fileURLs to string if it's an array
        let fileURLsString = '';
        if (Array.isArray(editingSubAssembly.subAssembly.fileURLs)) {
            fileURLsString = editingSubAssembly.subAssembly.fileURLs.filter(x => x !== '' && x != null && x.trim() !== '').join('~|~');
        } else if (typeof editingSubAssembly.subAssembly.fileURLs === 'string') {
            fileURLsString = editingSubAssembly.subAssembly.fileURLs;
        }
        
        const subAssemblyToUpdate = {
            ...editingSubAssembly.subAssembly,
            fileURLs: fileURLsString
        };
        
        assemblyService.updateSubAssembly(subAssemblyToUpdate).then(() => {
            window.$alertSuccess("Subassembly deactivated");
            self.setState({editSubAssemblyOpen: false, savingSubAssembly: false});
            globalProps.history.push('/admin/assemblies');
        });
    };

    reactivateSubAssembly = () => {
        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        editingSubAssembly.subAssembly.isActive = true;
        
        // Convert fileURLs to string if it's an array
        let fileURLsString = '';
        if (Array.isArray(editingSubAssembly.subAssembly.fileURLs)) {
            fileURLsString = editingSubAssembly.subAssembly.fileURLs.filter(x => x !== '' && x != null && x.trim() !== '').join('~|~');
        } else if (typeof editingSubAssembly.subAssembly.fileURLs === 'string') {
            fileURLsString = editingSubAssembly.subAssembly.fileURLs;
        }
        
        const subAssemblyToUpdate = {
            ...editingSubAssembly.subAssembly,
            fileURLs: fileURLsString
        };
        
        assemblyService.updateSubAssembly(subAssemblyToUpdate).then(() => {
            window.$alertSuccess("Subassembly reactivated");
            self.setState({editingSubAssembly, editSubAssemblyOpen: false, savingSubAssembly: false});
        });
    };

    deleteSubAssembly = () => {
        if (this.state.editingSubAssembly && this.state.editingSubAssembly.totalSold > 0) {
            return window.$alertWarning("You cannot delete this subassembly. It has sales data tied to it.");
        }

        // Check for dependencies before deletion
        const dependencies = [];
        const currentSubAssembly = this.state.editingSubAssembly || editingSubAssembly;
        const subAssemblyId = currentSubAssembly.subAssembly.id;



        // Check if this subassembly has parts (following the pattern from EditPart.js)
        const partsInSubAssembly = currentSubAssembly.subAssembly.partsToSubAssemblies || [];
        if (partsInSubAssembly.length > 0) {
            dependencies.push(`${partsInSubAssembly.length} parts`);
        }

        // Check if this subassembly has child subassemblies
        const childSubAssemblies = currentSubAssembly.subAssembly.childSubAssemblies || [];
        if (childSubAssemblies.length > 0) {
            const childNames = childSubAssemblies.map(child => {
                // Find the child subassembly to get its name
                const childSubAssembly = subAssemblies.find(sa => sa.subAssembly.id === child.childSubAssemblyFk);
                return childSubAssembly ? childSubAssembly.subAssembly.subAssemblyId : child.childSubAssemblyFk;
            }).join(", ");
            dependencies.push(`${childSubAssemblies.length} child subassemblies (${childNames})`);
        }

        // Check if this subassembly is used as a child in other subassemblies
        const usedAsChildInSubAssemblies = subAssemblies.filter(sa => 
            sa.subAssembly.childSubAssemblies && 
            sa.subAssembly.childSubAssemblies.some(child => child.childSubAssemblyFk === subAssemblyId)
        );
        
        if (usedAsChildInSubAssemblies.length > 0) {
            const subAssemblyNames = usedAsChildInSubAssemblies.map(sa => sa.subAssembly.subAssemblyId).join(", ");
            dependencies.push(`${usedAsChildInSubAssemblies.length} parent subassemblies (${subAssemblyNames})`);
        }

        // Check if this subassembly is used in assemblies
        const usedInAssemblies = assemblies.filter(a => 
            a.assembly.subAssembliesToAssemblies && 
            a.assembly.subAssembliesToAssemblies.some(sta => sta.subAssemblyFk === subAssemblyId)
        );
        if (usedInAssemblies.length > 0) {
            const assemblyNames = usedInAssemblies.map(a => a.assembly.assemblyId).join(", ");
            dependencies.push(`${usedInAssemblies.length} assemblies (${assemblyNames})`);
        }



        // If there are dependencies, show detailed error message
        if (dependencies.length > 0) {
            const dependencyList = dependencies.join(", ");
            return window.$alertError(`Cannot delete subassembly '${currentSubAssembly.subAssembly.subAssemblyId}' because of its dependencies. Clear the BOM before deleting the subassembly.`);
        }

        const confirm = window.confirm("Are you sure you want to permanently delete this subassembly? This cannot be undone.");
        if (!confirm) return;

        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        assemblyService.deleteSubAssembly(currentSubAssembly.subAssembly.id).then(() => {
            window.$alertSuccess("SubAssembly deleted");
            self.setState({editSubAssemblyOpen: false, savingSubAssembly: false});
            globalProps.history.push('/admin/assemblies');
        }).catch((error) => {
            console.error('Error deleting subassembly:', error);
            window.$alertError('Failed to delete subassembly. Please try again.');
        });
    };

    severPartToSubAssembly = (partId, subAssemblyId) => {
        // No confirmation needed - changes are now deferred to "Save All Changes"

        const self = this;
        const partService = ServiceLocator.Instance.getPartService();
        partService.severPartToSubAssembly(partId, subAssemblyId).then(() => {
            window.$alertSuccess("BOM changes saved successfully");
            self.loadData();
        });
    };

    severPartToAssembly = (partId, assemblyId) => {
        // No confirmation needed - changes are now deferred to "Save All Changes"

        const self = this;
        const partService = ServiceLocator.Instance.getPartService();
        partService.severPartToAssembly(partId, assemblyId).then(() => {
            window.$alertSuccess("BOM changes saved successfully");
            self.loadData();
        });
    };

    severSubAssemblyToAssembly = (subAssemblyId, assemblyId) => {
        // No confirmation needed - changes are now deferred to "Save All Changes"

        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        assemblyService.severSubAssemblyToAssembly(subAssemblyId, assemblyId).then(() => {
            window.$alertSuccess("BOM changes saved successfully");
            self.loadData();
        });
    };

    severSubAssemblyToSubAssembly = (childSubAssemblyId, parentSubAssemblyId) => {
        // No confirmation needed - changes are now deferred to "Save All Changes"

        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        assemblyService.severSubAssemblyToSubAssembly(childSubAssemblyId, parentSubAssemblyId).then(() => {
            window.$alertSuccess("BOM changes saved successfully");
            self.loadData();
        });
    };

    deleteBomConnectionById = async (item) => {
        if (!item?.id) return;
        const partService = ServiceLocator.Instance.getPartService();
        const assemblyService = ServiceLocator.Instance.getAssemblyService();

        if (this.state.editingAssembly) {
            if (item.subAssemblyFk) {
                await assemblyService.deleteSubAssemblyToAssemblyConnection(item.id);
            } else if (item.partFk) {
                await partService.deletePartsToAssemblyConnection(item.id);
            }
        } else if (this.state.editingSubAssembly) {
            if (item.childSubAssemblyFk || item.ChildSubAssemblyFk) {
                await assemblyService.deleteSubAssemblyToSubAssemblyConnection(item.id);
            } else if (item.partFk) {
                await partService.deletePartsToSubAssemblyConnection(item.id);
            }
        }
    };

    handleConnectionFromChange = (event, newValue) => {
        if (!newValue) this.setState({newConnectionFrom: null});
        else {
            this.setState({newConnectionFrom: newValue.value});
        }
    };

    saveConnection = () => {
        const self = this;
        if (this.state.newConnectionFrom === '' || !this.state.newConnectionFrom) return window.$alertWarning("You must select a part to connect");

        let confirm = true;
        if (this.state.editingAssembly && this.state.editingAssembly.totalSold > 0)
            confirm = window.confirm("Editing this BOM changes records tied to sales data. You are about to alter a lot of data. Are you sure you wish to continue?");
        if (this.state.editingSubAssembly && this.state.editingSubAssembly.totalSold > 0)
            confirm = window.confirm("Editing this BOM changes records tied to sales data. You are about to alter a lot of data. Are you sure you wish to continue?");

        if (!confirm) return;

        const partService = ServiceLocator.Instance.getPartService();
        const assemblyService = ServiceLocator.Instance.getAssemblyService();

        // Check if it's a family
        const family = this.state.partFamilies.find(x => x.id === this.state.newConnectionFrom);

        if (family) {
            // Get all parts that belong to this family through PartToFamily relationship
            const familyParts = this.state.parts.filter(part => {
                const isInFamily = part.part.partToFamilies && 
                    part.part.partToFamilies.some(ptf => ptf.familyId === family.id);
                return isInFamily;
            });
            
            if (familyParts.length === 0) {
                return window.$alertWarning("No parts found in the selected family.");
            }
            
            // Add all parts from the family
            const promises = familyParts.map(part => {
                // Get the PartToFamily relationship for this part and family
                const partToFamily = part.part.partToFamilies.find(ptf => ptf.familyId === family.id);
                const packageQuantity = parseInt(partToFamily?.packageQuantity) || 1;
                const connectionQuantity = parseInt(this.state.newConnectionQuantity) || 1;
                const totalQuantity = packageQuantity * connectionQuantity;



                if (self.state.editingSubAssembly) {
                    // Calculate the order for this family part
                    const allItems = [...self.state.editingSubAssembly.subAssembly.partsToSubAssemblies];
                    const maxOrder = allItems.length > 0 ? Math.max(...allItems.map(item => item.order || 0)) : 0;
                    const order = maxOrder + 1 + familyParts.indexOf(part);
                    
                    return partService.connectPartToSubAssembly(
                        part.part.id, 
                        self.state.editingSubAssembly.subAssembly.id, 
                        totalQuantity.toString(),
                        self.state.newConnectionBomRequired,
                        family.id, // Pass the family ID
                        order
                    );
                } else if (self.state.editingAssembly) {
                    // Calculate the order for this family part
                    const allItems = [
                        ...self.state.editingAssembly.assembly.partsToAssemblies,
                        ...self.state.editingAssembly.assembly.subAssembliesToAssemblies
                    ];
                    const maxOrder = allItems.length > 0 ? Math.max(...allItems.map(item => item.order || 0)) : 0;
                    const order = maxOrder + 1 + familyParts.indexOf(part);
                    
                    return partService.connectPartToAssembly(
                        part.part.id,
                        self.state.editingAssembly.assembly.id,
                        totalQuantity.toString(),
                        self.state.newConnectionBomRequired,
                        family.id, // Pass the family ID
                        order
                    );
                }
            }).filter(promise => promise !== undefined);
            
            if (promises.length === 0) {
                return window.$alertWarning("Unable to create connections for the selected family.");
            }
            
            self.setState({savingConnection: true});
            Promise.all(promises).then((results) => {
                window.$alertSuccess(`Parent-child connections created for ${promises.length} parts from family ${family.partFamilyId}`);
                self.setState({savingConnection: false, newConnectionFrom: null, newConnectionQuantity: 1, newConnectionBomRequired: true});
                // Reload data to ensure all items are properly loaded
                self.loadData();
            }).catch((error) => {
                console.error("Error creating family connections:", error);
                window.$alertError("Failed to create some or all family connections. Please check the console for details.");
                self.setState({savingConnection: false});
            });
            return;
        }

        // Handle regular parts and subassemblies
        if (this.state.parts.find(x => x.part.id === this.state.newConnectionFrom) !== undefined) {
            const quantity = parseNumberInputValue(this.state.newConnectionQuantity);
            if (this.state.editingSubAssembly) {
                self.setState({savingConnection: true});
                // Calculate the order for this part
                const allItems = [...this.state.editingSubAssembly.subAssembly.partsToSubAssemblies];
                const maxOrder = allItems.length > 0 ? Math.max(...allItems.map(item => item.order || 0)) : 0;
                const order = maxOrder + 1;
                
                partService.connectPartToSubAssembly(
                    this.state.newConnectionFrom, 
                    this.state.editingSubAssembly.subAssembly.id, 
                    quantity.toString(), 
                    this.state.newConnectionBomRequired,
                    undefined, // partFamilyId
                    order
                ).then(() => {
                    window.$alertSuccess("Parent-child connection created");
                    // Optimistically add the new row so it shows immediately without waiting for loadData
                    const updatedSubAssembly = { ...self.state.editingSubAssembly };
                    updatedSubAssembly.subAssembly.partsToSubAssemblies = [
                        ...(updatedSubAssembly.subAssembly.partsToSubAssemblies || []),
                        {
                            id: uuid(),
                            partFk: self.state.newConnectionFrom,
                            subAssemblyFk: self.state.editingSubAssembly.subAssembly.id,
                            quantity: quantity,
                            requiredForBomCheck: self.state.newConnectionBomRequired,
                            order: order
                        }
                    ];
                    self.setState({
                        savingConnection: false,
                        newConnectionFrom: null,
                        newConnectionQuantity: 1,
                        newConnectionBomRequired: true,
                        editingSubAssembly: updatedSubAssembly
                    });
                    // Reload data to ensure all items are properly loaded (ids, etc.)
                    self.loadData();
                }).catch((error) => {
                    console.error("Error creating part connection:", error);
                    window.$alertError("Failed to create connection. Please try again.");
                    self.setState({savingConnection: false});
                });
            } else if (this.state.editingAssembly) {
                self.setState({savingConnection: true});
                // Calculate the order for this part
                const allItems = [
                    ...this.state.editingAssembly.assembly.partsToAssemblies,
                    ...this.state.editingAssembly.assembly.subAssembliesToAssemblies
                ];
                const maxOrder = allItems.length > 0 ? Math.max(...allItems.map(item => item.order || 0)) : 0;
                const order = maxOrder + 1;
                
                partService.connectPartToAssembly(
                    this.state.newConnectionFrom, 
                    this.state.editingAssembly.assembly.id, 
                    quantity.toString(), 
                    this.state.newConnectionBomRequired,
                    undefined, // partFamilyId
                    order
                ).then(() => {
                    window.$alertSuccess("Parent-child connection created");
                    // Optimistically add the new row so it shows immediately without waiting for loadData
                    const updatedAssembly = { ...self.state.editingAssembly };
                    updatedAssembly.assembly.partsToAssemblies = [
                        ...(updatedAssembly.assembly.partsToAssemblies || []),
                        {
                            id: uuid(),
                            partFk: self.state.newConnectionFrom,
                            assemblyFk: self.state.editingAssembly.assembly.id,
                            quantity: quantity,
                            requiredForBomCheck: self.state.newConnectionBomRequired,
                            order: order
                        }
                    ];
                    self.setState({
                        savingConnection: false,
                        newConnectionFrom: null,
                        newConnectionQuantity: 1,
                        newConnectionBomRequired: true,
                        editingAssembly: updatedAssembly
                    });
                    // Reload data to ensure all items are properly loaded (ids, etc.)
                    self.loadData();
                }).catch((error) => {
                    console.error("Error creating part connection:", error);
                    window.$alertError("Failed to create connection. Please try again.");
                    self.setState({savingConnection: false});
                });
            }
        } else if (this.state.subAssemblies.find(x => x.subAssembly.id === this.state.newConnectionFrom) !== undefined) {
            const quantity = parseNumberInputValue(this.state.newConnectionQuantity);
            if (this.state.editingAssembly) {
                self.setState({savingConnection: true});
                // Calculate the order for this subassembly
                const allItems = [
                    ...this.state.editingAssembly.assembly.partsToAssemblies,
                    ...this.state.editingAssembly.assembly.subAssembliesToAssemblies
                ];
                const maxOrder = allItems.length > 0 ? Math.max(...allItems.map(item => item.order || 0)) : 0;
                const order = maxOrder + 1;
                
                assemblyService.connectSubAssemblyToAssembly(
                    this.state.newConnectionFrom, 
                    this.state.editingAssembly.assembly.id, 
                    quantity.toString(), 
                    this.state.newConnectionBomRequired,
                    order
                ).then(() => {
                    window.$alertSuccess("Parent-child connection created");
                    // Optimistically add the new row so it shows immediately without waiting for loadData
                    const updatedAssembly = { ...self.state.editingAssembly };
                    updatedAssembly.assembly.subAssembliesToAssemblies = [
                        ...(updatedAssembly.assembly.subAssembliesToAssemblies || []),
                        {
                            id: uuid(),
                            subAssemblyFk: self.state.newConnectionFrom,
                            assemblyFk: self.state.editingAssembly.assembly.id,
                            quantity: quantity,
                            requiredForBomCheck: self.state.newConnectionBomRequired,
                            order: order
                        }
                    ];
                    self.setState({
                        savingConnection: false,
                        newConnectionFrom: null,
                        newConnectionQuantity: 1,
                        newConnectionBomRequired: true,
                        editingAssembly: updatedAssembly
                    });
                    // Reload data to ensure all items are properly loaded (ids, etc.)
                    self.loadData();
                }).catch((error) => {
                    console.error("Error creating subassembly connection:", error);
                    window.$alertError("Failed to create connection. Please try again.");
                    self.setState({savingConnection: false});
                });
            }
        }
    };



    cloneNewRevision = () => {
        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        
        if (!this.state.newRevision || this.state.newRevision.trim() === '') {
            window.$alertError("Please enter a revision number");
            return;
        }

        if (editingAssembly) {
            // Check for duplicate assemblyId/revision combination
            const existingAssembly = assemblies.find(x => 
                x.assembly.assemblyId.toLowerCase().trim() === editingAssembly.assembly.assemblyId.toLowerCase().trim() && 
                x.assembly.revision.toLowerCase().trim() === this.state.newRevision.trim().toLowerCase().trim() &&
                x.assembly.id !== editingAssembly.assembly.id // Exclude current assembly when updating
            );
            
            const existingSubAssembly = subAssemblies.find(x => 
                x.subAssembly.subAssemblyId.toLowerCase().trim() === editingAssembly.assembly.assemblyId.toLowerCase().trim() && 
                x.subAssembly.revision.toLowerCase().trim() === this.state.newRevision.trim().toLowerCase().trim()
            );
            
            if (existingAssembly || existingSubAssembly) {
                return window.$alertWarning("An assembly already exists with that Assembly Number and Revision!");
            }

            // Check if there are images or files to clone
            const hasImages = editingAssembly.assembly.imageURLs && editingAssembly.assembly.imageURLs.length > 0 && editingAssembly.assembly.imageURLs.some(url => url && url.trim() !== '');
            const hasFiles = editingAssembly.assembly.fileURLs && (
                (Array.isArray(editingAssembly.assembly.fileURLs) && editingAssembly.assembly.fileURLs.length > 0 && editingAssembly.assembly.fileURLs.some(url => url && url.trim() !== '')) ||
                (typeof editingAssembly.assembly.fileURLs === 'string' && editingAssembly.assembly.fileURLs.trim() !== '')
            );
            
            // Determine image and file values based on checkbox (defaults to true)
            const cloneImagesAndFiles = this.state.cloneImagesAndFiles !== false; // Default to true
            let imageURL = '';
            let imageURLs = [];
            let fileURLs = '';
            
            if (cloneImagesAndFiles) {
                // Clone images if they exist
                if (hasImages && editingAssembly.assembly.imageURLs) {
                    imageURLs = [...editingAssembly.assembly.imageURLs];
                    imageURL = editingAssembly.assembly.imageURL || '';
                }
                
                // Clone files if they exist
                if (hasFiles && editingAssembly.assembly.fileURLs) {
                    if (typeof editingAssembly.assembly.fileURLs === 'string') {
                        fileURLs = editingAssembly.assembly.fileURLs;
                    } else if (Array.isArray(editingAssembly.assembly.fileURLs)) {
                        fileURLs = editingAssembly.assembly.fileURLs.filter(x => x && x.trim() !== '').join('~|~');
                    }
                }
            }
            
            const newAssembly = {
                ...editingAssembly.assembly,
                id: uuid(),
                revision: this.state.newRevision,
                revisionNotes: this.state.newRevisionNotes || '',
                imageURL: imageURL, // Clone or clear images
                imageURLs: imageURLs, // Clone or clear images
                fileURLs: fileURLs, // Clone or clear files
                currentlyPackaging: false,
                finalOrderNumber: null,
                partsToAssemblies: [], // Will be cloned separately
                subAssembliesToAssemblies: [], // Will be cloned separately,
                assemblyFamily: null,
                assemblyFamilyId: null,
                isDiscontinued: editingAssembly.assembly.isDiscontinued || false
            };

            assemblyService.createAssembly(newAssembly).then(async (createdAssembly) => {
                try {
                    // Clone parts to assembly connections with unique UUIDs
                    if (editingAssembly.assembly.partsToAssemblies && editingAssembly.assembly.partsToAssemblies.length > 0) {
                        const partsToAssemblies = editingAssembly.assembly.partsToAssemblies.map(pta => ({
                            id: uuid(), // Generate unique UUID for each BOM entry
                            partFk: pta.partFk,
                            assemblyFk: createdAssembly.id,
                            quantity: pta.quantity,
                            requiredForBomCheck: pta.requiredForBomCheck,
                            partFamilyId: pta.partFamilyId,
                            order: pta.order
                        }));
                        
                        await assemblyService.importPartsToAssemblies(partsToAssemblies);
                    }

                    // Clone subassemblies to assembly connections with unique UUIDs
                    if (editingAssembly.assembly.subAssembliesToAssemblies && editingAssembly.assembly.subAssembliesToAssemblies.length > 0) {
                        const subAssembliesToAssemblies = editingAssembly.assembly.subAssembliesToAssemblies.map(sata => ({
                            id: uuid(), // Generate unique UUID for each BOM entry
                            subAssemblyFk: sata.subAssemblyFk,
                            assemblyFk: createdAssembly.id,
                            quantity: sata.quantity,
                            requiredForBomCheck: sata.requiredForBomCheck,
                            order: sata.order
                        }));
                        
                        await assemblyService.importSubAssembliesToAssemblies(subAssembliesToAssemblies);
                    }

                    window.$alertSuccess("New assembly revision created");
                    self.setState({newRevision: '', newRevisionNotes: ''});
                    globalProps.history.push(`/admin/assemblies/${createdAssembly.id}`);
                    window.location.reload();
                } catch (error) {
                    console.error("Error cloning BOM connections:", error);
                    window.$alertError("Assembly revision created but some BOM connections failed to clone");
                    globalProps.history.push(`/admin/assemblies/${createdAssembly.id}`);
                    window.location.reload();
                }
            }).catch((error) => {
                console.error("Error creating new assembly revision:", error);
                window.$alertError("Error creating new assembly revision");
            });
        } else if (editingSubAssembly) {
            // Check for duplicate subAssemblyId/revision combination
            const existingSubAssembly = subAssemblies.find(x => 
                x.subAssembly.subAssemblyId.toLowerCase().trim() === editingSubAssembly.subAssembly.subAssemblyId.toLowerCase().trim() && 
                x.subAssembly.revision.toLowerCase().trim() === this.state.newRevision.trim().toLowerCase().trim() &&
                x.subAssembly.id !== editingSubAssembly.subAssembly.id // Exclude current subassembly when updating
            );
            
            const existingAssembly = assemblies.find(x => 
                x.assembly.assemblyId.toLowerCase().trim() === editingSubAssembly.subAssembly.subAssemblyId.toLowerCase().trim() && 
                x.assembly.revision.toLowerCase().trim() === this.state.newRevision.trim().toLowerCase().trim()
            );
            
            if (existingSubAssembly || existingAssembly) {
                return window.$alertWarning("An assembly already exists with that SubAssembly Number and Revision!");
            }

            // Check if there are images or files to clone
            const hasImages = editingSubAssembly.subAssembly.imageURLs && editingSubAssembly.subAssembly.imageURLs.length > 0 && editingSubAssembly.subAssembly.imageURLs.some(url => url && url.trim() !== '');
            const hasFiles = editingSubAssembly.subAssembly.fileURLs && (
                (Array.isArray(editingSubAssembly.subAssembly.fileURLs) && editingSubAssembly.subAssembly.fileURLs.length > 0 && editingSubAssembly.subAssembly.fileURLs.some(url => url && url.trim() !== '')) ||
                (typeof editingSubAssembly.subAssembly.fileURLs === 'string' && editingSubAssembly.subAssembly.fileURLs.trim() !== '')
            );
            
            // Determine image and file values based on checkbox (defaults to true)
            const cloneImagesAndFiles = this.state.cloneImagesAndFiles !== false; // Default to true
            let imageURL = '';
            let imageURLs = [];
            let fileURLs = '';
            
            if (cloneImagesAndFiles) {
                // Clone images if they exist
                if (hasImages && editingSubAssembly.subAssembly.imageURLs) {
                    imageURLs = [...editingSubAssembly.subAssembly.imageURLs];
                    imageURL = editingSubAssembly.subAssembly.imageURL || '';
                }
                
                // Clone files if they exist
                if (hasFiles && editingSubAssembly.subAssembly.fileURLs) {
                    if (typeof editingSubAssembly.subAssembly.fileURLs === 'string') {
                        fileURLs = editingSubAssembly.subAssembly.fileURLs;
                    } else if (Array.isArray(editingSubAssembly.subAssembly.fileURLs)) {
                        fileURLs = editingSubAssembly.subAssembly.fileURLs.filter(x => x && x.trim() !== '').join('~|~');
                    }
                }
            }
            
            const newSubAssembly = {
                ...editingSubAssembly.subAssembly,
                id: uuid(),
                revision: this.state.newRevision,
                revisionNotes: this.state.newRevisionNotes || '',
                imageURL: imageURL, // Clone or clear images
                imageURLs: imageURLs, // Clone or clear images
                fileURLs: fileURLs, // Clone or clear files
                currentlyPackaging: false,
                partsToSubAssemblies: [], // Will be cloned separately
                subAssembliesToSubAssemblies: [] // Will be cloned separately,
            };

            assemblyService.createSubAssembly(newSubAssembly).then(async (createdSubAssembly) => {
                try {
                    // Clone parts to subassembly connections with unique UUIDs
                    if (editingSubAssembly.subAssembly.partsToSubAssemblies && editingSubAssembly.subAssembly.partsToSubAssemblies.length > 0) {
                        const partsToSubAssemblies = editingSubAssembly.subAssembly.partsToSubAssemblies.map(pts => ({
                            id: uuid(), // Generate unique UUID for each BOM entry
                            partFk: pts.partFk,
                            subAssemblyFk: createdSubAssembly.id,
                            quantity: pts.quantity,
                            requiredForBomCheck: pts.requiredForBomCheck,
                            partFamilyId: pts.partFamilyId,
                            order: pts.order
                        }));
                        
                        await assemblyService.importPartsToSubAssemblies(partsToSubAssemblies);
                    }

                    window.$alertSuccess("New subassembly revision created");
                    self.setState({newRevision: '', newRevisionNotes: ''});
                    globalProps.history.push(`/admin/assemblies/${createdSubAssembly.id}`);
                    window.location.reload();
                } catch (error) {
                    console.error("Error cloning BOM connections:", error);
                    window.$alertError("Subassembly revision created but some BOM connections failed to clone");
                    globalProps.history.push(`/admin/assemblies/${createdSubAssembly.id}`);
                    window.location.reload();
                }
            }).catch((error) => {
                console.error("Error creating new subassembly revision:", error);
                window.$alertError("Error creating new subassembly revision");
            });
        }
    };

    cloneAssembly = () => {
        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        
        // Check if there are images or files to clone
        const hasImages = editingAssembly.assembly.imageURLs && editingAssembly.assembly.imageURLs.length > 0 && editingAssembly.assembly.imageURLs.some(url => url && url.trim() !== '');
        const hasFiles = editingAssembly.assembly.fileURLs && (
            (Array.isArray(editingAssembly.assembly.fileURLs) && editingAssembly.assembly.fileURLs.length > 0 && editingAssembly.assembly.fileURLs.some(url => url && url.trim() !== '')) ||
            (typeof editingAssembly.assembly.fileURLs === 'string' && editingAssembly.assembly.fileURLs.trim() !== '')
        );
        
        // Create cloned assembly data with new GUIDs (images/files will be set in handleCloneAssemblySubmit based on checkbox)
        const clonedAssembly = {
            ...editingAssembly.assembly,
            id: uuid(),
            assemblyId: '', // Will be set by user
            revision: '', // Will be set by user
            imageURL: '', // Will be set in handleCloneAssemblySubmit if checkbox is checked
            imageURLs: [], // Will be set in handleCloneAssemblySubmit if checkbox is checked
            fileURLs: '', // Will be set in handleCloneAssemblySubmit if checkbox is checked
            partsToAssemblies: [], // Will be cloned separately
            subAssembliesToAssemblies: [], // Will be cloned separately,
            finalOrderNumber: null,
            assemblyFamily: null,
            currentlyPackaging: true,
            isDiscontinued: false // New clones should not be discontinued
        };

        // Store cloned data in session storage for the clone dialog
        sessionStorage.setItem('clonedAssemblyData', JSON.stringify(clonedAssembly));
        
        // Open clone dialog with checkbox defaulting to true if there are images/files
        this.setState({
            showCloneDialog: true,
            clonedAssemblyData: clonedAssembly,
            isCloningAssembly: true,
            cloneImagesAndFiles: (hasImages || hasFiles) // Default to true if there are images/files
        });
    };

    cloneSubAssembly = () => {
        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        
        // Check if there are images or files to clone
        const hasImages = editingSubAssembly.subAssembly.imageURLs && editingSubAssembly.subAssembly.imageURLs.length > 0 && editingSubAssembly.subAssembly.imageURLs.some(url => url && url.trim() !== '');
        const hasFiles = editingSubAssembly.subAssembly.fileURLs && (
            (Array.isArray(editingSubAssembly.subAssembly.fileURLs) && editingSubAssembly.subAssembly.fileURLs.length > 0 && editingSubAssembly.subAssembly.fileURLs.some(url => url && url.trim() !== '')) ||
            (typeof editingSubAssembly.subAssembly.fileURLs === 'string' && editingSubAssembly.subAssembly.fileURLs.trim() !== '')
        );
        
        // Create cloned subassembly data with new GUIDs (images/files will be set in handleCloneSubAssemblySubmit based on checkbox)
        const clonedSubAssembly = {
            ...editingSubAssembly.subAssembly,
            id: uuid(),
            subAssemblyId: '', // Will be set by user
            revision: '', // Will be set by user
            imageURL: '', // Will be set in handleCloneSubAssemblySubmit if checkbox is checked
            imageURLs: [], // Will be set in handleCloneSubAssemblySubmit if checkbox is checked
            fileURLs: '', // Will be set in handleCloneSubAssemblySubmit if checkbox is checked
            partsToSubAssemblies: [], // Will be cloned separately
            assemblyFamilyId: null, // Clear family relationship
            assemblyFamily: null,
            currentlyPackaging: true
        };

        // Store cloned data in session storage for the clone dialog
        sessionStorage.setItem('clonedSubAssemblyData', JSON.stringify(clonedSubAssembly));
        
        // Open clone dialog with checkbox defaulting to true if there are images/files
        this.setState({
            showCloneDialog: true,
            clonedSubAssemblyData: clonedSubAssembly,
            isCloningAssembly: false,
            cloneImagesAndFiles: (hasImages || hasFiles) // Default to true if there are images/files
        });
    };

    handleCloneAssemblySubmit = () => {
        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        const partService = ServiceLocator.Instance.getPartService();
        
        if (!this.state.clonedAssemblyData.assemblyId || this.state.clonedAssemblyData.assemblyId.trim() === '') {
            window.$alertError("Please enter an assembly number");
            return;
        }

        if (!this.state.clonedAssemblyData.revision || this.state.clonedAssemblyData.revision.trim() === '') {
            window.$alertError("Please enter a revision");
            return;
        }

        // Check for duplicate assemblyId/revision combination
        const existingAssembly = assemblies.find(x => 
            x.assembly.assemblyId.toLowerCase().trim() === this.state.clonedAssemblyData.assemblyId.toLowerCase().trim() && 
            x.assembly.revision.toLowerCase().trim() === this.state.clonedAssemblyData.revision.toLowerCase().trim()
        );
        
        const existingSubAssembly = subAssemblies.find(x => 
            x.subAssembly.subAssemblyId.toLowerCase().trim() === this.state.clonedAssemblyData.assemblyId.toLowerCase().trim() && 
            x.subAssembly.revision.toLowerCase().trim() === this.state.clonedAssemblyData.revision.toLowerCase().trim()
        );
        
        if (existingAssembly || existingSubAssembly) {
            return window.$alertWarning("An assembly already exists with that Assembly Number and Revision!");
        }

        // Determine image and file values based on checkbox
        const hasImages = editingAssembly.assembly.imageURLs && editingAssembly.assembly.imageURLs.length > 0 && editingAssembly.assembly.imageURLs.some(url => url && url.trim() !== '');
        const hasFiles = editingAssembly.assembly.fileURLs && (
            (Array.isArray(editingAssembly.assembly.fileURLs) && editingAssembly.assembly.fileURLs.length > 0 && editingAssembly.assembly.fileURLs.some(url => url && url.trim() !== '')) ||
            (typeof editingAssembly.assembly.fileURLs === 'string' && editingAssembly.assembly.fileURLs.trim() !== '')
        );
        
        let imageURL = '';
        let imageURLs = [];
        let fileURLs = '';
        
        if (this.state.cloneImagesAndFiles !== false) {
            // Clone images if they exist
            if (hasImages && editingAssembly.assembly.imageURLs) {
                imageURLs = [...editingAssembly.assembly.imageURLs];
                imageURL = editingAssembly.assembly.imageURL || '';
            }
            
            // Clone files if they exist
            if (hasFiles && editingAssembly.assembly.fileURLs) {
                if (typeof editingAssembly.assembly.fileURLs === 'string') {
                    fileURLs = editingAssembly.assembly.fileURLs;
                } else if (Array.isArray(editingAssembly.assembly.fileURLs)) {
                    fileURLs = editingAssembly.assembly.fileURLs.filter(x => x && x.trim() !== '').join('~|~');
                }
            }
        }
        
        // Update cloned data with images/files
        const clonedAssemblyToSave = {
            ...this.state.clonedAssemblyData,
            imageURL: imageURL,
            imageURLs: imageURLs,
            fileURLs: fileURLs
        };

        // First create the assembly
        assemblyService.createAssembly(clonedAssemblyToSave).then(async (createdAssembly) => {
            try {
                // Clone parts to assembly connections with unique UUIDs
                if (editingAssembly.assembly.partsToAssemblies && editingAssembly.assembly.partsToAssemblies.length > 0) {
                    const partsToAssemblies = editingAssembly.assembly.partsToAssemblies.map(pta => ({
                        id: uuid(), // Generate unique UUID for each BOM entry
                        partFk: pta.partFk,
                        assemblyFk: createdAssembly.id,
                        quantity: pta.quantity,
                        requiredForBomCheck: pta.requiredForBomCheck,
                        partFamilyId: pta.partFamilyId,
                        order: pta.order
                    }));
                    
                    await assemblyService.importPartsToAssemblies(partsToAssemblies);
                }

                // Clone subassemblies to assembly connections with unique UUIDs
                if (editingAssembly.assembly.subAssembliesToAssemblies && editingAssembly.assembly.subAssembliesToAssemblies.length > 0) {
                    const subAssembliesToAssemblies = editingAssembly.assembly.subAssembliesToAssemblies.map(sata => ({
                        id: uuid(), // Generate unique UUID for each BOM entry
                        subAssemblyFk: sata.subAssemblyFk,
                        assemblyFk: createdAssembly.id,
                        quantity: sata.quantity,
                        requiredForBomCheck: sata.requiredForBomCheck,
                        order: sata.order
                    }));
                    
                    await assemblyService.importSubAssembliesToAssemblies(subAssembliesToAssemblies);
                }

                window.$alertSuccess("Assembly cloned successfully with all BOM connections");
                self.setState({
                    showCloneDialog: false,
                    clonedAssemblyData: null,
                    isCloningAssembly: false
                });
                sessionStorage.removeItem('clonedAssemblyData');
                globalProps.history.push(`/admin/assemblies/${createdAssembly.id}`);
                window.location.reload();
            } catch (error) {
                console.error("Error cloning BOM connections:", error);
                window.$alertError("Assembly created but some BOM connections failed to clone");
                globalProps.history.push(`/admin/assemblies/${createdAssembly.id}`);
                window.location.reload();
            }
        }).catch((error) => {
            console.error("Error cloning assembly:", error);
            window.$alertError("Error cloning assembly");
        });
    };

    handleCloneSubAssemblySubmit = () => {
        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        const partService = ServiceLocator.Instance.getPartService();
        
        if (!this.state.clonedSubAssemblyData.subAssemblyId || this.state.clonedSubAssemblyData.subAssemblyId.trim() === '') {
            window.$alertError("Please enter a subassembly number");
            return;
        }

        if (!this.state.clonedSubAssemblyData.revision || this.state.clonedSubAssemblyData.revision.trim() === '') {
            window.$alertError("Please enter a revision");
            return;
        }

        // Check for duplicate subAssemblyId/revision combination
        const existingSubAssembly = subAssemblies.find(x => 
            x.subAssembly.subAssemblyId.toLowerCase().trim() === this.state.clonedSubAssemblyData.subAssemblyId.toLowerCase().trim() && 
            x.subAssembly.revision.toLowerCase().trim() === this.state.clonedSubAssemblyData.revision.toLowerCase().trim()
        );
        
        const existingAssembly = assemblies.find(x => 
            x.assembly.assemblyId.toLowerCase().trim() === this.state.clonedSubAssemblyData.subAssemblyId.toLowerCase().trim() && 
            x.assembly.revision.toLowerCase().trim() === this.state.clonedSubAssemblyData.revision.toLowerCase().trim()
        );
        
        if (existingSubAssembly || existingAssembly) {
            return window.$alertWarning("An assembly already exists with that SubAssembly Number and Revision!");
        }

        // Determine image and file values based on checkbox
        const hasImages = editingSubAssembly.subAssembly.imageURLs && editingSubAssembly.subAssembly.imageURLs.length > 0 && editingSubAssembly.subAssembly.imageURLs.some(url => url && url.trim() !== '');
        const hasFiles = editingSubAssembly.subAssembly.fileURLs && (
            (Array.isArray(editingSubAssembly.subAssembly.fileURLs) && editingSubAssembly.subAssembly.fileURLs.length > 0 && editingSubAssembly.subAssembly.fileURLs.some(url => url && url.trim() !== '')) ||
            (typeof editingSubAssembly.subAssembly.fileURLs === 'string' && editingSubAssembly.subAssembly.fileURLs.trim() !== '')
        );
        
        let imageURL = '';
        let imageURLs = [];
        let fileURLs = '';
        
        if (this.state.cloneImagesAndFiles !== false) {
            // Clone images if they exist
            if (hasImages && editingSubAssembly.subAssembly.imageURLs) {
                imageURLs = [...editingSubAssembly.subAssembly.imageURLs];
                imageURL = editingSubAssembly.subAssembly.imageURL || '';
            }
            
            // Clone files if they exist
            if (hasFiles && editingSubAssembly.subAssembly.fileURLs) {
                if (typeof editingSubAssembly.subAssembly.fileURLs === 'string') {
                    fileURLs = editingSubAssembly.subAssembly.fileURLs;
                } else if (Array.isArray(editingSubAssembly.subAssembly.fileURLs)) {
                    fileURLs = editingSubAssembly.subAssembly.fileURLs.filter(x => x && x.trim() !== '').join('~|~');
                }
            }
        }
        
        // Update cloned data with images/files
        const clonedSubAssemblyToSave = {
            ...this.state.clonedSubAssemblyData,
            imageURL: imageURL,
            imageURLs: imageURLs,
            fileURLs: fileURLs
        };

        // First create the subassembly
        assemblyService.createSubAssembly(clonedSubAssemblyToSave).then(async (createdSubAssembly) => {
            try {
                // Clone parts to subassembly connections with unique UUIDs
                if (editingSubAssembly.subAssembly.partsToSubAssemblies && editingSubAssembly.subAssembly.partsToSubAssemblies.length > 0) {
                    const partsToSubAssemblies = editingSubAssembly.subAssembly.partsToSubAssemblies.map(pts => ({
                        id: uuid(), // Generate unique UUID for each BOM entry
                        partFk: pts.partFk,
                        subAssemblyFk: createdSubAssembly.id,
                        quantity: pts.quantity,
                        requiredForBomCheck: pts.requiredForBomCheck,
                        partFamilyId: pts.partFamilyId,
                        order: pts.order
                    }));
                    
                    await assemblyService.importPartsToSubAssemblies(partsToSubAssemblies);
                }

                window.$alertSuccess("Subassembly cloned successfully with all BOM connections");
                self.setState({
                    showCloneDialog: false,
                    clonedSubAssemblyData: null,
                    isCloningAssembly: false,
                    cloneImagesAndFiles: true // Reset to default
                });
                sessionStorage.removeItem('clonedSubAssemblyData');
                globalProps.history.push(`/admin/assemblies/${createdSubAssembly.id}`);
                window.location.reload();
            } catch (error) {
                console.error("Error cloning BOM connections:", error);
                window.$alertError("Subassembly created but some BOM connections failed to clone");
                globalProps.history.push(`/admin/assemblies/${createdSubAssembly.id}`);
                window.location.reload();
            }
        }).catch((error) => {
            console.error("Error cloning subassembly:", error);
            window.$alertError("Error cloning subassembly");
        });
    };

    cancelClone = () => {
        this.setState({
            showCloneDialog: false,
            clonedAssemblyData: null,
            clonedSubAssemblyData: null,
            isCloningAssembly: false,
            cloneImagesAndFiles: true // Reset to default
        });
        sessionStorage.removeItem('clonedAssemblyData');
        sessionStorage.removeItem('clonedSubAssemblyData');
    };

    changeClonedAssemblyId = (event) => {
        const clonedData = { ...this.state.clonedAssemblyData };
        clonedData.assemblyId = event.target.value;
        this.setState({ clonedAssemblyData: clonedData });
    };

    changeClonedAssemblyRevision = (event) => {
        const clonedData = { ...this.state.clonedAssemblyData };
        clonedData.revision = event.target.value;
        this.setState({ clonedAssemblyData: clonedData });
    };

    changeClonedSubAssemblyId = (event) => {
        const clonedData = { ...this.state.clonedSubAssemblyData };
        clonedData.subAssemblyId = event.target.value;
        this.setState({ clonedSubAssemblyData: clonedData });
    };

    changeClonedSubAssemblyRevision = (event) => {
        const clonedData = { ...this.state.clonedSubAssemblyData };
        clonedData.revision = event.target.value;
        this.setState({ clonedSubAssemblyData: clonedData });
    };

    // Bulk BOM editing methods
    startBulkBomEdit = () => {
        this.setState({
            isBulkEditingBom: true,
            bomChanges: {},
            newBomItem: {
                id: uuid(),
                itemType: 'part',
                partFk: null,
                subAssemblyFk: null,
                partFamilyId: null,
                quantity: 1,
                requiredForBomCheck: true,
                isImpliedPart: false,
                order: 0,
                isNew: true
            }
        }, () => {
            this.setState(prevState => ({
                newBomItem: {
                    ...prevState.newBomItem,
                    order: this.getNextBomOrder()
                }
            }));
        });
    };

    cancelBulkBomEdit = () => {
        this.setState({
            isBulkEditingBom: false,
            newBomItem: null,
            newBomItems: [],
            bomChanges: {}
        });
    };

    getBomItemCount = () => {
        if (this.state.editingAssembly) {
            return this.state.editingAssembly.assembly.subAssembliesToAssemblies.length + 
                   this.state.editingAssembly.assembly.partsToAssemblies.length;
        } else if (this.state.editingSubAssembly) {
            return this.state.editingSubAssembly.subAssembly.partsToSubAssemblies.length +
                   (this.state.editingSubAssembly.subAssembly.subAssembliesToSubAssemblies || []).length;
        }
        return 0;
    };

    getNextBomOrder = () => {
        let existingItems = [];
        if (this.state.editingAssembly) {
            existingItems = this.groupItemsForBomTable(
                this.state.editingAssembly.assembly.subAssembliesToAssemblies,
                this.state.editingAssembly.assembly.partsToAssemblies
            );
        } else if (this.state.editingSubAssembly) {
            existingItems = this.groupItemsForBomTable(
                [],
                this.state.editingSubAssembly.subAssembly.partsToSubAssemblies,
                this.state.editingSubAssembly.subAssembly.subAssembliesToSubAssemblies || []
            );
        }

        const orders = existingItems.map(i => i.order ?? 0);
        (this.state.newBomItems || []).forEach(i => orders.push(i.order ?? 0));
        const pending = this.state.newBomItem;
        if (pending && (pending.partFk || pending.subAssemblyFk || pending.partFamilyId)) {
            orders.push(pending.order ?? 0);
        }

        return orders.length === 0 ? 0 : Math.max(...orders) + 1;
    };

    enrichNewBomItemWithMetadata = (item) => {
        if (!item?.partFamilyId) return item;
        const family = this.state.partFamilies.find(f => f.id === item.partFamilyId);
        if (!family) return item;
        return {
            ...item,
            partFamilyName: family.partFamilyId,
            partFamilyDescription: family.description,
            familyName: family.partFamilyId,
        };
    };

    getPendingNewBomItemsForSave = () => {
        const items = (this.state.newBomItems || []).map(i => this.enrichNewBomItemWithMetadata(i));
        const { newBomItem } = this.state;
        if (newBomItem && (newBomItem.partFk || newBomItem.subAssemblyFk || newBomItem.partFamilyId)) {
            items.push(this.enrichNewBomItemWithMetadata({
                ...newBomItem,
                order: newBomItem.order ?? this.getNextBomOrder(),
            }));
        }
        return items;
    };

    updateBomItem = (itemId, field, value) => {
        this.setState(prevState => ({
            bomChanges: {
                ...prevState.bomChanges,
                [itemId]: {
                    ...prevState.bomChanges[itemId],
                    [field]: value
                }
            }
        }));
    };

    updateNewBomItem = (field, value) => {
        this.setState(prevState => ({
            newBomItem: {
                ...prevState.newBomItem,
                [field]: value
            }
        }));
    };

    addNewBomItem = () => {
        const { newBomItem } = this.state;
        if (!newBomItem.partFk && !newBomItem.subAssemblyFk && !newBomItem.partFamilyId) return;

        const itemToAdd = this.enrichNewBomItemWithMetadata({
            ...newBomItem,
            order: this.getNextBomOrder(),
        });

        this.setState(prevState => ({
            newBomItems: [...(prevState.newBomItems || []), itemToAdd],
            newBomItem: {
                id: uuid(),
                itemType: 'part',
                partFk: null,
                subAssemblyFk: null,
                partFamilyId: null,
                quantity: 1,
                requiredForBomCheck: true,
                isImpliedPart: false,
                order: 0,
                isNew: true
            }
        }), () => {
            this.setState(prevState => ({
                newBomItem: {
                    ...prevState.newBomItem,
                    order: this.getNextBomOrder()
                }
            }));

            // Focus the autocomplete field after state update
            setTimeout(() => {
                // Try multiple approaches to find the autocomplete input
                let autocompleteInput = null;
                
                // Approach 1: Look for the new BOM item row by background color
                const newBomRow = document.querySelector('tr[style*="f0fff0"]');
                if (newBomRow) {
                    autocompleteInput = newBomRow.querySelector('input[placeholder*="Select part or subassembly"]') ||
                                       newBomRow.querySelector('.MuiInputBase-input') ||
                                       newBomRow.querySelector('input[type="text"]') ||
                                       newBomRow.querySelector('input:not([type="number"]):not([type="checkbox"])');
                }
                
                // Approach 2: Look for the last autocomplete input on the page (should be the new one)
                if (!autocompleteInput) {
                    const allAutocompleteInputs = document.querySelectorAll('input[placeholder*="Select part or subassembly"]');
                    if (allAutocompleteInputs.length > 0) {
                        autocompleteInput = allAutocompleteInputs[allAutocompleteInputs.length - 1];
                    }
                }
                
                // Approach 3: Look for any MUI autocomplete input that's currently empty
                if (!autocompleteInput) {
                    const allInputs = document.querySelectorAll('.MuiInputBase-input');
                    for (const input of allInputs) {
                        if (input.value === '' && input.placeholder && input.placeholder.includes('Select')) {
                            autocompleteInput = input;
                            break;
                        }
                    }
                }
                
                if (autocompleteInput) {
                    autocompleteInput.focus();
                    autocompleteInput.click();
                    // Force focus in case the first attempt didn't work
                    setTimeout(() => autocompleteInput.focus(), 50);
                }
            }, 200); // Increased delay to ensure MUI components are fully rendered
        });
    };

    /**
     * Same payload as "Save Changes" for a finished good assembly: header fields + images/files,
     * with empty BOM arrays (BOM is updated via separate APIs). Does not call loadData.
     */
    persistAssemblyHeaderWithoutBomReload = async () => {
        if (!this.state.editingAssembly?.assembly?.id || this.state.editingAssembly.assembly.id === '') {
            return;
        }
        const assemblyService = ServiceLocator.Instance.getAssemblyService();

        if (this.state.editingAssembly !== editingAssembly) {
            editingAssembly = this.state.editingAssembly;
        }

        editingAssembly.assembly.imageURL = editingAssembly.assembly.imageURLs.filter(x => x !== '').join('~|~');

        let fileURLsArray = editingAssembly.assembly.fileURLs;
        if (!fileURLsArray) {
            fileURLsArray = [];
        } else if (typeof fileURLsArray === 'string') {
            fileURLsArray = fileURLsArray.split('~|~').filter(x => x && x.trim() !== '');
        } else if (!Array.isArray(fileURLsArray)) {
            fileURLsArray = [];
        }
        const filteredFileURLs = fileURLsArray.filter(x => x !== '' && x != null && x.trim() !== '');
        editingAssembly.assembly.fileURLs = filteredFileURLs.length > 0 ? filteredFileURLs.join('~|~') : '';

        const existingAssembly = assemblies.find(x =>
            x.assembly.assemblyId.toLowerCase().trim() === editingAssembly.assembly.assemblyId.toLowerCase().trim() &&
            x.assembly.revision.toLowerCase().trim() === editingAssembly.assembly.revision.toLowerCase().trim() &&
            x.assembly.id !== editingAssembly.assembly.id
        );

        const existingSubAssembly = subAssemblies.find(x =>
            x.subAssembly.subAssemblyId.toLowerCase().trim() === editingAssembly.assembly.assemblyId.toLowerCase().trim() &&
            x.subAssembly.revision.toLowerCase().trim() === editingAssembly.assembly.revision.toLowerCase().trim()
        );

        if (existingAssembly || existingSubAssembly) {
            window.$alertWarning("An assembly already exists with that Assembly Number and Revision!");
            const err = new Error('DUPLICATE_ASSEMBLY_ID');
            err.code = 'DUPLICATE_ASSEMBLY_ID';
            throw err;
        }

        let fileURLsString = '';
        if (Array.isArray(editingAssembly.assembly.fileURLs)) {
            fileURLsString = editingAssembly.assembly.fileURLs.filter(x => x !== '' && x != null && x.trim() !== '').join('~|~');
        } else if (typeof editingAssembly.assembly.fileURLs === 'string') {
            fileURLsString = editingAssembly.assembly.fileURLs;
        }

        if (this.state.newFamilyName !== '') {
            const familyPayload = {
                id: uuid(),
                assemblyFamilyId: this.state.newFamilyName,
                description: this.state.newFamilyDescription
            };
            const newFamily = await assemblyService.createAssemblyFamily(familyPayload);
            editingAssembly.assembly.assemblyFamilyId = newFamily.id;
            editingAssembly.assembly.assemblyFamily = newFamily;

            const assemblyToUpdate = {
                ...editingAssembly.assembly,
                fileURLs: fileURLsString,
                partsToAssemblies: [],
                subAssembliesToAssemblies: []
            };
            await assemblyService.updateAssembly(assemblyToUpdate);
            await new Promise((resolve) => this.setState({ editingAssembly, assemblyFamily: newFamily.id }, resolve));
            return;
        }

        const assemblyToUpdate = {
            ...this.state.editingAssembly.assembly,
            fileURLs: fileURLsString,
            partsToAssemblies: [],
            subAssembliesToAssemblies: []
        };
        await assemblyService.updateAssembly(assemblyToUpdate);
    };

    /**
     * Same as "Save Changes" for a subassembly header; empty BOM arrays. Does not call loadData.
     */
    persistSubAssemblyHeaderWithoutBomReload = async () => {
        if (!this.state.editingSubAssembly?.subAssembly?.id || this.state.editingSubAssembly.subAssembly.id === '') {
            return;
        }
        const assemblyService = ServiceLocator.Instance.getAssemblyService();

        if (this.state.editingSubAssembly !== editingSubAssembly) {
            editingSubAssembly = this.state.editingSubAssembly;
        }

        if (editingSubAssembly.subAssembly.imageURLs) {
            editingSubAssembly.subAssembly.imageURL = editingSubAssembly.subAssembly.imageURLs.filter(x => x !== '').join('~|~');
        }

        let fileURLsArray = editingSubAssembly.subAssembly.fileURLs;
        if (!fileURLsArray) {
            fileURLsArray = [];
        } else if (typeof fileURLsArray === 'string') {
            fileURLsArray = fileURLsArray.split('~|~').filter(x => x && x.trim() !== '');
        } else if (!Array.isArray(fileURLsArray)) {
            fileURLsArray = [];
        }
        const filteredSubFileURLs = fileURLsArray.filter(x => x !== '' && x != null && x.trim() !== '');
        editingSubAssembly.subAssembly.fileURLs = filteredSubFileURLs.length > 0 ? filteredSubFileURLs.join('~|~') : '';

        const existingSubAssembly = subAssemblies.find(x =>
            x.subAssembly.subAssemblyId.toLowerCase().trim() === editingSubAssembly.subAssembly.subAssemblyId.toLowerCase().trim() &&
            x.subAssembly.revision.toLowerCase().trim() === editingSubAssembly.subAssembly.revision.toLowerCase().trim() &&
            x.subAssembly.id !== editingSubAssembly.subAssembly.id
        );

        const existingAssembly = assemblies.find(x =>
            x.assembly.assemblyId.toLowerCase().trim() === editingSubAssembly.subAssembly.subAssemblyId.toLowerCase().trim() &&
            x.assembly.revision.toLowerCase().trim() === editingSubAssembly.subAssembly.revision.toLowerCase().trim()
        );

        if (existingSubAssembly || existingAssembly) {
            window.$alertWarning("An assembly already exists with that SubAssembly Number and Revision!");
            const err = new Error('DUPLICATE_SUBASSEMBLY_ID');
            err.code = 'DUPLICATE_SUBASSEMBLY_ID';
            throw err;
        }

        let fileURLsString = '';
        if (Array.isArray(editingSubAssembly.subAssembly.fileURLs)) {
            fileURLsString = editingSubAssembly.subAssembly.fileURLs.filter(x => x !== '' && x != null && x.trim() !== '').join('~|~');
        } else if (typeof editingSubAssembly.subAssembly.fileURLs === 'string') {
            fileURLsString = editingSubAssembly.subAssembly.fileURLs;
        }

        const subAssemblyToUpdate = {
            ...this.state.editingSubAssembly.subAssembly,
            fileURLs: fileURLsString,
            partsToSubAssemblies: [],
            subAssembliesToSubAssemblies: []
        };
        await assemblyService.updateSubAssembly(subAssemblyToUpdate);
    };

    saveBulkBomChanges = async () => {
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        const partService = ServiceLocator.Instance.getPartService();
        const { bomChanges } = this.state;
        const newBomItems = this.getPendingNewBomItemsForSave();
        
        try {
            // Process deletions first
            for (const [itemId, changes] of Object.entries(bomChanges || {})) {
                if (changes.markedForDeletion) {
                    // Find the item to determine its type
                    let item = null;
                    if (this.state.editingAssembly) {
                        item = this.state.editingAssembly.assembly.subAssembliesToAssemblies.find(x => x.id === itemId) ||
                               this.state.editingAssembly.assembly.partsToAssemblies.find(x => x.id === itemId);
                    } else if (this.state.editingSubAssembly) {
                        item = this.state.editingSubAssembly.subAssembly.partsToSubAssemblies.find(x => x.id === itemId) ||
                               (this.state.editingSubAssembly.subAssembly.childSubAssemblies || []).find(x => x.id === itemId);
                    }

                    if (item) {
                        await this.deleteBomConnectionById(item);
                    }
                }
            }

            // Save all changes to existing items (except deletions)
            for (const [itemId, changes] of Object.entries(bomChanges || {})) {
                if (changes.markedForDeletion) continue; // Skip deleted items

                // Find the item to determine its type
                let item = null;
                if (this.state.editingAssembly) {
                    item = this.state.editingAssembly.assembly.subAssembliesToAssemblies.find(x => x.id === itemId) ||
                           this.state.editingAssembly.assembly.partsToAssemblies.find(x => x.id === itemId);
                } else if (this.state.editingSubAssembly) {
                    item = this.state.editingSubAssembly.subAssembly.partsToSubAssemblies.find(x => x.id === itemId) ||
                           (this.state.editingSubAssembly.subAssembly.childSubAssemblies || []).find(x => x.id === itemId);
                }

                if (item) {
                    const updatedItem = { ...item, ...changes };
                    
                    // Ensure itemType is preserved if not explicitly changed
                    if (!changes.itemType) {
                        if (this.state.editingAssembly) {
                            updatedItem.itemType = item.subAssemblyFk ? 'subassembly' : 'part';
                        } else if (this.state.editingSubAssembly) {
                            updatedItem.itemType = (item.childSubAssemblyFk || item.ChildSubAssemblyFk) ? 'subassembly' : 'part';
                        }
                    }
                    
                    if (this.state.editingAssembly && !this.state.editingSubAssembly) {
                        // Check if the item type has changed
                        const originalType = item.subAssemblyFk ? 'subassembly' : 'part';
                        const newType = updatedItem.itemType;
                        
                        // Check if the partFk or subAssemblyFk has changed (indicating a different part/subassembly)
                        const partFkChanged = item.partFk !== updatedItem.partFk;
                        const subAssemblyFkChanged = item.subAssemblyFk !== updatedItem.subAssemblyFk;
                        
                        if (originalType !== newType || partFkChanged || subAssemblyFkChanged) {
                            // Type or specific item has changed - remove the original connection and create a new one
                            await this.deleteBomConnectionById(item);
                            
                            // Create the new connection
                            if (newType === 'subassembly') {
                                await assemblyService.connectSubAssemblyToAssembly(
                                    updatedItem.subAssemblyFk,
                                    this.state.editingAssembly.assembly.id,
                                    updatedItem.quantity.toString(),
                                    updatedItem.requiredForBomCheck,
                                    updatedItem.order
                                );
                            } else if (newType === 'part') {
                                await partService.connectPartToAssembly(
                                    updatedItem.partFk,
                                    this.state.editingAssembly.assembly.id,
                                    updatedItem.quantity.toString(),
                                    updatedItem.requiredForBomCheck,
                                    undefined,
                                    updatedItem.order
                                );
                            }
                        } else {
                            // Type and specific item haven't changed - just update the existing connection
                            if (newType === 'subassembly' || updatedItem.subAssemblyFk) {
                                await assemblyService.updateSubAssemblyToAssembly(updatedItem);
                            } else if (newType === 'part' || updatedItem.partFk) {
                                await partService.updatePartsToAssembly(updatedItem);
                            }
                        }
                    } else if (this.state.editingSubAssembly) {
                        // Check if the item type has changed
                        const originalType = (item.childSubAssemblyFk || item.ChildSubAssemblyFk) ? 'subassembly' : 'part';
                        const newType = updatedItem.itemType;
                        
                        // Check if the partFk or childSubAssemblyFk has changed (indicating a different part/subassembly)
                        const partFkChanged = item.partFk !== updatedItem.partFk;
                        const childSubAssemblyFkChanged = (item.childSubAssemblyFk || item.ChildSubAssemblyFk) !== (updatedItem.childSubAssemblyFk || updatedItem.ChildSubAssemblyFk);
                        
                        if (originalType !== newType || partFkChanged || childSubAssemblyFkChanged) {
                            // Type or specific item has changed - remove the original connection and create a new one
                            await this.deleteBomConnectionById(item);
                            
                            // Create the new connection
                            if (newType === 'subassembly') {
                                const childSubAssemblyId = updatedItem.childSubAssemblyFk || updatedItem.ChildSubAssemblyFk;
                                const parentSubAssemblyId = this.state.editingSubAssembly.subAssembly.id;
                                
                                // Check for circular dependency before creating connection
                                if (this.wouldCreateCircularDependency(childSubAssemblyId, parentSubAssemblyId)) {
                                    window.$alertError("Cannot add this subassembly as it would create a circular dependency!");
                                    return;
                                }
                                
                                await assemblyService.connectSubAssemblyToSubAssembly(
                                    childSubAssemblyId,
                                    parentSubAssemblyId,
                                    updatedItem.quantity.toString(),
                                    updatedItem.requiredForBomCheck,
                                    updatedItem.order
                                );
                            } else if (newType === 'part') {
                                await partService.connectPartToSubAssembly(
                                    updatedItem.partFk,
                                    this.state.editingSubAssembly.subAssembly.id,
                                    updatedItem.quantity.toString(),
                                    updatedItem.requiredForBomCheck,
                                    undefined,
                                    updatedItem.order
                                );
                            }
                        } else {
                            // Type and specific item haven't changed - just update the existing connection
                            if (newType === 'subassembly' || updatedItem.childSubAssemblyFk || updatedItem.ChildSubAssemblyFk) {
                                await assemblyService.updateSubAssemblyToSubAssembly(updatedItem);
                            } else if (newType === 'part' || updatedItem.partFk) {
                                await partService.updatePartsToSubAssembly(updatedItem);
                            }
                        }
                    }
                }
            }

            // Save all new items (only non-empty ones)
            const itemsToAdd = (newBomItems || []).filter(item => item.partFk || item.subAssemblyFk || item.partFamilyId);
            for (const item of itemsToAdd) {
                if (this.state.editingAssembly) {
                    if (item.partFamilyId) {
                        // Add family - find all parts in the family and add each one individually
                        const family = this.state.partFamilies.find(x => x.id === item.partFamilyId);
                        if (family) {
                            // Get all parts that belong to this family through PartToFamily relationship
                            const familyParts = this.state.parts.filter(part => {
                                const isInFamily = part.part.partToFamilies && 
                                    part.part.partToFamilies.some(ptf => ptf.familyId === family.id);
                                return isInFamily;
                            });
                            
                            // Add each part from the family
                            for (let i = 0; i < familyParts.length; i++) {
                                const part = familyParts[i];
                                const partToFamily = part.part.partToFamilies.find(ptf => ptf.familyId === family.id);
                                const packageQuantity = parseInt(partToFamily?.packageQuantity) || 1;
                                const totalQuantity = packageQuantity * parseNumberInputValue(item.quantity);
                                
                                await partService.connectPartToAssembly(
                                    part.part.id,
                                    this.state.editingAssembly.assembly.id,
                                    totalQuantity.toString(),
                                    item.requiredForBomCheck,
                                    family.id, // Pass the family ID
                                    item.order + i,
                                    partToFamily?.isImpliedPart || false // Pass the isImpliedPart flag from PartToFamily
                                );
                            }
                        }
                    } else if (item.subAssemblyFk) {
                        await assemblyService.connectSubAssemblyToAssembly(
                            item.subAssemblyFk,
                            this.state.editingAssembly.assembly.id,
                            item.quantity.toString(),
                            item.requiredForBomCheck,
                            item.order
                        );
                    } else if (item.partFk) {
                        await partService.connectPartToAssembly(
                            item.partFk,
                            this.state.editingAssembly.assembly.id,
                            item.quantity.toString(),
                            item.requiredForBomCheck,
                            undefined,
                            item.order,
                            item.isImpliedPart || false
                        );
                    }
                } else if (this.state.editingSubAssembly) {
                    if (item.partFamilyId) {
                        // Add family to subassembly - find all parts in the family and add each one individually
                        const family = this.state.partFamilies.find(x => x.id === item.partFamilyId);
                        if (family) {
                            // Get all parts that belong to this family through PartToFamily relationship
                            const familyParts = this.state.parts.filter(part => {
                                const isInFamily = part.part.partToFamilies && 
                                    part.part.partToFamilies.some(ptf => ptf.familyId === family.id);
                                return isInFamily;
                            });
                            
                            // Add each part from the family
                            for (let i = 0; i < familyParts.length; i++) {
                                const part = familyParts[i];
                                const partToFamily = part.part.partToFamilies.find(ptf => ptf.familyId === family.id);
                                const packageQuantity = parseInt(partToFamily?.packageQuantity) || 1;
                                const totalQuantity = packageQuantity * parseNumberInputValue(item.quantity);
                                
                                await partService.connectPartToSubAssembly(
                                    part.part.id,
                                    this.state.editingSubAssembly.subAssembly.id,
                                    totalQuantity.toString(),
                                    item.requiredForBomCheck,
                                    family.id, // Pass the family ID
                                    item.order + i,
                                    partToFamily?.isImpliedPart || false // Pass the isImpliedPart flag from PartToFamily
                                );
                            }
                        }
                    } else if (item.subAssemblyFk) {
                        const childSubAssemblyId = item.subAssemblyFk;
                        const parentSubAssemblyId = this.state.editingSubAssembly.subAssembly.id;
                        
                        // Check for circular dependency before creating connection
                        if (this.wouldCreateCircularDependency(childSubAssemblyId, parentSubAssemblyId)) {
                            window.$alertError("Cannot add this subassembly as it would create a circular dependency!");
                            return;
                        }
                        
                        await assemblyService.connectSubAssemblyToSubAssembly(
                            childSubAssemblyId,
                            parentSubAssemblyId,
                            item.quantity.toString(),
                            item.requiredForBomCheck,
                            item.order
                        );
                    } else if (item.partFk) {
                        await partService.connectPartToSubAssembly(
                            item.partFk,
                            this.state.editingSubAssembly.subAssembly.id,
                            item.quantity.toString(),
                            item.requiredForBomCheck,
                            undefined,
                            item.order,
                            item.isImpliedPart || false
                        );
                    }
                }
            }

            window.$alertSuccess("BOM changes saved successfully");

            try {
                if (this.state.editingAssembly) {
                    await this.persistAssemblyHeaderWithoutBomReload();
                } else if (this.state.editingSubAssembly) {
                    await this.persistSubAssemblyHeaderWithoutBomReload();
                }
            } catch (headerErr) {
                if (headerErr.code === 'DUPLICATE_ASSEMBLY_ID' || headerErr.code === 'DUPLICATE_SUBASSEMBLY_ID') {
                    // persist* already alerted; BOM is saved on server
                } else {
                    console.error("Error saving assembly header alongside BOM:", headerErr);
                    window.$alertWarning("BOM was saved, but saving assembly details failed. Use Save Changes or try again.");
                }
            }

            this.setState({
                isBulkEditingBom: false,
                newBomItem: null,
                newBomItems: [],
                bomChanges: {}
            });
            this.loadData();

        } catch (error) {
            console.error("Error saving BOM changes:", error);
            window.$alertError("Error saving BOM changes");
        }
    };

    onDragEnd = (result) => {
        if (!result.destination) return;

        // Only handle drag for assemblies or subassemblies in edit mode
        if (!this.state.editingAssembly && !this.state.editingSubAssembly) return;
        if (!this.state.isBulkEditingBom) return;

        const { source, destination } = result;

        // Get all items in their current order
        const allItems = this.getAllBomItemsForDrag();
        
        // Remove the item from its old position and insert it at the new position
        const [removed] = allItems.splice(source.index, 1);
        if (removed) {
            allItems.splice(destination.index, 0, removed);
        }

        // Update orders for all items
        allItems.forEach((item, index) => {
            item.order = index;
        });

        // Separate existing items from new items
        const existingItems = allItems.filter(item => !item.isNew);
        const newItems = allItems.filter(item => item.isNew).map(item => ({
            ...item,
            dragId: undefined, // Remove dragId from new items
            isNew: undefined   // Remove isNew flag
        }));

        // Assembly BOM
        if (this.state.editingAssembly) {
            // Flatten existing items back to the original structure
            const updatedSubAssemblies = [];
            const updatedParts = [];

            existingItems.forEach(item => {
                if (!item) return; // Skip undefined items
                
                if (item.itemType === 'subassembly') {
                    updatedSubAssemblies.push({ ...item }); // Keep the order that was already assigned
                } else if (item.itemType === 'part') {
                    updatedParts.push({ ...item }); // Keep the order that was already assigned
                } else if (item.itemType === 'family') {
                    // For family groups, assign the same order to all parts in the family
                    if (item.parts && Array.isArray(item.parts)) {
                        item.parts.forEach(part => {
                            if (part) {
                                updatedParts.push({ ...part, order: item.order }); // Use the family's order
                            }
                        });
                    }
                }
            });

            this.setState({
                editingAssembly: {
                    ...this.state.editingAssembly,
                    assembly: {
                        ...this.state.editingAssembly.assembly,
                        subAssembliesToAssemblies: updatedSubAssemblies,
                        partsToAssemblies: updatedParts
                    }
                },
                newBomItems: newItems
            }, () => {
                const allConnections = [...updatedSubAssemblies, ...updatedParts].map(item => ({ id: item.id, order: item.order }));
                ServiceLocator.Instance.getAssemblyService().updateConnectionOrders(allConnections).then(() => {
                    localStorage.removeItem('assemblies');
                    localStorage.removeItem('subAssemblies');
                    localStorage.removeItem('assembliesUpdated');
                    localStorage.removeItem('subAssembliesUpdated');
                })
                    .catch(err => console.error('Error updating order', err));
            });
        }

        // SubAssembly BOM
        if (this.state.editingSubAssembly) {
            // Flatten existing items back to the original structure
            const updatedParts = [];
            const updatedSubAssemblies = [];

            existingItems.forEach(item => {
                if (!item) return; // Skip undefined items
                
                if (item.itemType === 'subassembly') {
                    updatedSubAssemblies.push({ ...item }); // Keep the order that was already assigned
                } else if (item.itemType === 'part') {
                    updatedParts.push({ ...item }); // Keep the order that was already assigned
                } else if (item.itemType === 'family') {
                    // For family groups, assign the same order to all parts in the family
                    if (item.parts && Array.isArray(item.parts)) {
                        item.parts.forEach(part => {
                            if (part) {
                                updatedParts.push({ ...part, order: item.order }); // Use the family's order
                            }
                        });
                    }
                }
            });

            this.setState({
                editingSubAssembly: {
                    ...this.state.editingSubAssembly,
                    subAssembly: {
                        ...this.state.editingSubAssembly.subAssembly,
                        partsToSubAssemblies: updatedParts,
                        childSubAssemblies: updatedSubAssemblies
                    }
                },
                newBomItems: newItems
            }, () => {
                const connectionUpdates = [...updatedParts, ...updatedSubAssemblies].map(item => ({ id: item.id, order: item.order }));
                ServiceLocator.Instance.getAssemblyService().updateConnectionOrders(connectionUpdates).then(() => {
                    localStorage.removeItem('assemblies');
                    localStorage.removeItem('subAssemblies');
                    localStorage.removeItem('assembliesUpdated');
                    localStorage.removeItem('subAssembliesUpdated');
                })
                    .catch(err => console.error('Error updating order', err));
            });
        }
    };

    changeTertiaryTypeSelection = (event, newValue) => {
        if (!newValue || newValue === 'New...') {
            editingAssembly.assembly.tertiaryType = !newValue ? '' : newValue;
            this.setState({editingAssembly, newTertiaryType: true});
        } else {
            editingAssembly.assembly.tertiaryType = newValue;
            this.setState({editingAssembly, newTertiaryType: false});
        }
    };

    changeTertiaryType = (event) => {
        editingAssembly.assembly.tertiaryType = event.target.value;
        this.setState({editingAssembly});
    };

    // New freeSolo change handlers for assembly category dropdowns
    changeAssemblyTypeFreeSolo = (newValue) => {
        editingAssembly.assembly.assemblyType = newValue || '';
        this.setState({editingAssembly});
    };

    changeAssemblyTypeInput = (newInputValue) => {
        editingAssembly.assembly.assemblyType = newInputValue || '';
        this.setState({editingAssembly, assemblyTypeInputValue: newInputValue || ''});
    };

    changeManufacturerForFreeSolo = (newValue) => {
        editingAssembly.assembly.manufacturerFor = newValue || '';
        this.setState({editingAssembly});
    };

    changeManufacturerForInput = (newInputValue) => {
        editingAssembly.assembly.manufacturerFor = newInputValue || '';
        this.setState({editingAssembly, manufacturerForInputValue: newInputValue || ''});
    };

    changeTertiaryTypeFreeSolo = (newValue) => {
        editingAssembly.assembly.tertiaryType = newValue || '';
        this.setState({editingAssembly});
    };

    changeTertiaryTypeInput = (newInputValue) => {
        editingAssembly.assembly.tertiaryType = newInputValue || '';
        this.setState({editingAssembly, tertiaryTypeInputValue: newInputValue || ''});
    };

    changeBrandFreeSolo = (newValue) => {
        editingAssembly.assembly.brand = newValue || '';
        this.setState({editingAssembly});
    };

    changeBrandInput = (newInputValue) => {
        editingAssembly.assembly.brand = newInputValue || '';
        this.setState({editingAssembly, brandInputValue: newInputValue || ''});
    };

    // New freeSolo change handlers for sub-assembly category dropdowns
    changeSubAssemblyTypeFreeSolo = (newValue) => {
        editingSubAssembly.subAssembly.assemblyType = newValue || '';
        this.setState({editingSubAssembly});
    };

    changeSubAssemblyTypeInput = (newInputValue) => {
        editingSubAssembly.subAssembly.assemblyType = newInputValue || '';
        this.setState({editingSubAssembly, subAssemblyTypeInputValue: newInputValue || ''});
    };

    changeSubManufacturerForFreeSolo = (newValue) => {
        editingSubAssembly.subAssembly.manufacturerFor = newValue || '';
        this.setState({editingSubAssembly});
    };

    changeSubManufacturerForInput = (newInputValue) => {
        editingSubAssembly.subAssembly.manufacturerFor = newInputValue || '';
        this.setState({editingSubAssembly, subManufacturerForInputValue: newInputValue || ''});
    };

    changeSubTertiaryTypeFreeSolo = (newValue) => {
        editingSubAssembly.subAssembly.tertiaryType = newValue || '';
        this.setState({editingSubAssembly});
    };

    changeSubTertiaryTypeInput = (newInputValue) => {
        editingSubAssembly.subAssembly.tertiaryType = newInputValue || '';
        this.setState({editingSubAssembly, subTertiaryTypeInputValue: newInputValue || ''});
    };

    changeSubBrandFreeSolo = (newValue) => {
        editingSubAssembly.subAssembly.brand = newValue || '';
        this.setState({editingSubAssembly});
    };

    changeSubBrandInput = (newInputValue) => {
        editingSubAssembly.subAssembly.brand = newInputValue || '';
        this.setState({editingSubAssembly, subBrandInputValue: newInputValue || ''});
    };

    changeSubTertiaryTypeSelection = (event, newValue) => {
        if (!newValue || newValue === 'New...') {
            editingSubAssembly.subAssembly.tertiaryType = !newValue ? '' : newValue;
            this.setState({editingSubAssembly, newSubTertiaryType: true});
        } else {
            editingSubAssembly.subAssembly.tertiaryType = newValue;
            this.setState({editingSubAssembly, newSubTertiaryType: false});
        }
    };

    changeSubTertiaryType = (event) => {
        editingSubAssembly.subAssembly.tertiaryType = event.target.value;
        this.setState({editingSubAssembly});
    };

    changeAssemblyIsInventoryItem = (event) => {
        editingAssembly.assembly.isInventoryItem = event.target.checked;
        this.setState({editingAssembly});
    };

    changeSubAssemblyIsInventoryItem = (event) => {
        editingSubAssembly.subAssembly.isInventoryItem = event.target.checked;
        this.setState({editingSubAssembly});
    };

    changeAssemblyIsOnBackorder = (event) => {
        editingAssembly.assembly.isOnBackorder = event.target.checked;
        if (!event.target.checked) {
            editingAssembly.assembly.backorderEta = null;
        }
        this.setState({editingAssembly});
    };

    changeAssemblyBackorderEta = (date) => {
        editingAssembly.assembly.backorderEta = date;
        this.setState({editingAssembly});
    };

    changeSubAssemblyIsOnBackorder = (event) => {
        editingSubAssembly.subAssembly.isOnBackorder = event.target.checked;
        if (!event.target.checked) {
            editingSubAssembly.subAssembly.backorderEta = null;
        }
        this.setState({editingSubAssembly});
    };

    changeSubAssemblyBackorderEta = (date) => {
        editingSubAssembly.subAssembly.backorderEta = date;
        this.setState({editingSubAssembly});
    };

    printAssemblyBoxLabel = (assemblyExpanded) => {
        if (!this.state.editingAssembly || !this.state.editingAssembly.assembly) return;

        const assembly = this.state.editingAssembly.assembly;
        
        const user = AuthenticationService.getInstance().getUser();
        
        // Create HTML for the label
        let containerHtml = '<div class=\'top-gutter\'></div><div class=\'left-gutter\'></div>';

        const uniqueTag = user.firstName[0].toUpperCase() + user.lastName[0].toUpperCase() + new Date(new Date().setHours(0,0,0,0)).toISOString().slice(0,10).replace(/-/g,"");
        
        // Calculate dynamic font size for description based on length
        const fullDescription = assembly.description || "";
        const descriptionLength = fullDescription.length;
        let fontSize = 13; // Default font size
        
        // Adjust font size based on description length
        if (descriptionLength > 100) {
            fontSize = 8;
        } else if (descriptionLength > 80) {
            fontSize = 9;
        } else if (descriptionLength > 60) {
            fontSize = 10;
        } else if (descriptionLength > 40) {
            fontSize = 11;
        } else if (descriptionLength > 20) {
            fontSize = 12;
        }
        
        // Check if install video URL exists
        const hasInstallVideo = assembly.installVideoURL && assembly.installVideoURL.trim() !== "";
        
        containerHtml += `<div class="label-4x2">
            <div class="rev-tag">${assembly.revision}</div>
            <div class="label-left">
                <div><img src="https://jxbstorage.blob.core.windows.net/jxbui/JXB%20BW.png" width="160"/></div>
                <div style="font-weight: 700; font-size: 28px;">${assembly.assemblyId}</div>
                <div style="font-size: ${fontSize}px; line-height: 1.2; word-break: break-word; overflow-wrap: break-word;">${fullDescription}</div>
                <div style="font-size: 17px; font-weight: 1000; margin-top: 3px;">${this.formatWeight(assembly)}</div>
            </div>
            <div class="label-right">
                ${hasInstallVideo ? 
                    `<div style="position: relative;">
                        <img src="https://jxbstorage.blob.core.windows.net/jxbui/Install%20Video.png" width="125"/>
                        <img src='https://api.qrserver.com/v1/create-qr-code/?size=500x500&data=${assembly.installVideoURL}' style="position: absolute; left: 21px; top: 29px; width: 100px; height: 100px;"/>
                    </div>` 
                    : ''
                }
            </div>
            <div class="unique-tag">${uniqueTag}</div>
        </div>`;
    
        containerHtml += '<div class=\'right-gutter\'></div><div class=\'bottom-gutter\'></div>';

        const win = window.open("", "Title", "toolbar=no,location=no,directories=no,status=no,menubar=no,scrollbars=yes,resizable=no,width=384,height=1100");
        win.document.body.innerHTML = `<html><head><style>@media print { @page { margin: 0 !important; } body, html { margin: 0 !important; padding: 0 !important; } }.label-left {width: 54%; display: inline-block; font-size: 13px; padding: 0 9px;} .label-right {width: 37%; display: inline-block; font-size: 11px; font-style: italic;} .labels_page {font-family: Montserrat, sans-serif; width: 4in; position: absolute; top: 0; left: 0; .label-4x2 { height: 2in; text-align: center; display: flex; align-items: center; position: relative; } .unique-tag { position: absolute; right: 16px; bottom: 8px; font-size: 10px; font-style: italic; }.rev-tag { position: absolute; width: 14px; height: 14px; left: 13px; bottom: 8px; font-size: 15px; border: 1px solid black; padding: 5px; line-height: 14px; border-radius: 50%; font-weight: 600; }</style></head><body><div id="container" class="labels_page">` + containerHtml + `</div></body></html>`;
        setTimeout(() => {
            win.print();
        }, 2000);
    };

    formatWeight = (assembly) => {
        const pounds = assembly.weightPounds || 0;
        const ounces = assembly.weightOunces || 0;
        
        if (pounds === 0 && ounces === 0) return "";
        if (pounds === 0) return `${ounces} oz`;
        if (ounces === 0) return `${pounds} lbs`;
        return `${pounds} ${pounds === 1 ? 'lb' : 'lbs'} ${ounces} oz`;
    };

    printAssemblyLabelSmall = () => {
        if (!this.state.editingAssembly || !this.state.editingAssembly.assembly) return;
        
        const assembly = this.state.editingAssembly.assembly;
        
        // Create HTML for the label
        let containerHtml = '<div class=\'top-gutter\'></div><div class=\'left-gutter\'></div>';
        
        // New 4" x 2" small label design (using only left 3" for content, right 1" is scrap)
        let partNumberFontSize;
        if (assembly.assemblyId.length >= 18) {
            partNumberFontSize = '20px';
        } else if (assembly.assemblyId.length >= 16) {
            partNumberFontSize = '24px';
        } else if (assembly.assemblyId.length >= 14) {
            partNumberFontSize = '28px';
        } else if (assembly.assemblyId.length >= 12) {
            partNumberFontSize = '32px';
        } else {
            partNumberFontSize = '36px';
        }
        containerHtml += `<div class="label-small-4x2">
            <div class="label-content-area">
                <div class="label-text-area">
                    <div class="part-number" style="font-size: ${partNumberFontSize};">${assembly.assemblyId}</div>
                    <div class="revision">rev ${assembly.revision}</div>
                    <div class="description">${assembly.description}</div>
                </div>
                <div class="label-qr-area">
                    <div class="qr-left">
                        <img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(window.location.origin + '/search/' + assembly.id)}" style="width: 70px; height: 70px;"/>
                        <div class="qr-label">Homepage</div>
                    </div>
                </div>
                <div class="cut-line-top"></div>
                <div class="cut-line-bottom"></div>
            </div>
            <div class="scrap-area"></div>
        </div>`;
        
        containerHtml += '<div class=\'right-gutter\'></div><div class=\'bottom-gutter\'></div>';
        
        // Open a new window and print the label
        const win = window.open("", "Title", `toolbar=no,location=no,directories=no,status=no,menubar=no,scrollbars=yes,resizable=no,width=384,height=1100`);
        win.document.body.innerHTML = `
            <html>
                <head>
                    <style>
                        @media print { @page { margin: 0 !important; } body, html { margin: 0 !important; padding: 0 !important; } }
                        .labels_page {
                            font-family: Montserrat, sans-serif;
                            width: 4in;
                            position: absolute;
                            top: 0;
                            left: 0;
                        }
                        .label-small-4x2 {
                            width: 4in;
                            height: 2in;
                            position: relative;
                            border: 0px solid #000;
                            font-weight: 800;
                            display: flex;
                        }
                        .label-content-area {
                            width: 3in;
                            height: 2in;
                            position: relative;
                            border-right: 1px dashed #666;
                        }
                        .scrap-area {
                            width: 1in;
                            height: 2in;
                            background-color: #f0f0f0;
                        }
                        .label-text-area {
                            width: 100%;
                            height: 0.75in;
                            display: flex;
                            flex-direction: column;
                            justify-content: center;
                            align-items: center;
                            padding: 1px;
                            box-sizing: border-box;
                        }
                        .part-number {
                            font-weight: 900;
                            line-height: 1.0;
                            margin-bottom: 0px;
                        }
                        .revision {
                            font-size: 8px;
                            font-weight: 600;
                            line-height: 1.0;
                            margin-bottom: 0px;
                        }
                        .description {
                            font-size: 7px;
                            font-weight: 500;
                            text-align: center;
                            word-break: break-word;
                            max-width: 100%;
                            line-height: 1.0;
                        }
                        .label-qr-area {
                            width: 100%;
                            height: 1in;
                            display: flex;
                            justify-content: center;
                            align-items: center;
                            padding: 1px;
                            box-sizing: border-box;
                        }
                        .qr-left {
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                        }
                        .qr-label {
                            font-size: 10px;
                            margin-top: 1px;
                            font-weight: 600;
                        }
                        .cut-line-top {
                            position: absolute;
                            top: 0.75in;
                            left: 0;
                            right: 0;
                            height: 1px;
                            border-top: 1px dashed #666;
                        }
                        .cut-line-bottom {
                            position: absolute;
                            bottom: 0.25in;
                            left: 0;
                            right: 0;
                            height: 1px;
                            border-top: 1px dashed #666;
                        }
                    </style>
                </head>
                <body>
                    <div id="container" class="labels_page">${containerHtml}</div>
                </body>
            </html>`;
        
        setTimeout(() => {
            win.print();
        }, 2000);
    };

    printAssemblyLabelLarge = () => {
        if (!this.state.editingAssembly || !this.state.editingAssembly.assembly) return;
        
        const assembly = this.state.editingAssembly.assembly;
        
        // Create HTML for the label
        let containerHtml = '<div class=\'top-gutter\'></div><div class=\'left-gutter\'></div>';
        
        containerHtml += `<div class="label-standard">
            <div class="label-left">
                <div style="text-align: center;">
                    <img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(window.location.origin + '/search/' + assembly.id)}" style="width: 75px; height: 75px;"/>
                    <div style="font-size: 11px; margin-top: 10px;">Homepage</div>
                </div>
            </div>
            <div class="label-center">
                <div style="font-weight: 700; font-size: 38px;">${assembly.assemblyId}<br />rev ${assembly.revision}</div>
            </div>
        </div>`;
        
        containerHtml += '<div class=\'right-gutter\'></div><div class=\'bottom-gutter\'></div>';
        
        // Open a new window and print the label
        const win = window.open("", "Title", "toolbar=no,location=no,directories=no,status=no,menubar=no,scrollbars=yes,resizable=no,width=384,height=1100");
        win.document.body.innerHTML = `
            <html>
                <head>
                    <style>
                        @media print { @page { margin: 0 !important; } body, html { margin: 0 !important; padding: 0 !important; } }
                        .label-left, .label-right {
                            width: 25%;
                            display: inline-block;
                            text-align: center;
                        }
                        .label-center {
                            width: 75%;
                            display: inline-block;
                            text-align: center;
                        }
                        .labels_page {
                            font-family: Montserrat, sans-serif;
                            width: 4in;
                            position: absolute;
                            top: 0;
                            left: 0;
                        }
                        .label-standard {
                            height: 2in;
                            text-align: center;
                            display: flex;
                            align-items: center;
                            position: relative;
                            padding: 0 10px;
                        }
                    </style>
                </head>
                <body>
                    <div id="container" class="labels_page">${containerHtml}</div>
                </body>
            </html>`;
        
        setTimeout(() => {
            win.print();
        }, 2000);
    };

    navigateToPreviousAssembly = () => {
        if (!this.state.assemblies || !editingAssembly) return;
        
        const currentIndex = this.state.assemblies.findIndex(a => a.assembly.id === editingAssembly.assembly.id);
        if (currentIndex > 0) {
            const previousAssembly = this.state.assemblies[currentIndex - 1];
            globalProps.history.push(`/admin/assemblies/${previousAssembly.assembly.id}`);
            window.location.reload();
        }
    };

    navigateToNextAssembly = () => {
        if (!this.state.assemblies || !editingAssembly) return;
        
        const currentIndex = this.state.assemblies.findIndex(a => a.assembly.id === editingAssembly.assembly.id);
        if (currentIndex < this.state.assemblies.length - 1) {
            const nextAssembly = this.state.assemblies[currentIndex + 1];
            globalProps.history.push(`/admin/assemblies/${nextAssembly.assembly.id}`);
            window.location.reload();
        }
    };

    navigateToPreviousSubAssembly = () => {
        if (!this.state.subAssemblies || !editingSubAssembly) return;
        
        const currentIndex = this.state.subAssemblies.findIndex(sa => sa.subAssembly.id === editingSubAssembly.subAssembly.id);
        if (currentIndex > 0) {
            const previousSubAssembly = this.state.subAssemblies[currentIndex - 1];
            globalProps.history.push(`/admin/assemblies/${previousSubAssembly.subAssembly.id}`);
            window.location.reload();
        }
    };

    navigateToNextSubAssembly = () => {
        if (!this.state.subAssemblies || !editingSubAssembly) return;
        
        const currentIndex = this.state.subAssemblies.findIndex(sa => sa.subAssembly.id === editingSubAssembly.subAssembly.id);
        if (currentIndex < this.state.subAssemblies.length - 1) {
            const nextSubAssembly = this.state.subAssemblies[currentIndex + 1];
            globalProps.history.push(`/admin/assemblies/${nextSubAssembly.subAssembly.id}`);
            window.location.reload();
        }
    };

    // New method to handle checkbox change for any revision's currently packaging
    handleRevisionCurrentlyPackagingChange = async (itemId, checked, isAssembly) => {
        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        
        try {
            // If unchecking, just update that item
            if (!checked) {
                if (isAssembly) {
                    const assemblyToUpdate = assemblies.find(a => a.assembly.id === itemId);
                    if (!assemblyToUpdate) return;
                    
                    // Update local state immediately
                    const updatedAssemblies = assemblies.map(a => ({
                        ...a,
                        assembly: a.assembly.id === itemId 
                            ? { ...a.assembly, currentlyPackaging: false }
                            : a.assembly
                    }));
                    assemblies = updatedAssemblies;
                    
                    // Update editingAssembly if it's the one being changed
                    if (editingAssembly && editingAssembly.assembly.id === itemId) {
                        editingAssembly.assembly.currentlyPackaging = false;
                        self.setState({ editingAssembly });
                    } else {
                        self.setState({});
                    }
                    
                    await assemblyService.updateAssembly({
                        ...assemblyToUpdate.assembly,
                        currentlyPackaging: false,
                        isDiscontinued: assemblyToUpdate.assembly.isDiscontinued || false
                    });
                } else {
                    const subAssemblyToUpdate = subAssemblies.find(sa => sa.subAssembly.id === itemId);
                    if (!subAssemblyToUpdate) return;
                    
                    // Update local state immediately
                    const updatedSubAssemblies = subAssemblies.map(sa => ({
                        ...sa,
                        subAssembly: sa.subAssembly.id === itemId 
                            ? { ...sa.subAssembly, currentlyPackaging: false }
                            : sa.subAssembly
                    }));
                    subAssemblies = updatedSubAssemblies;
                    
                    // Update editingSubAssembly if it's the one being changed
                    if (editingSubAssembly && editingSubAssembly.subAssembly.id === itemId) {
                        editingSubAssembly.subAssembly.currentlyPackaging = false;
                        self.setState({ editingSubAssembly });
                    } else {
                        self.setState({});
                    }
                    
                    await assemblyService.updateSubAssembly({
                        ...subAssemblyToUpdate.subAssembly,
                        currentlyPackaging: false
                    });
                }
            } else {
                // If checking, we need to uncheck all others for this item number and check this one
                if (isAssembly) {
                    const currentAssemblyNumber = assemblies.find(a => a.assembly.id === itemId)?.assembly.assemblyId;
                    if (!currentAssemblyNumber) return;
                    
                    // Get all assemblies with the same assembly number
                    const sameAssemblyNumberItems = assemblies.filter(a => a.assembly.assemblyId === currentAssemblyNumber);
                    
                    // NEW: Handle assembly family ID swap logic - capture the previously packaging assembly BEFORE updating state
                    // Find the assembly that was currently packaging before this update
                    const previouslyPackagingAssembly = sameAssemblyNumberItems.find(a => 
                        a.assembly.currentlyPackaging && a.assembly.id !== itemId
                    );
                    
                    // Update local state immediately - uncheck all others, check this one
                    const updatedAssemblies = assemblies.map(a => ({
                        ...a,
                        assembly: a.assembly.assemblyId === currentAssemblyNumber 
                            ? { ...a.assembly, currentlyPackaging: a.assembly.id === itemId }
                            : a.assembly
                    }));
                    assemblies = updatedAssemblies;
                    
                    // Update editingAssembly if it's one of the assemblies being changed
                    if (editingAssembly && editingAssembly.assembly.assemblyId === currentAssemblyNumber) {
                        editingAssembly.assembly.currentlyPackaging = editingAssembly.assembly.id === itemId;
                        self.setState({ editingAssembly });
                    } else {
                        self.setState({});
                    }
                    
                    // If we found a previously packaging assembly with a family, transfer its family to the new assembly
                    if (previouslyPackagingAssembly && previouslyPackagingAssembly.assembly.assemblyFamilyId) {
                        const newAssembly = sameAssemblyNumberItems.find(a => a.assembly.id === itemId);
                        if (newAssembly) {
                            // Null out the old assembly's family ID
                            await assemblyService.updateAssembly({
                                ...previouslyPackagingAssembly.assembly,
                                currentlyPackaging: false,
                                assemblyFamilyId: null,
                                assemblyFamilyOrder: null,
                                assemblyFamily: null,
                                isDiscontinued: previouslyPackagingAssembly.assembly.isDiscontinued || false
                            });
                            
                            // Transfer the family ID to the new assembly
                            await assemblyService.updateAssembly({
                                ...newAssembly.assembly,
                                currentlyPackaging: true,
                                assemblyFamilyId: previouslyPackagingAssembly.assembly.assemblyFamilyId,
                                assemblyFamilyOrder: previouslyPackagingAssembly.assembly.assemblyFamilyOrder,
                                isDiscontinued: newAssembly.assembly.isDiscontinued || false
                            });
                            
                            // Update local state to reflect the family transfer
                            const updatedAssembliesWithFamily = assemblies.map(a => {
                                if (a.assembly.id === previouslyPackagingAssembly.assembly.id) {
                                    return {
                                        ...a,
                                        assembly: {
                                            ...a.assembly,
                                            currentlyPackaging: false,
                                            assemblyFamilyId: null,
                                            assemblyFamilyOrder: null
                                        }
                                    };
                                } else if (a.assembly.id === newAssembly.assembly.id) {
                                    return {
                                        ...a,
                                        assembly: {
                                            ...a.assembly,
                                            currentlyPackaging: true,
                                            assemblyFamilyId: previouslyPackagingAssembly.assembly.assemblyFamilyId,
                                            assemblyFamilyOrder: previouslyPackagingAssembly.assembly.assemblyFamilyOrder
                                        }
                                    };
                                }
                                return a;
                            });
                            assemblies = updatedAssembliesWithFamily;
                            
                            // Update editingAssembly if it's one of the assemblies being changed
                            if (editingAssembly) {
                                if (editingAssembly.assembly.id === previouslyPackagingAssembly.assembly.id) {
                                    editingAssembly.assembly.currentlyPackaging = false;
                                    editingAssembly.assembly.assemblyFamilyId = null;
                                    editingAssembly.assembly.assemblyFamilyOrder = null;
                                    self.setState({ editingAssembly });
                                } else if (editingAssembly.assembly.id === newAssembly.assembly.id) {
                                    editingAssembly.assembly.currentlyPackaging = true;
                                    editingAssembly.assembly.assemblyFamilyId = previouslyPackagingAssembly.assembly.assemblyFamilyId;
                                    editingAssembly.assembly.assemblyFamilyOrder = previouslyPackagingAssembly.assembly.assemblyFamilyOrder;
                                    self.setState({ editingAssembly });
                                }
                            }
                        }
                    } else {
                        // Original logic for assemblies without family transfer
                        const updatePromises = sameAssemblyNumberItems.map(assemblyData => {
                            return assemblyService.updateAssemblyNoCache({
                                ...assemblyData.assembly,
                                currentlyPackaging: assemblyData.assembly.id === itemId
                            });
                        });
                        
                        await Promise.all(updatePromises);
                    }
                } else {
                    const currentSubAssemblyNumber = subAssemblies.find(sa => sa.subAssembly.id === itemId)?.subAssembly.subAssemblyId;
                    if (!currentSubAssemblyNumber) return;
                    
                    // Get all subassemblies with the same subassembly number
                    const sameSubAssemblyNumberItems = subAssemblies.filter(sa => sa.subAssembly.subAssemblyId === currentSubAssemblyNumber);
                    
                    // Update local state immediately - uncheck all others, check this one
                    const updatedSubAssemblies = subAssemblies.map(sa => ({
                        ...sa,
                        subAssembly: sa.subAssembly.subAssemblyId === currentSubAssemblyNumber 
                            ? { ...sa.subAssembly, currentlyPackaging: sa.subAssembly.id === itemId }
                            : sa.subAssembly
                    }));
                    subAssemblies = updatedSubAssemblies;
                    
                    // Update editingSubAssembly if it's one of the subassemblies being changed
                    if (editingSubAssembly && editingSubAssembly.subAssembly.subAssemblyId === currentSubAssemblyNumber) {
                        editingSubAssembly.subAssembly.currentlyPackaging = editingSubAssembly.subAssembly.id === itemId;
                        self.setState({ editingSubAssembly });
                    } else {
                        self.setState({});
                    }
                    
                    // Update all subassemblies with the same subassembly number via API
                    const updatePromises = sameSubAssemblyNumberItems.map(subAssemblyData => {
                        return assemblyService.updateSubAssemblyNoCache({
                            ...subAssemblyData.subAssembly,
                            currentlyPackaging: subAssemblyData.subAssembly.id === itemId
                        });
                    });
                    
                    await Promise.all(updatePromises);
                }
            }
            
            // Manually update the cache with the correct data after all operations complete
            await self.updateAssembliesCacheAfterBatchUpdate();
            
            window.$alertSuccess("Assembly updated successfully");
        } catch (error) {
            console.error('Error updating assembly:', error);
            window.$alertError("Error updating assembly");
            // Revert local state on error
            self.loadData();
        }
    };

    // Method to manually update the assemblies and subassemblies cache after batch operations
    updateAssembliesCacheAfterBatchUpdate = async () => {
        try {
            // Update assemblies cache
            const cachedAssembliesCompressed = localStorage.getItem('assemblies');
            if (cachedAssembliesCompressed) {
                const cachedAssemblies = JSON.parse(LzString.decompress(cachedAssembliesCompressed));
                if (Array.isArray(cachedAssemblies)) {
                    // Create a map of updated assemblies from the current state
                    const updatedAssembliesMap = new Map();
                    assemblies.forEach(assemblyData => {
                        updatedAssembliesMap.set(assemblyData.assembly.id, assemblyData.assembly);
                    });

                    // Update the cached assemblies with the current state data
                    const updatedCachedAssemblies = cachedAssemblies.map(cachedAssembly => {
                        const assemblyId = cachedAssembly.assembly?.id || cachedAssembly.id;
                        const updatedAssembly = updatedAssembliesMap.get(assemblyId);
                        if (updatedAssembly) {
                            // Return the updated assembly with the same structure as cached
                            return {
                                ...cachedAssembly,
                                assembly: {
                                    ...cachedAssembly.assembly,
                                    currentlyPackaging: updatedAssembly.currentlyPackaging
                                }
                            };
                        }
                        return cachedAssembly;
                    });

                    // Save the updated cache
                    localStorage.setItem('assemblies', LzString.compress(JSON.stringify(updatedCachedAssemblies)));
                }
            }

            // Update subassemblies cache
            const cachedSubAssembliesCompressed = localStorage.getItem('subAssemblies');
            if (cachedSubAssembliesCompressed) {
                const cachedSubAssemblies = JSON.parse(LzString.decompress(cachedSubAssembliesCompressed));
                if (Array.isArray(cachedSubAssemblies)) {
                    // Create a map of updated subassemblies from the current state
                    const updatedSubAssembliesMap = new Map();
                    subAssemblies.forEach(subAssemblyData => {
                        updatedSubAssembliesMap.set(subAssemblyData.subAssembly.id, subAssemblyData.subAssembly);
                    });

                    // Update the cached subassemblies with the current state data
                    const updatedCachedSubAssemblies = cachedSubAssemblies.map(cachedSubAssembly => {
                        const subAssemblyId = cachedSubAssembly.subAssembly?.id || cachedSubAssembly.id;
                        const updatedSubAssembly = updatedSubAssembliesMap.get(subAssemblyId);
                        if (updatedSubAssembly) {
                            // Return the updated subassembly with the same structure as cached
                            return {
                                ...cachedSubAssembly,
                                subAssembly: {
                                    ...cachedSubAssembly.subAssembly,
                                    currentlyPackaging: updatedSubAssembly.currentlyPackaging
                                }
                            };
                        }
                        return cachedSubAssembly;
                    });

                    // Save the updated cache
                    localStorage.setItem('subAssemblies', LzString.compress(JSON.stringify(updatedCachedSubAssemblies)));
                }
            }

            // Update the timestamps to force a refresh if needed
            const assemblyService = ServiceLocator.Instance.getAssemblyService();
            const assembliesUpdatedOn = await assemblyService.getTableUpdatedDate('Assemblies');
            const subAssembliesUpdatedOn = await assemblyService.getTableUpdatedDate('SubAssemblies');
            localStorage.setItem('assembliesUpdated', assembliesUpdatedOn);
            localStorage.setItem('subAssembliesUpdated', subAssembliesUpdatedOn);
            
            console.log('Assemblies and subassemblies cache updated successfully after batch operation');
        } catch (error) {
            console.error('Error updating assemblies cache:', error);
        }
    };

    changeFinalOrderNumber = (event) => {
        if (editingAssembly) {
            const value = event.target.value;
            editingAssembly.assembly.finalOrderNumber = value === '' ? null : value;
            this.setState({editingAssembly});
        }
    };

    // Handle calculation contributions tab change
    handleCalculationContributionsTabChange = (event, newValue) => {
        this.setState({ calculationContributionsTab: newValue });
        // Lazy load data when tab is selected
        this.loadCalculationContributionsData(newValue);
    };

    prefetchCalculationContributionsTableData = () => {
        if (!editingAssembly && !editingSubAssembly) return;
        const opts = { background: true };
        void this.loadCalculationContributionsData(0, opts);
        void this.loadCalculationContributionsData(1, opts);
    };

    // Lazy load calculation contributions data (functional merges support parallel background prefetch)
    loadCalculationContributionsData = async (tabIndex, options = {}) => {
        const { background = false } = options;
        const currentAssembly = editingAssembly || editingSubAssembly;
        if (!currentAssembly) return;

        const assemblyId = editingAssembly ? editingAssembly.assembly.id : editingSubAssembly.subAssembly.id;
        const currentData = this.state.calculationContributionsData;

        // Don't reload if data already exists
        if (tabIndex === 0 && currentData.packageLogs !== null) return; // Package Logs
        if (tabIndex === 1 && currentData.salesLogs !== null) return; // Sales Logs

        if (!background) {
            this.setState({ loadingCalculationContributions: true });
        }

        try {
            const assemblyService = ServiceLocator.Instance.getAssemblyService();

            if (tabIndex === 0) {
                // Load Package Logs
                const allPackageLogs = await assemblyService.getPackageLogs();
                console.log('All package logs:', allPackageLogs);
                console.log('Current assemblyId:', assemblyId);
                console.log('Is editingAssembly:', !!editingAssembly);
                const bomIndex = buildBomIndex(assemblies, subAssemblies);
                const target = { id: assemblyId, kind: 'subassembly' };
                const filteredLogs = allPackageLogs.filter(log => {
                    if (editingAssembly) {
                        // For assemblies: match logs for this assembly
                        return log.assemblyFk === assemblyId || log.assemblyId === assemblyId;
                    } else if (editingSubAssembly) {
                        // For subassemblies: match logs where this subassembly was directly packaged,
                        // OR packaged into ANY ancestor assembly/subassembly (full BOM depth, not just direct parents).
                        const directMatch = log.subAssemblyFk === assemblyId || log.subAssemblyId === assemblyId;
                        if (directMatch) return true;

                        const refAssemblyId = log.assemblyFk || log.assemblyId;
                        if (refAssemblyId) {
                            const node = bomIndex.assemblyById.get(refAssemblyId);
                            if (node && containerHasTarget(node, 'assembly', target, bomIndex)) return true;
                        }

                        const refSubId = log.subAssemblyFk || log.subAssemblyId;
                        if (refSubId && refSubId !== assemblyId) {
                            const node = bomIndex.subById.get(refSubId);
                            if (node && containerHasTarget(node, 'subassembly', target, bomIndex)) return true;
                        }

                        return false;
                    }
                    return false;
                }).sort((a, b) => {
                    // Sort by logDate descending (newest first)
                    const dateA = new Date(a.logDate || 0);
                    const dateB = new Date(b.logDate || 0);
                    return dateB - dateA;
                });
                console.log('Filtered package logs:', filteredLogs.length);
                this.setState(prev => ({
                    calculationContributionsData: {
                        ...prev.calculationContributionsData,
                        packageLogs: filteredLogs
                    }
                }));
            } else if (tabIndex === 1) {
                // Load Sales Logs
                const allSalesLogs = await assemblyService.getSalesLogs();
                console.log('All sales logs:', allSalesLogs);
                console.log('Current assemblyId:', assemblyId);
                console.log('Is editingAssembly:', !!editingAssembly);
                const bomIndex = buildBomIndex(assemblies, subAssemblies);
                const target = { id: assemblyId, kind: 'subassembly' };
                const filteredLogs = allSalesLogs.filter(log => {
                    if (editingAssembly) {
                        // For assemblies: match logs for this assembly
                        return log.assemblyFk === assemblyId || log.assemblyId === assemblyId;
                    } else if (editingSubAssembly) {
                        // For subassemblies: match logs where this subassembly was directly sold,
                        // OR sold inside ANY ancestor assembly/subassembly (full BOM depth, not just direct parents).
                        const directMatch = log.subAssemblyFk === assemblyId || log.subAssemblyId === assemblyId;
                        if (directMatch) return true;

                        const refAssemblyId = log.assemblyFk || log.assemblyId;
                        if (refAssemblyId) {
                            const node = bomIndex.assemblyById.get(refAssemblyId);
                            if (node && containerHasTarget(node, 'assembly', target, bomIndex)) return true;
                        }

                        const refSubId = log.subAssemblyFk || log.subAssemblyId;
                        if (refSubId && refSubId !== assemblyId) {
                            const node = bomIndex.subById.get(refSubId);
                            if (node && containerHasTarget(node, 'subassembly', target, bomIndex)) return true;
                        }

                        return false;
                    }
                    return false;
                }).sort((a, b) => {
                    // Sort by logDate descending (newest first)
                    const dateA = new Date(a.logDate || 0);
                    const dateB = new Date(b.logDate || 0);
                    return dateB - dateA;
                });
                console.log('Filtered sales logs:', filteredLogs.length);
                this.setState(prev => ({
                    calculationContributionsData: {
                        ...prev.calculationContributionsData,
                        salesLogs: filteredLogs
                    }
                }));
            }
        } catch (error) {
            console.error('Error loading calculation contributions data:', error);
        } finally {
            if (!background) {
                this.setState({ loadingCalculationContributions: false });
            }
        }
    };

    startAddingBomItem = () => {
        this.setState({
            isAddingBomItem: true,
            newBomItemFrom: null,
            newBomItemQuantity: 1,
            newBomItemBomRequired: true
        });
    };

    cancelAddingBomItem = () => {
        this.setState({
            isAddingBomItem: false,
            newBomItemFrom: null,
            newBomItemQuantity: 1,
            newBomItemBomRequired: true
        });
    };

    handleNewBomItemFromChange = (event, newValue) => {
        if (!newValue) {
            this.setState({newBomItemFrom: null, newBomItemIsImpliedPart: false});
        } else {
            // Automatically set isImpliedPart to false for families and subassemblies
            const isImpliedPart = newValue.type === 'part' ? this.state.newBomItemIsImpliedPart : false;
            this.setState({
                newBomItemFrom: newValue,
                newBomItemIsImpliedPart: isImpliedPart
            });
        }
    };

    saveInlineBomConnection = () => {
        // Use the existing saveConnection logic but with new state variables
        const originalNewConnectionFrom = this.state.newConnectionFrom;
        const originalNewConnectionQuantity = this.state.newConnectionQuantity;
        const originalNewConnectionBomRequired = this.state.newConnectionBomRequired;

        // Temporarily set the old state variables to the new ones
        this.setState({
            newConnectionFrom: this.state.newBomItemFrom.value || this.state.newBomItemFrom,
            newConnectionQuantity: this.state.newBomItemQuantity,
            newConnectionBomRequired: this.state.newBomItemBomRequired
        }, () => {
            // Call the existing saveConnection method
            this.saveConnection();
            
            // Reset the inline adding state
            this.setState({
                isAddingBomItem: false,
                newBomItemFrom: null,
                newBomItemQuantity: 1,
                newBomItemBomRequired: true,
                newBomItemIsImpliedPart: false,
                // Restore original values
                newConnectionFrom: originalNewConnectionFrom,
                newConnectionQuantity: originalNewConnectionQuantity,
                newConnectionBomRequired: originalNewConnectionBomRequired
            });
        });
    };

    canSaveInlineBomItem = () => {
        return this.state.newBomItemFrom && this.state.newBomItemQuantity > 0;
    };

    // Inline editing methods
    startEditingBomItem = (item, itemType) => {
        this.setState({
            editingBomItem: item,
            editingBomItemType: itemType,
            editingBomItemPart: itemType === 'PartToAssembly' || itemType === 'PartToSubAssembly' ? item.partFk : item.subAssemblyFk,
            editingBomItemQuantity: item.quantity,
            editingBomItemBomRequired: item.requiredForBomCheck,
            editingBomItemIsImpliedPart: item.isImpliedPart || false
        });
    };

    cancelEditingBomItem = () => {
        this.setState({
            editingBomItem: null,
            editingBomItemType: null,
            editingBomItemPart: null,
            editingBomItemQuantity: 1,
            editingBomItemBomRequired: true,
            editingBomItemIsImpliedPart: false
        });
    };

    handleEditingBomItemPartChange = (event, newValue) => {
        if (!newValue) this.setState({editingBomItemPart: null});
        else {
            this.setState({editingBomItemPart: newValue.value});
        }
    };

    saveInlineBomItemEdit = () => {
        const self = this;
        const {editingBomItem, editingBomItemType, editingBomItemPart, editingBomItemQuantity, editingBomItemBomRequired, editingBomItemIsImpliedPart} = this.state;
        
        if (!editingBomItem || !editingBomItemType) return window.$alertWarning("Something went wrong, the item was not found.");

        let confirm = true;
        if (this.state.editingAssembly && this.state.editingAssembly.totalSold > 0)
            confirm = window.confirm("Editing this BOM changes records tied to sales data. You are about to alter a lot of data. Are you sure you wish to continue?");
        if (this.state.editingSubAssembly && this.state.editingSubAssembly.totalSold > 0)
            confirm = window.confirm("Editing this BOM changes records tied to sales data. You are about to alter a lot of data. Are you sure you wish to continue?");

        if (!confirm) return;

        const partService = ServiceLocator.Instance.getPartService();
        const assemblyService = ServiceLocator.Instance.getAssemblyService();

        // Check if part number changed
        const partChanged = editingBomItemPart !== (editingBomItemType === 'PartToAssembly' || editingBomItemType === 'PartToSubAssembly' ? editingBomItem.partFk : editingBomItem.subAssemblyFk);
        
        // Check if anything actually changed
        const quantityChanged = parseNumberInputValue(editingBomItemQuantity) !== editingBomItem.quantity;
        const bomRequiredChanged = editingBomItemBomRequired !== editingBomItem.requiredForBomCheck;
        const isImpliedPartChanged = editingBomItemIsImpliedPart !== (editingBomItem.isImpliedPart || false);
        const anythingChanged = partChanged || quantityChanged || bomRequiredChanged || isImpliedPartChanged;

        if (!anythingChanged) {
            // Nothing changed, just close the edit mode
            self.setState({
                editingBomItem: null,
                editingBomItemType: null,
                editingBomItemPart: null,
                editingBomItemQuantity: 1,
                editingBomItemBomRequired: true,
                savingConnection: false
            });
            return;
        }

        if (partChanged) {
            // Remove old connection and add new one with preserved order
            const promises = [];
            
            if (editingBomItemType === 'PartToAssembly') {
                promises.push(partService.deletePartsToAssemblyConnection(editingBomItem.id));
                promises.push(partService.connectPartToAssembly(editingBomItemPart, editingBomItem.assemblyFk, parseNumberInputValue(editingBomItemQuantity), editingBomItemBomRequired, undefined, editingBomItem.order));
            } else if (editingBomItemType === 'PartToSubAssembly') {
                promises.push(partService.deletePartsToSubAssemblyConnection(editingBomItem.id));
                promises.push(partService.connectPartToSubAssembly(editingBomItemPart, editingBomItem.subAssemblyFk, parseNumberInputValue(editingBomItemQuantity), editingBomItemBomRequired, undefined, editingBomItem.order));
            } else if (editingBomItemType === 'SubAssemblyToAssembly') {
                promises.push(assemblyService.deleteSubAssemblyToAssemblyConnection(editingBomItem.id));
                promises.push(assemblyService.connectSubAssemblyToAssembly(editingBomItemPart, editingBomItem.assemblyFk, parseNumberInputValue(editingBomItemQuantity), editingBomItemBomRequired, editingBomItem.order));
            }

            self.setState({savingConnection: true});
            Promise.all(promises).then(() => {
                // When part changes, we need to reload to get the correct order from server
                self.loadData();
                window.$alertSuccess("Item updated successfully");
                self.setState({
                    editingBomItem: null,
                    editingBomItemType: null,
                    editingBomItemPart: null,
                    editingBomItemQuantity: 1,
                    editingBomItemBomRequired: true,
                    savingConnection: false
                });
            }).catch((error) => {
                console.error("Error updating item:", error);
                window.$alertError("Failed to update item. Please try again.");
                self.setState({savingConnection: false});
            });
        } else {
            // Only quantity or BOM check changed, use direct update to preserve order
            const promises = [];
            
            if (editingBomItemType === 'PartToAssembly') {
                const updatedItem = {
                    ...editingBomItem,
                    quantity: parseNumberInputValue(editingBomItemQuantity),
                    requiredForBomCheck: editingBomItemBomRequired,
                    isImpliedPart: editingBomItemIsImpliedPart
                };
                promises.push(partService.updatePartsToAssembly(updatedItem));
            } else if (editingBomItemType === 'PartToSubAssembly') {
                const updatedItem = {
                    ...editingBomItem,
                    quantity: parseNumberInputValue(editingBomItemQuantity),
                    requiredForBomCheck: editingBomItemBomRequired,
                    isImpliedPart: editingBomItemIsImpliedPart
                };
                promises.push(partService.updatePartsToSubAssembly(updatedItem));
            } else if (editingBomItemType === 'SubAssemblyToAssembly') {
                const updatedItem = {
                    ...editingBomItem,
                    quantity: parseNumberInputValue(editingBomItemQuantity),
                    requiredForBomCheck: editingBomItemBomRequired,
                    isImpliedPart: editingBomItemIsImpliedPart
                };
                promises.push(assemblyService.updateSubAssemblyToAssembly(updatedItem));
            }

            self.setState({savingConnection: true});
            Promise.all(promises).then(() => {
                // When only quantity/BOM check changes, we can update local state
                self.updateLocalBomItem(editingBomItem, editingBomItemType, editingBomItem.partFk || editingBomItem.subAssemblyFk, parseNumberInputValue(editingBomItemQuantity), editingBomItemBomRequired, editingBomItemIsImpliedPart);
                window.$alertSuccess("Item updated successfully");
                self.setState({
                    editingBomItem: null,
                    editingBomItemType: null,
                    editingBomItemPart: null,
                    editingBomItemQuantity: 1,
                    editingBomItemBomRequired: true,
                    savingConnection: false
                });
            }).catch((error) => {
                console.error("Error updating item:", error);
                window.$alertError("Failed to update item. Please try again.");
                self.setState({savingConnection: false});
            });
        }
    };

    canSaveInlineBomItemEdit = () => {
        return this.state.editingBomItemPart && parseNumberInputValue(this.state.editingBomItemQuantity) > 0;
    };

    updateLocalBomItem = (oldItem, itemType, newPartId, newQuantity, newBomRequired, newIsImpliedPart) => {
        if (this.state.editingAssembly) {
            const updatedAssembly = { ...this.state.editingAssembly };
            
            if (itemType === 'PartToAssembly') {
                const partIndex = updatedAssembly.assembly.partsToAssemblies.findIndex(p => p.id === oldItem.id);
                if (partIndex !== -1) {
                    updatedAssembly.assembly.partsToAssemblies[partIndex] = {
                        ...updatedAssembly.assembly.partsToAssemblies[partIndex],
                        partFk: newPartId,
                        quantity: newQuantity,
                        requiredForBomCheck: newBomRequired,
                        isImpliedPart: newIsImpliedPart,
                        order: oldItem.order // Preserve the original order
                    };
                }
            } else if (itemType === 'SubAssemblyToAssembly') {
                const subAssemblyIndex = updatedAssembly.assembly.subAssembliesToAssemblies.findIndex(s => s.id === oldItem.id);
                if (subAssemblyIndex !== -1) {
                    updatedAssembly.assembly.subAssembliesToAssemblies[subAssemblyIndex] = {
                        ...updatedAssembly.assembly.subAssembliesToAssemblies[subAssemblyIndex],
                        subAssemblyFk: newPartId,
                        quantity: newQuantity,
                        requiredForBomCheck: newBomRequired,
                        isImpliedPart: newIsImpliedPart,
                        order: oldItem.order // Preserve the original order
                    };
                }
            }
            
            this.setState({ editingAssembly: updatedAssembly });
        } else if (this.state.editingSubAssembly) {
            const updatedSubAssembly = { ...this.state.editingSubAssembly };
            
            if (itemType === 'PartToSubAssembly') {
                const partIndex = updatedSubAssembly.subAssembly.partsToSubAssemblies.findIndex(p => p.id === oldItem.id);
                if (partIndex !== -1) {
                    updatedSubAssembly.subAssembly.partsToSubAssemblies[partIndex] = {
                        ...updatedSubAssembly.subAssembly.partsToSubAssemblies[partIndex],
                        partFk: newPartId,
                        quantity: newQuantity,
                        requiredForBomCheck: newBomRequired,
                        isImpliedPart: newIsImpliedPart,
                        order: oldItem.order // Preserve the original order
                    };
                }
            }
            
            this.setState({ editingSubAssembly: updatedSubAssembly });
        }
    };

    addPartToLocalBom = (partId, quantity, bomRequired) => {
        if (this.state.editingAssembly) {
            const updatedAssembly = { ...this.state.editingAssembly };
            const allItems = [
                ...updatedAssembly.assembly.partsToAssemblies,
                ...updatedAssembly.assembly.subAssembliesToAssemblies
            ];
            const maxOrder = allItems.length > 0 ? Math.max(...allItems.map(item => item.order || 0)) : 0;
            
            const newPartToAssembly = {
                id: uuid(),
                partFk: partId,
                assemblyFk: this.state.editingAssembly.assembly.id,
                quantity: quantity,
                requiredForBomCheck: bomRequired,
                order: maxOrder + 1
            };
            updatedAssembly.assembly.partsToAssemblies.push(newPartToAssembly);
            this.setState({ editingAssembly: updatedAssembly });
        } else if (this.state.editingSubAssembly) {
            const updatedSubAssembly = { ...this.state.editingSubAssembly };
            const maxOrder = updatedSubAssembly.subAssembly.partsToSubAssemblies.length > 0 ? 
                Math.max(...updatedSubAssembly.subAssembly.partsToSubAssemblies.map(p => p.order || 0)) : 0;
            
            const newPartToSubAssembly = {
                id: uuid(),
                partFk: partId,
                subAssemblyFk: this.state.editingSubAssembly.subAssembly.id,
                quantity: quantity,
                requiredForBomCheck: bomRequired,
                order: maxOrder + 1
            };
            updatedSubAssembly.subAssembly.partsToSubAssemblies.push(newPartToSubAssembly);
            this.setState({ editingSubAssembly: updatedSubAssembly });
        }
    };

    addSubAssemblyToLocalBom = (subAssemblyId, quantity, bomRequired) => {
        if (this.state.editingAssembly) {
            const updatedAssembly = { ...this.state.editingAssembly };
            const allItems = [
                ...updatedAssembly.assembly.partsToAssemblies,
                ...updatedAssembly.assembly.subAssembliesToAssemblies
            ];
            const maxOrder = allItems.length > 0 ? Math.max(...allItems.map(item => item.order || 0)) : 0;
            
            const newSubAssemblyToAssembly = {
                id: uuid(),
                subAssemblyFk: subAssemblyId,
                assemblyFk: this.state.editingAssembly.assembly.id,
                quantity: quantity,
                requiredForBomCheck: bomRequired,
                order: maxOrder + 1
            };
            updatedAssembly.assembly.subAssembliesToAssemblies.push(newSubAssemblyToAssembly);
            this.setState({ editingAssembly: updatedAssembly });
        }
    };

    addFamilyToLocalBom = (family, familyParts, quantity, bomRequired) => {
        if (this.state.editingAssembly) {
            const updatedAssembly = { ...this.state.editingAssembly };
            const allItems = [
                ...updatedAssembly.assembly.partsToAssemblies,
                ...updatedAssembly.assembly.subAssembliesToAssemblies
            ];
            const maxOrder = allItems.length > 0 ? Math.max(...allItems.map(item => item.order || 0)) : 0;
            
            familyParts.forEach((part, index) => {
                const partToFamily = part.part.partToFamilies.find(ptf => ptf.familyId === family.id);
                const packageQuantity = parseInt(partToFamily?.packageQuantity) || 1;
                const totalQuantity = packageQuantity * quantity;
                
                const newPartToAssembly = {
                    id: uuid(),
                    partFk: part.part.id,
                    assemblyFk: this.state.editingAssembly.assembly.id,
                    quantity: totalQuantity,
                    requiredForBomCheck: bomRequired,
                    order: maxOrder + 1 + index, // Each family part gets a unique order
                    partFamilyId: family.id,
                    partFamilyName: family.partFamilyId,
                    partFamilyDescription: family.description,
                    partFamilyImageURL: family.imageURL
                };
                updatedAssembly.assembly.partsToAssemblies.push(newPartToAssembly);
            });
            
            this.setState({ editingAssembly: updatedAssembly });
        } else if (this.state.editingSubAssembly) {
            const updatedSubAssembly = { ...this.state.editingSubAssembly };
            const maxOrder = Math.max(...updatedSubAssembly.subAssembly.partsToSubAssemblies.map(p => p.order || 0));
            
            familyParts.forEach((part, index) => {
                const partToFamily = part.part.partToFamilies.find(ptf => ptf.familyId === family.id);
                const packageQuantity = parseInt(partToFamily?.packageQuantity) || 1;
                const totalQuantity = packageQuantity * quantity;
                
                const newPartToSubAssembly = {
                    id: uuid(),
                    partFk: part.part.id,
                    subAssemblyFk: this.state.editingSubAssembly.subAssembly.id,
                    quantity: totalQuantity,
                    requiredForBomCheck: bomRequired,
                    order: maxOrder + 1,
                    partFamilyId: family.id,
                    partFamilyName: family.partFamilyId,
                    partFamilyDescription: family.description,
                    partFamilyImageURL: family.imageURL
                };
                updatedSubAssembly.subAssembly.partsToSubAssemblies.push(newPartToSubAssembly);
            });
            
            this.setState({ editingSubAssembly: updatedSubAssembly });
        }
    };

    groupPartsByFamily = (partsToAssemblies) => {
        const families = {};
        const individualParts = [];

        partsToAssemblies.forEach((partToAssembly) => {
            if (partToAssembly.partFamilyId) {
                if (!families[partToAssembly.partFamilyId]) {
                    families[partToAssembly.partFamilyId] = {
                        familyId: partToAssembly.partFamilyId,
                        familyName: partToAssembly.partFamilyName,
                        familyDescription: partToAssembly.partFamilyDescription,
                        familyImageURL: partToAssembly.partFamilyImageURL,
                        parts: []
                    };
                }
                families[partToAssembly.partFamilyId].parts.push(partToAssembly);
            } else {
                individualParts.push(partToAssembly);
            }
        });

        // Sort parts within each family by order
        Object.values(families).forEach(family => {
            family.parts.sort((a, b) => (a.order || 0) - (b.order || 0));
        });

        return {
            families: Object.values(families),
            individualParts
        };
    };

    // Group all items (subassemblies and parts) for drag and drop, with family grouping
    groupItemsForBomTable = (subAssembliesToAssemblies, partsToAssemblies, subAssembliesToSubAssemblies = []) =>
        groupBomLinesByOrder(subAssembliesToAssemblies, partsToAssemblies, subAssembliesToSubAssemblies);

    // Calculate total cost of a subassembly (parts + nested subassemblies). Used for BOM unit/ext cost and total.
    calculateSubAssemblyCost = (subAssembly) => {
        let cost = 0;
        if (subAssembly.partsToSubAssemblies && subAssembly.partsToSubAssemblies.length > 0) {
            subAssembly.partsToSubAssemblies.forEach((partToSubAssembly) => {
                const expandedPart = this.state.parts && this.state.parts.find(x => x.part.id === partToSubAssembly.partFk);
                if (expandedPart && !isBomLineImpliedPart(partToSubAssembly)) {
                    const unitCost = expandedPart.quantity ? (expandedPart.cost / expandedPart.quantity) : 0;
                    cost += unitCost * (partToSubAssembly.quantity || 0);
                }
            });
        }
        if (subAssembly.childSubAssemblies && subAssembly.childSubAssemblies.length > 0) {
            const sorted = [...subAssembly.childSubAssemblies].sort((a, b) => (a.order || 0) - (b.order || 0));
            sorted.forEach((childRelation) => {
                const childId = childRelation.childSubAssemblyFk || childRelation.ChildSubAssemblyFk;
                const childExpanded = this.state.subAssemblies && this.state.subAssemblies.find(x => x.subAssembly.id === childId);
                if (childExpanded && childExpanded.subAssembly) {
                    cost += this.calculateSubAssemblyCost(childExpanded.subAssembly) * (childRelation.quantity || 0);
                }
            });
        }
        return cost;
    };

    // Get total part cost for current BOM (assembly or subassembly). Uses same logic as BillOfMaterials.js.
    getBomTotalPartCost = () => {
        let totalPartCost = 0;
        const parts = this.state.parts || [];
        const subAssembliesList = this.state.subAssemblies || [];

        if (this.state.editingAssembly) {
            const assembly = this.state.editingAssembly.assembly;
            (assembly.subAssembliesToAssemblies || []).forEach((sata) => {
                const subExpanded = subAssembliesList.find(x => x.subAssembly.id === sata.subAssemblyFk);
                if (subExpanded && subExpanded.subAssembly) {
                    const unitCost = this.calculateSubAssemblyCost(subExpanded.subAssembly);
                    totalPartCost += unitCost * (sata.quantity || 0);
                }
            });
            (assembly.partsToAssemblies || []).forEach((pta) => {
                if (pta.partFamilyId) {
                    const familyParts = (assembly.partsToAssemblies || []).filter(p => p.partFamilyId === pta.partFamilyId);
                    if (familyParts.length > 0 && familyParts[0] === pta) {
                        familyParts.forEach((fp) => {
                            const expandedPart = parts.find(x => x.part.id === fp.partFk);
                            if (expandedPart && !isBomLineImpliedPart(fp)) {
                                const unitCost = expandedPart.quantity ? (expandedPart.cost / expandedPart.quantity) : 0;
                                totalPartCost += unitCost * (fp.quantity || 0);
                            }
                        });
                    }
                } else {
                    const expandedPart = parts.find(x => x.part.id === pta.partFk);
                    if (expandedPart && !isBomLineImpliedPart(pta)) {
                        const unitCost = expandedPart.quantity ? (expandedPart.cost / expandedPart.quantity) : 0;
                        totalPartCost += unitCost * (pta.quantity || 0);
                    }
                }
            });
        } else if (this.state.editingSubAssembly) {
            const subAssembly = this.state.editingSubAssembly.subAssembly;
            (subAssembly.childSubAssemblies || []).forEach((sats) => {
                const childId = sats.childSubAssemblyFk || sats.ChildSubAssemblyFk;
                const childExpanded = subAssembliesList.find(x => x.subAssembly.id === childId);
                if (childExpanded && childExpanded.subAssembly) {
                    const unitCost = this.calculateSubAssemblyCost(childExpanded.subAssembly);
                    totalPartCost += unitCost * (sats.quantity || 0);
                }
            });
            (subAssembly.partsToSubAssemblies || []).forEach((pts) => {
                if (pts.partFamilyId) {
                    const familyParts = (subAssembly.partsToSubAssemblies || []).filter(p => p.partFamilyId === pts.partFamilyId);
                    if (familyParts.length > 0 && familyParts[0] === pts) {
                        familyParts.forEach((fp) => {
                            const expandedPart = parts.find(x => x.part.id === fp.partFk);
                            if (expandedPart && !isBomLineImpliedPart(fp)) {
                                const unitCost = expandedPart.quantity ? (expandedPart.cost / expandedPart.quantity) : 0;
                                totalPartCost += unitCost * (fp.quantity || 0);
                            }
                        });
                    }
                } else {
                    const expandedPart = parts.find(x => x.part.id === pts.partFk);
                    if (expandedPart && !isBomLineImpliedPart(pts)) {
                        const unitCost = expandedPart.quantity ? (expandedPart.cost / expandedPart.quantity) : 0;
                        totalPartCost += unitCost * (pts.quantity || 0);
                    }
                }
            });
        }
        return totalPartCost;
    };

    // Check if adding a subassembly would create a circular dependency
    wouldCreateCircularDependency = (childSubAssemblyId, parentSubAssemblyId) => {
        // If trying to add parent as child of itself
        if (childSubAssemblyId === parentSubAssemblyId) return true;
        
        // Check if the child already has the parent in its hierarchy
        const checkPath = (currentChildId, targetParentId, visited = new Set()) => {
            if (visited.has(currentChildId)) return false; // Already checked this path
            visited.add(currentChildId);
            
            const currentChild = this.state.subAssemblies.find(x => x.subAssembly.id === currentChildId);
            if (!currentChild) return false;
            
            // Check all subassemblies that this child depends on (using correct property name)
            const childDependencies = currentChild.subAssembly.childSubAssemblies || [];
            for (const dep of childDependencies) {
                if (dep.childSubAssemblyFk === targetParentId) return true;
                if (checkPath(dep.childSubAssemblyFk, targetParentId, visited)) return true;
            }
            return false;
        };
        
        return checkPath(childSubAssemblyId, parentSubAssemblyId);
    };

    getAllBomItemsForDrag = () => {
        let existingItems = [];
        
        if (this.state.editingAssembly) {
            existingItems = this.groupItemsForBomTable(
                this.state.editingAssembly.assembly.subAssembliesToAssemblies,
                this.state.editingAssembly.assembly.partsToAssemblies
            );
        } else if (this.state.editingSubAssembly) {
            existingItems = this.groupItemsForBomTable(
                [], // No assembly-to-subassembly relationships when editing subassembly
                this.state.editingSubAssembly.subAssembly.partsToSubAssemblies,
                this.state.editingSubAssembly.subAssembly.childSubAssemblies || []
            );
        }
        
        // Add new items with drag metadata
        const newItems = (this.state.newBomItems || []).map(item => ({
            ...item,
            dragId: `new-item-${item.id}`,
            isNew: true
        }));

        return [...existingItems, ...newItems].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    };

    renderBomTableItem = (item, idx, provided) => {
        if (item.itemType === 'subassembly') {
            const subAssemblyExpanded = this.state.subAssemblies.find(x => x.subAssembly.id === item.subAssemblyFk);
            if (!subAssemblyExpanded || !subAssemblyExpanded.subAssembly) {
                // SubAssembly not found in state, skip rendering this item
                return null;
            }
            const subAssembly = subAssemblyExpanded.subAssembly;
            if (!subAssembly.isActive) return null;
            
            const isEditing = this.state.editingBomItem && this.state.editingBomItem.id === item.id;
            const isMarkedForDeletion = this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].markedForDeletion;
            
            if (this.state.isBulkEditingBom) {
                return (
                    <tr ref={provided.innerRef} {...provided.draggableProps} style={{
                        ...provided.draggableProps.style,
                        opacity: isMarkedForDeletion ? 0.5 : 1,
                        textDecoration: isMarkedForDeletion ? 'line-through' : 'none'
                    }}>
                        <td {...provided.dragHandleProps}>
                            <FontAwesomeIcon icon={faGripVertical} />
                        </td>
                        <td className="text-left">
                            <Autocomplete autoHighlight
                                options={[
                                    ...this.state.partFamilies.map(family => ({
                                        title: `${family.partFamilyId} (Family)`,
                                        value: family.id,
                                        type: 'family',
                                        family: family
                                    })),
                                    ...this.state.parts.filter(x => x.part.isActive).map(p => ({
                                        title: `${p.part.partId} rev ${p.part.revision}${p.part.currentlyPurchasing ? ' 🛒' : ''}`,
                                        value: p.part.id,
                                        type: 'part',
                                        part: p.part,
                                        partId: p.part.partId,
                                        revision: p.part.revision,
                                        description: p.part.description,
                                        currentlyPurchasing: p.part.currentlyPurchasing
                                    })),
                                    ...this.state.subAssemblies.filter(sub => {
                                        if (!sub.subAssembly.isActive) return false;
                                        
                                        // If editing a subassembly, exclude self-reference and circular dependencies
                                        if (this.state.editingSubAssembly) {
                                            if (sub.subAssembly.id === this.state.editingSubAssembly.subAssembly.id) return false;
                                            if (this.wouldCreateCircularDependency(sub.subAssembly.id, this.state.editingSubAssembly.subAssembly.id)) return false;
                                        }
                                        
                                        return true;
                                    }).map(sub => ({
                                        title: `${sub.subAssembly.subAssemblyId} rev ${sub.subAssembly.revision}${sub.subAssembly.currentlyPackaging ? ' 📦' : ''}`,
                                        value: sub.subAssembly.id,
                                        type: 'subassembly',
                                        partId: sub.subAssembly.subAssemblyId,
                                        revision: sub.subAssembly.revision,
                                        description: sub.subAssembly.description,
                                        currentlyPackaging: sub.subAssembly.currentlyPackaging
                                    }))
                                ].sort((a, b) => a.title.localeCompare(b.title))}
                                onChange={(event, newValue) => {
                                    if (newValue) {
                                        if (newValue.type === 'family') {
                                            this.updateBomItem(item.id, 'partFamilyId', newValue.value);
                                            this.updateBomItem(item.id, 'partFk', null);
                                            this.updateBomItem(item.id, 'subAssemblyFk', null);
                                            this.updateBomItem(item.id, 'itemType', 'family');
                                        } else if (newValue.type === 'subassembly') {
                                            this.updateBomItem(item.id, 'subAssemblyFk', newValue.value);
                                            this.updateBomItem(item.id, 'partFk', null);
                                            this.updateBomItem(item.id, 'partFamilyId', null);
                                            this.updateBomItem(item.id, 'itemType', 'subassembly');
                                        } else if (newValue.type === 'part') {
                                            this.updateBomItem(item.id, 'partFk', newValue.value);
                                            this.updateBomItem(item.id, 'subAssemblyFk', null);
                                            this.updateBomItem(item.id, 'partFamilyId', null);
                                            this.updateBomItem(item.id, 'itemType', 'part');
                                        }
                                    }
                                }}
                                getOptionLabel={(option) => option.title}
                                renderOption={(option) => <PartAutocompleteOption option={option} classes={this.props.classes} />}
                                value={(() => {
                                    // Check for changed values first, then fall back to original item values
                                    const currentPartFamilyId = (this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].partFamilyId !== undefined) 
                                        ? this.state.bomChanges[item.id].partFamilyId 
                                        : item.partFamilyId;
                                    const currentPartFk = (this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].partFk !== undefined) 
                                        ? this.state.bomChanges[item.id].partFk 
                                        : item.partFk;
                                    const currentSubAssemblyFk = (this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].subAssemblyFk !== undefined) 
                                        ? this.state.bomChanges[item.id].subAssemblyFk 
                                        : item.subAssemblyFk;
                                    
                                    if (currentPartFamilyId) {
                                        const currentFamily = this.state.partFamilies.find(f => f.id === currentPartFamilyId);
                                        if (currentFamily) {
                                            return {
                                                title: `${currentFamily.partFamilyId} (Family)`,
                                                value: currentPartFamilyId,
                                                type: 'family'
                                            };
                                        }
                                    } else if (currentPartFk) {
                                        const currentPart = this.state.parts.find(p => p.part.id === currentPartFk);
                                        if (currentPart) {
                                            return {
                                                title: `${currentPart.part.partId} rev ${currentPart.part.revision}${currentPart.part.currentlyPurchasing ? ' 🛒' : ''}`,
                                                value: currentPartFk,
                                                type: 'part',
                                                part: currentPart.part,
                                                partId: currentPart.part.partId,
                                                revision: currentPart.part.revision,
                                                description: currentPart.part.description,
                                                currentlyPurchasing: currentPart.part.currentlyPurchasing
                                            };
                                        }
                                    } else if (currentSubAssemblyFk) {
                                        const currentSubAssembly = this.state.subAssemblies.find(s => s.subAssembly.id === currentSubAssemblyFk);
                                        if (currentSubAssembly) {
                                            return {
                                                title: `${currentSubAssembly.subAssembly.subAssemblyId} rev ${currentSubAssembly.subAssembly.revision}${currentSubAssembly.subAssembly.currentlyPackaging ? ' 📦' : ''}`,
                                                value: currentSubAssemblyFk,
                                                type: 'subassembly',
                                                partId: currentSubAssembly.subAssembly.subAssemblyId,
                                                revision: currentSubAssembly.subAssembly.revision,
                                                description: currentSubAssembly.subAssembly.description,
                                                currentlyPackaging: currentSubAssembly.subAssembly.currentlyPackaging
                                            };
                                        }
                                    }
                                    return null;
                                })()}
                                style={{width: '100%'}}
                                size="small"
                                renderInput={(params) => <TextField
                                    {...params}
                                    variant="outlined"
                                    size="small"
                                    autoComplete="new-password"
                                />}
                            />
                        </td>
                        <td>
                            {(() => {
                                // Get the current item based on changes
                                const currentPartFamilyId = (this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].partFamilyId !== undefined) 
                                    ? this.state.bomChanges[item.id].partFamilyId : item.partFamilyId;
                                const currentPartFk = (this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].partFk !== undefined) 
                                    ? this.state.bomChanges[item.id].partFk : item.partFk;
                                const currentSubAssemblyFk = (this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].subAssemblyFk !== undefined) 
                                    ? this.state.bomChanges[item.id].subAssemblyFk : item.subAssemblyFk;
                                
                                if (currentPartFamilyId) {
                                    return ''; // Families don't have revisions
                                } else if (currentPartFk) {
                                    const part = this.state.parts.find(p => p.part.id === currentPartFk);
                                    return part ? part.part.revision : '';
                                } else if (currentSubAssemblyFk) {
                                    const subAssembly = this.state.subAssemblies.find(s => s.subAssembly.id === currentSubAssemblyFk);
                                    return subAssembly ? subAssembly.subAssembly.revision : '';
                                }
                                return subAssembly.revision || '';
                            })()}
                        </td>
                        <td className="description-ellipsis text-left">
                            {(() => {
                                // Get the current item based on changes
                                const currentPartFamilyId = (this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].partFamilyId !== undefined) 
                                    ? this.state.bomChanges[item.id].partFamilyId : item.partFamilyId;
                                const currentPartFk = (this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].partFk !== undefined) 
                                    ? this.state.bomChanges[item.id].partFk : item.partFk;
                                const currentSubAssemblyFk = (this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].subAssemblyFk !== undefined) 
                                    ? this.state.bomChanges[item.id].subAssemblyFk : item.subAssemblyFk;
                                
                                if (currentPartFamilyId) {
                                    const family = this.state.partFamilies.find(f => f.id === currentPartFamilyId);
                                    return family ? family.description || '' : '';
                                } else if (currentPartFk) {
                                    const part = this.state.parts.find(p => p.part.id === currentPartFk);
                                    return part ? part.part.description : '';
                                } else if (currentSubAssemblyFk) {
                                    const subAssembly = this.state.subAssemblies.find(s => s.subAssembly.id === currentSubAssemblyFk);
                                    return subAssembly ? subAssembly.subAssembly.description : '';
                                }
                                return subAssembly.description || '';
                            })()}
                        </td>
                        <td>
                            <TextField
                                value={getNumberInputDisplayValue((this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].quantity !== undefined) ? this.state.bomChanges[item.id].quantity : item.quantity)}
                                onChange={(e) => this.updateBomItem(item.id, 'quantity', e.target.value)}
                                type="number"
                                variant="outlined"
                                size="small"
                                style={{width: '60px'}}
                                inputProps={{min: 0, step: 1}}
                            />
                        </td>
                        <td style={{textAlign: 'center'}}>
                            <Checkbox
                                checked={(this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].requiredForBomCheck !== undefined) ? this.state.bomChanges[item.id].requiredForBomCheck : item.requiredForBomCheck}
                                onChange={(e) => this.updateBomItem(item.id, 'requiredForBomCheck', e.target.checked)}
                                size="small"
                            />
                        </td>
                        <td style={{textAlign: 'center'}}>
                            <Checkbox
                                checked={(this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].isImpliedPart !== undefined) ? this.state.bomChanges[item.id].isImpliedPart : item.isImpliedPart}
                                onChange={(e) => this.updateBomItem(item.id, 'isImpliedPart', e.target.checked)}
                                size="small"
                            />
                        </td>
                        <td>${(this.calculateSubAssemblyCost(subAssembly)).toFixed(2)}</td>
                        <td>${(this.calculateSubAssemblyCost(subAssembly) * (parseNumberInputValue((this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].quantity !== undefined) ? this.state.bomChanges[item.id].quantity : item.quantity) || 0)).toFixed(2)}</td>
                        <td>
                            <FontAwesomeIcon 
                                icon={faTrash} 
                                size="1x" 
                                style={{cursor: "pointer", color: '#dc3545'}}
                                onClick={() => {
                                    // Mark item for deletion instead of immediately severing
                                    this.updateBomItem(item.id, 'markedForDeletion', true);
                                }}
                            />
                        </td>
                    </tr>
                );
            } else {
                const subAssemblyUnitCost = this.calculateSubAssemblyCost(subAssembly);
                const subAssemblyQty = parseNumberInputValue(item.quantity) || 0;
                return (
                    <tr ref={provided.innerRef} {...provided.draggableProps} style={{
                        ...provided.draggableProps.style,
                        opacity: isMarkedForDeletion ? 0.5 : 1,
                        textDecoration: isMarkedForDeletion ? 'line-through' : 'none'
                    }}>
                        <td className="text-left">
                            <a 
                                href={`/admin/assemblies/${item.subAssemblyFk}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{cursor: 'pointer', color: "black", fontWeight: 'bold', textDecoration: 'none'}}
                                onClick={(e) => {
                                    if (!e.ctrlKey && !e.metaKey) {
                                        e.preventDefault();
                                        window.location.href = `/admin/assemblies/${item.subAssemblyFk}`;
                                    }
                                }}
                            >
                                {subAssembly.subAssemblyId}
                            </a>
                        </td>
                        <td>
                            {(() => {
                                // Get the current item based on changes
                                const currentPartFamilyId = (this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].partFamilyId !== undefined) 
                                    ? this.state.bomChanges[item.id].partFamilyId : item.partFamilyId;
                                const currentPartFk = (this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].partFk !== undefined) 
                                    ? this.state.bomChanges[item.id].partFk : item.partFk;
                                const currentSubAssemblyFk = (this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].subAssemblyFk !== undefined) 
                                    ? this.state.bomChanges[item.id].subAssemblyFk : item.subAssemblyFk;
                                
                                if (currentPartFamilyId) {
                                    return ''; // Families don't have revisions
                                } else if (currentPartFk) {
                                    const part = this.state.parts.find(p => p.part.id === currentPartFk);
                                    return part ? part.part.revision : '';
                                } else if (currentSubAssemblyFk) {
                                    const subAssembly = this.state.subAssemblies.find(s => s.subAssembly.id === currentSubAssemblyFk);
                                    return subAssembly ? subAssembly.subAssembly.revision : '';
                                }
                                return subAssembly.revision || '';
                            })()}
                        </td>
                        <td className="description-ellipsis text-left">
                            {(() => {
                                // Get the current item based on changes
                                const currentPartFamilyId = (this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].partFamilyId !== undefined) 
                                    ? this.state.bomChanges[item.id].partFamilyId : item.partFamilyId;
                                const currentPartFk = (this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].partFk !== undefined) 
                                    ? this.state.bomChanges[item.id].partFk : item.partFk;
                                const currentSubAssemblyFk = (this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].subAssemblyFk !== undefined) 
                                    ? this.state.bomChanges[item.id].subAssemblyFk : item.subAssemblyFk;
                                
                                if (currentPartFamilyId) {
                                    const family = this.state.partFamilies.find(f => f.id === currentPartFamilyId);
                                    return family ? family.description || '' : '';
                                } else if (currentPartFk) {
                                    const part = this.state.parts.find(p => p.part.id === currentPartFk);
                                    return part ? part.part.description : '';
                                } else if (currentSubAssemblyFk) {
                                    const subAssembly = this.state.subAssemblies.find(s => s.subAssembly.id === currentSubAssemblyFk);
                                    return subAssembly ? subAssembly.subAssembly.description : '';
                                }
                                return subAssembly.description || '';
                            })()}
                        </td>
                        <td>
                            <span>{getNumberInputDisplayValue(item.quantity)}</span>
                        </td>
                        <td style={{textAlign: 'center'}}>
                            <span>
                                {item.requiredForBomCheck ? <FontAwesomeIcon icon={faCheck} size="1x"/> : ''}
                            </span>
                        </td>
                        <td style={{textAlign: 'center'}}>
                            <span>
                                {item.isImpliedPart ? <FontAwesomeIcon icon={faCheck} size="1x"/> : ''}
                            </span>
                        </td>
                        <td>${subAssemblyUnitCost.toFixed(2)}</td>
                        <td>${(subAssemblyUnitCost * subAssemblyQty).toFixed(2)}</td>
                    </tr>
                );
            }
        } else if (item.itemType === 'part') {
            const partExpanded = this.state.parts.find(x => x.part.id === item.partFk);
            if (!partExpanded || !partExpanded.part) {
                // Part not found in state, skip rendering this item
                return null;
            }
            const part = partExpanded.part;
            if (!part.isActive) return null;
            
            const isEditing = this.state.editingBomItem && this.state.editingBomItem.id === item.id;
            const isMarkedForDeletion = this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].markedForDeletion;
            
            if (this.state.isBulkEditingBom) {
                return (
                    <tr ref={provided.innerRef} {...provided.draggableProps} style={{
                        ...provided.draggableProps.style,
                        opacity: isMarkedForDeletion ? 0.5 : 1,
                        textDecoration: isMarkedForDeletion ? 'line-through' : 'none'
                    }}>
                        <td {...provided.dragHandleProps}>
                            <FontAwesomeIcon icon={faGripVertical} />
                        </td>
                        <td className="text-left">
                            <Autocomplete autoHighlight
                                options={[
                                    ...this.state.partFamilies.map(family => ({
                                        title: `${family.partFamilyId} (Family)`,
                                        value: family.id,
                                        type: 'family',
                                        family: family
                                    })),
                                    ...this.state.parts.filter(x => x.part.isActive).map(p => ({
                                        title: `${p.part.partId} rev ${p.part.revision}${p.part.currentlyPurchasing ? ' 🛒' : ''}`,
                                        value: p.part.id,
                                        type: 'part',
                                        part: p.part,
                                        partId: p.part.partId,
                                        revision: p.part.revision,
                                        description: p.part.description,
                                        currentlyPurchasing: p.part.currentlyPurchasing
                                    })),
                                    ...this.state.subAssemblies.filter(sub => {
                                        if (!sub.subAssembly.isActive) return false;
                                        
                                        // If editing a subassembly, exclude self-reference and circular dependencies
                                        if (this.state.editingSubAssembly) {
                                            if (sub.subAssembly.id === this.state.editingSubAssembly.subAssembly.id) return false;
                                            if (this.wouldCreateCircularDependency(sub.subAssembly.id, this.state.editingSubAssembly.subAssembly.id)) return false;
                                        }
                                        
                                        return true;
                                    }).map(sub => ({
                                        title: `${sub.subAssembly.subAssemblyId} rev ${sub.subAssembly.revision}${sub.subAssembly.currentlyPackaging ? ' 📦' : ''}`,
                                        value: sub.subAssembly.id,
                                        type: 'subassembly',
                                        partId: sub.subAssembly.subAssemblyId,
                                        revision: sub.subAssembly.revision,
                                        description: sub.subAssembly.description,
                                        currentlyPackaging: sub.subAssembly.currentlyPackaging
                                    }))
                                ].sort((a, b) => a.title.localeCompare(b.title))}
                                onChange={(event, newValue) => {
                                    if (newValue) {
                                        if (newValue.type === 'family') {
                                            this.updateBomItem(item.id, 'partFamilyId', newValue.value);
                                            this.updateBomItem(item.id, 'partFk', null);
                                            this.updateBomItem(item.id, 'subAssemblyFk', null);
                                            this.updateBomItem(item.id, 'itemType', 'family');
                                        } else if (newValue.type === 'subassembly') {
                                            this.updateBomItem(item.id, 'subAssemblyFk', newValue.value);
                                            this.updateBomItem(item.id, 'partFk', null);
                                            this.updateBomItem(item.id, 'partFamilyId', null);
                                            this.updateBomItem(item.id, 'itemType', 'subassembly');
                                        } else if (newValue.type === 'part') {
                                            this.updateBomItem(item.id, 'partFk', newValue.value);
                                            this.updateBomItem(item.id, 'subAssemblyFk', null);
                                            this.updateBomItem(item.id, 'partFamilyId', null);
                                            this.updateBomItem(item.id, 'itemType', 'part');
                                        }
                                    }
                                }}
                                getOptionLabel={(option) => option.title}
                                renderOption={(option) => <PartAutocompleteOption option={option} classes={this.props.classes} />}
                                value={(() => {
                                    // Check for changed values first, then fall back to original item values
                                    const currentPartFamilyId = (this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].partFamilyId !== undefined) 
                                        ? this.state.bomChanges[item.id].partFamilyId 
                                        : item.partFamilyId;
                                    const currentPartFk = (this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].partFk !== undefined) 
                                        ? this.state.bomChanges[item.id].partFk 
                                        : item.partFk;
                                    const currentSubAssemblyFk = (this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].subAssemblyFk !== undefined) 
                                        ? this.state.bomChanges[item.id].subAssemblyFk 
                                        : item.subAssemblyFk;
                                    
                                    if (currentPartFamilyId) {
                                        const currentFamily = this.state.partFamilies.find(f => f.id === currentPartFamilyId);
                                        if (currentFamily) {
                                            return {
                                                title: `${currentFamily.partFamilyId} (Family)`,
                                                value: currentPartFamilyId,
                                                type: 'family'
                                            };
                                        }
                                    } else if (currentPartFk) {
                                        const currentPart = this.state.parts.find(p => p.part.id === currentPartFk);
                                        if (currentPart) {
                                            return {
                                                title: `${currentPart.part.partId} rev ${currentPart.part.revision}${currentPart.part.currentlyPurchasing ? ' 🛒' : ''}`,
                                                value: currentPartFk,
                                                type: 'part',
                                                part: currentPart.part,
                                                partId: currentPart.part.partId,
                                                revision: currentPart.part.revision,
                                                description: currentPart.part.description,
                                                currentlyPurchasing: currentPart.part.currentlyPurchasing
                                            };
                                        }
                                    } else if (currentSubAssemblyFk) {
                                        const currentSubAssembly = this.state.subAssemblies.find(s => s.subAssembly.id === currentSubAssemblyFk);
                                        if (currentSubAssembly) {
                                            return {
                                                title: `${currentSubAssembly.subAssembly.subAssemblyId} rev ${currentSubAssembly.subAssembly.revision}${currentSubAssembly.subAssembly.currentlyPackaging ? ' 📦' : ''}`,
                                                value: currentSubAssemblyFk,
                                                type: 'subassembly',
                                                partId: currentSubAssembly.subAssembly.subAssemblyId,
                                                revision: currentSubAssembly.subAssembly.revision,
                                                description: currentSubAssembly.subAssembly.description,
                                                currentlyPackaging: currentSubAssembly.subAssembly.currentlyPackaging
                                            };
                                        }
                                    }
                                    return null;
                                })()}
                                style={{width: '100%'}}
                                size="small"
                                renderInput={(params) => <TextField
                                    {...params}
                                    variant="outlined"
                                    size="small"
                                    autoComplete="new-password"
                                />}
                            />
                        </td>
                    <td>
                        {(() => {
                            // Get the current item based on changes
                            const currentPartFamilyId = (this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].partFamilyId !== undefined) 
                                ? this.state.bomChanges[item.id].partFamilyId : item.partFamilyId;
                            const currentPartFk = (this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].partFk !== undefined) 
                                ? this.state.bomChanges[item.id].partFk : item.partFk;
                            const currentSubAssemblyFk = (this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].subAssemblyFk !== undefined) 
                                ? this.state.bomChanges[item.id].subAssemblyFk : item.subAssemblyFk;
                            
                            if (currentPartFamilyId) {
                                return ''; // Families don't have revisions
                            } else if (currentPartFk) {
                                const currentPart = this.state.parts.find(p => p.part.id === currentPartFk);
                                return currentPart ? currentPart.part.revision : '';
                            } else if (currentSubAssemblyFk) {
                                const subAssembly = this.state.subAssemblies.find(s => s.subAssembly.id === currentSubAssemblyFk);
                                return subAssembly ? subAssembly.subAssembly.revision : '';
                            }
                            return part.revision || '';
                        })()}
                    </td>
                    <td className="description-ellipsis text-left">
                        {(() => {
                            // Get the current item based on changes
                            const currentPartFamilyId = (this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].partFamilyId !== undefined) 
                                ? this.state.bomChanges[item.id].partFamilyId : item.partFamilyId;
                            const currentPartFk = (this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].partFk !== undefined) 
                                ? this.state.bomChanges[item.id].partFk : item.partFk;
                            const currentSubAssemblyFk = (this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].subAssemblyFk !== undefined) 
                                ? this.state.bomChanges[item.id].subAssemblyFk : item.subAssemblyFk;
                            
                            if (currentPartFamilyId) {
                                const family = this.state.partFamilies.find(f => f.id === currentPartFamilyId);
                                return family ? family.description || '' : '';
                            } else if (currentPartFk) {
                                const currentPart = this.state.parts.find(p => p.part.id === currentPartFk);
                                return currentPart ? currentPart.part.description : '';
                            } else if (currentSubAssemblyFk) {
                                const subAssembly = this.state.subAssemblies.find(s => s.subAssembly.id === currentSubAssemblyFk);
                                return subAssembly ? subAssembly.subAssembly.description : '';
                            }
                            return part.description || '';
                        })()}
                    </td>
                    <td>
                        <TextField
                            value={getNumberInputDisplayValue((this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].quantity !== undefined) ? this.state.bomChanges[item.id].quantity : item.quantity)}
                                                                onChange={(e) => this.updateBomItem(item.id, 'quantity', e.target.value)}
                            type="number"
                            variant="outlined"
                            size="small"
                            style={{width: '60px'}}
                                                                                                 inputProps={{min: 0, step: 1}}
                        />
                    </td>
                    <td style={{textAlign: 'center'}}>
                        <Checkbox
                            checked={(this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].requiredForBomCheck !== undefined) ? this.state.bomChanges[item.id].requiredForBomCheck : item.requiredForBomCheck}
                            onChange={(e) => this.updateBomItem(item.id, 'requiredForBomCheck', e.target.checked)}
                            size="small"
                        />
                    </td>
                    <td style={{textAlign: 'center'}}>
                        <Checkbox
                            checked={(this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].isImpliedPart !== undefined) ? this.state.bomChanges[item.id].isImpliedPart : item.isImpliedPart}
                            onChange={(e) => this.updateBomItem(item.id, 'isImpliedPart', e.target.checked)}
                            size="small"
                        />
                    </td>
                    <td>
                        {this.formatBomItemUnitCost(item, partExpanded)}
                    </td>
                    <td>
                        {this.formatBomItemExtendedCost(
                            item,
                            partExpanded,
                            (this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].quantity !== undefined)
                                ? this.state.bomChanges[item.id].quantity
                                : item.quantity
                        )}
                    </td>
                    <td>
                        <FontAwesomeIcon 
                            icon={faTrash} 
                            size="1x" 
                            style={{cursor: "pointer", color: '#dc3545'}}
                            onClick={() => {
                                // Mark item for deletion instead of immediately severing
                                this.updateBomItem(item.id, 'markedForDeletion', true);
                            }}
                        />
                    </td>
                </tr>
            );
        } else {
            const partUnitCost = getBomLineUnitCost(item, partExpanded, this.getBomCostDisplayOptions(item)) ?? 0;
            const partQty = parseNumberInputValue(item.quantity) || 0;
            return (
                <tr ref={provided.innerRef} {...provided.draggableProps} style={{
                    ...provided.draggableProps.style,
                    opacity: isMarkedForDeletion ? 0.5 : 1,
                    textDecoration: isMarkedForDeletion ? 'line-through' : 'none'
                }}>
                    <td className="text-left">
                        <a 
                            href={`/admin/parts/${item.partFk}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{cursor: 'pointer', color: "black", fontWeight: 'bold', textDecoration: 'none'}}
                            onClick={(e) => {
                                if (!e.ctrlKey && !e.metaKey) {
                                    e.preventDefault();
                                    window.location.href = `/admin/parts/${item.partFk}`;
                                }
                            }}
                        >
                            {part.partId}
                        </a>
                    </td>
                    <td>
                        {(() => {
                            // Get the current item based on changes
                            const currentPartFamilyId = (this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].partFamilyId !== undefined) 
                                ? this.state.bomChanges[item.id].partFamilyId : item.partFamilyId;
                            const currentPartFk = (this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].partFk !== undefined) 
                                ? this.state.bomChanges[item.id].partFk : item.partFk;
                            const currentSubAssemblyFk = (this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].subAssemblyFk !== undefined) 
                                ? this.state.bomChanges[item.id].subAssemblyFk : item.subAssemblyFk;
                            
                            if (currentPartFamilyId) {
                                return ''; // Families don't have revisions
                            } else if (currentPartFk) {
                                const currentPart = this.state.parts.find(p => p.part.id === currentPartFk);
                                return currentPart ? currentPart.part.revision : '';
                            } else if (currentSubAssemblyFk) {
                                const subAssembly = this.state.subAssemblies.find(s => s.subAssembly.id === currentSubAssemblyFk);
                                return subAssembly ? subAssembly.subAssembly.revision : '';
                            }
                            return part.revision || '';
                        })()}
                    </td>
                    <td className="description-ellipsis text-left">
                        {(() => {
                            // Get the current item based on changes
                            const currentPartFamilyId = (this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].partFamilyId !== undefined) 
                                ? this.state.bomChanges[item.id].partFamilyId : item.partFamilyId;
                            const currentPartFk = (this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].partFk !== undefined) 
                                ? this.state.bomChanges[item.id].partFk : item.partFk;
                            const currentSubAssemblyFk = (this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].subAssemblyFk !== undefined) 
                                ? this.state.bomChanges[item.id].subAssemblyFk : item.subAssemblyFk;
                            
                            if (currentPartFamilyId) {
                                const family = this.state.partFamilies.find(f => f.id === currentPartFamilyId);
                                return family ? family.description || '' : '';
                            } else if (currentPartFk) {
                                const currentPart = this.state.parts.find(p => p.part.id === currentPartFk);
                                return currentPart ? currentPart.part.description : '';
                            } else if (currentSubAssemblyFk) {
                                const subAssembly = this.state.subAssemblies.find(s => s.subAssembly.id === currentSubAssemblyFk);
                                return subAssembly ? subAssembly.subAssembly.description : '';
                            }
                            return part.description || '';
                        })()}
                    </td>
                    <td>
                        <span>{item.quantity}</span>
                    </td>
                    <td style={{textAlign: 'center'}}>
                        <span>
                            {item.requiredForBomCheck ? <FontAwesomeIcon icon={faCheck} size="1x"/> : ''}
                        </span>
                    </td>
                    <td style={{textAlign: 'center'}}>
                        <span>
                            {item.isImpliedPart ? <FontAwesomeIcon icon={faCheck} size="1x"/> : ''}
                        </span>
                    </td>
                    <td>${partUnitCost.toFixed(2)}</td>
                    <td>${(partUnitCost * partQty).toFixed(2)}</td>
                </tr>
            );
        }
    } else if (item.itemType === 'family') {
        const familyRows = [];
        
        // Family header row
        familyRows.push(
            <tr key="family-header" style={{backgroundColor: '#e6f3ff', fontWeight: 'bold'}}>
                {this.state.isBulkEditingBom && (
                    <td {...provided.dragHandleProps}>
                        <FontAwesomeIcon icon={faGripVertical} />
                    </td>
                )}
                    <td className="text-left" style={{borderLeft: '4px solid #2196F3', padding: '8px'}}>
                        {(() => {
                            return <>{item.familyName}<FontAwesomeIcon icon={faPeopleCarry} style={{ marginLeft: '6px' }} /></>;
                        })()}
                    </td>
                    <td></td>
                    <td className="text-left">
                        {(() => {
                            return item.familyDescription || '';
                        })()}
                    </td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    {this.state.isBulkEditingBom && <td></td>}
                </tr>
            );
            
            // Family parts rows
            item.parts.forEach((partItem, partIdx) => {
                // Get the current partFk (either from changes or original)
                const currentPartFk = (this.state.bomChanges && this.state.bomChanges[partItem.id] && this.state.bomChanges[partItem.id].partFk !== undefined) 
                    ? this.state.bomChanges[partItem.id].partFk 
                    : partItem.partFk;
                
                const partExpanded = this.state.parts.find(x => x.part.id === currentPartFk);
                if (!partExpanded || !partExpanded.part.isActive) return;
                const part = partExpanded.part;
                
                const isEditing = this.state.editingBomItem && this.state.editingBomItem.id === partItem.id;
                const isMarkedForDeletion = this.state.bomChanges && this.state.bomChanges[partItem.id] && this.state.bomChanges[partItem.id].markedForDeletion;
                
                familyRows.push(
                    <tr key={`family-part-${partIdx}`} style={{
                        backgroundColor: '#f0f8ff',
                        opacity: isMarkedForDeletion ? 0.5 : 1,
                        textDecoration: isMarkedForDeletion ? 'line-through' : 'none'
                    }}>
                        {this.state.isBulkEditingBom && <td style={{paddingLeft: '20px'}}></td>}
                        {this.state.isBulkEditingBom ? (
                            <>
                                <td className="text-left">
                                    <Autocomplete autoHighlight
                                        disabled
                                        options={this.state.parts.filter(x => x.part.isActive).map(p => ({
                                            title: `${p.part.partId} rev ${p.part.revision}${p.part.currentlyPurchasing ? ' 🛒' : ''}`,
                                            value: p.part.id,
                                            type: 'part'
                                        }))}
                                        onChange={(event, newValue) => {
                                            if (newValue) {
                                                this.updateBomItem(partItem.id, 'partFk', newValue.value);
                                                this.updateBomItem(partItem.id, 'subAssemblyFk', null);
                                                this.updateBomItem(partItem.id, 'itemType', 'part');
                                            }
                                        }}
                                        getOptionLabel={(option) => option.title}
                                        renderOption={(option) => <PartAutocompleteOption option={option} classes={this.props.classes} />}
                                        value={(() => {
                                            const currentPartFk = (this.state.bomChanges && this.state.bomChanges[partItem.id] && this.state.bomChanges[partItem.id].partFk !== undefined) 
                                                ? this.state.bomChanges[partItem.id].partFk 
                                                : partItem.partFk;
                                            
                                            if (currentPartFk) {
                                                const currentPart = this.state.parts.find(p => p.part.id === currentPartFk);
                                                if (currentPart) {
                                                    return {
                                                        title: `${currentPart.part.partId} rev ${currentPart.part.revision}`,
                                                        value: currentPartFk,
                                                        type: 'part'
                                                    };
                                                }
                                            }
                                            return null;
                                        })()}
                                        style={{width: '100%'}}
                                        size="small"
                                        renderInput={(params) => <TextField
                                            {...params}
                                            variant="outlined"
                                            size="small"
                                            autoComplete="new-password"
                                        />}
                                    />
                                </td>
                                <td>{part.revision}</td>
                                <td className="description-ellipsis text-left">{part.description}</td>
                            </>
                        ) : (
                            <>
                                                                <td className="text-left">
                                    <a 
                                        href={`/admin/parts/${currentPartFk}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{cursor: 'pointer', color: "black", fontWeight: 'bold', textDecoration: 'none'}}
                                        onClick={(e) => {
                                            if (!e.ctrlKey && !e.metaKey) {
                                                e.preventDefault();
                                                window.location.href = `/admin/parts/${currentPartFk}`;
                                            }
                                        }}
                                    >
                                        {part.partId}
                                    </a>
                                </td>
                                <td>{part.revision}</td>
                                <td className="description-ellipsis text-left">{part.description}</td>
                            </>
                        )}
                        <td>
                            {this.state.isBulkEditingBom ? (
                                <TextField
                                    value={getNumberInputDisplayValue((this.state.bomChanges && this.state.bomChanges[partItem.id] && this.state.bomChanges[partItem.id].quantity !== undefined) ? this.state.bomChanges[partItem.id].quantity : partItem.quantity)}
                                    onChange={(e) => this.updateBomItem(partItem.id, 'quantity', e.target.value)}
                                    type="number"
                                    variant="outlined"
                                    size="small"
                                    style={{width: '60px'}}
                                    inputProps={{min: 0, step: 1}}
                                />
                            ) : isEditing ? (
                                <TextField
                                    value={getNumberInputDisplayValue(this.state.editingBomItemQuantity)}
                                    onChange={(event) => this.setState({editingBomItemQuantity: event.target.value})}
                                    type="number"
                                    variant="outlined"
                                    size="small"
                                    style={{width: '60px'}}
                                    inputProps={{min: 0, step: 1}}
                                />
                            ) : (
                                <span>{getNumberInputDisplayValue(partItem.quantity)}</span>
                            )}
                        </td>
                        <td style={{textAlign: 'center'}}>
                            {this.state.isBulkEditingBom ? (
                                <Checkbox
                                    checked={(this.state.bomChanges && this.state.bomChanges[partItem.id] && this.state.bomChanges[partItem.id].requiredForBomCheck !== undefined) ? this.state.bomChanges[partItem.id].requiredForBomCheck : partItem.requiredForBomCheck}
                                    onChange={(e) => this.updateBomItem(partItem.id, 'requiredForBomCheck', e.target.checked)}
                                    size="small"
                                />
                            ) : isEditing ? (
                                <Checkbox
                                    checked={this.state.editingBomItemBomRequired}
                                    onChange={(event) => this.setState({editingBomItemBomRequired: event.target.checked})}
                                    size="small"
                                />
                            ) : (
                                <span>
                                    {partItem.requiredForBomCheck ? <FontAwesomeIcon icon={faCheck} size="1x"/> : ''}
                                </span>
                            )}
                        </td>
                        <td style={{textAlign: 'center'}}>
                            {this.state.isBulkEditingBom ? (
                                <Checkbox
                                    checked={(this.state.bomChanges && this.state.bomChanges[partItem.id] && this.state.bomChanges[partItem.id].isImpliedPart !== undefined) ? this.state.bomChanges[partItem.id].isImpliedPart : partItem.isImpliedPart}
                                    onChange={(e) => this.updateBomItem(partItem.id, 'isImpliedPart', e.target.checked)}
                                    size="small"
                                />
                            ) : isEditing ? (
                                <Checkbox
                                    checked={this.state.editingBomItemIsImpliedPart}
                                    onChange={(event) => this.setState({editingBomItemIsImpliedPart: event.target.checked})}
                                    size="small"
                                />
                            ) : (
                                <span>
                                    {partItem.isImpliedPart ? <FontAwesomeIcon icon={faCheck} size="1x"/> : ''}
                                </span>
                            )}
                        </td>
                        <td>
                            {this.formatBomItemUnitCost(partItem, partExpanded)}
                        </td>
                        <td>
                            {this.formatBomItemExtendedCost(partItem, partExpanded, partItem.quantity)}
                        </td>
                        {this.state.isBulkEditingBom && (
                            <td>
                                {isEditing ? (
                                <div style={{display: 'flex', gap: '5px'}}>
                                    {this.canSaveInlineBomItemEdit() && (
                                        <button
                                            onClick={this.saveInlineBomItemEdit}
                                            style={{
                                                backgroundColor: '#28a745',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                padding: '5px 8px',
                                                cursor: 'pointer',
                                                fontSize: '12px'
                                            }}
                                            disabled={this.state.savingConnection}
                                        >
                                            {'✓'}
                                        </button>
                                    )}
                                    <button
                                        onClick={this.cancelEditingBomItem}
                                        style={{
                                            backgroundColor: '#dc3545',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            padding: '5px 8px',
                                            cursor: 'pointer',
                                            fontSize: '12px'
                                        }}
                                    >
                                        ✕
                                    </button>
                                </div>
                            ) : this.state.isBulkEditingBom ? (
                                                                    <FontAwesomeIcon 
                                        icon={faTrash} 
                                        size="1x" 
                                        style={{cursor: "pointer", color: '#dc3545'}}
                                        onClick={() => {
                                            // Mark item for deletion instead of immediately severing
                                            this.updateBomItem(partItem.id, 'markedForDeletion', true);
                                        }}
                                    />
                            ) : null}
                            </td>
                        )}
                    </tr>
                );
            });
            
            return (
                <React.Fragment key={item.dragId}>
                    {familyRows.map((row, rowIdx) => (
                        <React.Fragment key={`${item.dragId}-row-${rowIdx}`}>
                            {rowIdx === 0 ? (
                                React.cloneElement(row, {
                                    ref: provided.innerRef,
                                    ...provided.draggableProps,
                                    style: { ...provided.draggableProps.style, ...row.props.style }
                                })
                            ) : row}
                        </React.Fragment>
                    ))}
                </React.Fragment>
            );
        }
        
        return null;
    };

    renderNewBomTableItem = (item, idx, provided) => {
        return (
            <tr ref={provided.innerRef} {...provided.draggableProps} style={{
                ...provided.draggableProps.style,
                backgroundColor: '#f0fff0',
                border: '1px solid #f0fff0'
            }}>
                <td {...provided.dragHandleProps}>
                    <FontAwesomeIcon icon={faGripVertical} />
                </td>
                <td className="text-left">
                    <Autocomplete autoHighlight
                        options={[
                            ...this.state.partFamilies.map(family => ({
                                title: `${family.partFamilyId} (Family)`,
                                value: family.id,
                                type: 'family'
                            })),
                            ...this.state.parts.filter(x => x.part.isActive).map(p => ({
                                title: `${p.part.partId} rev ${p.part.revision}${p.part.currentlyPurchasing ? ' 🛒' : ''}`,
                                value: p.part.id,
                                type: 'part',
                                part: p.part,
                                partId: p.part.partId,
                                revision: p.part.revision,
                                description: p.part.description,
                                currentlyPurchasing: p.part.currentlyPurchasing
                            })),
                            ...this.state.subAssemblies.filter(x => x.subAssembly.isActive).map(sub => ({
                                title: `${sub.subAssembly.subAssemblyId} rev ${sub.subAssembly.revision}${sub.subAssembly.currentlyPackaging ? ' 📦' : ''}`,
                                value: sub.subAssembly.id,
                                type: 'subassembly',
                                partId: sub.subAssembly.subAssemblyId,
                                revision: sub.subAssembly.revision,
                                description: sub.subAssembly.description,
                                currentlyPackaging: sub.subAssembly.currentlyPackaging
                            }))
                        ].sort((a, b) => a.title.localeCompare(b.title))}
                        onChange={(event, newValue) => {
                            if (newValue) {
                                const updatedItems = [...(this.state.newBomItems || [])];
                                const itemIndex = updatedItems.findIndex(x => x.id === item.id);
                                if (itemIndex !== -1) {
                                    if (newValue.type === 'family') {
                                        const family = this.state.partFamilies.find(f => f.id === newValue.value);
                                        updatedItems[itemIndex] = {
                                            ...updatedItems[itemIndex],
                                            partFamilyId: newValue.value,
                                            partFamilyName: family?.partFamilyId,
                                            partFamilyDescription: family?.description,
                                            familyName: family?.partFamilyId,
                                            partFk: null,
                                            subAssemblyFk: null,
                                            itemType: 'family',
                                            isImpliedPart: false
                                        };
                                    } else if (newValue.type === 'subassembly') {
                                        updatedItems[itemIndex] = { ...updatedItems[itemIndex], subAssemblyFk: newValue.value, partFk: null, partFamilyId: null, partFamilyName: null, familyName: null, itemType: 'subassembly', isImpliedPart: false };
                                    } else if (newValue.type === 'part') {
                                        updatedItems[itemIndex] = { ...updatedItems[itemIndex], partFk: newValue.value, subAssemblyFk: null, partFamilyId: null, partFamilyName: null, familyName: null, itemType: 'part' };
                                    }
                                    this.setState({ newBomItems: updatedItems });
                                }
                            }
                        }}
                        getOptionLabel={(option) => option.title}
                        renderOption={(option) => <PartAutocompleteOption option={option} classes={this.props.classes} />}
                        value={(() => {
                            if (item.partFamilyId) {
                                const family = this.state.partFamilies.find(f => f.id === item.partFamilyId);
                                if (family) {
                                    return { title: `${family.partFamilyId} (Family)`, value: item.partFamilyId, type: 'family' };
                                }
                            } else if (item.partFk) {
                                const part = this.state.parts.find(p => p.part.id === item.partFk);
                                return part ? { 
                                    title: `${part.part.partId} rev ${part.part.revision}${part.part.currentlyPurchasing ? ' 🛒' : ''}`, 
                                    value: item.partFk, 
                                    type: 'part',
                                    part: part.part,
                                    partId: part.part.partId,
                                    revision: part.part.revision,
                                    description: part.part.description,
                                    currentlyPurchasing: part.part.currentlyPurchasing
                                } : null;
                            } else if (item.subAssemblyFk) {
                                const subAssembly = this.state.subAssemblies.find(s => s.subAssembly.id === item.subAssemblyFk);
                                return subAssembly ? { 
                                    title: `${subAssembly.subAssembly.subAssemblyId} rev ${subAssembly.subAssembly.revision}${subAssembly.subAssembly.currentlyPackaging ? ' 📦' : ''}`, 
                                    value: item.subAssemblyFk, 
                                    type: 'subassembly',
                                    partId: subAssembly.subAssembly.subAssemblyId,
                                    revision: subAssembly.subAssembly.revision,
                                    description: subAssembly.subAssembly.description,
                                    currentlyPackaging: subAssembly.subAssembly.currentlyPackaging
                                } : null;
                            }
                            return null;
                        })()}
                        style={{width: '100%'}}
                        size="small"
                        renderInput={(params) => <TextField
                            {...params}
                            variant="outlined"
                            size="small"
                            autoComplete="new-password"
                        />}
                    />
                </td>
                <td>
                    {item.partFamilyId ? 
                        '' // Families don't have revisions
                        : item.partFk ? 
                        this.state.parts.find(p => p.part.id === item.partFk)?.part.revision
                        : item.subAssemblyFk ?
                        this.state.subAssemblies.find(s => s.subAssembly.id === item.subAssemblyFk)?.subAssembly.revision
                        : ''
                    }
                </td>
                <td className="description-ellipsis text-left">
                    {item.partFamilyId ? 
                        this.state.partFamilies.find(f => f.id === item.partFamilyId)?.description || ''
                        : item.partFk ? 
                        this.state.parts.find(p => p.part.id === item.partFk)?.part.description || ''
                        : item.subAssemblyFk ?
                        this.state.subAssemblies.find(s => s.subAssembly.id === item.subAssemblyFk)?.subAssembly.description || ''
                        : ''
                    }
                </td>
                <td>
                    <TextField
                        value={getNumberInputDisplayValue(item.quantity)}
                        onChange={(e) => {
                            const updatedItems = [...(this.state.newBomItems || [])];
                            const itemIndex = updatedItems.findIndex(x => x.id === item.id);
                            if (itemIndex !== -1) {
                                updatedItems[itemIndex] = { ...updatedItems[itemIndex], quantity: e.target.value };
                                this.setState({ newBomItems: updatedItems });
                            }
                        }}
                        type="number"
                        variant="outlined"
                        size="small"
                        style={{width: '60px'}}
                                                                                             inputProps={{min: 0, step: 1}}
                    />
                </td>
                <td style={{textAlign: 'center'}}>
                    <Checkbox
                        checked={item.requiredForBomCheck}
                        onChange={(e) => {
                            const updatedItems = [...(this.state.newBomItems || [])];
                            const itemIndex = updatedItems.findIndex(x => x.id === item.id);
                            if (itemIndex !== -1) {
                                updatedItems[itemIndex] = { ...updatedItems[itemIndex], requiredForBomCheck: e.target.checked };
                                this.setState({ newBomItems: updatedItems });
                            }
                        }}
                        size="small"
                    />
                </td>
                <td style={{textAlign: 'center'}}>
                    {/* Only show Implied Part checkbox for individual parts, not for families or subassemblies */}
                    {(item.itemType === 'part' || (!item.itemType && item.partFk)) ? (
                        <Checkbox
                            checked={item.isImpliedPart || false}
                            onChange={(e) => {
                                const updatedItems = [...(this.state.newBomItems || [])];
                                const itemIndex = updatedItems.findIndex(x => x.id === item.id);
                                if (itemIndex !== -1) {
                                    updatedItems[itemIndex] = { ...updatedItems[itemIndex], isImpliedPart: e.target.checked };
                                    this.setState({ newBomItems: updatedItems });
                                }
                            }}
                            size="small"
                        />
                    ) : (
                        <span></span>
                    )}
                </td>
                <td>
                    {(() => {
                        if (isBomLineImpliedPart(item)) return '$0.00';
                        if (item.partFk) {
                            const expanded = this.getAllPartsExpanded().find(x => x.part && x.part.id === item.partFk);
                            if (expanded && expanded.quantity) return `$${(expanded.cost / expanded.quantity).toFixed(2)}`;
                        }
                        if (item.subAssemblyFk) {
                            const sub = this.state.subAssemblies.find(s => s.subAssembly.id === item.subAssemblyFk);
                            if (sub) return `$${this.calculateSubAssemblyCost(sub).toFixed(2)}`;
                        }
                        return '-';
                    })()}
                </td>
                <td>
                    {(() => {
                        if (isBomLineImpliedPart(item)) return '$0.00';
                        const qty = parseNumberInputValue(item.quantity) || 0;
                        if (item.partFk) {
                            const expanded = this.getAllPartsExpanded().find(x => x.part && x.part.id === item.partFk);
                            if (expanded && expanded.quantity) return `$${((expanded.cost / expanded.quantity) * qty).toFixed(2)}`;
                        }
                        if (item.subAssemblyFk) {
                            const sub = this.state.subAssemblies.find(s => s.subAssembly.id === item.subAssemblyFk);
                            if (sub) return `$${(this.calculateSubAssemblyCost(sub) * qty).toFixed(2)}`;
                        }
                        return '-';
                    })()}
                </td>
                <td>
                    <FontAwesomeIcon 
                        icon={faTrash} 
                        size="1x" 
                        style={{cursor: "pointer", color: '#dc3545'}}
                        onClick={() => {
                            const updatedItems = [...(this.state.newBomItems || [])];
                            const itemIndex = updatedItems.findIndex(x => x.id === item.id);
                            if (itemIndex !== -1) {
                                updatedItems.splice(itemIndex, 1);
                                this.setState({ newBomItems: updatedItems });
                            }
                        }}
                    />
                </td>
            </tr>
        );
    };

    renderBomTableItemForSubAssembly = (item, idx, provided) => {
        if (item.itemType === 'subassembly') {
            // Handle subassembly-to-subassembly relationships
            const childSubAssemblyId = item.childSubAssemblyFk || item.ChildSubAssemblyFk || item.subAssemblyFk; // Handle both camelCase and PascalCase
            const subAssemblyExpanded = this.state.subAssemblies.find(x => x.subAssembly.id === childSubAssemblyId);
            if (!subAssemblyExpanded || !subAssemblyExpanded.subAssembly) {
                // SubAssembly not found in state, skip rendering this item
                return null;
            }
            const subAssembly = subAssemblyExpanded.subAssembly;
            if (!subAssembly.isActive) return null;
            
            const isEditing = this.state.editingBomItem && this.state.editingBomItem.id === item.id;
            const isMarkedForDeletion = this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].markedForDeletion;
            
            return (
                <tr ref={provided.innerRef} {...provided.draggableProps} style={{
                    ...provided.draggableProps.style,
                    opacity: isMarkedForDeletion ? 0.5 : 1,
                    textDecoration: isMarkedForDeletion ? 'line-through' : 'none'
                }}>
                    {this.state.isBulkEditingBom && (
                        <td {...provided.dragHandleProps}>
                            <FontAwesomeIcon icon={faGripVertical} />
                        </td>
                    )}
                    {this.state.isBulkEditingBom ? (
                        <>
                            <td className="text-left">
                                <Autocomplete autoHighlight
                                    options={[
                                        ...this.state.partFamilies.map(family => ({
                                            title: `${family.partFamilyId} (Family)`,
                                            value: family.id,
                                            type: 'family',
                                            family: family
                                        })),
                                        ...this.state.parts.filter(x => x.part.isActive).map(p => ({
                                            title: `${p.part.partId} rev ${p.part.revision}${p.part.currentlyPurchasing ? ' 🛒' : ''}`,
                                            value: p.part.id,
                                            type: 'part',
                                            part: p.part,
                                            partId: p.part.partId,
                                            revision: p.part.revision,
                                            description: p.part.description,
                                            currentlyPurchasing: p.part.currentlyPurchasing
                                        })),
                                        ...this.state.subAssemblies.filter(sub => {
                                            if (!sub.subAssembly.isActive) return false;
                                            
                                            // If editing a subassembly, exclude self-reference and circular dependencies
                                            if (this.state.editingSubAssembly) {
                                                if (sub.subAssembly.id === this.state.editingSubAssembly.subAssembly.id) return false;
                                                if (this.wouldCreateCircularDependency(sub.subAssembly.id, this.state.editingSubAssembly.subAssembly.id)) return false;
                                            }
                                            
                                            return true;
                                        }).map(sub => ({
                                            title: `${sub.subAssembly.subAssemblyId} rev ${sub.subAssembly.revision}${sub.subAssembly.currentlyPackaging ? ' 📦' : ''}`,
                                            value: sub.subAssembly.id,
                                            type: 'subassembly',
                                            partId: sub.subAssembly.subAssemblyId,
                                            revision: sub.subAssembly.revision,
                                            description: sub.subAssembly.description,
                                            currentlyPackaging: sub.subAssembly.currentlyPackaging
                                        }))
                                    ].sort((a, b) => a.title.localeCompare(b.title))}
                                    onChange={(event, newValue) => {
                                        if (newValue) {
                                            if (newValue.type === 'family') {
                                                this.updateBomItem(item.id, 'partFamilyId', newValue.value);
                                                this.updateBomItem(item.id, 'partFk', null);
                                                this.updateBomItem(item.id, 'childSubAssemblyFk', null);
                                                this.updateBomItem(item.id, 'itemType', 'family');
                                                this.updateBomItem(item.id, 'isImpliedPart', false);
                                            } else if (newValue.type === 'subassembly') {
                                                this.updateBomItem(item.id, 'childSubAssemblyFk', newValue.value);
                                                this.updateBomItem(item.id, 'partFk', null);
                                                this.updateBomItem(item.id, 'partFamilyId', null);
                                                this.updateBomItem(item.id, 'itemType', 'subassembly');
                                                this.updateBomItem(item.id, 'isImpliedPart', false);
                                            } else if (newValue.type === 'part') {
                                                this.updateBomItem(item.id, 'partFk', newValue.value);
                                                this.updateBomItem(item.id, 'childSubAssemblyFk', null);
                                                this.updateBomItem(item.id, 'partFamilyId', null);
                                                this.updateBomItem(item.id, 'itemType', 'part');
                                            }
                                        }
                                    }}
                                    getOptionLabel={(option) => option.title}
                                    renderOption={(option) => <PartAutocompleteOption option={option} classes={this.props.classes} />}
                                    value={(() => {
                                        const currentChildSubAssemblyFk = (this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].childSubAssemblyFk !== undefined) 
                                            ? this.state.bomChanges[item.id].childSubAssemblyFk 
                                            : item.childSubAssemblyFk || item.ChildSubAssemblyFk || item.subAssemblyFk;
                                        
                                        if (currentChildSubAssemblyFk) {
                                            const currentSubAssembly = this.state.subAssemblies.find(s => s.subAssembly.id === currentChildSubAssemblyFk);
                                            if (currentSubAssembly) {
                                                return {
                                                    title: `${currentSubAssembly.subAssembly.subAssemblyId} rev ${currentSubAssembly.subAssembly.revision}${currentSubAssembly.subAssembly.currentlyPackaging ? ' 📦' : ''}`,
                                                    value: currentChildSubAssemblyFk,
                                                    type: 'subassembly',
                                                    partId: currentSubAssembly.subAssembly.subAssemblyId,
                                                    revision: currentSubAssembly.subAssembly.revision,
                                                    description: currentSubAssembly.subAssembly.description,
                                                    currentlyPackaging: currentSubAssembly.subAssembly.currentlyPackaging
                                                };
                                            }
                                        }
                                        return null;
                                    })()}
                                    style={{width: '100%'}}
                                    size="small"
                                    renderInput={(params) => <TextField
                                        {...params}
                                        variant="outlined"
                                        size="small"
                                        autoComplete="new-password"
                                    />}
                                />
                            </td>
                            <td>{subAssembly.revision}</td>
                            <td className="description-ellipsis text-left">{subAssembly.description}</td>
                        </>
                    ) : (
                        <>
                            <td className="text-left">
                                <a 
                                    href={`/admin/assemblies/${childSubAssemblyId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{cursor: 'pointer', color: "black", fontWeight: 'bold', textDecoration: 'none'}}
                                    onClick={(e) => {
                                        if (!e.ctrlKey && !e.metaKey) {
                                            e.preventDefault();
                                            window.location.href = `/admin/assemblies/${childSubAssemblyId}`;
                                        }
                                    }}
                                >
                                    {subAssembly.subAssemblyId}
                                </a>
                            </td>
                            <td>{subAssembly.revision}</td>
                            <td className="description-ellipsis text-left">{subAssembly.description}</td>
                        </>
                    )}
                    <td>
                        {this.state.isBulkEditingBom ? (
                            <TextField
                                value={getNumberInputDisplayValue((this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].quantity !== undefined) ? this.state.bomChanges[item.id].quantity : item.quantity)}
                                onChange={(e) => this.updateBomItem(item.id, 'quantity', e.target.value)}
                                type="number"
                                variant="outlined"
                                size="small"
                                style={{width: '60px'}}
                                inputProps={{min: 0, step: 1}}
                            />
                        ) : (
                            <span>{getNumberInputDisplayValue(item.quantity)}</span>
                        )}
                    </td>
                    <td style={{textAlign: 'center'}}>
                        {this.state.isBulkEditingBom ? (
                            <Checkbox
                                checked={(this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].requiredForBomCheck !== undefined) ? this.state.bomChanges[item.id].requiredForBomCheck : item.requiredForBomCheck}
                                onChange={(e) => this.updateBomItem(item.id, 'requiredForBomCheck', e.target.checked)}
                                size="small"
                            />
                        ) : (
                            <span>
                                {item.requiredForBomCheck ? <FontAwesomeIcon icon={faCheck} size="1x"/> : ''}
                            </span>
                        )}
                    </td>
                    <td style={{textAlign: 'center'}}>
                        {this.state.isBulkEditingBom ? (
                            <Checkbox
                                checked={(this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].isImpliedPart !== undefined) ? this.state.bomChanges[item.id].isImpliedPart : item.isImpliedPart}
                                onChange={(e) => this.updateBomItem(item.id, 'isImpliedPart', e.target.checked)}
                                size="small"
                            />
                        ) : (
                            <span>
                                {item.isImpliedPart ? <FontAwesomeIcon icon={faCheck} size="1x"/> : ''}
                            </span>
                        )}
                    </td>
                    <td>${(this.calculateSubAssemblyCost(subAssembly)).toFixed(2)}</td>
                    <td>${(this.calculateSubAssemblyCost(subAssembly) * (parseNumberInputValue(item.quantity) || 0)).toFixed(2)}</td>
                    {this.state.isBulkEditingBom && (
                        <td>
                            <FontAwesomeIcon 
                                icon={faTrash} 
                                size="1x" 
                                style={{cursor: "pointer", color: '#dc3545'}}
                                onClick={() => {
                                    this.updateBomItem(item.id, 'markedForDeletion', true);
                                }}
                            />
                        </td>
                    )}
                </tr>
            );
        } else if (item.itemType === 'part') {
            const partExpanded = this.state.parts.find(x => x.part.id === item.partFk);
            if (!partExpanded || !partExpanded.part) {
                // Part not found in state, skip rendering this item
                return null;
            }
            const part = partExpanded.part;
            if (!part.isActive) return null;
            
            const isEditing = this.state.editingBomItem && this.state.editingBomItem.id === item.id;
            const isMarkedForDeletion = this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].markedForDeletion;
            
            return (
                <tr ref={provided.innerRef} {...provided.draggableProps} style={{
                    ...provided.draggableProps.style,
                    opacity: isMarkedForDeletion ? 0.5 : 1,
                    textDecoration: isMarkedForDeletion ? 'line-through' : 'none'
                }}>
                    {this.state.isBulkEditingBom && (
                        <td {...provided.dragHandleProps}>
                            <FontAwesomeIcon icon={faGripVertical} />
                        </td>
                    )}
                    {this.state.isBulkEditingBom ? (
                        <>
                            <td className="text-left">
                                <Autocomplete autoHighlight
                                    options={[
                                        ...this.state.partFamilies.map(family => ({
                                            title: `${family.partFamilyId} (Family)`,
                                            value: family.id,
                                            type: 'family',
                                            family: family
                                        })),
                                        ...this.state.parts.filter(x => x.part.isActive).map(p => ({
                                            title: `${p.part.partId} rev ${p.part.revision}${p.part.currentlyPurchasing ? ' 🛒' : ''}`,
                                            value: p.part.id,
                                            type: 'part',
                                            part: p.part,
                                            partId: p.part.partId,
                                            revision: p.part.revision,
                                            description: p.part.description,
                                            currentlyPurchasing: p.part.currentlyPurchasing
                                        })),
                                        ...this.state.subAssemblies.filter(sub => {
                                            if (!sub.subAssembly.isActive) return false;
                                            
                                            // If editing a subassembly, exclude self-reference and circular dependencies
                                            if (this.state.editingSubAssembly) {
                                                if (sub.subAssembly.id === this.state.editingSubAssembly.subAssembly.id) return false;
                                                if (this.wouldCreateCircularDependency(sub.subAssembly.id, this.state.editingSubAssembly.subAssembly.id)) return false;
                                            }
                                            
                                            return true;
                                        }).map(sub => ({
                                            title: `${sub.subAssembly.subAssemblyId} rev ${sub.subAssembly.revision}${sub.subAssembly.currentlyPackaging ? ' 📦' : ''}`,
                                            value: sub.subAssembly.id,
                                            type: 'subassembly',
                                            partId: sub.subAssembly.subAssemblyId,
                                            revision: sub.subAssembly.revision,
                                            description: sub.subAssembly.description,
                                            currentlyPackaging: sub.subAssembly.currentlyPackaging
                                        }))
                                    ].sort((a, b) => a.title.localeCompare(b.title))}
                                    onChange={(event, newValue) => {
                                        if (newValue) {
                                            if (newValue.type === 'family') {
                                                this.updateBomItem(item.id, 'partFamilyId', newValue.value);
                                                this.updateBomItem(item.id, 'partFk', null);
                                                this.updateBomItem(item.id, 'subAssemblyFk', null);
                                                this.updateBomItem(item.id, 'itemType', 'family');
                                                this.updateBomItem(item.id, 'isImpliedPart', false);
                                            } else if (newValue.type === 'subassembly') {
                                                this.updateBomItem(item.id, 'subAssemblyFk', newValue.value);
                                                this.updateBomItem(item.id, 'partFk', null);
                                                this.updateBomItem(item.id, 'partFamilyId', null);
                                                this.updateBomItem(item.id, 'itemType', 'subassembly');
                                                this.updateBomItem(item.id, 'isImpliedPart', false);
                                            } else if (newValue.type === 'part') {
                                                this.updateBomItem(item.id, 'partFk', newValue.value);
                                                this.updateBomItem(item.id, 'subAssemblyFk', null);
                                                this.updateBomItem(item.id, 'partFamilyId', null);
                                                this.updateBomItem(item.id, 'itemType', 'part');
                                            }
                                        }
                                    }}
                                    getOptionLabel={(option) => option.title}
                                    renderOption={(option) => <PartAutocompleteOption option={option} classes={this.props.classes} />}
                                    value={(() => {
                                        const currentPartFamilyId = (this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].partFamilyId !== undefined) 
                                            ? this.state.bomChanges[item.id].partFamilyId 
                                            : item.partFamilyId;
                                        const currentPartFk = (this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].partFk !== undefined) 
                                            ? this.state.bomChanges[item.id].partFk 
                                            : item.partFk;
                                        const currentSubAssemblyFk = (this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].subAssemblyFk !== undefined) 
                                            ? this.state.bomChanges[item.id].subAssemblyFk 
                                            : item.subAssemblyFk;
                                        
                                        if (currentPartFamilyId) {
                                            const currentFamily = this.state.partFamilies.find(f => f.id === currentPartFamilyId);
                                            if (currentFamily) {
                                                return {
                                                    title: `${currentFamily.partFamilyId} (Family)`,
                                                    value: currentPartFamilyId,
                                                    type: 'family',
                                                    family: currentFamily
                                                };
                                            }
                                        } else if (currentPartFk) {
                                            const currentPart = this.state.parts.find(p => p.part.id === currentPartFk);
                                            if (currentPart) {
                                                return {
                                                    title: `${currentPart.part.partId} rev ${currentPart.part.revision}${currentPart.part.currentlyPurchasing ? ' 🛒' : ''}`,
                                                    value: currentPartFk,
                                                    type: 'part',
                                                    part: currentPart.part,
                                                    partId: currentPart.part.partId,
                                                    revision: currentPart.part.revision,
                                                    description: currentPart.part.description,
                                                    currentlyPurchasing: currentPart.part.currentlyPurchasing
                                                };
                                            }
                                        } else if (currentSubAssemblyFk) {
                                            const currentSubAssembly = this.state.subAssemblies.find(s => s.subAssembly.id === currentSubAssemblyFk);
                                            if (currentSubAssembly) {
                                                return {
                                                    title: `${currentSubAssembly.subAssembly.subAssemblyId} rev ${currentSubAssembly.subAssembly.revision}${currentSubAssembly.subAssembly.currentlyPackaging ? ' 📦' : ''}`,
                                                    value: currentSubAssemblyFk,
                                                    type: 'subassembly',
                                                    partId: currentSubAssembly.subAssembly.subAssemblyId,
                                                    revision: currentSubAssembly.subAssembly.revision,
                                                    description: currentSubAssembly.subAssembly.description,
                                                    currentlyPackaging: currentSubAssembly.subAssembly.currentlyPackaging
                                                };
                                            }
                                        }
                                        return null;
                                    })()}
                                    style={{width: '100%'}}
                                    size="small"
                                    renderInput={(params) => <TextField
                                        {...params}
                                        variant="outlined"
                                        size="small"
                                        autoComplete="new-password"
                                    />}
                                />
                            </td>
                            <td>{part.revision}</td>
                            <td className="description-ellipsis text-left">{part.description}</td>
                        </>
                    ) : isEditing ? (
                        <>
                            <td className="text-left">
                                <Autocomplete autoHighlight
                                    options={[
                                        ...this.state.partFamilies.map(family => ({
                                            title: `${family.partFamilyId} (Family)`,
                                            value: family.id,
                                            type: 'family',
                                            family: family
                                        })),
                                        ...this.state.parts.filter(x => x.part.isActive).map(p => ({
                                            title: `${p.part.partId} rev ${p.part.revision}${p.part.currentlyPurchasing ? ' 🛒' : ''}`,
                                            value: p.part.id,
                                            type: 'part',
                                            part: p.part,
                                            partId: p.part.partId,
                                            revision: p.part.revision,
                                            description: p.part.description,
                                            currentlyPurchasing: p.part.currentlyPurchasing
                                        })),
                                        ...this.state.subAssemblies.filter(sub => {
                                            if (!sub.subAssembly.isActive) return false;
                                            
                                            // If editing a subassembly, exclude self-reference and circular dependencies
                                            if (this.state.editingSubAssembly) {
                                                if (sub.subAssembly.id === this.state.editingSubAssembly.subAssembly.id) return false;
                                                if (this.wouldCreateCircularDependency(sub.subAssembly.id, this.state.editingSubAssembly.subAssembly.id)) return false;
                                            }
                                            
                                            return true;
                                        }).map(sub => ({
                                            title: `${sub.subAssembly.subAssemblyId} rev ${sub.subAssembly.revision}${sub.subAssembly.currentlyPackaging ? ' 📦' : ''}`,
                                            value: sub.subAssembly.id,
                                            type: 'subassembly',
                                            partId: sub.subAssembly.subAssemblyId,
                                            revision: sub.subAssembly.revision,
                                            description: sub.subAssembly.description,
                                            currentlyPackaging: sub.subAssembly.currentlyPackaging
                                        }))
                                    ].sort((a, b) => a.title.localeCompare(b.title))}
                                    onChange={this.handleEditingBomItemPartChange}
                                    getOptionLabel={(option) => option.title}
                                    renderOption={(option) => <PartAutocompleteOption option={option} classes={this.props.classes} />}
                                    value={this.state.parts.find(x => x.part.id === this.state.editingBomItemPart) ? {
                                        title: `${this.state.parts.find(x => x.part.id === this.state.editingBomItemPart).part.partId} rev ${this.state.parts.find(x => x.part.id === this.state.editingBomItemPart).part.revision}`,
                                        value: this.state.editingBomItemPart
                                    } : null}
                                    style={{width: '100%'}}
                                    size="small"
                                    renderInput={(params) => <TextField
                                        {...params}
                                        variant="outlined"
                                        size="small"
                                        autoComplete="new-password"
                                    />}
                                />
                            </td>
                            <td>{part.revision}</td>
                            <td className="description-ellipsis text-left">{part.description}</td>
                        </>
                    ) : (
                        <>
                            <td className="text-left">
                                <a 
                                    href={`/admin/parts/${item.partFk}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{cursor: 'pointer', color: "black", fontWeight: 'bold', textDecoration: 'none'}}
                                    onClick={(e) => {
                                        if (!e.ctrlKey && !e.metaKey) {
                                            e.preventDefault();
                                            window.location.href = `/admin/parts/${item.partFk}`;
                                        }
                                    }}
                                >
                                    {part.partId}
                                </a>
                            </td>
                            <td>{part.revision}</td>
                            <td className="description-ellipsis text-left">{part.description}</td>
                        </>
                    )}
                    <td>
                        {this.state.isBulkEditingBom ? (
                            <TextField
                                value={getNumberInputDisplayValue((this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].quantity !== undefined) ? this.state.bomChanges[item.id].quantity : item.quantity)}
                                onChange={(e) => this.updateBomItem(item.id, 'quantity', e.target.value)}
                                type="number"
                                variant="outlined"
                                size="small"
                                style={{width: '60px'}}
                                inputProps={{min: 0, step: 1}}
                            />
                        ) : isEditing ? (
                            <TextField
                                value={this.state.editingBomItemQuantity}
                                onChange={(event) => this.setState({editingBomItemQuantity: event.target.value})}
                                type="number"
                                variant="outlined"
                                size="small"
                                style={{width: '60px'}}
                                inputProps={{min: 0, step: 1}}
                            />
                        ) : (
                            <span>{getNumberInputDisplayValue(item.quantity)}</span>
                        )}
                    </td>
                    <td style={{textAlign: 'center'}}>
                        {this.state.isBulkEditingBom ? (
                            <Checkbox
                                checked={(this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].requiredForBomCheck !== undefined) ? this.state.bomChanges[item.id].requiredForBomCheck : item.requiredForBomCheck}
                                onChange={(e) => this.updateBomItem(item.id, 'requiredForBomCheck', e.target.checked)}
                                size="small"
                            />
                        ) : isEditing ? (
                            <Checkbox
                                checked={this.state.editingBomItemBomRequired}
                                onChange={(event) => this.setState({editingBomItemBomRequired: event.target.checked})}
                                size="small"
                            />
                        ) : (
                            <span>
                                {item.requiredForBomCheck ? <FontAwesomeIcon icon={faCheck} size="1x"/> : ''}
                            </span>
                        )}
                    </td>
                    <td style={{textAlign: 'center'}}>
                        {this.state.isBulkEditingBom ? (
                            <Checkbox
                                checked={(this.state.bomChanges && this.state.bomChanges[item.id] && this.state.bomChanges[item.id].isImpliedPart !== undefined) ? this.state.bomChanges[item.id].isImpliedPart : item.isImpliedPart}
                                onChange={(e) => this.updateBomItem(item.id, 'isImpliedPart', e.target.checked)}
                                size="small"
                            />
                        ) : isEditing ? (
                            <Checkbox
                                checked={this.state.editingBomItemIsImpliedPart}
                                onChange={(event) => this.setState({editingBomItemIsImpliedPart: event.target.checked})}
                                size="small"
                            />
                        ) : (
                            <span>
                                {item.isImpliedPart ? <FontAwesomeIcon icon={faCheck} size="1x"/> : ''}
                            </span>
                        )}
                    </td>
                    <td>
                        {this.formatBomItemUnitCost(item, partExpanded)}
                    </td>
                    <td>
                        {this.formatBomItemExtendedCost(item, partExpanded, item.quantity)}
                    </td>
                    {this.state.isBulkEditingBom && (
                        <td>
                            {isEditing ? (
                            <div style={{display: 'flex', gap: '5px'}}>
                                {this.canSaveInlineBomItemEdit() && (
                                    <button
                                        onClick={this.saveInlineBomItemEdit}
                                        style={{
                                            backgroundColor: '#28a745',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            padding: '5px 8px',
                                            cursor: 'pointer',
                                            fontSize: '12px'
                                        }}
                                        disabled={this.state.savingConnection}
                                    >
                                        {'✓'}
                                    </button>
                                )}
                                <button
                                    onClick={this.cancelEditingBomItem}
                                    style={{
                                        backgroundColor: '#dc3545',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        padding: '5px 8px',
                                        cursor: 'pointer',
                                        fontSize: '12px'
                                    }}
                                >
                                    ✕
                                </button>
                            </div>
                        ) : (
                            <FontAwesomeIcon 
                                icon={faTrash} 
                                size="1x" 
                                style={{cursor: "pointer", color: '#dc3545'}}
                                onClick={() => {
                                    // Mark item for deletion instead of immediately severing
                                    this.updateBomItem(item.id, 'markedForDeletion', true);
                                }}
                            />
                        )}
                        </td>
                    )}
                </tr>
            );
        } else if (item.itemType === 'family') {
            const familyRows = [];
            
            // Family header row
            familyRows.push(
                <tr key="family-header" style={{backgroundColor: '#e6f3ff', fontWeight: 'bold'}}>
                    {this.state.isBulkEditingBom && (
                        <td {...provided.dragHandleProps}>
                            <FontAwesomeIcon icon={faGripVertical} />
                        </td>
                    )}
                    <td className="text-left" style={{borderLeft: '4px solid #2196F3', padding: '8px'}}>
                        {(() => {
                            return <>{item.familyName}<FontAwesomeIcon icon={faPeopleCarry} style={{ marginLeft: '6px' }} /></>;
                        })()}
                    </td>
                    <td></td>
                    <td className="text-left" style={{padding: '8px'}}>
                        {(() => {
                            return item.familyDescription || '';
                        })()}
                    </td>
                    <td></td>
                    <td></td>
                    <td></td>
                    {this.state.isBulkEditingBom && <td></td>}
                </tr>
            );
            
            // Family parts rows
            item.parts.forEach((partItem, partIdx) => {
                // Get the current partFk (either from changes or original)
                const currentPartFk = (this.state.bomChanges && this.state.bomChanges[partItem.id] && this.state.bomChanges[partItem.id].partFk !== undefined) 
                    ? this.state.bomChanges[partItem.id].partFk 
                    : partItem.partFk;
                
                const partExpanded = this.state.parts.find(x => x.part.id === currentPartFk);
                if (!partExpanded || !partExpanded.part.isActive) return;
                const part = partExpanded.part;
                
                const isEditing = this.state.editingBomItem && this.state.editingBomItem.id === partItem.id;
                const isMarkedForDeletion = this.state.bomChanges && this.state.bomChanges[partItem.id] && this.state.bomChanges[partItem.id].markedForDeletion;
                
                familyRows.push(
                    <tr key={`family-part-${partIdx}`} style={{
                        backgroundColor: '#f0f8ff',
                        opacity: isMarkedForDeletion ? 0.5 : 1,
                        textDecoration: isMarkedForDeletion ? 'line-through' : 'none'
                    }}>
                        {this.state.isBulkEditingBom && <td style={{paddingLeft: '20px'}}></td>}
                        {this.state.isBulkEditingBom ? (
                            <>
                                <td className="text-left">
                                    <Autocomplete autoHighlight
                                        options={this.state.parts.filter(x => x.part.isActive).map(p => ({
                                            title: `${p.part.partId} rev ${p.part.revision}${p.part.currentlyPurchasing ? ' 🛒' : ''}`,
                                            value: p.part.id,
                                            type: 'part'
                                        }))}
                                        onChange={(event, newValue) => {
                                            if (newValue) {
                                                this.updateBomItem(partItem.id, 'partFk', newValue.value);
                                                this.updateBomItem(partItem.id, 'subAssemblyFk', null);
                                                this.updateBomItem(partItem.id, 'itemType', 'part');
                                            }
                                        }}
                                        getOptionLabel={(option) => option.title}
                                        renderOption={(option) => <PartAutocompleteOption option={option} classes={this.props.classes} />}
                                        value={(() => {
                                            const currentPartFk = (this.state.bomChanges && this.state.bomChanges[partItem.id] && this.state.bomChanges[partItem.id].partFk !== undefined) 
                                                ? this.state.bomChanges[partItem.id].partFk 
                                                : partItem.partFk;
                                            
                                            if (currentPartFk) {
                                                const currentPart = this.state.parts.find(p => p.part.id === currentPartFk);
                                                if (currentPart) {
                                                    return {
                                                        title: `${currentPart.part.partId} rev ${currentPart.part.revision}`,
                                                        value: currentPartFk,
                                                        type: 'part'
                                                    };
                                                }
                                            }
                                            return null;
                                        })()}
                                        style={{width: '100%'}}
                                        size="small"
                                        renderInput={(params) => <TextField
                                            {...params}
                                            variant="outlined"
                                            size="small"
                                            autoComplete="new-password"
                                        />}
                                    />
                                </td>
                                <td>{part.revision}</td>
                                <td className="description-ellipsis text-left">{part.description}</td>
                            </>
                        ) : isEditing ? (
                            <>
                                <td className="text-left">
                                    <Autocomplete autoHighlight
                                        options={this.state.parts.filter(x => x.part.isActive).map(p => ({
                                            title: `${p.part.partId} rev ${p.part.revision}${p.part.currentlyPurchasing ? ' 🛒' : ''}`,
                                            value: p.part.id
                                        }))}
                                        onChange={this.handleEditingBomItemPartChange}
                                        getOptionLabel={(option) => option.title}
                                        renderOption={(option) => <PartAutocompleteOption option={option} classes={this.props.classes} />}
                                        value={this.state.parts.find(x => x.part.id === this.state.editingBomItemPart) ? {
                                            title: `${this.state.parts.find(x => x.part.id === this.state.editingBomItemPart).part.partId} rev ${this.state.parts.find(x => x.part.id === this.state.editingBomItemPart).part.revision}`,
                                            value: this.state.editingBomItemPart
                                        } : null}
                                        style={{width: '100%'}}
                                        size="small"
                                        renderInput={(params) => <TextField
                                            {...params}
                                            variant="outlined"
                                            size="small"
                                            autoComplete="new-password"
                                        />}
                                    />
                                </td>
                                <td>{part.revision}</td>
                                <td className="description-ellipsis text-left">{part.description}</td>
                            </>
                        ) : (
                            <>
                                <td className="text-left">
                                    <span style={{cursor: 'pointer', color: "black"}} onClick={() => this.startEditingBomItem(partItem, 'PartToSubAssembly')}>
                                        {part.partId}
                                    </span>
                                </td>
                                <td>{part.revision}</td>
                                <td className="description-ellipsis text-left">{part.description}</td>
                            </>
                        )}
                        <td>
                            {this.state.isBulkEditingBom ? (
                                <TextField
                                    value={getNumberInputDisplayValue((this.state.bomChanges && this.state.bomChanges[partItem.id] && this.state.bomChanges[partItem.id].quantity !== undefined) ? this.state.bomChanges[partItem.id].quantity : partItem.quantity)}
                                    onChange={(e) => this.updateBomItem(partItem.id, 'quantity', e.target.value)}
                                    type="number"
                                    variant="outlined"
                                    size="small"
                                    style={{width: '60px'}}
                                    inputProps={{min: 0, step: 1}}
                                />
                            ) : isEditing ? (
                                <TextField
                                    value={getNumberInputDisplayValue(this.state.editingBomItemQuantity)}
                                    onChange={(event) => this.setState({editingBomItemQuantity: event.target.value})}
                                    type="number"
                                    variant="outlined"
                                    size="small"
                                    style={{width: '60px'}}
                                    inputProps={{min: 0, step: 1}}
                                />
                            ) : (
                                <span style={{cursor: 'pointer', color: "black"}} onClick={() => this.startEditingBomItem(partItem, 'PartToSubAssembly')}>
                                    {getNumberInputDisplayValue(partItem.quantity)}
                                </span>
                            )}
                        </td>
                        <td style={{textAlign: 'center'}}>
                            {this.state.isBulkEditingBom ? (
                                <Checkbox
                                    checked={(this.state.bomChanges && this.state.bomChanges[partItem.id] && this.state.bomChanges[partItem.id].requiredForBomCheck !== undefined) ? this.state.bomChanges[partItem.id].requiredForBomCheck : partItem.requiredForBomCheck}
                                    onChange={(e) => this.updateBomItem(partItem.id, 'requiredForBomCheck', e.target.checked)}
                                    size="small"
                                />
                            ) : isEditing ? (
                                <Checkbox
                                    checked={this.state.editingBomItemBomRequired}
                                    onChange={(event) => this.setState({editingBomItemBomRequired: event.target.checked})}
                                    size="small"
                                />
                            ) : (
                                <span style={{cursor: 'pointer'}} onClick={() => this.startEditingBomItem(partItem, 'PartToSubAssembly')}>
                                    {partItem.requiredForBomCheck ? <FontAwesomeIcon icon={faCheck} size="1x"/> : ''}
                                </span>
                            )}
                        </td>
                        <td style={{textAlign: 'center'}}>
                            {this.state.isBulkEditingBom ? (
                                <Checkbox
                                    checked={(this.state.bomChanges && this.state.bomChanges[partItem.id] && this.state.bomChanges[partItem.id].isImpliedPart !== undefined) ? this.state.bomChanges[partItem.id].isImpliedPart : partItem.isImpliedPart}
                                    onChange={(e) => this.updateBomItem(partItem.id, 'isImpliedPart', e.target.checked)}
                                    size="small"
                                />
                            ) : isEditing ? (
                                <Checkbox
                                    checked={this.state.editingBomItemIsImpliedPart}
                                    onChange={(event) => this.setState({editingBomItemIsImpliedPart: event.target.checked})}
                                    size="small"
                                />
                            ) : (
                                <span style={{cursor: 'pointer'}} onClick={() => this.startEditingBomItem(partItem, 'PartToSubAssembly')}>
                                    {partItem.isImpliedPart ? <FontAwesomeIcon icon={faCheck} size="1x"/> : ''}
                                </span>
                            )}
                        </td>
                        {this.state.isBulkEditingBom && (
                            <td>
                                {isEditing ? (
                                    <div style={{display: 'flex', gap: '5px'}}>
                                        {this.canSaveInlineBomItemEdit() && (
                                            <button
                                                onClick={this.saveInlineBomItemEdit}
                                                style={{
                                                    backgroundColor: '#28a745',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    padding: '5px 8px',
                                                    cursor: 'pointer',
                                                    fontSize: '12px'
                                                }}
                                                disabled={this.state.savingConnection}
                                            >
                                                {'✓'}
                                            </button>
                                        )}
                                        <button
                                            onClick={this.cancelEditingBomItem}
                                            style={{
                                                backgroundColor: '#dc3545',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                padding: '5px 8px',
                                                cursor: 'pointer',
                                                fontSize: '12px'
                                            }}
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ) : (
                                    <FontAwesomeIcon 
                                        icon={faTrash} 
                                        size="1x" 
                                        style={{cursor: "pointer", color: '#dc3545'}}
                                        onClick={() => {
                                            // Mark item for deletion instead of immediately severing
                                            this.updateBomItem(partItem.id, 'markedForDeletion', true);
                                        }}
                                    />
                                )}
                            </td>
                        )}
                    </tr>
                );
            });
            
            return (
                <React.Fragment key={item.dragId}>
                    {familyRows.map((row, rowIdx) => (
                        <React.Fragment key={`${item.dragId}-row-${rowIdx}`}>
                            {rowIdx === 0 ? (
                                React.cloneElement(row, {
                                    ref: provided.innerRef,
                                    ...provided.draggableProps,
                                    style: { ...provided.draggableProps.style, ...row.props.style }
                                })
                            ) : row}
                        </React.Fragment>
                    ))}
                </React.Fragment>
            );
        }
        
        return null;
    };

    render() {
        if (this.state.loading) {
            return (
                <DashboardTemplate title="Edit Assembly" onSyncDatabase={this.loadData}>
                    <div className="main-dashboard-root">
                        <CircularProgress />
                    </div>
                </DashboardTemplate>
            );
        }

        let assemblyTypeOptions = [];
        let assemblyOptions = [];
        let manufacturerForOptions = [];
        let tertiaryTypeOptions = [];
        let allBrands = []; // Collect ALL brands from parts, assemblies, and subassemblies
        
        this.state.assemblies.filter(x => x.assembly.isActive).forEach((assembly) => {
            assemblyOptions.push(assembly.assembly);
            if (assembly.assembly.assemblyType !== '' && !assemblyTypeOptions.includes(assembly.assembly.assemblyType))
                assemblyTypeOptions.push(assembly.assembly.assemblyType);
            if (assembly.assembly.manufacturerFor !== '' && !manufacturerForOptions.find(x => x.subType === assembly.assembly.manufacturerFor && x.assemblyType === assembly.assembly.assemblyType))
                manufacturerForOptions.push({subType: assembly.assembly.manufacturerFor, assemblyType: assembly.assembly.assemblyType});
            if (assembly.assembly.tertiaryType !== '' && !tertiaryTypeOptions.find(x => x.subType === assembly.assembly.tertiaryType && x.assemblyType === assembly.assembly.assemblyType && x.manufacturerFor === assembly.assembly.manufacturerFor))
                tertiaryTypeOptions.push({subType: assembly.assembly.tertiaryType, assemblyType: assembly.assembly.assemblyType, manufacturerFor: assembly.assembly.manufacturerFor});
            if (assembly.assembly.brand && assembly.assembly.brand !== '' && !allBrands.includes(assembly.assembly.brand))
                allBrands.push(assembly.assembly.brand);
        });
        assemblyTypeOptions = assemblyTypeOptions.sort();
        manufacturerForOptions = manufacturerForOptions.sort((a, b) => a.subType > b.subType ? 1 : -1);
        tertiaryTypeOptions = tertiaryTypeOptions.sort((a, b) => a.subType > b.subType ? 1 : -1);

        let subAssemblyTypeOptions = [];
        let subManufacturerForOptions = [];
        let subTertiaryTypeOptions = [];
        this.state.subAssemblies.filter(x => x.subAssembly.isActive).forEach((subAssembly) => {
            if (subAssembly.subAssembly.assemblyType !== '' && !subAssemblyTypeOptions.includes(subAssembly.subAssembly.assemblyType))
                subAssemblyTypeOptions.push(subAssembly.subAssembly.assemblyType);
            if (subAssembly.subAssembly.manufacturerFor !== '' && !subManufacturerForOptions.find(x => x.subType === subAssembly.subAssembly.manufacturerFor && x.assemblyType === subAssembly.subAssembly.assemblyType))
                subManufacturerForOptions.push({subType: subAssembly.subAssembly.manufacturerFor, assemblyType: subAssembly.subAssembly.assemblyType});
            if (subAssembly.subAssembly.tertiaryType !== '' && !subTertiaryTypeOptions.find(x => x.subType === subAssembly.subAssembly.tertiaryType && x.assemblyType === subAssembly.subAssembly.assemblyType && x.manufacturerFor === subAssembly.subAssembly.manufacturerFor))
                subTertiaryTypeOptions.push({subType: subAssembly.subAssembly.tertiaryType, assemblyType: subAssembly.subAssembly.assemblyType, manufacturerFor: subAssembly.subAssembly.manufacturerFor});
            if (subAssembly.subAssembly.brand && subAssembly.subAssembly.brand !== '' && !allBrands.includes(subAssembly.subAssembly.brand))
                allBrands.push(subAssembly.subAssembly.brand);
        });
        subAssemblyTypeOptions = subAssemblyTypeOptions.sort();
        subManufacturerForOptions = subManufacturerForOptions.sort((a, b) => a.subType > b.subType ? 1 : -1);
        subTertiaryTypeOptions = subTertiaryTypeOptions.sort((a, b) => a.subType > b.subType ? 1 : -1);

        // Collect brands from parts
        this.state.parts.filter(x => x.part.isActive).forEach((part) => {
            if (part.part.brand && part.part.brand !== '' && !allBrands.includes(part.part.brand))
                allBrands.push(part.part.brand);
        });
        
        // Use the same brand list for both assemblies and subassemblies
        const brandOptions = allBrands.sort();
        const subBrandOptions = allBrands.sort();

        let editingAssemblyBom = [];
        let connectionFromOptions = [];
        
        // Add packaging families first
        this.state.partFamilies.forEach((family) => {
            connectionFromOptions.push({
                title: `${family.partFamilyId} (Family)`,
                value: family.id,
                type: 'family',
                family: family
            });
        });

        let sortedPartsToAssemblies = [];
        let sortedSubAssembliesToAssemblies = [];

        if (editingAssembly) {
            // Combine and sort all items by order
            const allItems = [
                ...editingAssembly.assembly.subAssembliesToAssemblies,
                ...editingAssembly.assembly.partsToAssemblies
            ].sort((a, b) => (a.order || 0) - (b.order || 0));
            
            // Split back into their respective arrays
            sortedSubAssembliesToAssemblies = allItems.filter(item => item.subAssemblyFk);
            sortedPartsToAssemblies = allItems.filter(item => item.partFk);
        } else if (editingSubAssembly) {
            sortedPartsToAssemblies = [...editingSubAssembly.subAssembly.partsToSubAssemblies].sort((a, b) => (a.order || 0) - (b.order || 0));
            sortedSubAssembliesToAssemblies = [...(editingSubAssembly.subAssembly.childSubAssemblies || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
        }

        if (editingAssembly) {
            this.state.subAssemblies.filter(x => x.subAssembly.isActive).forEach((subAssembly) => {
                connectionFromOptions.push({
                    title: `${subAssembly.subAssembly.subAssemblyId} rev ${subAssembly.subAssembly.revision}${subAssembly.subAssembly.currentlyPackaging ? ' 📦' : ''}`,
                    value: subAssembly.subAssembly.id,
                    type: 'subassembly',
                    partId: subAssembly.subAssembly.subAssemblyId,
                    revision: subAssembly.subAssembly.revision,
                    description: subAssembly.subAssembly.description,
                    currentlyPackaging: subAssembly.subAssembly.currentlyPackaging
                });
            });
        }
        
        // Add parts to connection options
        this.state.parts.filter(x => x.part.isActive).forEach((part) => {
            connectionFromOptions.push({
                title: `${part.part.partId} rev ${part.part.revision}${part.part.currentlyPurchasing ? ' 🛒' : ''}`,
                value: part.part.id,
                type: 'part',
                part: part.part,
                partId: part.part.partId,
                revision: part.part.revision,
                description: part.part.description,
                currentlyPurchasing: part.part.currentlyPurchasing
            });
        });

        // Sort connectionFromOptions alphabetically
        connectionFromOptions.sort((a, b) => a.title.localeCompare(b.title));

        let discountStrategyOptions = [];
        this.state.discountStrategies.forEach((discountStrategy) => {
            discountStrategyOptions.push({title: discountStrategy.name, value: discountStrategy.id});
        });

        let assemblyFamilyOptions = [];
        assemblyFamilyOptions.push({title: 'New...', value: 'New'});
        this.state.assemblyFamilies.forEach((assemblyFamily) => {
            assemblyFamilyOptions.push({title: assemblyFamily.assemblyFamilyId, value: assemblyFamily.id});
        });
        
        return (
            <DashboardTemplate title="Edit Assembly" onSyncDatabase={this.loadData}>
                <div className="main-dashboard-root">
                    <div style={{marginBottom: 0, marginTop: -10}}>
                        <div style={{position: 'relative', display: 'flex', alignItems: 'center', marginBottom: 0}}>
                            {/* Left: Back and Save buttons */}
                            <div style={{flex: '1', display: 'flex', alignItems: 'center', gap: '15px', justifyContent: 'flex-start'}}>
                                <Button onClick={() => globalProps.history.length > 1 ? globalProps.history.goBack() : globalProps.history.push('/admin/assemblies')} className="success-button-lg">Back</Button>
                                {this.state.savingAssembly === true ?
                                    <CircularProgress/>
                                    :
                                    <Button
                                        onClick={editingAssembly ? this.saveAssembly : this.saveSubAssembly}
                                        className="save-button-lg"
                                        style={{ 
                                            backgroundColor: this.checkForChanges() ? '#7fff84' : '#93e7eb',
                                            color: 'black'
                                        }}
                                    >Save Changes</Button>
                                }
                            </div>
                            
                            {/* Center: Assembly number and revision with navigation arrows */}
                            <div style={{position: 'absolute', left: '50%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center', gap: '15px'}}>
                                {/* Get current index for navigation */}
                                {(() => {
                                    let currentIndex = -1;
                                    let hasPrevious = false;
                                    let hasNext = false;
                                    
                                    if (this.state.editingAssembly) {
                                        currentIndex = this.state.assemblies.findIndex(a => a.assembly.id === editingAssembly.assembly.id);
                                        hasPrevious = currentIndex > 0;
                                        hasNext = currentIndex < this.state.assemblies.length - 1;
                                    } else if (this.state.editingSubAssembly) {
                                        currentIndex = this.state.subAssemblies.findIndex(sa => sa.subAssembly.id === editingSubAssembly.subAssembly.id);
                                        hasPrevious = currentIndex > 0;
                                        hasNext = currentIndex < this.state.subAssemblies.length - 1;
                                    }
                                    
                                    return (
                                        <>
                                            <div 
                                                onClick={hasPrevious ? (this.state.editingAssembly ? this.navigateToPreviousAssembly : this.navigateToPreviousSubAssembly) : undefined}
                                                style={{
                                                    fontSize: '24px',
                                                    color: hasPrevious ? 'white' : '#666',
                                                    cursor: hasPrevious ? 'pointer' : 'default',
                                                    userSelect: 'none',
                                                    padding: '5px'
                                                }}
                                            >
                                                ←
                                            </div>
                                            <h1 style={{margin: 0, textAlign: 'center', color: 'white'}}>
                                                {this.state.editingAssembly ? 
                                                    `${this.state.editingAssembly.assembly.assemblyId} rev ${this.state.editingAssembly.assembly.revision}` : 
                                                    this.state.editingSubAssembly ? 
                                                    `${this.state.editingSubAssembly.subAssembly.subAssemblyId} rev ${this.state.editingSubAssembly.subAssembly.revision}` :
                                                    'Edit Assembly'
                                                }
                                            </h1>
                                            <div 
                                                onClick={hasNext ? (this.state.editingAssembly ? this.navigateToNextAssembly : this.navigateToNextSubAssembly) : undefined}
                                                style={{
                                                    fontSize: '24px',
                                                    color: hasNext ? 'white' : '#666',
                                                    cursor: hasNext ? 'pointer' : 'default',
                                                    userSelect: 'none',
                                                    padding: '5px'
                                                }}
                                            >
                                                →
                                            </div>
                                        </>
                                    );
                                })()}
                            </div>
                            
                            {/* Right: Duplicate, Deactivate and Delete buttons */}
                            <div style={{flex: '1', display: 'flex', alignItems: 'center', gap: '15px', justifyContent: 'flex-end'}}>
                                {this.state.savingAssembly !== true && this.state.editingAssembly && editingAssembly.assembly.id !== '' ?
                                    <>
                                        <Button onClick={this.cloneAssembly} className="success-button-lg">Duplicate</Button>
                                        {editingAssembly.totalSoldWithRev === 0 && editingAssembly.totalAssembledWithRev === 0 ?
                                            <Button onClick={this.deleteAssembly} className="delete-button-lg">Permanently Delete</Button>
                                            : ""}
                                        {editingAssembly.assembly.isActive ?
                                            <Button onClick={this.deactivateAssembly} className="delete-button-lg">Deactivate</Button>
                                            :
                                            <Button onClick={this.reactivateAssembly} className="success-button-lg">Reactivate</Button>
                                        }
                                    </>
                                    : ""}
                                {this.state.savingSubAssembly !== true && this.state.editingSubAssembly && editingSubAssembly.subAssembly.id !== '' ?
                                    <>
                                        <Button onClick={this.printSubAssemblyLabelSmall} className="success-button-lg"><PrintIcon style={{verticalAlign: 'middle', marginRight: '8px'}} /> Label (sm)</Button>
                                        <Button onClick={this.printSubAssemblyLabelLarge} className="success-button-lg"><PrintIcon style={{verticalAlign: 'middle', marginRight: '8px'}} /> Label (lg)</Button>
                                        <Button onClick={this.cloneSubAssembly} className="success-button-lg">Duplicate</Button>
                                        {editingSubAssembly.totalSold === 0 && editingSubAssembly.totalAssembled === 0 ?
                                            <Button onClick={this.deleteSubAssembly} className="delete-button-lg">Permanently Delete</Button>
                                            : ""}
                                        {editingSubAssembly.subAssembly.isActive ?
                                            <Button onClick={this.deactivateSubAssembly} className="delete-button-lg">Deactivate</Button>
                                            :
                                            <Button onClick={this.reactivateSubAssembly} className="success-button-lg">Reactivate</Button>
                                        }
                                    </>
                                    : ""}
                            </div>
                        </div>
                    </div>
                    
                    {/* Search Bar for Assembly/SubAssembly Navigation */}
                    <div style={{display: 'flex', justifyContent: 'center'}}>
                        <Autocomplete
                            options={[
                                ...(this.state.assemblies ? this.state.assemblies.map(assembly => ({
                                    type: 'assembly',
                                    partId: assembly.assembly.assemblyId,
                                    revision: assembly.assembly.revision,
                                    description: assembly.assembly.description,
                                    value: assembly.assembly.id
                                })) : []),
                                ...(this.state.subAssemblies ? this.state.subAssemblies.map(subAssembly => ({
                                    type: 'subassembly',
                                    partId: subAssembly.subAssembly.subAssemblyId,
                                    revision: subAssembly.subAssembly.revision,
                                    description: subAssembly.subAssembly.description,
                                    value: subAssembly.subAssembly.id
                                })) : [])
                            ]}
                            getOptionLabel={(option) => option.partId ? `${option.partId} rev ${option.revision}` : ''}
                            style={{width: 300, padding: 8, backgroundColor: 'white'}}
                            renderOption={(option) => <PartAutocompleteOption option={option} classes={this.props.classes} />}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Search for another assembly..."
                                    variant="outlined"
                                    size="small"
                                />
                            )}
                            onChange={(event, newValue) => {
                                if (newValue) {
                                    globalProps.history.push(`/admin/assemblies/${newValue.value}`);
                                    window.location.reload();
                                }
                            }}
                        />
                    </div>
                    
                    <div style={{display: 'flex', gap: '20px', maxWidth: this.state.editingAssembly ? 1800 : 1700, margin: '0 auto', alignItems: 'flex-start'}}>
                        {/* Column 1: Basic Information */}
                        <div style={{flex: '1', minWidth: '300px', maxWidth: '40%', display: 'flex', flexDirection: 'column', gap: '15px'}}>
                            {this.state.editingAssembly ?
                                <>
                                    {/* Basic Information Section */}
                                    <div className="container" style={{padding: '20px', backgroundColor: 'white', borderRadius: 8, boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: 0, marginTop: 15}}>
                                        <h2 style={{marginTop: 0}}>Basic Information</h2>
                                        <TextField defaultValue={this.state.editingAssembly.assembly.assemblyId}
                                                   onChange={this.changeAssemblyId} label={"Part Number"} variant="outlined"
                                                   style={{width: '24%', margin: '0 5px 5px'}} inputProps={{maxLength: 50}}/>
                                        <TextField defaultValue={this.state.editingAssembly.assembly.description}
                                                   onChange={this.changeDescription} label={"Description"} 
                                                   variant="outlined" multiline={true} style={{width: '70%', margin: '0 5px 20px'}}
                                                   inputProps={{maxLength: 500}}/>
                                        <TextField defaultValue={this.state.editingAssembly.assembly.revision}
                                                   onChange={this.changeRevision} label={"Revision"} variant="outlined"
                                                   style={{width: '24%', marginBottom: 20}} inputProps={{maxLength: 5}}/>
                                        <TextField defaultValue={this.state.editingAssembly.assembly.revisionNotes}
                                                   onChange={this.changeRevisionNotes} label={"Revision Notes"}
                                                   variant="outlined"
                                                   style={{width: '70%', marginBottom: 20, marginLeft: 10}}
                                                   inputProps={{maxLength: 500}}/>
                                        <div style={{display: 'flex', justifyContent: 'center', margin: '0 auto 10px'}}>
                                            <FormControl variant="outlined" style={{width: '32%', display: 'inline-block', margin: '0 5px'}}>
                                                <InputLabel id="assembly-status-label">Status</InputLabel>
                                                <Select
                                                    value={this.state.editingAssembly.assembly.status ?? 0}
                                                    style={{width: '100%'}}
                                                    label="Status"
                                                >
                                                    <MenuItem value={0}
                                                            onClick={() => this.handleAssemblyStatusChange(0)}>Standard</MenuItem>
                                                    <MenuItem value={1} onClick={() => this.handleAssemblyStatusChange(1)}>New
                                                        Product</MenuItem>
                                                    <MenuItem value={2} onClick={() => this.handleAssemblyStatusChange(2)}>Preorder
                                                        Phase</MenuItem>
                                                    <MenuItem value={3} onClick={() => this.handleAssemblyStatusChange(3)}>Future
                                                        Product</MenuItem>
                                                    <MenuItem value={4}
                                                            onClick={() => this.handleAssemblyStatusChange(4)}>Updates</MenuItem>
                                                </Select>
                                            </FormControl>
                                            <TextField value={this.state.editingAssembly.assembly.shippingCost ?? ''}
                                                    onChange={this.changeShippingCost} label={"Shipping Cost"}
                                                    variant="outlined" style={{width: '32%', minWidth: 90, margin: '0 5px'}}
                                                    InputProps={{
                                                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                                                    }}/>
                                            <TextField value={this.state.editingAssembly.assembly.msrp ?? ''}
                                                    onChange={this.changeMSRP} label={"MSRP"}
                                                    variant="outlined" style={{width: '32%', minWidth: 90, margin: '0 5px'}}
                                                    InputProps={{
                                                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                                                    }}/>
                                        </div>
                                        <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '0 auto 12px', gap: 16, flexWrap: 'wrap'}}>
                                            <div style={{display: 'flex', alignItems: 'center'}}>
                                                <Checkbox
                                                    checked={!!this.state.editingAssembly.assembly.isOnBackorder}
                                                    onChange={this.changeAssemblyIsOnBackorder}
                                                    color="primary"
                                                />
                                                On backorder
                                            </div>
                                            {this.state.editingAssembly.assembly.isOnBackorder && (
                                                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                    <DatePicker
                                                        label="Backorder ETA"
                                                        value={this.state.editingAssembly.assembly.backorderEta || null}
                                                        onChange={this.changeAssemblyBackorderEta}
                                                        format="MM/dd/yyyy"
                                                        inputVariant="outlined"
                                                        style={{width: 220}}
                                                        minDate={addDays(new Date(), 1)}
                                                        clearable
                                                    />
                                                </MuiPickersUtilsProvider>
                                            )}
                                        </div>
                                        <div>Current Strategy: {this.state.matchingDiscountStrategy ? this.state.matchingDiscountStrategy.name : 'Custom'}</div>
                                        <div style={{marginTop: 10}}>
                                            <Autocomplete autoHighlight
                                                id="ddlDiscountStrategy"
                                                style={{width: '30%', display: 'inline-block', margin: '0 5px 20px', minWidth: 190}}
                                                options={discountStrategyOptions}
                                                onChange={this.handleDiscountStrategyChange}
                                                getOptionLabel={(option) => option.title}
                                                getOptionSelected={(option) => option.value === this.state.discountStrategy}
                                                className="autocomplete-dropdown"
                                                renderInput={(params) => <TextField {...params} inputProps={{
                                                    ...params.inputProps
                                                }} label="Load Discount Strategy" autoComplete="new-password"
                                                                                    variant="outlined"/>}
                                            />
                                            <TextField
                                                value={this.state.editingAssembly.assembly.discountTier1 < 1 ? this.state.editingAssembly.assembly.discountTier1 * 100 : this.state.editingAssembly.assembly.discountTier1}
                                                onChange={this.changeDiscountTier1} label={"Discount Tier 1"}
                                                variant="outlined" style={{width: '15%', minWidth: 105, margin: '0 5px 20px'}}
                                                helperText={this.state.editingAssembly.assembly.msrp ? `$${(this.state.editingAssembly.assembly.msrp * (1 - (this.state.editingAssembly.assembly.discountTier1 < 1 ? this.state.editingAssembly.assembly.discountTier1 : this.state.editingAssembly.assembly.discountTier1 / 100))).toFixed(2)}` : ''}
                                                InputProps={{
                                                    endAdornment: <InputAdornment position="end">%</InputAdornment>
                                                }}/>
                                            <TextField
                                                value={this.state.editingAssembly.assembly.discountTier2 < 1 ? this.state.editingAssembly.assembly.discountTier2 * 100 : this.state.editingAssembly.assembly.discountTier2}
                                                onChange={this.changeDiscountTier2} label={"Discount Tier 2"}
                                                variant="outlined" style={{width: '15%', minWidth: 105, margin: '0 5px 20px'}}
                                                helperText={this.state.editingAssembly.assembly.msrp ? `$${(this.state.editingAssembly.assembly.msrp * (1 - (this.state.editingAssembly.assembly.discountTier2 < 1 ? this.state.editingAssembly.assembly.discountTier2 : this.state.editingAssembly.assembly.discountTier2 / 100))).toFixed(2)}` : ''}
                                                InputProps={{
                                                    endAdornment: <InputAdornment position="end">%</InputAdornment>
                                                }}/>
                                            <TextField
                                                value={this.state.editingAssembly.assembly.discountTier3 < 1 ? this.state.editingAssembly.assembly.discountTier3 * 100 : this.state.editingAssembly.assembly.discountTier3}
                                                onChange={this.changeDiscountTier3} label={"Discount Tier 3"}
                                                variant="outlined" style={{width: '15%', minWidth: 105, margin: '0 5px 20px'}}
                                                helperText={this.state.editingAssembly.assembly.msrp ? `$${(this.state.editingAssembly.assembly.msrp * (1 - (this.state.editingAssembly.assembly.discountTier3 < 1 ? this.state.editingAssembly.assembly.discountTier3 : this.state.editingAssembly.assembly.discountTier3 / 100))).toFixed(2)}` : ''}
                                                InputProps={{
                                                    endAdornment: <InputAdornment position="end">%</InputAdornment>
                                                }}/>
                                        </div>
                                        
                                        {/* Inventory Analytics */}
                                        {this.state.inventoryData ? (
                                            <div style={{maxWidth: '300px', margin: '0 auto'}}>
                                                <div style={{display: 'flex', flexDirection: 'column', gap: '3px'}}>
                                                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', backgroundColor: '#efefef', borderRadius: '3px', border: '1px solid #e9ecef'}}>
                                                        <span style={{fontWeight: 'bold', fontSize: '13px'}}>Total Sold w/ Rev:</span>
                                                        <span style={{fontWeight: 'bold', fontSize: '13px'}}>{this.state.inventoryData.totalSoldWithRev.toFixed(0)}</span>
                                                    </div>
                                                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', backgroundColor: '#efefef', borderRadius: '3px', border: '1px solid #e9ecef'}}>
                                                        <span style={{fontWeight: 'bold', fontSize: '13px'}}>Total Ass'd w/ Rev:</span>
                                                        <span style={{fontWeight: 'bold', fontSize: '13px'}}>{this.state.inventoryData.totalAssembledWithRev.toFixed(0)}</span>
                                                    </div>
                                                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', backgroundColor: '#efefef', borderRadius: '3px', border: '1px solid #e9ecef'}}>
                                                        <span style={{fontWeight: 'bold', fontSize: '13px'}}>Total Ass'd On Hand w/ Rev:</span>
                                                        <span style={{fontWeight: 'bold', fontSize: '13px'}}>{this.state.inventoryData.totalAssembledOnHandWithRev.toFixed(0)}</span>
                                                    </div>
                                                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', backgroundColor: '#efefef', borderRadius: '3px', border: '1px solid #e9ecef'}}>
                                                        <span style={{fontWeight: 'bold', fontSize: '13px'}}>4 Week Sales / wk w/ Rev:</span>
                                                        <span style={{fontWeight: 'bold', fontSize: '13px'}}>{this.state.inventoryData.fourWeekSalesPerWeekWithRev.toFixed(2)}</span>
                                                    </div>
                                                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', backgroundColor: '#efefef', borderRadius: '3px', border: '1px solid #e9ecef'}}>
                                                        <span style={{fontWeight: 'bold', fontSize: '13px'}}>24 Week Sales / wk w/ Rev:</span>
                                                        <span style={{fontWeight: 'bold', fontSize: '13px'}}>{this.state.inventoryData.twentyFourWeekSalesPerWeekWithRev.toFixed(2)}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div style={{marginTop: 20, padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '4px', border: '1px solid #dee2e6', textAlign: 'center'}}>
                                                <CircularProgress size={20} />
                                                <div style={{marginTop: '8px', fontSize: '13px', color: '#6c757d'}}>Loading inventory data...</div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Organization Section */}
                                    <div className="container" style={{padding: '20px', backgroundColor: 'white', borderRadius: 8, boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: 0, marginTop: 5}}>
                                        <h2 style={{marginTop: 0}}>Organization</h2>
                                <Autocomplete autoHighlight
                                    id="ddlAssemblyFamily"
                                    style={{marginBottom: 20}}
                                    options={assemblyFamilyOptions}
                                    onChange={this.handleAssemblyFamilyChange}
                                    getOptionLabel={(option) => option.title}
                                    getOptionSelected={(option) => option.value === this.state.assemblyFamily}
                                    className="autocomplete-dropdown"
                                    renderInput={(params) => <TextField {...params} inputProps={{
                                        ...params.inputProps
                                    }} label="Assembly Family (optional)" autoComplete="new-password"
                                                                        variant="outlined"/>}
                                />
                                {this.state.matchingFamily ?
                                    <div style={{textAlign: 'left', marginBottom: 25, display: 'flex', alignItems: 'center', gap: '10px'}}>
                                        <span>Current Family: {this.state.matchingFamily.assemblyFamilyId}</span>
                                        <button
                                            onClick={this.removeAssemblyFamily}
                                            style={{
                                                backgroundColor: '#dc3545',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                padding: '4px 8px',
                                                cursor: 'pointer',
                                                fontSize: '12px'
                                            }}
                                        >
                                            Remove
                                        </button>
                                    </div> : ''}
                                {this.state.assemblyFamily === 'New' ?
                                    <div>
                                        <TextField defaultValue=''
                                                    onChange={this.changeNewFamilyName} label={"New Family Name"}
                                                    variant="outlined"
                                                    style={{width: '100%', marginBottom: 20}}
                                                    inputProps={{maxLength: 50}}/>
                                        <TextField defaultValue=''
                                                    onChange={this.changeNewFamilyDescription}
                                                    label={"New Family Description"} variant="outlined"
                                                    style={{width: '100%', marginBottom: 20}}
                                                    inputProps={{maxLength: 500}}/>
                                    </div>
                                    : ""}
                                <Autocomplete autoHighlight
                                    id="ddlAssemblyType"
                                    style={{marginBottom: 20}}
                                    options={assemblyTypeOptions}
                                    onChange={(event, newValue) => this.changeAssemblyTypeFreeSolo(newValue)}
                                    onInputChange={(event, newInputValue) => this.changeAssemblyTypeInput(newInputValue)}
                                    value={this.state.editingAssembly.assembly.assemblyType}
                                    freeSolo
                                    filterOptions={(options, { inputValue }) => {
                                        const inputVal = this.state.assemblyTypeInputValue || inputValue || '';
                                        
                                        if (!inputVal || inputVal.trim() === '') {
                                            // Only exclude current value if it's unique to this assembly
                                            const currentValue = this.state.editingAssembly.assembly.assemblyType;
                                            const isValueUsedByOtherAssemblies = this.state.assemblies.some(assembly => 
                                                assembly.assembly.id !== this.state.editingAssembly.assembly.id && 
                                                assembly.assembly.assemblyType === currentValue
                                            );
                                            return isValueUsedByOtherAssemblies ? options : options.filter(option => option !== currentValue);
                                        }
                                        
                                        // When typing, show ALL matching options (including current value if it matches)
                                        return options.filter(option => {
                                            return option && option.toLowerCase().includes(inputVal.toLowerCase());
                                        });
                                    }}
                                    className="autocomplete-dropdown"
                                    renderInput={(params) => <TextField {...params} inputProps={{
                                        ...params.inputProps
                                    }} label="Assembly Category (Primary)" autoComplete="new-password" variant="outlined"/>}
                                />
                                {this.state.editingAssembly.assembly.assemblyType.length > 0 ?
                                    <div>
                                        <Autocomplete autoHighlight
                                            id="ddlManufacturerFor"
                                            style={{marginBottom: 20}}
                                            options={manufacturerForOptions.filter(x => x.assemblyType === this.state.editingAssembly.assembly.assemblyType).map(x => x.subType)}
                                            onChange={(event, newValue) => this.changeManufacturerForFreeSolo(newValue)}
                                            onInputChange={(event, newInputValue) => this.changeManufacturerForInput(newInputValue)}
                                            value={this.state.editingAssembly.assembly.manufacturerFor}
                                            freeSolo
                                            filterOptions={(options, { inputValue }) => {
                                                const inputVal = this.state.manufacturerForInputValue || inputValue || '';
                                                
                                                if (!inputVal || inputVal.trim() === '') {
                                                    // Only exclude current value if it's unique to this assembly
                                                    const currentValue = this.state.editingAssembly.assembly.manufacturerFor;
                                                    const isValueUsedByOtherAssemblies = this.state.assemblies.some(assembly => 
                                                        assembly.assembly.id !== this.state.editingAssembly.assembly.id && 
                                                        assembly.assembly.manufacturerFor === currentValue
                                                    );
                                                    return isValueUsedByOtherAssemblies ? options : options.filter(option => option !== currentValue);
                                                }
                                                
                                                // When typing, show ALL matching options (including current value if it matches)
                                                return options.filter(option => {
                                                    return option && option.toLowerCase().includes(inputVal.toLowerCase());
                                                });
                                            }}
                                            className="autocomplete-dropdown"
                                            renderInput={(params) => <TextField {...params} inputProps={{
                                                ...params.inputProps
                                            }} label="Assembly Category (Secondary)" autoComplete="new-password"
                                                                                variant="outlined"/>}
                                        />
                                        <Autocomplete autoHighlight
                                            id="ddlTertiaryType"
                                            style={{marginBottom: 20, width: '100%'}}
                                            options={[...new Set(tertiaryTypeOptions.map(x => x.subType))]}
                                            onChange={(event, newValue) => this.changeTertiaryTypeFreeSolo(newValue)}
                                            onInputChange={(event, newInputValue) => this.changeTertiaryTypeInput(newInputValue)}
                                            value={this.state.editingAssembly.assembly.tertiaryType}
                                            freeSolo
                                            filterOptions={(options, { inputValue }) => {
                                                const inputVal = this.state.tertiaryTypeInputValue || inputValue || '';
                                                
                                                if (!inputVal || inputVal.trim() === '') {
                                                    // Only exclude current value if it's unique to this assembly
                                                    const currentValue = this.state.editingAssembly.assembly.tertiaryType;
                                                    const isValueUsedByOtherAssemblies = this.state.assemblies.some(assembly => 
                                                        assembly.assembly.id !== this.state.editingAssembly.assembly.id && 
                                                        assembly.assembly.tertiaryType === currentValue
                                                    );
                                                    return isValueUsedByOtherAssemblies ? options : options.filter(option => option !== currentValue);
                                                }
                                                
                                                // When typing, show ALL matching options (including current value if it matches)
                                                return options.filter(option => {
                                                    return option && option.toLowerCase().includes(inputVal.toLowerCase());
                                                });
                                            }}
                                            renderInput={(params) => <TextField {...params} inputProps={{
                                                ...params.inputProps
                                            }} label="Assembly Category (Tertiary)" autoComplete="new-password"
                                                                                variant="outlined"/>}
                                        />
                                        <Autocomplete autoHighlight
                                            id="ddlBrand"
                                            style={{marginBottom: 20, width: '100%'}}
                                            options={brandOptions}
                                            onChange={(event, newValue) => this.changeBrandFreeSolo(newValue)}
                                            onInputChange={(event, newInputValue) => this.changeBrandInput(newInputValue)}
                                            value={this.state.editingAssembly.assembly.brand || ''}
                                            inputValue={this.state.brandInputValue}
                                            freeSolo
                                            filterOptions={(options, { inputValue }) => {
                                                const inputVal = this.state.brandInputValue || inputValue || '';
                                                
                                                if (!inputVal || inputVal.trim() === '') {
                                                    // Only exclude current value if it's unique to this assembly
                                                    const currentValue = this.state.editingAssembly.assembly.brand;
                                                    const isValueUsedByOtherAssemblies = this.state.assemblies.some(assembly => 
                                                        assembly.assembly.id !== this.state.editingAssembly.assembly.id && 
                                                        assembly.assembly.brand === currentValue
                                                    );
                                                    return isValueUsedByOtherAssemblies ? options : options.filter(option => option !== currentValue);
                                                }
                                                
                                                // When typing, show ALL matching options (including current value if it matches)
                                                return options.filter(option => {
                                                    return option && option.toLowerCase().includes(inputVal.toLowerCase());
                                                });
                                            }}
                                            className="autocomplete-dropdown"
                                            renderInput={(params) => <TextField {...params} inputProps={{
                                                ...params.inputProps
                                            }} label="Brand" autoComplete="new-password"
                                                                                variant="outlined"/>}
                                        />
                                        
                                        {/* Inventory Item Checkbox */}
                                        <div style={{marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                            <Checkbox
                                                checked={this.state.editingAssembly.assembly.isInventoryItem}
                                                onChange={this.changeAssemblyIsInventoryItem}
                                                color="primary"
                                            />
                                            <center>Inventory item</center>
                                        </div>
                                    </div>
                                    : ""}
                                    </div>

                                    {/* Vehicle Fitment Container (SEMA / dealer) */}
                                    <div className="container" style={{padding: '20px', backgroundColor: 'white', borderRadius: 8, boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: 5, marginTop: 5}}>
                                        <VehicleFitmentPanel productType="Assembly" productId={this.state.editingAssembly.assembly.id}/>
                                    </div>

                                    {/* Images Section */}
                                    <div 
                                        className="container" 
                                        onDragOver={this.handleDragOverAssemblyMain}
                                        onDragLeave={this.handleDragLeaveAssemblyMain}
                                        onDrop={async (event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            this.setState({ isDraggingOverAssemblyMain: false });
                                            const files = Array.from(event.dataTransfer.files);
                                            if (files.length > 0) {
                                                // Process files - will replace blank slots first, then append
                                                await this.processAssemblyImageFiles(files, 0);
                                            }
                                        }}
                                        style={{
                                            padding: '20px', 
                                            backgroundColor: this.state.isDraggingOverAssemblyMain ? '#E3F2FD' : 'white', 
                                            borderRadius: 8, 
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)', 
                                            marginBottom: 0, 
                                            marginTop: 5,
                                            border: this.state.isDraggingOverAssemblyMain ? '2px dashed #2196F3' : 'none',
                                            transition: 'all 0.2s ease-in-out'
                                        }}
                                    >
                                        <h2 style={{marginTop: 0}}>
                                            {this.state.isDraggingOverAssemblyMain ? 'Drop images here to add' : 'Images'}
                                        </h2>
                                        {this.state.editingAssembly.assembly.imageURLs.map((imageURL, index) => {
                                            return (
                                                <div key={index} style={{
                                                    marginBottom: 20
                                                }}>
                                                    <div style={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        marginBottom: 10
                                                    }}>
                                                        {this.state.editingAssembly.assembly.imageURLs.length > 1 ?
                                                            <div style={{marginRight: 5}}
                                                                 onClick={() => this.removeImage(imageURL)}>
                                                                <FontAwesomeIcon icon={faTrash} size="1x" style={{
                                                                    width: 28,
                                                                    fontSize: 18,
                                                                    cursor: "pointer"
                                                                }}/>
                                                            </div>
                                                            : ""
                                                        }
                                                        <TextField defaultValue={imageURL}
                                                                   onChange={(event) => this.changeImageURL(this.state.editingAssembly.assembly.imageURLs.indexOf(imageURL), event)}
                                                                   label={"Image URL"} variant="outlined" style={{width: '80%'}}
                                                                   inputProps={{maxLength: 500}}/>
                                                        {imageURL.length > 0 ?
                                                            <img
                                                                src={this.state.editingAssembly.assembly.imageURLs[this.state.editingAssembly.assembly.imageURLs.indexOf(imageURL)]}
                                                                width="18%" alt={this.state.editingAssembly.assembly.assemblyId}
                                                                style={{display: 'inline-block', marginLeft: 10, cursor: 'pointer'}}
                                                                onClick={() => this.openImageModal(imageURL)}/>
                                                            : ""}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            marginTop: 10,
                                            marginBottom: 20
                                        }}>
                                            <span style={{marginRight: 10, fontSize: 14, color: '#666'}}>OR</span>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                multiple
                                                onChange={(event) => this.handleAssemblyImageUpload(0, event)}
                                                style={{marginRight: 10, maxWidth: '180px', width: 'auto'}}
                                            />
                                            {this.state.uploadingAssemblyImage0 && <CircularProgress size={20} />}
                                        </div>
                                        <div style={{display: 'flex', justifyContent: 'center', marginBottom: 20}}>
                                            <Button onClick={this.addNewImage} className="success-button-lg">Add Another Image</Button>
                                        </div>
                                    </div>
                                    
                                    {/* Files Section */}
                                    <div 
                                        className="container" 
                                        onDragOver={this.handleDragOverAssemblyFilesMain}
                                        onDragLeave={this.handleDragLeaveAssemblyFilesMain}
                                        onDrop={async (event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            this.setState({ isDraggingOverAssemblyFilesMain: false });
                                            const files = Array.from(event.dataTransfer.files);
                                            if (files.length > 0) {
                                                // Process files - will replace blank slots first, then append
                                                await this.processAssemblyFileFiles(files, 0);
                                            }
                                        }}
                                        style={{
                                            padding: '20px', 
                                            backgroundColor: this.state.isDraggingOverAssemblyFilesMain ? '#E3F2FD' : 'white', 
                                            borderRadius: 8, 
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)', 
                                            marginBottom: 0, 
                                            marginTop: 15,
                                            border: this.state.isDraggingOverAssemblyFilesMain ? '2px dashed #2196F3' : 'none',
                                            transition: 'all 0.2s ease-in-out'
                                        }}
                                    >
                                        <h2 style={{marginTop: 0}}>
                                            {this.state.isDraggingOverAssemblyFilesMain ? 'Drop files here to add' : 'Files'}
                                        </h2>
                                        {this.getAssemblyFileURLsArray().map((fileURL, index) => {
                                            return (
                                                <div key={index} style={{
                                                    marginBottom: 20
                                                }}>
                                                    <div style={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        marginBottom: 10
                                                    }}>
                                                        {fileURL && fileURL.length > 0 ?
                                                            <div style={{marginRight: 5}}
                                                                 onClick={() => this.removeAssemblyFile(fileURL)}>
                                                                <FontAwesomeIcon icon={faTrash} size="1x" style={{
                                                                    width: 28,
                                                                    fontSize: 18,
                                                                    cursor: "pointer"
                                                                }}/>
                                                            </div>
                                                            : ""
                                                        }
                                                        {fileURL && fileURL.length > 0 ? (
                                                            <>
                                                                <Typography 
                                                                    variant="body1" 
                                                                    style={{
                                                                        width: '80%',
                                                                        padding: '12px 14px',
                                                                        border: '1px solid rgba(0, 0, 0, 0.23)',
                                                                        borderRadius: '4px',
                                                                        backgroundColor: '#f5f5f5',
                                                                        color: '#333',
                                                                        fontSize: '16px',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap'
                                                                    }}
                                                                >
                                                                    {this.getFileNameFromUrl(fileURL)}
                                                                </Typography>
                                                                <Button
                                                                    onClick={() => this.downloadAssemblyFile(fileURL, this.getAssemblyFileURLsArray().indexOf(fileURL))}
                                                                    style={{
                                                                        marginLeft: 10,
                                                                        padding: '8px 16px',
                                                                        backgroundColor: '#2196F3',
                                                                        color: 'white',
                                                                        borderRadius: '4px',
                                                                        fontSize: '14px',
                                                                        textTransform: 'none'
                                                                    }}
                                                                >
                                                                    Download
                                                                </Button>
                                                            </>
                                                        ) : (
                                                            <Typography 
                                                                variant="body2" 
                                                                style={{
                                                                    width: '80%',
                                                                    color: '#999',
                                                                    fontStyle: 'italic'
                                                                }}
                                                            >
                                                                No file uploaded
                                                            </Typography>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            marginTop: 10,
                                            marginBottom: 20
                                        }}>
                                            <input
                                                type="file"
                                                multiple
                                                onChange={(event) => this.handleAssemblyFileUpload(0, event)}
                                                style={{marginRight: 10, maxWidth: '180px', width: 'auto'}}
                                            />
                                            {this.state.uploadingAssemblyFile0 && <CircularProgress size={20} />}
                                        </div>
                                    </div>
                                </>
                            : ""}
                        {this.state.editingSubAssembly ?
                                <>
                                    {/* Basic Information Section */}
                                    <div className="container" style={{padding: '20px', backgroundColor: 'white', borderRadius: 8, boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: 0, marginTop: 15}}>
                                        <h2 style={{marginTop: 0}}>Basic Information</h2>
                                        <TextField defaultValue={this.state.editingSubAssembly.subAssembly.subAssemblyId}
                                                   onChange={this.changeSubAssemblyId} label={"Part Number"}
                                                   variant="outlined"
                                                   style={{width: '24%', margin: '0 5px 20px'}} inputProps={{maxLength: 50}}/>
                                        <TextField defaultValue={this.state.editingSubAssembly.subAssembly.description}
                                                    onChange={this.changeSubDescription} label={"Description"}
                                                    variant="outlined" multiline={true} style={{width: '70%', margin: '0 5px 20px'}}
                                                    inputProps={{maxLength: 500}}/>
                                        <TextField defaultValue={this.state.editingSubAssembly.subAssembly.revision}
                                                   onChange={this.changeSubRevision} label={"Revision"} variant="outlined"
                                                   style={{width: '24%', margin: '0 5px 20px'}} inputProps={{maxLength: 5}}/>
                                        <TextField defaultValue={this.state.editingSubAssembly.subAssembly.revisionNotes}
                                                   onChange={this.changeSubRevisionNotes} label={"Revision Notes"}
                                                   variant="outlined" inputProps={{maxLength: 500}}
                                                   style={{width: '70%', margin: '0 5px 20px'}}/>
                                        <TextField
                                            value={this.state.editingSubAssembly.subAssembly.weightPounds || ''}
                                            onChange={this.changeSubWeightPounds}
                                            label={"Weight (Pounds)"}
                                            variant="outlined"
                                            type="number"
                                            style={{width: '23%', minWidth: 135, margin: '0 5px 20px'}}
                                            inputProps={{min: 0}}/>
                                        <TextField
                                            value={this.state.editingSubAssembly.subAssembly.weightOunces || ''}
                                            onChange={this.changeSubWeightOunces}
                                            label={"Weight (Ounces)"}
                                            variant="outlined"
                                            type="number"
                                            style={{width: '23%', minWidth: 135, margin: '0 5px 20px'}}
                                            inputProps={{min: 0, max: 15}}/>
                                        
                                        {/* Inventory Analytics */}
                                        {this.state.inventoryData ? (
                                            <div style={{maxWidth: '300px', margin: '0 auto'}}>
                                                <div style={{display: 'flex', flexDirection: 'column', gap: '3px'}}>
                                                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', backgroundColor: '#efefef', borderRadius: '3px', border: '1px solid #e9ecef'}}>
                                                        <span style={{fontWeight: 'bold', fontSize: '13px'}}>Total Sold w/ Rev:</span>
                                                        <span style={{fontWeight: 'bold', fontSize: '13px'}}>{this.state.inventoryData.totalSoldWithRev.toFixed(0)}</span>
                                                    </div>
                                                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', backgroundColor: '#efefef', borderRadius: '3px', border: '1px solid #e9ecef'}}>
                                                        <span style={{fontWeight: 'bold', fontSize: '13px'}}>Total Ass'd w/ Rev:</span>
                                                        <span style={{fontWeight: 'bold', fontSize: '13px'}}>{this.state.inventoryData.totalAssembledWithRev.toFixed(0)}</span>
                                                    </div>
                                                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', backgroundColor: '#efefef', borderRadius: '3px', border: '1px solid #e9ecef'}}>
                                                        <span style={{fontWeight: 'bold', fontSize: '13px'}}>Total Ass'd On Hand w/ Rev:</span>
                                                        <span style={{fontWeight: 'bold', fontSize: '13px'}}>{this.state.inventoryData.totalAssembledOnHandWithRev.toFixed(0)}</span>
                                                    </div>
                                                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', backgroundColor: '#efefef', borderRadius: '3px', border: '1px solid #e9ecef'}}>
                                                        <span style={{fontWeight: 'bold', fontSize: '13px'}}>SubA Total On Hand in FG w/ Rev:</span>
                                                        <span style={{fontWeight: 'bold', fontSize: '13px'}}>{this.state.inventoryData.subATotalOnHandFGWithRev.toFixed(0)}</span>
                                                    </div>
                                                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', backgroundColor: '#efefef', borderRadius: '3px', border: '1px solid #e9ecef'}}>
                                                        <span style={{fontWeight: 'bold', fontSize: '13px'}}>4 Week Sales / wk w/ Rev:</span>
                                                        <span style={{fontWeight: 'bold', fontSize: '13px'}}>{this.state.inventoryData.fourWeekSalesPerWeekWithRev.toFixed(2)}</span>
                                                    </div>
                                                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', backgroundColor: '#efefef', borderRadius: '3px', border: '1px solid #e9ecef'}}>
                                                        <span style={{fontWeight: 'bold', fontSize: '13px'}}>24 Week Sales / wk w/ Rev:</span>
                                                        <span style={{fontWeight: 'bold', fontSize: '13px'}}>{this.state.inventoryData.twentyFourWeekSalesPerWeekWithRev.toFixed(2)}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div style={{marginTop: 20, padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '4px', border: '1px solid #dee2e6', textAlign: 'center'}}>
                                                <CircularProgress size={20} />
                                                <div style={{marginTop: '8px', fontSize: '13px', color: '#6c757d'}}>Loading inventory data...</div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Organization Section */}
                                    <div className="container" style={{padding: '20px', backgroundColor: 'white', borderRadius: 8, boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: 0, marginTop: 5}}>
                                        <h2 style={{marginTop: 0}}>Organization</h2>
                                        <Autocomplete autoHighlight
                                            id="ddlSubAssemblyType"
                                            style={{marginBottom: 20}}
                                            options={subAssemblyTypeOptions}
                                            onChange={(event, newValue) => this.changeSubAssemblyTypeFreeSolo(newValue)}
                                            onInputChange={(event, newInputValue) => this.changeSubAssemblyTypeInput(newInputValue)}
                                            value={this.state.editingSubAssembly.subAssembly.assemblyType}
                                            freeSolo
                                            filterOptions={(options, { inputValue }) => {
                                                const inputVal = this.state.subAssemblyTypeInputValue || inputValue || '';
                                                
                                                if (!inputVal || inputVal.trim() === '') {
                                                    // Only exclude current value if it's unique to this sub-assembly
                                                    const currentValue = this.state.editingSubAssembly.subAssembly.assemblyType;
                                                    const isValueUsedByOtherSubAssemblies = this.state.subAssemblies.some(subAssembly => 
                                                        subAssembly.subAssembly.id !== this.state.editingSubAssembly.subAssembly.id && 
                                                        subAssembly.subAssembly.assemblyType === currentValue
                                                    );
                                                    return isValueUsedByOtherSubAssemblies ? options : options.filter(option => option !== currentValue);
                                                }
                                                
                                                // When typing, show ALL matching options (including current value if it matches)
                                                return options.filter(option => {
                                                    return option && option.toLowerCase().includes(inputVal.toLowerCase());
                                                });
                                            }}
                                            className="autocomplete-dropdown"
                                            renderInput={(params) => <TextField {...params} inputProps={{
                                                ...params.inputProps
                                            }} label="Assembly Category (Primary)" autoComplete="new-password" variant="outlined"/>}
                                        />
                                        {this.state.editingSubAssembly.subAssembly.assemblyType.length > 0 ?
                                            <div>
                                                <Autocomplete autoHighlight
                                                    id="ddlSubManufacturerFor"
                                                    style={{marginBottom: 20}}
                                                    options={subManufacturerForOptions.filter(x => x.assemblyType === this.state.editingSubAssembly.subAssembly.assemblyType).map(x => x.subType)}
                                                    onChange={(event, newValue) => this.changeSubManufacturerForFreeSolo(newValue)}
                                                    onInputChange={(event, newInputValue) => this.changeSubManufacturerForInput(newInputValue)}
                                                    value={this.state.editingSubAssembly.subAssembly.manufacturerFor}
                                                    freeSolo
                                                    filterOptions={(options, { inputValue }) => {
                                                        const inputVal = this.state.subManufacturerForInputValue || inputValue || '';
                                                        
                                                        if (!inputVal || inputVal.trim() === '') {
                                                            // Only exclude current value if it's unique to this sub-assembly
                                                            const currentValue = this.state.editingSubAssembly.subAssembly.manufacturerFor;
                                                            const isValueUsedByOtherSubAssemblies = this.state.subAssemblies.some(subAssembly => 
                                                                subAssembly.subAssembly.id !== this.state.editingSubAssembly.subAssembly.id && 
                                                                subAssembly.subAssembly.manufacturerFor === currentValue
                                                            );
                                                            return isValueUsedByOtherSubAssemblies ? options : options.filter(option => option !== currentValue);
                                                        }
                                                        
                                                        // When typing, show ALL matching options (including current value if it matches)
                                                        return options.filter(option => {
                                                            return option && option.toLowerCase().includes(inputVal.toLowerCase());
                                                        });
                                                    }}
                                                    className="autocomplete-dropdown"
                                                    renderInput={(params) => <TextField {...params} inputProps={{
                                                        ...params.inputProps
                                                    }} label="Assembly Category (Secondary)" autoComplete="new-password"
                                                                                        variant="outlined"/>}
                                                />
                                                <Autocomplete autoHighlight
                                                    id="ddlSubTertiaryType"
                                                    style={{marginBottom: 20, width: '100%'}}
                                                    options={[...new Set(subTertiaryTypeOptions.map(x => x.subType))]}
                                                    onChange={(event, newValue) => this.changeSubTertiaryTypeFreeSolo(newValue)}
                                                    onInputChange={(event, newInputValue) => this.changeSubTertiaryTypeInput(newInputValue)}
                                                    value={this.state.editingSubAssembly.subAssembly.tertiaryType}
                                                    freeSolo
                                                    filterOptions={(options, { inputValue }) => {
                                                        const inputVal = this.state.subTertiaryTypeInputValue || inputValue || '';
                                                        
                                                        if (!inputVal || inputVal.trim() === '') {
                                                            // Only exclude current value if it's unique to this sub-assembly
                                                            const currentValue = this.state.editingSubAssembly.subAssembly.tertiaryType;
                                                            const isValueUsedByOtherSubAssemblies = this.state.subAssemblies.some(subAssembly => 
                                                                subAssembly.subAssembly.id !== this.state.editingSubAssembly.subAssembly.id && 
                                                                subAssembly.subAssembly.tertiaryType === currentValue
                                                            );
                                                            return isValueUsedByOtherSubAssemblies ? options : options.filter(option => option !== currentValue);
                                                        }
                                                        
                                                        // When typing, show ALL matching options (including current value if it matches)
                                                        return options.filter(option => {
                                                            return option && option.toLowerCase().includes(inputVal.toLowerCase());
                                                        });
                                                    }}
                                                    renderInput={(params) => <TextField {...params} inputProps={{
                                                        ...params.inputProps
                                                    }} label="Assembly Category (Tertiary)" autoComplete="new-password"
                                                                                        variant="outlined"/>}
                                                />
                                                <Autocomplete autoHighlight
                                                    id="ddlSubBrand"
                                                    style={{marginBottom: 20, width: '100%'}}
                                                    options={subBrandOptions}
                                                    onChange={(event, newValue) => this.changeSubBrandFreeSolo(newValue)}
                                                    onInputChange={(event, newInputValue) => this.changeSubBrandInput(newInputValue)}
                                                    value={this.state.editingSubAssembly.subAssembly.brand || ''}
                                                    inputValue={this.state.subBrandInputValue}
                                                    freeSolo
                                                    filterOptions={(options, { inputValue }) => {
                                                        const inputVal = this.state.subBrandInputValue || inputValue || '';
                                                        
                                                        if (!inputVal || inputVal.trim() === '') {
                                                            // Only exclude current value if it's unique to this sub-assembly
                                                            const currentValue = this.state.editingSubAssembly.subAssembly.brand;
                                                            const isValueUsedByOtherSubAssemblies = this.state.subAssemblies.some(subAssembly => 
                                                                subAssembly.subAssembly.id !== this.state.editingSubAssembly.subAssembly.id && 
                                                                subAssembly.subAssembly.brand === currentValue
                                                            );
                                                            return isValueUsedByOtherSubAssemblies ? options : options.filter(option => option !== currentValue);
                                                        }
                                                        
                                                        // When typing, show ALL matching options (including current value if it matches)
                                                        return options.filter(option => {
                                                            return option && option.toLowerCase().includes(inputVal.toLowerCase());
                                                        });
                                                    }}
                                                    className="autocomplete-dropdown"
                                                    renderInput={(params) => <TextField {...params} inputProps={{
                                                        ...params.inputProps
                                                    }} label="Brand" autoComplete="new-password"
                                                                                        variant="outlined"/>}
                                                />
                                                
                                                {/* Inventory Item Checkbox */}
                                                <div style={{marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                                    <Checkbox
                                                        checked={this.state.editingSubAssembly.subAssembly.isInventoryItem}
                                                        onChange={this.changeSubAssemblyIsInventoryItem}
                                                        color="primary"
                                                    />
                                                    <center>Inventory item</center>
                                                </div>
                                            </div>
                                            : ""}
                                    </div>

                                    {/* Vehicle Fitment Container (SEMA / dealer) */}
                                    <div className="container" style={{padding: '20px', backgroundColor: 'white', borderRadius: 8, boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: 5, marginTop: 5}}>
                                        <VehicleFitmentPanel productType="Subassembly" productId={this.state.editingSubAssembly.subAssembly.id}/>
                                    </div>

                                    {/* Images Section */}
                                    <div 
                                        className="container" 
                                        onDragOver={this.handleDragOverSubAssembly}
                                        onDragLeave={this.handleDragLeaveSubAssembly}
                                        onDrop={async (event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            this.setState({ isDraggingOverSubAssembly: false });
                                            const files = Array.from(event.dataTransfer.files);
                                            if (files.length > 0) {
                                                // Initialize imageURLs if needed
                                                if (!editingSubAssembly.subAssembly.imageURLs) {
                                                    editingSubAssembly.subAssembly.imageURLs = editingSubAssembly.subAssembly.imageURL 
                                                        ? editingSubAssembly.subAssembly.imageURL.split('~|~') 
                                                        : [''];
                                                }
                                                // Process files - will replace blank slots first, then append
                                                await this.processSubAssemblyImageFiles(files, 0);
                                            }
                                        }}
                                        style={{
                                            padding: '20px', 
                                            backgroundColor: this.state.isDraggingOverSubAssembly ? '#E3F2FD' : 'white', 
                                            borderRadius: 8, 
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)', 
                                            marginBottom: 0, 
                                            marginTop: 5,
                                            border: this.state.isDraggingOverSubAssembly ? '2px dashed #2196F3' : 'none',
                                            transition: 'all 0.2s ease-in-out'
                                        }}
                                    >
                                        <h2 style={{marginTop: 0}}>
                                            {this.state.isDraggingOverSubAssembly ? 'Drop images here to add' : 'Images'}
                                        </h2>
                                        {(() => {
                                            // Initialize imageURLs if needed
                                            if (!editingSubAssembly.subAssembly.imageURLs) {
                                                editingSubAssembly.subAssembly.imageURLs = editingSubAssembly.subAssembly.imageURL 
                                                    ? editingSubAssembly.subAssembly.imageURL.split('~|~') 
                                                    : [''];
                                            }
                                            return editingSubAssembly.subAssembly.imageURLs.map((imageURL, index) => {
                                                return (
                                                    <div key={index} style={{
                                                        marginBottom: 20
                                                    }}>
                                                        <div style={{
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            marginBottom: 10
                                                        }}>
                                                            {editingSubAssembly.subAssembly.imageURLs.length > 1 ?
                                                                <div style={{marginRight: 5}}
                                                                     onClick={() => this.removeSubAssemblyImage(imageURL)}>
                                                                    <FontAwesomeIcon icon={faTrash} size="1x" style={{
                                                                        width: 28,
                                                                        fontSize: 18,
                                                                        cursor: "pointer"
                                                                    }}/>
                                                                </div>
                                                                : ""
                                                            }
                                                            <TextField defaultValue={imageURL}
                                                                       onChange={(event) => this.changeSubAssemblyImageURL(editingSubAssembly.subAssembly.imageURLs.indexOf(imageURL), event)}
                                                                       label={"Image URL"} variant="outlined" style={{width: '80%'}}
                                                                       inputProps={{maxLength: 500}}/>
                                                            {imageURL.length > 0 ?
                                                                <img
                                                                    src={editingSubAssembly.subAssembly.imageURLs[editingSubAssembly.subAssembly.imageURLs.indexOf(imageURL)]}
                                                                    width="18%" alt={editingSubAssembly.subAssembly.subAssemblyId}
                                                                    style={{display: 'inline-block', marginLeft: 10, cursor: 'pointer'}}
                                                                    onClick={() => this.openImageModal(imageURL)}/>
                                                                : ""}
                                                        </div>
                                                    </div>
                                                );
                                            });
                                        })()}
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            marginTop: 10,
                                            marginBottom: 20
                                        }}>
                                            <span style={{marginRight: 10, fontSize: 14, color: '#666'}}>OR</span>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                multiple
                                                onChange={(event) => this.handleSubAssemblyImageUpload(0, event)}
                                                style={{marginRight: 10, maxWidth: '180px', width: 'auto'}}
                                            />
                                            {this.state.uploadingSubAssemblyImage && <CircularProgress size={20} />}
                                        </div>
                                        <div style={{display: 'flex', justifyContent: 'center', marginBottom: 20}}>
                                            <Button onClick={this.addNewSubAssemblyImage} className="success-button-lg">Add Another Image</Button>
                                        </div>
                                    </div>
                                    
                                    {/* Files Section */}
                                    <div 
                                        className="container" 
                                        onDragOver={this.handleDragOverSubAssemblyFiles}
                                        onDragLeave={this.handleDragLeaveSubAssemblyFiles}
                                        onDrop={async (event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            this.setState({ isDraggingOverSubAssemblyFiles: false });
                                            const files = Array.from(event.dataTransfer.files);
                                            if (files.length > 0) {
                                                // Process files - will replace blank slots first, then append
                                                await this.processSubAssemblyFileFiles(files, 0);
                                            }
                                        }}
                                        style={{
                                            padding: '20px', 
                                            backgroundColor: this.state.isDraggingOverSubAssemblyFiles ? '#E3F2FD' : 'white', 
                                            borderRadius: 8, 
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)', 
                                            marginBottom: 0, 
                                            marginTop: 15,
                                            border: this.state.isDraggingOverSubAssemblyFiles ? '2px dashed #2196F3' : 'none',
                                            transition: 'all 0.2s ease-in-out'
                                        }}
                                    >
                                        <h2 style={{marginTop: 0}}>
                                            {this.state.isDraggingOverSubAssemblyFiles ? 'Drop files here to add' : 'Files'}
                                        </h2>
                                        {this.getSubAssemblyFileURLsArray().map((fileURL, index) => {
                                                return (
                                                    <div key={index} style={{
                                                        marginBottom: 20
                                                    }}>
                                                        <div style={{
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            marginBottom: 10
                                                        }}>
                                                            {fileURL && fileURL.length > 0 ?
                                                                <div style={{marginRight: 5}}
                                                                     onClick={() => this.removeSubAssemblyFile(fileURL)}>
                                                                    <FontAwesomeIcon icon={faTrash} size="1x" style={{
                                                                        width: 28,
                                                                        fontSize: 18,
                                                                        cursor: "pointer"
                                                                    }}/>
                                                                </div>
                                                                : ""
                                                            }
                                                            {fileURL && fileURL.length > 0 ? (
                                                                <>
                                                                    <Typography 
                                                                        variant="body1" 
                                                                        style={{
                                                                            width: '80%',
                                                                            padding: '12px 14px',
                                                                            border: '1px solid rgba(0, 0, 0, 0.23)',
                                                                            borderRadius: '4px',
                                                                            backgroundColor: '#f5f5f5',
                                                                            color: '#333',
                                                                            fontSize: '16px',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                            whiteSpace: 'nowrap'
                                                                        }}
                                                                    >
                                                                        {this.getFileNameFromUrl(fileURL)}
                                                                    </Typography>
                                                                    <Button
                                                                        onClick={() => this.downloadSubAssemblyFile(fileURL, this.getSubAssemblyFileURLsArray().indexOf(fileURL))}
                                                                        style={{
                                                                            marginLeft: 10,
                                                                            padding: '8px 16px',
                                                                            backgroundColor: '#2196F3',
                                                                            color: 'white',
                                                                            borderRadius: '4px',
                                                                            fontSize: '14px',
                                                                            textTransform: 'none'
                                                                        }}
                                                                    >
                                                                        Download
                                                                    </Button>
                                                                </>
                                                            ) : (
                                                                <Typography 
                                                                    variant="body2" 
                                                                    style={{
                                                                        width: '80%',
                                                                        color: '#999',
                                                                        fontStyle: 'italic'
                                                                    }}
                                                                >
                                                                    No file uploaded
                                                                </Typography>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            marginTop: 10,
                                            marginBottom: 20
                                        }}>
                                            <input
                                                type="file"
                                                multiple
                                                onChange={(event) => this.handleSubAssemblyFileUpload(0, event)}
                                                style={{marginRight: 10, maxWidth: '180px', width: 'auto'}}
                                            />
                                            {this.state.uploadingSubAssemblyFile && <CircularProgress size={20} />}
                                        </div>
                                    </div>
                                </>
                            : ""}
                        </div>
                        <div style={{
                            flex: '1',
                            minWidth: '300px',
                            maxWidth: '60%',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '15px'
                        }}>
                            <div className="container" style={{padding: 20, borderRadius: 8, marginBottom: 0, marginTop: 15}}>
                                <h2 style={{marginTop: 0}}>Bill of Materials</h2>
                                {(this.state.editingAssembly || this.state.editingSubAssembly) && (
                                    <TextField
                                        label="BOM Memo"
                                        variant="outlined"
                                        size="small"
                                        multiline
                                        rows={getBomNotesFieldRows(this.state.editingAssembly
                                            ? this.state.editingAssembly.assembly.bomNotes
                                            : this.state.editingSubAssembly.subAssembly.bomNotes)}
                                        rowsMax={8}
                                        fullWidth
                                        placeholder="Shown on the BOM lookup page to help prevent packing errors."
                                        value={this.state.editingAssembly
                                            ? (this.state.editingAssembly.assembly.bomNotes || '')
                                            : (this.state.editingSubAssembly.subAssembly.bomNotes || '')}
                                        onChange={this.state.editingAssembly ? this.changeBomNotes : this.changeSubBomNotes}
                                        inputProps={{ maxLength: 2000 }}
                                        style={{ marginBottom: 8 }}
                                    />
                                )}
                                <div className="parts-table-container" style={{marginBottom: 20}}>
                                    <table className="parts-table">
                                                                <thead>
                        <tr>
                            {this.state.isBulkEditingBom && <th style={{width: 28}}></th>}
                            <th style={{minWidth: 200}}>PN</th>
                            <th style={{minWidth: 25}}>Rev</th>
                            <th>Description</th>
                            <th style={{minWidth: 60}}>Qty</th>
                            <th style={{minWidth: 40}}>BOM<br />Check</th>
                            <th style={{minWidth: 40}}>Implied<br />Part</th>
                            <th style={{minWidth: 50}}>Unit Cost</th>
                            <th style={{minWidth: 50}}>Ext. Cost</th>
                            {this.state.isBulkEditingBom && <th style={{width: 28}}></th>}
                        </tr>
                        </thead>
                                        <DragDropContext onDragEnd={this.onDragEnd}>
                                            <Droppable droppableId="bom-table">
                                                {(provided) => (
                                                    <tbody ref={provided.innerRef} {...provided.droppableProps}>
                                                    {this.state.isBulkEditingBom && this.getAllBomItemsForDrag().map((item, idx) => {
                                                        if (item.isNew) {
                                                            return (
                                                                <Draggable key={item.dragId} draggableId={item.dragId} index={idx}>
                                                                    {(provided) => this.renderNewBomTableItem(item, idx, provided)}
                                                                </Draggable>
                                                            );
                                                        } else if (this.state.editingAssembly) {
                                                            return (
                                                                <Draggable key={item.dragId} draggableId={item.dragId} index={idx}>
                                                                    {(provided) => this.renderBomTableItem(item, idx, provided)}
                                                                </Draggable>
                                                            );
                                                        } else if (this.state.editingSubAssembly) {
                                                            return (
                                                                <Draggable key={item.dragId} draggableId={item.dragId} index={idx}>
                                                                    {(provided) => this.renderBomTableItemForSubAssembly(item, idx, provided)}
                                                                </Draggable>
                                                            );
                                                        }
                                                        return null;
                                                    })}
                                                    {!this.state.isBulkEditingBom && this.state.editingAssembly &&
                                                        this.groupItemsForBomTable(
                                                            this.state.editingAssembly.assembly.subAssembliesToAssemblies,
                                                            this.state.editingAssembly.assembly.partsToAssemblies
                                                        ).map((item, idx) => (
                                                            <Draggable key={item.dragId} draggableId={item.dragId}
                                                                       index={idx}>
                                                                {(provided) => this.renderBomTableItem(item, idx, provided)}
                                                            </Draggable>
                                                        ))
                                                    }
                                                    {!this.state.isBulkEditingBom && this.state.editingSubAssembly &&
                                                        this.groupItemsForBomTable(
                                                            [], // No assembly-to-subassembly relationships when editing subassembly
                                                            this.state.editingSubAssembly.subAssembly.partsToSubAssemblies,
                                                            this.state.editingSubAssembly.subAssembly.childSubAssemblies || []
                                                        ).map((item, idx) => (
                                                            <Draggable key={item.dragId} draggableId={item.dragId}
                                                                       index={idx}>
                                                                {(provided) => this.renderBomTableItemForSubAssembly(item, idx, provided)}
                                                            </Draggable>
                                                        ))
                                                    }
                                                    {provided.placeholder}
                                                    {/* Inline Add Row */}
                                                    {this.state.isAddingBomItem && (
                                                        <tr style={{
                                                            backgroundColor: '#f8f9fa',
                                                            border: '2px dashed #007bff'
                                                        }}>
                                                            <td></td>
                                                            <td style={{padding: '10px'}} colSpan={3}>
                                                                <Autocomplete autoHighlight
                                                                    options={connectionFromOptions}
                                                                    onChange={this.handleNewBomItemFromChange}
                                                                    getOptionLabel={(option) => option.title}
                                                                    style={{width: '100%'}}
                                                                    size="small"
                                                                    renderOption={(option) => <PartAutocompleteOption option={option} classes={this.props.classes} />}
                                                                    renderInput={(params) => <TextField
                                                                        {...params}
                                                                        label="Select Item"
                                                                        variant="outlined"
                                                                        size="small"
                                                                        autoComplete="new-password"
                                                                    />}
                                                                />
                                                            </td>
                                                            <td style={{padding: '10px'}}>
                                                                <TextField
                                                                    value={getNumberInputDisplayValue(this.state.newBomItemQuantity)}
                                                                    onChange={(event) => this.setState({newBomItemQuantity: event.target.value})}
                                                                    type="number"
                                                                    variant="outlined"
                                                                    size="small"
                                                                    style={{width: '80px'}}
                                                                    inputProps={{min: 0, step: 1}}
                                                                />
                                                            </td>
                                                            <td style={{textAlign: 'center', padding: '10px'}}>
                                                                <Checkbox
                                                                    checked={this.state.newBomItemBomRequired}
                                                                    onChange={(event) => this.setState({newBomItemBomRequired: event.target.checked})}
                                                                    size="small"
                                                                />
                                                            </td>
                                                            <td style={{textAlign: 'center', padding: '10px'}}>
                                                                {/* Only show Implied Part checkbox for individual parts, not for families or subassemblies */}
                                                                {(this.state.newBomItemFrom && this.state.newBomItemFrom.type === 'part') ? (
                                                                    <Checkbox
                                                                        checked={this.state.newBomItemIsImpliedPart || false}
                                                                        onChange={(event) => this.setState({newBomItemIsImpliedPart: event.target.checked})}
                                                                        size="small"
                                                                    />
                                                                ) : (
                                                                    <span></span>
                                                                )}
                                                            </td>
                                                            <td style={{padding: '10px'}}>
                                                                <div style={{display: 'flex', gap: '5px'}}>
                                                                    {this.canSaveInlineBomItem() && (
                                                                        <button
                                                                            onClick={this.saveInlineBomConnection}
                                                                            style={{
                                                                                backgroundColor: '#28a745',
                                                                                color: 'white',
                                                                                border: 'none',
                                                                                borderRadius: '4px',
                                                                                padding: '5px 8px',
                                                                                cursor: 'pointer',
                                                                                fontSize: '12px'
                                                                            }}
                                                                            disabled={this.state.savingConnection}
                                                                        >
                                                                            {'✓'}
                                                                        </button>
                                                                    )}
                                                                    <button
                                                                        onClick={this.cancelAddingBomItem}
                                                                        style={{
                                                                            backgroundColor: '#dc3545',
                                                                            color: 'white',
                                                                            border: 'none',
                                                                            borderRadius: '4px',
                                                                            padding: '5px 8px',
                                                                            cursor: 'pointer',
                                                                            fontSize: '12px'
                                                                        }}
                                                                    >
                                                                        ✕
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                                                                         )}

                                                     {/* New BOM Item Row in Edit Mode */}
                                                     {this.state.isBulkEditingBom && this.state.newBomItem && (
                                                         <tr style={{backgroundColor: '#f0fff0', border: '2px dashed #28a745'}}>
                                                             {this.state.isBulkEditingBom && <td></td>}
                                                             <td>
                                                                 <Autocomplete autoHighlight
                                                                     options={[
                                                                         ...this.state.partFamilies.map(family => ({
                                                                             title: `${family.partFamilyId} (Family)`,
                                                                             value: family.id,
                                                                             type: 'family',
                                                                             family: family
                                                                         })),
                                                                         ...this.state.parts.filter(x => x.part.isActive).map(p => ({
                                                                             title: `${p.part.partId} rev ${p.part.revision}${p.part.currentlyPurchasing ? ' 🛒' : ''}`,
                                                                             value: p.part.id,
                                                                             type: 'part',
                                                                             part: p.part,
                                                                             partId: p.part.partId,
                                                                             revision: p.part.revision,
                                                                             description: p.part.description,
                                                                             currentlyPurchasing: p.part.currentlyPurchasing
                                                                         })),
                                                                         ...this.state.subAssemblies.filter(x => x.subAssembly.isActive).map(sub => ({
                                                                             title: `${sub.subAssembly.subAssemblyId} rev ${sub.subAssembly.revision}${sub.subAssembly.currentlyPackaging ? ' 📦' : ''}`,
                                                                             value: sub.subAssembly.id,
                                                                             type: 'subassembly',
                                                                             partId: sub.subAssembly.subAssemblyId,
                                                                             revision: sub.subAssembly.revision,
                                                                             description: sub.subAssembly.description,
                                                                             currentlyPackaging: sub.subAssembly.currentlyPackaging
                                                                         }))
                                                                     ].sort((a, b) => a.title.localeCompare(b.title))}
                                                                     onChange={(event, newValue) => {
                                                                         if (newValue) {
                                                                             if (newValue.type === 'family') {
                                                                                 const family = this.state.partFamilies.find(f => f.id === newValue.value);
                                                                                 this.setState(prevState => ({
                                                                                     newBomItem: {
                                                                                         ...prevState.newBomItem,
                                                                                         partFamilyId: newValue.value,
                                                                                         partFamilyName: family?.partFamilyId,
                                                                                         partFamilyDescription: family?.description,
                                                                                         familyName: family?.partFamilyId,
                                                                                         partFk: null,
                                                                                         subAssemblyFk: null,
                                                                                         itemType: 'family',
                                                                                         isImpliedPart: false
                                                                                     }
                                                                                 }));
                                                                             } else if (newValue.type === 'subassembly') {
                                                                                 this.setState(prevState => ({
                                                                                     newBomItem: {
                                                                                         ...prevState.newBomItem,
                                                                                         subAssemblyFk: newValue.value,
                                                                                         partFk: null,
                                                                                         partFamilyId: null,
                                                                                         partFamilyName: null,
                                                                                         familyName: null,
                                                                                         itemType: 'subassembly',
                                                                                         isImpliedPart: false
                                                                                     }
                                                                                 }));
                                                                             } else {
                                                                                 this.setState(prevState => ({
                                                                                     newBomItem: {
                                                                                         ...prevState.newBomItem,
                                                                                         partFk: newValue.value,
                                                                                         subAssemblyFk: null,
                                                                                         partFamilyId: null,
                                                                                         partFamilyName: null,
                                                                                         familyName: null,
                                                                                         itemType: 'part'
                                                                                     }
                                                                                 }));
                                                                             }
                                                                         }
                                                                     }}
                                                                     getOptionLabel={(option) => option.title}
                                                                     value={(() => {
                                                                         if (this.state.newBomItem.partFamilyId) {
                                                                             const family = this.state.partFamilies.find(f => f.id === this.state.newBomItem.partFamilyId);
                                                                             if (family) {
                                                                                 return {
                                                                                     title: `${family.partFamilyId} (Family)`,
                                                                                     value: this.state.newBomItem.partFamilyId,
                                                                                     type: 'family'
                                                                                 };
                                                                             }
                                                                         } else if (this.state.newBomItem.partFk) {
                                                                             const part = this.state.parts.find(p => p.part.id === this.state.newBomItem.partFk);
                                                                             if (part) {
                                                                                 return {
                                                                                     title: `${part.part.partId} rev ${part.part.revision}${part.part.currentlyPurchasing ? ' 🛒' : ''}`,
                                                                                     value: this.state.newBomItem.partFk,
                                                                                     type: 'part',
                                                                                     part: part.part,
                                                                                     partId: part.part.partId,
                                                                                     revision: part.part.revision,
                                                                                     description: part.part.description,
                                                                                     currentlyPurchasing: part.part.currentlyPurchasing
                                                                                 };
                                                                             }
                                                                         } else if (this.state.newBomItem.subAssemblyFk) {
                                                                             const subAssembly = this.state.subAssemblies.find(s => s.subAssembly.id === this.state.newBomItem.subAssemblyFk);
                                                                             if (subAssembly) {
                                                                                 return {
                                                                                     title: `${subAssembly.subAssembly.subAssemblyId} rev ${subAssembly.subAssembly.revision}${subAssembly.subAssembly.currentlyPackaging ? ' 📦' : ''}`,
                                                                                     value: this.state.newBomItem.subAssemblyFk,
                                                                                     type: 'subassembly',
                                                                                     partId: subAssembly.subAssembly.subAssemblyId,
                                                                                     revision: subAssembly.subAssembly.revision,
                                                                                     description: subAssembly.subAssembly.description,
                                                                                     currentlyPackaging: subAssembly.subAssembly.currentlyPackaging
                                                                                 };
                                                                             }
                                                                         }
                                                                         return null;
                                                                     })()}
                                                                     style={{width: '100%'}}
                                                                     size="small"
                                                                     renderInput={(params) => <TextField
                                                                         {...params}
                                                                         variant="outlined"
                                                                         size="small"
                                                                         placeholder="Select item..."
                                                                     />}
                                                                 />
                                                             </td>
                                                             <td>
                                                                 {(() => {
                                                                     if (this.state.newBomItem.partFk) {
                                                                         const part = this.state.parts.find(p => p.part.id === this.state.newBomItem.partFk);
                                                                         return part ? part.part.revision : '';
                                                                     } else if (this.state.newBomItem.subAssemblyFk) {
                                                                         const subAssembly = this.state.subAssemblies.find(s => s.subAssembly.id === this.state.newBomItem.subAssemblyFk);
                                                                         return subAssembly ? subAssembly.subAssembly.revision : '';
                                                                     }
                                                                     return '';
                                                                 })()}
                                                             </td>
                                                             <td className="description-ellipsis text-left">
                                                                 {(() => {
                                                                     if (this.state.newBomItem.partFamilyId) {
                                                                         const family = this.state.partFamilies.find(f => f.id === this.state.newBomItem.partFamilyId);
                                                                         return family?.description || '';
                                                                     }
                                                                     if (this.state.newBomItem.partFk) {
                                                                         const part = this.state.parts.find(p => p.part.id === this.state.newBomItem.partFk);
                                                                         return part ? part.part.description : '';
                                                                     } else if (this.state.newBomItem.subAssemblyFk) {
                                                                         const subAssembly = this.state.subAssemblies.find(s => s.subAssembly.id === this.state.newBomItem.subAssemblyFk);
                                                                         return subAssembly ? subAssembly.subAssembly.description : '';
                                                                     }
                                                                     return '';
                                                                 })()}
                                                                                                                          </td>
                                                             <td>
                                                                 <TextField
                                                                     value={getNumberInputDisplayValue(this.state.newBomItem.quantity)}
                                                                     onChange={(e) => this.updateNewBomItem('quantity', e.target.value)}
                                                                     type="number"
                                                                     variant="outlined"
                                                                     size="small"
                                                                     style={{width: '60px'}}
                                                                     inputProps={{min: 0, step: 1}}
                                                                 />
                                                             </td>
                                                             <td style={{textAlign: 'center'}}>
                                                                 <Checkbox
                                                                     checked={this.state.newBomItem.requiredForBomCheck || false}
                                                                     onChange={(e) => this.updateNewBomItem('requiredForBomCheck', e.target.checked)}
                                                                     size="small"
                                                                     onKeyDown={(e) => {
                                                                         if (e.key === 'Tab' && (this.state.newBomItem.partFk || this.state.newBomItem.subAssemblyFk || this.state.newBomItem.partFamilyId)) {
                                                                             e.preventDefault();
                                                                             this.addNewBomItem();
                                                                         }
                                                                     }}
                                                                 />
                                                             </td>
                                                             <td style={{textAlign: 'center'}}>
                                                                 {/* Only show Implied Part checkbox for individual parts, not for families or subassemblies */}
                                                                 {(this.state.newBomItem.itemType === 'part' || (!this.state.newBomItem.itemType && this.state.newBomItem.partFk)) ? (
                                                                     <Checkbox
                                                                         checked={this.state.newBomItem.isImpliedPart || false}
                                                                         onChange={(e) => this.updateNewBomItem('isImpliedPart', e.target.checked)}
                                                                         size="small"
                                                                     />
                                                                 ) : (
                                                                     <span></span>
                                                                 )}
                                                             </td>
                                                             <td>-</td>
                                                             <td>-</td>
                                                             <td>
                                                                 <button
                                                                     onClick={this.addNewBomItem}
                                                                     disabled={!this.state.newBomItem.partFk && !this.state.newBomItem.subAssemblyFk && !this.state.newBomItem.partFamilyId}
                                                                     style={{
                                                                         backgroundColor: '#28a745',
                                                                         color: 'white',
                                                                         border: 'none',
                                                                         borderRadius: '4px',
                                                                         padding: '5px 8px',
                                                                         cursor: this.state.newBomItem.partFk || this.state.newBomItem.subAssemblyFk || this.state.newBomItem.partFamilyId ? 'pointer' : 'not-allowed',
                                                                         opacity: this.state.newBomItem.partFk || this.state.newBomItem.subAssemblyFk || this.state.newBomItem.partFamilyId ? 1 : 0.5
                                                                     }}
                                                                 >
                                                                     ✓
                                                                 </button>
                                                             </td>
                                                         </tr>
                                                     )}
                                                     {provided.placeholder}
                                                     </tbody>
                                                )}
                                            </Droppable>
                                        </DragDropContext>
                                    </table>
                                                                </div>

                                    {/* Edit/Save BOM Buttons */}
                                    <div style={{textAlign: 'center', margin: '12px 0 0'}}>
                                        {!this.state.isBulkEditingBom ? (
                                            <Button onClick={this.startBulkBomEdit} className="success-button-lg">Edit BOM</Button>
                                        ) : (
                                            <div style={{display: 'flex', gap: '10px', justifyContent: 'center'}}>
                                                <Button onClick={this.saveBulkBomChanges} className="success-button-lg">Save All Changes</Button>
                                                <Button onClick={this.cancelBulkBomEdit} className="delete-button-lg">Cancel</Button>
                                            </div>
                                        )}
                                    </div>
                                                                <div style={{margin: '12px 0'}}>
                                                                    <b>Total Part Cost: ${this.getBomTotalPartCost().toFixed(2)}</b>
                                                                </div>
                                                                {this.state.editingAssembly && this.state.editingAssembly.assembly && (() => {
                                                                    const a = this.state.editingAssembly.assembly;
                                                                    const totalPartCost = this.getBomTotalPartCost();
                                                                    const txFee = (amt) => (amt * 0.03);
                                                                    const msrpNum = Number(a.msrp) || 0;
                                                                    const shipNum = Number(a.shippingCost) || 0;
                                                                    const tier1Price = msrpNum * (1 - (a.discountTier1 || 0));
                                                                    const tier2Price = msrpNum * (1 - (a.discountTier2 || 0));
                                                                    const tier3Price = msrpNum * (1 - (a.discountTier3 || 0));
                                                                    const wholesaleMarginPct = (tierP) => (tierP ? ((1 - ((totalPartCost + txFee(tierP) + shipNum) / tierP)) * 100) : 0);
                                                                    return (
                                                                        <div className="container" style={{marginTop: 16, marginBottom: 0, maxWidth: 400}}>
                                                                            <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                                                                                <table className="parts-table" style={{minWidth: 100}}>
                                                                                    <tbody>
                                                                                        <tr style={{backgroundColor: '#bcdfbc'}}><td style={{fontWeight: 'bold', width: '60%'}}>MSRP</td><td style={{fontWeight: 'bold', width: '40%'}}>${(Number(a.msrp) || 0).toFixed(2)}</td></tr>
                                                                                        <tr><td style={{width: '60%'}}>Shipping Cost</td><td style={{width: '40%'}}>${(Number(a.shippingCost) || 0).toFixed(2)}</td></tr>
                                                                                        <tr><td style={{width: '60%'}}>Transaction Fees (3%)</td><td style={{width: '40%'}}>${(txFee(Number(a.msrp) || 0)).toFixed(2)}</td></tr>
                                                                                        <tr style={{backgroundColor: '#bcdfbc'}}><td style={{fontWeight: 'bold', width: '60%'}}>Retail Profit</td><td style={{fontWeight: 'bold', width: '40%'}}>${((Number(a.msrp) || 0) - ((Number(a.shippingCost) || 0) + txFee(Number(a.msrp) || 0) + totalPartCost)).toFixed(2)} ({((1 - ((totalPartCost + txFee(Number(a.msrp) || 0) + (Number(a.shippingCost) || 0)) / (Number(a.msrp) || 1))) * 100).toFixed(0)}%)</td></tr>
                                                                                    </tbody>
                                                                                </table>
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => this.setState({ profitTiersExpanded: !this.state.profitTiersExpanded })}
                                                                                    style={{ alignSelf: 'flex-start', padding: '4px 8px', cursor: 'pointer', fontSize: '13px', border: '1px solid #ccc', borderRadius: 4, background: '#f5f5f5' }}
                                                                                >
                                                                                    {this.state.profitTiersExpanded ? '▼ Hide tiers' : '▶ Show tiers'}
                                                                                </button>
                                                                                {this.state.profitTiersExpanded && (
                                                                                    <>
                                                                                        <table className="parts-table" style={{minWidth: 100}}><thead><tr style={{backgroundColor: '#000', color: '#FFF'}}><th style={{width: '60%'}}>Tier 1</th><th style={{width: '40%'}}>{((a.discountTier1 || 0) * 100)}%</th></tr></thead><tbody>
                                                                                            <tr style={{backgroundColor: '#F4E4BC'}}><td>Price</td><td>${tier1Price.toFixed(2)}</td></tr>
                                                                                            <tr><td>Shipping</td><td>${shipNum.toFixed(2)}</td></tr>
                                                                                            <tr><td>Transaction Fees</td><td>${txFee(tier1Price).toFixed(2)}</td></tr>
                                                                                            <tr style={{backgroundColor: '#F4E4BC'}}><td style={{fontWeight: 'bold'}}>Wholesale Profit</td><td style={{fontWeight: 'bold'}}>${(tier1Price - (shipNum + txFee(tier1Price) + totalPartCost)).toFixed(2)} ({wholesaleMarginPct(tier1Price).toFixed(0)}%)</td></tr>
                                                                                        </tbody></table>
                                                                                        <table className="parts-table" style={{minWidth: 100}}><thead><tr style={{backgroundColor: '#000', color: '#FFF'}}><th style={{width: '60%'}}>Tier 2</th><th style={{width: '40%'}}>{((a.discountTier2 || 0) * 100)}%</th></tr></thead><tbody>
                                                                                            <tr style={{backgroundColor: '#ffe4e4'}}><td>Price</td><td>${tier2Price.toFixed(2)}</td></tr>
                                                                                            <tr><td>Shipping</td><td>${shipNum.toFixed(2)}</td></tr>
                                                                                            <tr><td>Transaction Fees</td><td>${txFee(tier2Price).toFixed(2)}</td></tr>
                                                                                            <tr style={{backgroundColor: '#ffe4e4'}}><td style={{fontWeight: 'bold'}}>Wholesale Profit</td><td style={{fontWeight: 'bold'}}>${(tier2Price - (shipNum + txFee(tier2Price) + totalPartCost)).toFixed(2)} ({wholesaleMarginPct(tier2Price).toFixed(0)}%)</td></tr>
                                                                                        </tbody></table>
                                                                                        <table className="parts-table" style={{minWidth: 100}}><thead><tr style={{backgroundColor: '#000', color: '#FFF'}}><th style={{width: '60%'}}>Tier 3</th><th style={{width: '40%'}}>{((a.discountTier3 || 0) * 100)}%</th></tr></thead><tbody>
                                                                                            <tr style={{backgroundColor: '#F4A460'}}><td>Price</td><td>${tier3Price.toFixed(2)}</td></tr>
                                                                                            <tr><td>Shipping</td><td>${shipNum.toFixed(2)}</td></tr>
                                                                                            <tr><td>Transaction Fees</td><td>${txFee(tier3Price).toFixed(2)}</td></tr>
                                                                                            <tr style={{backgroundColor: '#F4A460'}}><td style={{fontWeight: 'bold'}}>Wholesale Profit</td><td style={{fontWeight: 'bold'}}>${(tier3Price - (shipNum + txFee(tier3Price) + totalPartCost)).toFixed(2)} ({wholesaleMarginPct(tier3Price).toFixed(0)}%)</td></tr>
                                                                                        </tbody></table>
                                                                                        {/* Custom Discount */}
                                                                                        <table className="parts-table" style={{minWidth: 100}}>
                                                                                            <thead>
                                                                                                <tr style={{backgroundColor: '#000', color: '#FFF'}}>
                                                                                                    <th style={{width: '60%'}}>Custom Discount</th>
                                                                                                    <th style={{width: '40%'}}>
                                                                                                        <TextField value={this.state.customDiscount ?? ''} onChange={(e) => this.setState({ customDiscount: e.target.value })} style={{width: 60, backgroundColor: '#FFF', color: '#000', paddingLeft: '8px', paddingRight: '8px'}} type="number" size="small"/>&nbsp;%
                                                                                                    </th>
                                                                                                </tr>
                                                                                            </thead>
                                                                                            <tbody>
                                                                                                <tr style={{backgroundColor: '#ADD8E6'}}>
                                                                                                    <td style={{width: '60%'}}>Custom Price</td>
                                                                                                    <td style={{width: '40%'}}>${((Number(a.msrp) || 0) * (1 - ((parseNumberInputValue(this.state.customDiscount) || 0) / 100))).toFixed(2)}</td>
                                                                                                </tr>
                                                                                                <tr><td style={{width: '60%'}}>Shipping Cost</td><td style={{width: '40%'}}>${(Number(a.shippingCost) || 0).toFixed(2)}</td></tr>
                                                                                                <tr><td style={{width: '60%'}}>Transaction Fees</td><td style={{width: '40%'}}>${(txFee((Number(a.msrp) || 0) * (1 - ((parseNumberInputValue(this.state.customDiscount) || 0) / 100)))).toFixed(2)}</td></tr>
                                                                                                <tr style={{backgroundColor: '#ADD8E6'}}>
                                                                                                    <td style={{fontWeight: 'bold', width: '60%'}}>Wholesale Profit</td>
                                                                                                    <td style={{fontWeight: 'bold', width: '40%'}}>
                                                                                                        {(() => {
                                                                                                            const customPrice = (Number(a.msrp) || 0) * (1 - ((parseNumberInputValue(this.state.customDiscount) || 0) / 100));
                                                                                                            const profit = customPrice - ((Number(a.shippingCost) || 0) + txFee(customPrice) + totalPartCost);
                                                                                                            const marginPct = customPrice ? ((1 - ((totalPartCost + txFee(customPrice) + (Number(a.shippingCost) || 0)) / customPrice)) * 100) : 0;
                                                                                                            return `$${profit.toFixed(2)} (${marginPct.toFixed(0)}%)`;
                                                                                                        })()}
                                                                                                    </td>
                                                                                                </tr>
                                                                                            </tbody>
                                                                                        </table>
                                                                                    </>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })()}
                                
                                {/* Note: Inline editing has replaced the DrawerMenuRight for editing connections */}
                                {/* Click on any part number, quantity, or BOM check field to edit inline */}
                            </div>
                            <div style={{
                                flex: '1',
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'baseline',
                                gap: this.state.editingAssembly ? '15px' : 0,
                                marginTop: -5
                            }}>
                                <div className="container" style={{padding: 20, borderRadius: 8, flex: 1, width: '33%', marginTop: 0, minWidth: 350, marginTop: 10}}>
                                    <h2 style={{marginTop: 0}}>Revisions</h2>
                                    {/* Final Order Number Section - Only for assemblies */}
                                    {editingAssembly && (
                                        <div style={{
                                            marginBottom: 20,
                                            padding: '0 20px 20px 20px',
                                            border: '1px solid #ddd',
                                            borderRadius: 8
                                        }}>
                                            <h3>Currently Selling Control</h3>
                                            <TextField
                                                value={editingAssembly.assembly.finalOrderNumber || ''}
                                                onChange={this.changeFinalOrderNumber}
                                                label="Final Order Number"
                                                variant="outlined"
                                                style={{width: '100%', marginBottom: 10}}
                                                placeholder="Leave empty if currently selling"
                                            />
                                            <div style={{fontSize: '0.9em', color: '#666'}}>
                                                Final Order Number controls which revision currently being sold.
                                                <br />Empty = currently sold revision. Use the auto-generated System ID from sales logs (I.E. '12345-1').
                                            </div>
                                        </div>
                                    )}

                                    {/* All revisions table */}
                                    <div style={{marginBottom: 20}}>
                                        <h3>All Revisions</h3>
                                        <table className="parts-table"
                                               style={{width: '100%', maxWidth: 450, margin: '0 auto'}}>
                                            <thead>
                                            <tr>
                                                <th style={{minWidth: 110}}>{editingAssembly ? 'Assembly Number' : 'Subassembly Number'}</th>
                                                <th>Revision</th>
                                                <th>Currently<br/>Packaging</th>
                                                {editingAssembly && <th>Currently<br/>Selling</th>}
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {editingAssembly && assemblies
                                                .filter(a => a.assembly.assemblyId === editingAssembly.assembly.assemblyId)
                                                .sort((a, b) => a.assembly.revision.localeCompare(b.assembly.revision))
                                                .map(assemblyData => {
                                                    const assembly = assemblyData.assembly;
                                                    const isCurrentlySelling = isAssemblyCurrentlySelling(assembly, assemblies);
                                                    return (
                                                        <tr key={assembly.id}
                                                            style={{backgroundColor: !assembly.isActive ? 'gray' : assembly.id === editingAssembly.assembly.id ? '#e2f6ff' : 'transparent'}}>
                                                            <td>
                                                                <a href={`/admin/assemblies/${assembly.id}`}
                                                                   style={{color: '#007bff', textDecoration: 'none'}}>
                                                                    {assembly.assemblyId}
                                                                </a>
                                                            </td>
                                                            <td>{assembly.revision}</td>
                                                            <td>
                                                                <Checkbox
                                                                    checked={assembly.currentlyPackaging || false}
                                                                    onChange={() => this.handleRevisionCurrentlyPackagingChange(assembly.id, !assembly.currentlyPackaging, true)}
                                                                    style={{padding: 0}}
                                                                />
                                                            </td>
                                                            <td>
                                                                {isCurrentlySelling ? '🛒' : ''}
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            }
                                            {editingSubAssembly && subAssemblies
                                                .filter(sa => sa.subAssembly.subAssemblyId === editingSubAssembly.subAssembly.subAssemblyId)
                                                .sort((a, b) => a.subAssembly.revision.localeCompare(b.subAssembly.revision))
                                                .map(subAssemblyData => {
                                                    const subAssembly = subAssemblyData.subAssembly;
                                                    return (
                                                        <tr key={subAssembly.id}
                                                            style={{backgroundColor: !subAssembly.isActive ? 'gray' : subAssembly.id === editingSubAssembly.subAssembly.id ? '#e2f6ff' : 'transparent'}}>
                                                            <td>
                                                                <a href={`/admin/assemblies/${subAssembly.id}`}
                                                                   style={{color: '#007bff', textDecoration: 'none'}}>
                                                                    {subAssembly.subAssemblyId}
                                                                </a>
                                                            </td>
                                                            <td>{subAssembly.revision}</td>
                                                            <td>
                                                                <Checkbox
                                                                    checked={subAssembly.currentlyPackaging || false}
                                                                    onChange={() => this.handleRevisionCurrentlyPackagingChange(subAssembly.id, !subAssembly.currentlyPackaging, false)}
                                                                />
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            }
                                            </tbody>
                                        </table>
                                    </div>
                                    {!this.state.editingAssembly?.assembly?.isDiscontinued && (
                                        <Button onClick={() => this.setState({newRevisionOpen: true})} className="success-button-lg" style={{margin: '20px 0 0'}}>New Revision</Button>
                                    )}
                                </div>
                                <div>
                                    {this.state.editingAssembly ?
                                        <div className="container" style={{
                                            padding: '20px',
                                            backgroundColor: 'white',
                                            borderRadius: 8,
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                            marginBottom: 0,
                                            marginTop: 0,
                                            flex: '1'
                                        }}>
                                            <h2 style={{marginTop: 0}}>Spares</h2>
                                            <Autocomplete autoHighlight
                                                id="ddlSpareBushings"
                                                options={assemblyOptions}
                                                defaultValue={assemblyOptions.find(x => x.id === this.state.editingAssembly.assembly.spareBushingsSetId)}
                                                onChange={this.changeSpareBushings}
                                                getOptionLabel={(option) => `${option.assemblyId} rev ${option.revision}${option.currentlyPackaging ? ' 📦' : ''}${isAssemblyCurrentlySelling(option, assemblies) ? ' 🛒' : ''}`}
                                                getOptionSelected={(option) => option.id === this.state.editingAssembly.assembly.spareBushingsSetId}
                                                className="autocomplete-dropdown"
                                                style={{width: '74%', marginBottom: 20, display: 'inline-block'}}
                                                renderInput={(params) => <TextField {...params} inputProps={{
                                                    ...params.inputProps
                                                }} label="Spare Bushings Set" variant="outlined" autoComplete="new-password"/>}
                                            />
                                            <TextField
                                                defaultValue={this.state.editingAssembly.assembly.spareBushingsSetQuantity}
                                                onChange={this.changeSpareBushingsQuantity}
                                                label={"Quantity"}
                                                variant="outlined" type="number"
                                                style={{width: '20%', marginBottom: 20, marginLeft: 10}}
                                                inputProps={{maxLength: 500}}/>
                                            <Autocomplete autoHighlight
                                                id="ddlSpareHardware"
                                                options={assemblyOptions}
                                                defaultValue={assemblyOptions.find(x => x.id === this.state.editingAssembly.assembly.spareHardwareSetId)}
                                                onChange={this.changeSpareHardware}
                                                getOptionLabel={(option) => `${option.assemblyId} rev ${option.revision}${option.currentlyPackaging ? ' 📦' : ''}${isAssemblyCurrentlySelling(option, assemblies) ? ' 🛒' : ''}`}
                                                getOptionSelected={(option) => option.id === this.state.editingAssembly.assembly.spareHardwareSetId}
                                                className="autocomplete-dropdown"
                                                style={{width: '74%', display: 'inline-block'}}
                                                renderInput={(params) => <TextField {...params} inputProps={{
                                                    ...params.inputProps
                                                }} label="Spare Hardware Set" variant="outlined" autoComplete="new-password"/>}
                                            />
                                            <TextField
                                                defaultValue={this.state.editingAssembly.assembly.spareHardwareSetQuantity}
                                                onChange={this.changeSpareHardwareQuantity}
                                                label={"Quantity"}
                                                variant="outlined" type="number"
                                                style={{width: '20%', marginBottom: 20, marginLeft: 10}}
                                                inputProps={{maxLength: 500}}/>
                                        </div>
                                        : ""}

                                    {/* Printing Section */}
                                    {this.state.editingAssembly ?
                                        <div className="container" style={{padding: '20px', backgroundColor: 'white', borderRadius: 8, boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: 0, marginTop: 20}}>
                                            <h2 style={{marginTop: 0}}>Printing</h2>
                                            <div style={{display: 'flex', justifyContent: 'center', marginBottom: 20, gap: 10}}>
                                                <Button onClick={this.printAssemblyBoxLabel} className="success-button-lg"><PrintIcon style={{verticalAlign: 'middle', marginRight: '8px'}} /> Box Labels</Button>
                                                <Button onClick={this.printAssemblyLabelSmall} className="success-button-lg"><PrintIcon style={{verticalAlign: 'middle', marginRight: '8px'}} /> Homepage Label (sm)</Button>
                                                <Button onClick={this.printAssemblyLabelLarge} className="success-button-lg"><PrintIcon style={{verticalAlign: 'middle', marginRight: '8px'}} /> Homepage Label (lg)</Button>
                                            </div>
                                            <TextField
                                                value={this.state.editingAssembly.assembly.weightPounds || ''}
                                                onChange={this.changeWeightPounds}
                                                label={"Weight (Pounds)"}
                                                variant="outlined"
                                                type="number"
                                                style={{width: '23%', minWidth: 135, margin: '0 5px 20px'}}
                                                inputProps={{min: 0}}/>
                                            <TextField
                                                value={this.state.editingAssembly.assembly.weightOunces || ''}
                                                onChange={this.changeWeightOunces}
                                                label={"Weight (Ounces)"}
                                                variant="outlined"
                                                type="number"
                                                style={{width: '23%', minWidth: 135, margin: '0 5px 20px'}}
                                                inputProps={{min: 0, max: 15}}/>
                                            <TextField defaultValue={this.state.editingAssembly.assembly.installVideoURL}
                                                       onChange={this.changeInstallVideoURL} label={"Install Video URL"}
                                                       variant="outlined" style={{width: '43%', margin: '0 5px 20px'}}/>
                                        </div>
                                    : ""}
                                </div>
                            </div>
                        </div>
                    </div>

                    
                    {/* Calculation Contributions Container - Full Width Tile (spans all columns) */}
                    <div className="container" style={{minHeight: '80vh', padding: '20px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: 30, marginTop: 20, width: '100%', maxWidth: this.state.editingAssembly ? 1800 : 1700, marginLeft: 'auto', marginRight: 'auto'}}>
                            <h2 style={{marginTop: 0}}>Calculation Contributions</h2>
                            <Tabs value={this.state.calculationContributionsTab} onChange={this.handleCalculationContributionsTabChange} style={{marginTop: '10px', marginBottom: '20px'}}>
                                <Tab label="Package Logs" />
                                <Tab label="Sales Logs" />
                                <Tab label="Parent/Child" />
                            </Tabs>
                            
                            {/* Tab Panel 0: Package Logs */}
                            {this.state.calculationContributionsTab === 0 && (
                                <Box>
                                    {this.state.loadingCalculationContributions ? (
                                        <div style={{textAlign: 'center', padding: '20px', color: '#666'}}>
                                            <CircularProgress size={24} />
                                            <div style={{marginTop: '10px'}}>Loading package logs...</div>
                                        </div>
                                    ) : this.state.calculationContributionsData.packageLogs === null ? (
                                        <div style={{textAlign: 'center', padding: '20px', color: '#666'}}>
                                            <CircularProgress size={24} />
                                            <div style={{marginTop: '10px'}}>Loading package logs...</div>
                                        </div>
                                    ) : this.state.calculationContributionsData.packageLogs.length === 0 ? (
                                        <Typography variant="body2" color="textSecondary" style={{textAlign: 'center', padding: 20}}>
                                            No package logs found for this {editingAssembly ? 'assembly' : 'subassembly'}
                                        </Typography>
                                    ) : (
                                        <div className="parts-table-container">
                                            <table className="parts-table">
                                                <thead>
                                                    <tr>
                                                        <th style={{minWidth: 170}}>Date</th>
                                                        <th style={{minWidth: 150}}>Part Number</th>
                                                        <th style={{minWidth: 130}}>User</th>
                                                        <th style={{minWidth: 100}}>Quantity</th>
                                                        <th style={{minWidth: 200}}>Reason</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {this.state.calculationContributionsData.packageLogs
                                                        .sort((a, b) => new Date(b.logDate) - new Date(a.logDate))
                                                        .map((log) => {
                                                        const assemblyData = log.assemblyId ? 
                                                            assemblies?.find(a => a.assembly.id === log.assemblyId) : null;
                                                        const subAssemblyData = log.subAssemblyId ?
                                                            subAssemblies?.find(sa => sa.subAssembly.id === log.subAssemblyId) : null;
                                                        const partNumber = assemblyData?.assembly.assemblyId || subAssemblyData?.subAssembly.subAssemblyId || '-';
                                                        const partId = assemblyData?.assembly.id || subAssemblyData?.subAssembly.id;
                                                        const adminUser = this.state.adminUsers.find(x => x.id === log.userId);
                                                        const userName = adminUser ? `${adminUser.firstName} ${adminUser.lastName}` : 'Unknown';
                                                        return (
                                                            <tr key={log.id}>
                                                                <td>{formatUtcForDisplay(log.logDate)}</td>
                                                                <td>
                                                                    {partId ? (
                                                                        <span
                                                                            onClick={(e) => {
                                                                                if (e.ctrlKey || e.metaKey) {
                                                                                    window.open(`/admin/assemblies/${partId}`, '_blank');
                                                                                } else {
                                                                                    globalProps.history.push(`/admin/assemblies/${partId}`);
                                                                                }
                                                                            }}
                                                                            style={{ cursor: 'pointer', color: 'black', fontWeight: 'bold', textDecoration: 'none' }}
                                                                            title="Click to open (Ctrl+Click for new tab)"
                                                                        >
                                                                            {partNumber}
                                                                        </span>
                                                                    ) : (
                                                                        partNumber
                                                                    )}
                                                                </td>
                                                                <td>{userName}</td>
                                                                <td>{log.quantity}</td>
                                                                <td>{log.reason}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </Box>
                            )}
                            
                            {/* Tab Panel 1: Sales Logs */}
                            {this.state.calculationContributionsTab === 1 && (
                                <Box>
                                    {this.state.loadingCalculationContributions ? (
                                        <div style={{textAlign: 'center', padding: '20px', color: '#666'}}>
                                            <CircularProgress size={24} />
                                            <div style={{marginTop: '10px'}}>Loading sales logs...</div>
                                        </div>
                                    ) : this.state.calculationContributionsData.salesLogs === null ? (
                                        <div style={{textAlign: 'center', padding: '20px', color: '#666'}}>
                                            <CircularProgress size={24} />
                                            <div style={{marginTop: '10px'}}>Loading sales logs...</div>
                                        </div>
                                    ) : this.state.calculationContributionsData.salesLogs.length === 0 ? (
                                        <Typography variant="body2" color="textSecondary" style={{textAlign: 'center', padding: 20}}>
                                            No sales logs found for this {editingAssembly ? 'assembly' : 'subassembly'}
                                        </Typography>
                                    ) : (
                                        <div className="parts-table-container">
                                            <table className="parts-table">
                                                <thead>
                                                    <tr>
                                                        <th style={{minWidth: 170}}>Date</th>
                                                        <th style={{minWidth: 130}}>Sales Order</th>
                                                        <th style={{minWidth: 100}}>Dealer Order #</th>
                                                        <th style={{minWidth: 150}}>Part Number</th>
                                                        <th style={{minWidth: 150}}>Source</th>
                                                        <th style={{minWidth: 100}}>Status</th>
                                                        <th style={{minWidth: 100}}>Quantity</th>
                                                        <th style={{minWidth: 300}}>Reason</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(() => {
                                                      const salesBomIndex = buildBomIndex(assemblies, subAssemblies);
                                                      const chainTarget = editingSubAssembly
                                                        ? { id: editingSubAssembly.subAssembly.id, kind: 'subassembly' }
                                                        : null;
                                                      return this.state.calculationContributionsData.salesLogs
                                                        .sort((a, b) => new Date(b.logDate) - new Date(a.logDate))
                                                        .map((log) => {
                                                        const assemblyData = log.assemblyId ? 
                                                            assemblies?.find(a => a.assembly.id === log.assemblyId) : null;
                                                        const subAssemblyData = log.subAssemblyId ?
                                                            subAssemblies?.find(sa => sa.subAssembly.id === log.subAssemblyId) : null;
                                                        const partNumber = assemblyData?.assembly.assemblyId || subAssemblyData?.subAssembly.subAssemblyId || '-';
                                                        const partId = assemblyData?.assembly.id || subAssemblyData?.subAssembly.id;

                                                        // Full BOM hierarchy (with quantities) from the sold item down to this subassembly,
                                                        // plus the rolled-up effective quantity (sold qty x BOM multipliers).
                                                        let contributionChains = [];
                                                        let effectiveQty = log.quantity;
                                                        if (chainTarget) {
                                                            const rootNode = assemblyData?.assembly || subAssemblyData?.subAssembly || null;
                                                            const rootType = assemblyData ? 'assembly' : 'subassembly';
                                                            if (rootNode && rootNode.id !== chainTarget.id) {
                                                                contributionChains = buildContributionChains(rootNode, rootType, chainTarget, salesBomIndex);
                                                                if (contributionChains.length > 0) {
                                                                    effectiveQty = contributionChains.reduce(
                                                                        (sum, ch) => sum + (Number(log.quantity) || 0) * chainMultiplier(ch), 0);
                                                                }
                                                            }
                                                        }
                                                        return (
                                                            <tr key={log.id}>
                                                                <td>{formatUtcForDisplay(log.logDate)}</td>
                                                                <td>
                                                                    {log.globalSequenceNumber != null ? (
                                                                        <span
                                                                            onClick={(e) => {
                                                                                if (e.ctrlKey || e.metaKey) {
                                                                                    window.open(`/admin/sales/${log.globalSequenceNumber}`, '_blank');
                                                                                } else {
                                                                                    globalProps.history.push(`/admin/sales/${log.globalSequenceNumber}`);
                                                                                }
                                                                            }}
                                                                            style={{ cursor: 'pointer', textDecoration: 'none', fontWeight: 600 }}
                                                                            title="Click to open Edit Sales Order (Ctrl+Click for new tab)"
                                                                        >
                                                                            {log.compositeOrderNumber || '-'}
                                                                        </span>
                                                                    ) : (
                                                                        (log.compositeOrderNumber || '-')
                                                                    )}
                                                                </td>
                                                                <td>{log.orderNumber}</td>
                                                                <td style={{ textAlign: 'left' }}>
                                                                    {contributionChains.length === 0 ? (
                                                                        partId ? (
                                                                            <span
                                                                                onClick={(e) => {
                                                                                    if (e.ctrlKey || e.metaKey) {
                                                                                        window.open(`/admin/assemblies/${partId}`, '_blank');
                                                                                    } else {
                                                                                        globalProps.history.push(`/admin/assemblies/${partId}`);
                                                                                    }
                                                                                }}
                                                                                style={{ cursor: 'pointer', color: 'black', fontWeight: 'bold', textDecoration: 'none' }}
                                                                                title="Click to open (Ctrl+Click for new tab)"
                                                                            >
                                                                                {partNumber}
                                                                            </span>
                                                                        ) : (
                                                                            partNumber
                                                                        )
                                                                    ) : (
                                                                        contributionChains.map((chain, ci) => (
                                                                            <div key={ci} style={{ marginBottom: ci < contributionChains.length - 1 ? 8 : 0 }}>
                                                                                <div style={{ whiteSpace: 'nowrap' }}>
                                                                                    {formatBomQty(log.quantity)}×{' '}
                                                                                    {partId ? (
                                                                                        <span
                                                                                            onClick={(e) => {
                                                                                                if (e.ctrlKey || e.metaKey) {
                                                                                                    window.open(`/admin/assemblies/${partId}`, '_blank');
                                                                                                } else {
                                                                                                    globalProps.history.push(`/admin/assemblies/${partId}`);
                                                                                                }
                                                                                            }}
                                                                                            style={{ cursor: 'pointer', color: 'black', fontWeight: 'bold', textDecoration: 'none' }}
                                                                                            title="Click to open (Ctrl+Click for new tab)"
                                                                                        >
                                                                                            {partNumber}
                                                                                        </span>
                                                                                    ) : (
                                                                                        partNumber
                                                                                    )}
                                                                                </div>
                                                                                {chain.map((step, si) => (
                                                                                    <div
                                                                                        key={si}
                                                                                        style={{ marginLeft: 0, color: '#555', fontSize: '0.85em', whiteSpace: 'nowrap' }}
                                                                                    >
                                                                                        ↳ {formatBomQty(step.qty)}× {step.partNumber}
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        ))
                                                                    )}
                                                                </td>
                                                                <td>{log.source}</td>
                                                                <td style={{
                                                                    color: normalizeStatus(log.status) === 'Fulfilled' ? '#4caf50' : 
                                                                           normalizeStatus(log.status) === 'Pending' ? '#ff9800' : 
                                                                           normalizeStatus(log.status) === 'Cancelled' ? '#f44336' : 
                                                                           normalizeStatus(log.status) === 'Refunded' ? '#9c27b0' : '#666',
                                                                    fontWeight: 'bold'
                                                                }}>{normalizeStatus(log.status)}</td>
                                                                <td title={contributionChains.length > 0 ? `Rolled up from sold quantity ${log.quantity} via BOM` : undefined}>{contributionChains.length > 0 ? formatBomQty(effectiveQty) : log.quantity}</td>
                                                                <td>{log.reason}</td>
                                                            </tr>
                                                        );
                                                    });
                                                    })()}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </Box>
                            )}
                            
                            {/* Tab Panel 2: Parent/Child */}
                            {this.state.calculationContributionsTab === 2 && (
                                <Box>
                                    <div style={{display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap'}}>
                                        <div className="filter-toggle" onClick={() => this.setState({showParentChildOptionalColumns: !this.state.showParentChildOptionalColumns})}>
                                            <Checkbox checked={this.state.showParentChildOptionalColumns} />
                                            <span className="filter-label">Show All Columns</span>
                                        </div>
                                        <div className="filter-toggle" onClick={() => this.setState({showParentChildImpliedParts: !this.state.showParentChildImpliedParts})}>
                                            <Checkbox checked={this.state.showParentChildImpliedParts} />
                                            <span className="filter-label">Show Implied Parts</span>
                                        </div>
                                    </div>
                                    <div className={`parts-table-container cc-pc ${editingSubAssembly ? 'cc-pc-sub' : 'cc-pc-asm'}${this.state.showParentChildOptionalColumns ? '' : ' cc-hide-optional'}`}>
                                        <table className="parts-table">
                                            <thead>
                                                <tr className="top-sticky-header">
                                                    {editingSubAssembly ? (
                                                        <>
                                                            <th colSpan={4}>Parent Assemblies</th>
                                                            <th colSpan={5}>Child Parts / Subassemblies</th>
                                                        </>
                                                    ) : (
                                                        <th colSpan={5}>Child Parts / Subassemblies</th>
                                                    )}
                                                    <th colSpan={14}>Parent Calculations</th>
                                                    <th colSpan={24}>Child Calculations</th>
                                                </tr>
                                                <tr className="second-sticky-header">
                                                    {editingSubAssembly && (
                                                        <>
                                                            <th style={{minWidth: 120}}>Assembly</th>
                                                            <th style={{minWidth: 35}}>Rev</th>
                                                            <th style={{minWidth: 20}}>FG</th>
                                                            <th style={{minWidth: 500}}>Description</th>
                                                        </>
                                                    )}
                                                    <th style={{minWidth: 60}}>Quantity</th>
                                                    <th style={{minWidth: 120}}>Part Number</th>
                                                    <th style={{minWidth: 35}}>Rev</th>
                                                    <th style={{minWidth: 50}}>Implied Part</th>
                                                    <th style={{minWidth: 500, borderRight: '1px solid #fe65ff'}}>Description</th>
                                                    <th style={{minWidth: 70, border: '1px solid #fe65ff'}}>
                                                        <Tooltip
                                                            title="Lifetime total number assembled."
                                                            placement="top" TransitionComponent={Zoom}>
                                                            <span>Total Ass'd</span>
                                                        </Tooltip>
                                                    </th>
                                                    <th style={{minWidth: 70, border: '1px solid #fe65ff'}}>
                                                        <Tooltip
                                                            title="Lifetime total number assembled."
                                                            placement="top" TransitionComponent={Zoom}>
                                                            <span>Total Ass'd w/ Rev</span>
                                                        </Tooltip>
                                                    </th>
                                                    <th style={{minWidth: 60}}>Total To<br/>Package</th>
                                                    <th style={{minWidth: 60}}>Total To<br/>Package<br/>w/ Rev</th>
                                                    <th style={{minWidth: 120}}>Min Ass'd On Hand<br/>w/ Rev</th>
                                                    <th style={{minWidth: 120}}>Avg Ass'd On Hand<br/>w/ Rev</th>
                                                    <th style={{minWidth: 70}}>Total Ass'd<br/>On Hand</th>
                                                    <th style={{minWidth: 90}}>Total Ass'd On<br/>Hand w/ Rev</th>
                                                    <th style={{minWidth: 70}}>Total Sold</th>
                                                    <th style={{minWidth: 70}}>Total Sold<br/>w/ Rev</th>
                                                    <th style={{minWidth: 80}}>4 week<br/>Sales / wk</th>
                                                    <th style={{minWidth: 115}}>4 week<br/>Sales / wk w/ Rev</th>
                                                    <th style={{minWidth: 80}}>24 week<br/>Sales / wk</th>
                                                    <th style={{minWidth: 115, borderRight: '1px solid #fe65ff'}}>24 week<br/>Sales / wk w/ Rev</th>
                                                    <th style={{minWidth: 95, border: '1px solid #fe65ff'}}>
                                                        <Tooltip
                                                            title="Lifetime total number packaged into higher level assemblies."
                                                            placement="top" TransitionComponent={Zoom}>
                                                            <span>Total Packaged</span>
                                                        </Tooltip>
                                                    </th>
                                                    <th style={{minWidth: 95, border: '1px solid #fe65ff'}}>
                                                        <Tooltip
                                                            title="Lifetime total number packaged into higher level assemblies."
                                                            placement="top" TransitionComponent={Zoom}>
                                                            <span>Total Packaged<br/>w/ Rev</span>
                                                        </Tooltip>
                                                    </th>
                                                    <th style={{minWidth: 115, border: '1px solid #fe65ff'}}>
                                                        <Tooltip
                                                            title="Total number of children parts/subassemblies ready to be packed into parent assemblies/finished goods."
                                                            placement="top" TransitionComponent={Zoom}>
                                                            <span>Total Unpackaged</span>
                                                        </Tooltip>
                                                    </th>
                                                    <th style={{minWidth: 115, border: '1px solid #fe65ff'}}>
                                                        <Tooltip
                                                            title="Total number of children parts/subassemblies ready to be packed into parent assemblies/finished goods."
                                                            placement="top" TransitionComponent={Zoom}>
                                                            <span>Total Unpackaged<br/>w/ Rev</span>
                                                        </Tooltip>
                                                    </th>
                                                    <th style={{minWidth: 115, border: '1px solid #fe65ff'}}>
                                                        <Tooltip
                                                            title="Current total needed to package parent assemblies."
                                                            placement="top" TransitionComponent={Zoom}>
                                                            <span>Total Needed<br/>to Package<br/>w/ Rev</span>
                                                        </Tooltip>
                                                    </th>
                                                    <th style={{minWidth: 125, border: '1px solid #fe65ff'}}>
                                                        <Tooltip
                                                            title="Compares total unpackaged to total needed to package. If there are sufficient on hand to fulfill packaging requirements, it reports a 0. Otherwise it reports a 1."
                                                            placement="top" TransitionComponent={Zoom}>
                                                            <span>Enough to Package?<br/>w/ Rev</span>
                                                        </Tooltip>
                                                    </th>
                                                    <th style={{minWidth: 115}}>Min Needed<br/>On Hand<br/>w/ Rev</th>
                                                    <th style={{minWidth: 115}}>Avg On Hand<br/>Packaged<br/>w/ Rev</th>
                                                    <th style={{minWidth: 115}}>Total On Hand<br/>Packaged</th>
                                                    <th style={{minWidth: 115, borderRight: '1px solid #fe65ff'}}>Total On Hand<br/>Packaged<br/>w/ Rev</th>
                                                    <th style={{minWidth: 115, border: '1px solid #fe65ff'}}>
                                                        <Tooltip
                                                            title="The difference between the Avg On Hand Pckgd (calculated value based on the average of minimum assembled on hand and target assembled on hand) and the Total On Hand Pckgd. Basically it's telling us if there are a low number of assemblies packed, we need to consider that we are behind on packaging."
                                                            placement="top" TransitionComponent={Zoom}>
                                                            <span>Packaging Deficit<br/>w/ Rev</span>
                                                        </Tooltip>
                                                    </th>
                                                    <th style={{minWidth: 70}}>Total Sold</th>
                                                    <th style={{minWidth: 70}}>Total Sold<br/>w/ Rev</th>
                                                    <th style={{minWidth: 80}}>4 week<br/>Sales / wk</th>
                                                    <th style={{minWidth: 115}}>4 week<br/>Sales / wk w/ Rev</th>
                                                    <th style={{minWidth: 80}}>24 week<br/>Sales / wk</th>
                                                    <th style={{minWidth: 115}}>24 week<br/>Sales / wk w/ Rev</th>
                                                    <th style={{minWidth: 70}}>Unit Cost</th>
                                                    <th style={{minWidth: 100}}>Extended Cost</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(() => {
                                                    // Calculate parent values needed for child calculations
                                                    let parentExpanded = null;
                                                    let totalToPackage = 0;
                                                    let totalToPackageWithRev = 0;
                                                    let minAssembledOnHand = 0;
                                                    let avgAssembledOnHand = 0;
                                                    
                                                    if (editingAssembly) {
                                                        parentExpanded = assemblies?.find(a => a.assembly.id === editingAssembly.assembly.id);
                                                        if (parentExpanded) {
                                                            const averageSalesFourAndTwentyFour = (parentExpanded.fourWeekSalesPerWeekWithRev + parentExpanded.twentyFourWeekSalesPerWeekWithRev) / 2;
                                                            let targetAssembledOnHand = 10 * averageSalesFourAndTwentyFour < 2 ? 2 : 10 * averageSalesFourAndTwentyFour;
                                                            minAssembledOnHand = 2 * averageSalesFourAndTwentyFour;
                                                            avgAssembledOnHand = (minAssembledOnHand + targetAssembledOnHand) / 2;
                                                            totalToPackage = parentExpanded.totalAssembledOnHand < targetAssembledOnHand ? targetAssembledOnHand - parentExpanded.totalAssembledOnHand : 0;
                                                            totalToPackageWithRev = parentExpanded.totalAssembledOnHandWithRev < targetAssembledOnHand ? targetAssembledOnHand - parentExpanded.totalAssembledOnHandWithRev : 0;
                                                        }
                                                    } else if (editingSubAssembly) {
                                                        parentExpanded = subAssemblies?.find(sa => sa.subAssembly.id === editingSubAssembly.subAssembly.id);
                                                        if (parentExpanded) {
                                                            const averageSalesFourAndTwentyFour = (parentExpanded.fourWeekSalesPerWeekWithRev + parentExpanded.twentyFourWeekSalesPerWeekWithRev) / 2;
                                                            let targetAssembledOnHand = 10 * averageSalesFourAndTwentyFour < 2 ? 2 : 10 * averageSalesFourAndTwentyFour;
                                                            minAssembledOnHand = 2 * averageSalesFourAndTwentyFour;
                                                            avgAssembledOnHand = (minAssembledOnHand + targetAssembledOnHand) / 2;
                                                            totalToPackage = parentExpanded.totalAssembledOnHand < targetAssembledOnHand ? targetAssembledOnHand - parentExpanded.totalAssembledOnHand : 0;
                                                            totalToPackageWithRev = parentExpanded.totalAssembledOnHandWithRev < targetAssembledOnHand ? targetAssembledOnHand - parentExpanded.totalAssembledOnHandWithRev : 0;
                                                        }
                                                    }
                                                    
                                                    // Helper function to calculate subassembly cost
                                                    const calculateSubAssemblyCost = (subAssembly) => {
                                                        let cost = 0;
                                                        if (subAssembly.partsToSubAssemblies) {
                                                            subAssembly.partsToSubAssemblies.forEach((partToSubAssembly) => {
                                                                const expandedPart = this.state.parts?.find(x => x.part.id === partToSubAssembly.partFk);
                                                                if (expandedPart && expandedPart.part.isActive === true && expandedPart.cost && expandedPart.quantity) {
                                                                    cost += (expandedPart.cost / expandedPart.quantity) * partToSubAssembly.quantity;
                                                                }
                                                            });
                                                        }
                                                        if (subAssembly.subAssembliesToSubAssemblies) {
                                                            subAssembly.subAssembliesToSubAssemblies.forEach((childRelation) => {
                                                                const childSubAssemblyExpanded = subAssemblies?.find(x => x.subAssembly.id === childRelation.subAssemblyFk);
                                                                if (childSubAssemblyExpanded && childSubAssemblyExpanded.subAssembly.isActive) {
                                                                    cost += calculateSubAssemblyCost(childSubAssemblyExpanded.subAssembly) * childRelation.quantity;
                                                                }
                                                            });
                                                        }
                                                        return cost;
                                                    };
                                                    
                                                    const showImpliedParts = this.state.showParentChildImpliedParts;
                                                    let rows = [];
                                                    
                                                    // For subassemblies: Add parent assemblies/subassemblies that use this subassembly
                                                    if (editingSubAssembly && editingSubAssembly.subAssembly) {
                                                        const currentSubAssemblyId = editingSubAssembly.subAssembly.id;
                                                        
                                                        // Find parent assemblies that use this subassembly
                                                        assemblies?.forEach((parentAssemblyExpanded) => {
                                                            if (!parentAssemblyExpanded.assembly.isActive) return;
                                                            const parentAssembly = parentAssemblyExpanded.assembly;
                                                            const subAssemblyRelation = parentAssembly.subAssembliesToAssemblies?.find(staa => staa.subAssemblyFk === currentSubAssemblyId);
                                                            if (!subAssemblyRelation) return;
                                                            if (subAssemblyRelation.isImpliedPart && !showImpliedParts) return;
                                                            
                                                            const parentAverageSales = (parentAssemblyExpanded.fourWeekSalesPerWeekWithRev + parentAssemblyExpanded.twentyFourWeekSalesPerWeekWithRev) / 2;
                                                            let parentTargetAssembledOnHand = 10 * parentAverageSales < 2 ? 2 : 10 * parentAverageSales;
                                                            let parentMinAssembledOnHand = 2 * parentAverageSales;
                                                            let parentAvgAssembledOnHand = (parentMinAssembledOnHand + parentTargetAssembledOnHand) / 2;
                                                            let parentTotalToPackage = parentAssemblyExpanded.totalAssembledOnHand < parentTargetAssembledOnHand ? parentTargetAssembledOnHand - parentAssemblyExpanded.totalAssembledOnHand : 0;
                                                            let parentTotalToPackageWithRev = parentAssemblyExpanded.totalAssembledOnHandWithRev < parentTargetAssembledOnHand ? parentTargetAssembledOnHand - parentAssemblyExpanded.totalAssembledOnHandWithRev : 0;
                                                            const totalNeededToPackage = Math.ceil(parentTotalToPackage) * subAssemblyRelation.quantity;
                                                            
                                                            rows.push(
                                                                <tr key={`parent-assembly-${parentAssembly.id}`}>
                                                                    <td className="text-left">
                                                                        {this.state.permissions?.includes('EditAssembliesAndParts') ? (
                                                                            <a 
                                                                                href={`/admin/assemblies/${parentAssembly.id}`}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                style={{cursor: 'pointer', color: "black", fontWeight: 'bold', textDecoration: 'none'}}
                                                                                onClick={(e) => {
                                                                                    if (!e.ctrlKey && !e.metaKey) {
                                                                                        e.preventDefault();
                                                                                        window.location.href = `/admin/assemblies/${parentAssembly.id}`;
                                                                                    }
                                                                                }}
                                                                            >
                                                                                {parentAssembly.assemblyId}
                                                                            </a>
                                                                        ) : (
                                                                            <span>{parentAssembly.assemblyId}</span>
                                                                        )}
                                                                    </td>
                                                                    <td>{parentAssembly.revision}</td>
                                                                    <td>x</td>
                                                                    <td className="description-ellipsis text-left">{parentAssembly.description}</td>
                                                                    <td>{subAssemblyRelation.quantity || 0}</td>
                                                                    <td className="text-left">
                                                                        {this.state.permissions?.includes('EditAssembliesAndParts') ? (
                                                                            <a 
                                                                                href={`/admin/assemblies/${currentSubAssemblyId}`}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                style={{cursor: 'pointer', color: "black", fontWeight: 'bold', textDecoration: 'none'}}
                                                                                onClick={(e) => {
                                                                                    if (!e.ctrlKey && !e.metaKey) {
                                                                                        e.preventDefault();
                                                                                        window.location.href = `/admin/assemblies/${currentSubAssemblyId}`;
                                                                                    }
                                                                                }}
                                                                            >
                                                                                {editingSubAssembly.subAssembly.subAssemblyId}
                                                                            </a>
                                                                        ) : (
                                                                            <span>{editingSubAssembly.subAssembly.subAssemblyId}</span>
                                                                        )}
                                                                    </td>
                                                                    <td>{editingSubAssembly.subAssembly.revision}</td>
                                                                    <td style={{textAlign: 'center'}}>{subAssemblyRelation.isImpliedPart ? '✓' : ''}</td>
                                                                    <td className="description-ellipsis text-left">{editingSubAssembly.subAssembly.description}</td>
                                                                    <td>{parentAssemblyExpanded.totalAssembled.toFixed(0)}</td>
                                                                    <td>{parentAssemblyExpanded.totalAssembledWithRev.toFixed(0)}</td>
                                                                    <td>{parentTotalToPackage.toFixed(0)}</td>
                                                                    <td>{parentTotalToPackageWithRev.toFixed(0)}</td>
                                                                    <td>{parentMinAssembledOnHand.toFixed(0)}</td>
                                                                    <td>{parentAvgAssembledOnHand.toFixed(0)}</td>
                                                                    <td>{parentAssemblyExpanded.totalAssembledOnHand.toFixed(0)}</td>
                                                                    <td>{parentAssemblyExpanded.totalAssembledOnHandWithRev.toFixed(0)}</td>
                                                                    <td>{parentAssemblyExpanded.totalSold.toFixed(0)}</td>
                                                                    <td>{(parentAssemblyExpanded.totalSoldWithRev ?? parentAssemblyExpanded.totalSold).toFixed(0)}</td>
                                                                    <td>{parentAssemblyExpanded.fourWeekSalesPerWeek.toFixed(2)}</td>
                                                                    <td>{parentAssemblyExpanded.fourWeekSalesPerWeekWithRev.toFixed(2)}</td>
                                                                    <td>{parentAssemblyExpanded.twentyFourWeekSalesPerWeek.toFixed(2)}</td>
                                                                    <td>{parentAssemblyExpanded.twentyFourWeekSalesPerWeekWithRev.toFixed(2)}</td>
                                                                    <td>{parentAssemblyExpanded.totalAssembled * subAssemblyRelation.quantity}</td>
                                                                    <td>{parentAssemblyExpanded.totalAssembledWithRev * subAssemblyRelation.quantity}</td>
                                                                    <td>{parentExpanded.totalAssembledOnHand || 0}</td>
                                                                    <td>{parentExpanded.totalAssembledOnHandWithRev || 0}</td>
                                                                    <td>{totalNeededToPackage.toFixed(0)}</td>
                                                                    <td>{(parentExpanded.totalAssembledOnHandWithRev || 0) >= totalNeededToPackage ? 0 : 1}</td>
                                                                    <td>{(subAssemblyRelation && !subAssemblyRelation.isImpliedPart ? Math.ceil(parentMinAssembledOnHand) * subAssemblyRelation.quantity : 0).toFixed(0)}</td>
                                                                    <td>{(Math.ceil(parentAvgAssembledOnHand) * subAssemblyRelation.quantity).toFixed(0)}</td>
                                                                    <td>{parentAssemblyExpanded.totalAssembledOnHand * subAssemblyRelation.quantity}</td>
                                                                    <td>{parentAssemblyExpanded.totalAssembledOnHandWithRev * subAssemblyRelation.quantity}</td>
                                                                    <td>{((parentAssemblyExpanded.totalAssembledOnHandWithRev * subAssemblyRelation.quantity) - (Math.ceil(parentAvgAssembledOnHand) * subAssemblyRelation.quantity)).toFixed(0)}</td>
                                                                    <td>{(parentAssemblyExpanded.totalSold * subAssemblyRelation.quantity).toFixed(0)}</td>
                                                                    <td>{((parentAssemblyExpanded.totalSoldWithRev ?? parentAssemblyExpanded.totalSold) * subAssemblyRelation.quantity).toFixed(0)}</td>
                                                                    <td>{(parentAssemblyExpanded.fourWeekSalesPerWeek * subAssemblyRelation.quantity).toFixed(2)}</td>
                                                                    <td>{(parentAssemblyExpanded.fourWeekSalesPerWeekWithRev * subAssemblyRelation.quantity).toFixed(2)}</td>
                                                                    <td>{(parentAssemblyExpanded.twentyFourWeekSalesPerWeek * subAssemblyRelation.quantity).toFixed(2)}</td>
                                                                    <td>{(parentAssemblyExpanded.twentyFourWeekSalesPerWeekWithRev * subAssemblyRelation.quantity).toFixed(2)}</td>
                                                                    <td>${calculateSubAssemblyCost(editingSubAssembly.subAssembly).toFixed(2)}</td>
                                                                    <td>${(calculateSubAssemblyCost(editingSubAssembly.subAssembly) * subAssemblyRelation.quantity).toFixed(2)}</td>
                                                                </tr>
                                                            );
                                                        });
                                                        
                                                        // Find parent subassemblies that use this subassembly
                                                        subAssemblies?.forEach((parentSubAssemblyExpanded) => {
                                                            if (!parentSubAssemblyExpanded.subAssembly.isActive || parentSubAssemblyExpanded.subAssembly.id === currentSubAssemblyId) return;
                                                            const parentSubAssembly = parentSubAssemblyExpanded.subAssembly;
                                                            const childSubAssemblyRelation = parentSubAssembly.childSubAssemblies?.find(csa => csa.childSubAssemblyFk === currentSubAssemblyId);
                                                            if (!childSubAssemblyRelation) return;
                                                            const childSubAssemblyExpanded = subAssemblies?.find(sa => sa.subAssembly.id === currentSubAssemblyId);
                                                            if (!childSubAssemblyExpanded) return;
                                                            if (childSubAssemblyRelation.isImpliedPart && !showImpliedParts) return;
                                                            
                                                            const parentAverageSales = (parentSubAssemblyExpanded.fourWeekSalesPerWeekWithRev + parentSubAssemblyExpanded.twentyFourWeekSalesPerWeekWithRev) / 2;
                                                            let parentTargetAssembledOnHand = 10 * parentAverageSales < 2 ? 2 : 10 * parentAverageSales;
                                                            let parentMinAssembledOnHand = 2 * parentAverageSales;
                                                            let parentAvgAssembledOnHand = (parentMinAssembledOnHand + parentTargetAssembledOnHand) / 2;
                                                            let parentTotalToPackage = parentSubAssemblyExpanded.totalAssembledOnHand < parentTargetAssembledOnHand ? parentTargetAssembledOnHand - parentSubAssemblyExpanded.totalAssembledOnHand : 0;
                                                            let parentTotalToPackageWithRev = parentSubAssemblyExpanded.totalAssembledOnHandWithRev < parentTargetAssembledOnHand ? parentTargetAssembledOnHand - parentSubAssemblyExpanded.totalAssembledOnHandWithRev : 0;
                                                            const totalNeededToPackage = Math.ceil(parentTotalToPackage) * childSubAssemblyRelation.quantity;
                                                            
                                                            rows.push(
                                                                <tr key={`parent-subassembly-${parentSubAssembly.id}`}>
                                                                    <td className="text-left">
                                                                        {this.state.permissions?.includes('EditAssembliesAndParts') ? (
                                                                            <a 
                                                                                href={`/admin/assemblies/${parentSubAssembly.id}`}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                style={{cursor: 'pointer', color: "black", fontWeight: 'bold', textDecoration: 'none'}}
                                                                                onClick={(e) => {
                                                                                    if (!e.ctrlKey && !e.metaKey) {
                                                                                        e.preventDefault();
                                                                                        window.location.href = `/admin/assemblies/${parentSubAssembly.id}`;
                                                                                    }
                                                                                }}
                                                                            >
                                                                                {parentSubAssembly.subAssemblyId}
                                                                            </a>
                                                                        ) : (
                                                                            <span>{parentSubAssembly.subAssemblyId}</span>
                                                                        )}
                                                                    </td>
                                                                    <td>{parentSubAssembly.revision}</td>
                                                                    <td></td>
                                                                    <td className="description-ellipsis text-left">{parentSubAssembly.description}</td>
                                                                    <td>{childSubAssemblyRelation.quantity || 0}</td>
                                                                    <td className="text-left">
                                                                        {this.state.permissions?.includes('EditAssembliesAndParts') ? (
                                                                            <a 
                                                                                href={`/admin/assemblies/${currentSubAssemblyId}`}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                style={{cursor: 'pointer', color: "black", fontWeight: 'bold', textDecoration: 'none'}}
                                                                                onClick={(e) => {
                                                                                    if (!e.ctrlKey && !e.metaKey) {
                                                                                        e.preventDefault();
                                                                                        window.location.href = `/admin/assemblies/${currentSubAssemblyId}`;
                                                                                    }
                                                                                }}
                                                                            >
                                                                                {editingSubAssembly.subAssembly.subAssemblyId}
                                                                            </a>
                                                                        ) : (
                                                                            <span>{editingSubAssembly.subAssembly.subAssemblyId}</span>
                                                                        )}
                                                                    </td>
                                                                    <td>{editingSubAssembly.subAssembly.revision}</td>
                                                                    <td style={{textAlign: 'center'}}>{childSubAssemblyRelation.isImpliedPart ? '✓' : ''}</td>
                                                                    <td className="description-ellipsis text-left">{editingSubAssembly.subAssembly.description}</td>
                                                                    <td>{parentSubAssemblyExpanded.totalAssembled.toFixed(0)}</td>
                                                                    <td>{parentSubAssemblyExpanded.totalAssembledWithRev.toFixed(0)}</td>
                                                                    <td>{parentTotalToPackage.toFixed(0)}</td>
                                                                    <td>{parentTotalToPackageWithRev.toFixed(0)}</td>
                                                                    <td>{parentMinAssembledOnHand.toFixed(0)}</td>
                                                                    <td>{parentAvgAssembledOnHand.toFixed(0)}</td>
                                                                    <td>{parentSubAssemblyExpanded.totalAssembledOnHand.toFixed(0)}</td>
                                                                    <td>{parentSubAssemblyExpanded.totalAssembledOnHandWithRev.toFixed(0)}</td>
                                                                    <td>{parentSubAssemblyExpanded.totalSold.toFixed(0)}</td>
                                                                    <td>{(parentSubAssemblyExpanded.totalSoldWithRev ?? parentSubAssemblyExpanded.totalSold).toFixed(0)}</td>
                                                                    <td>{parentSubAssemblyExpanded.fourWeekSalesPerWeek.toFixed(2)}</td>
                                                                    <td>{parentSubAssemblyExpanded.fourWeekSalesPerWeekWithRev.toFixed(2)}</td>
                                                                    <td>{parentSubAssemblyExpanded.twentyFourWeekSalesPerWeek.toFixed(2)}</td>
                                                                    <td>{parentSubAssemblyExpanded.twentyFourWeekSalesPerWeekWithRev.toFixed(2)}</td>
                                                                    <td>{parentSubAssemblyExpanded.totalAssembled * childSubAssemblyRelation.quantity}</td>
                                                                    <td>{parentSubAssemblyExpanded.totalAssembledWithRev * childSubAssemblyRelation.quantity}</td>
                                                                    <td>{childSubAssemblyExpanded.totalAssembledOnHand || 0}</td>
                                                                    <td>{childSubAssemblyExpanded.totalAssembledOnHandWithRev || 0}</td>
                                                                    <td>{totalNeededToPackage.toFixed(0)}</td>
                                                                    <td>{(childSubAssemblyExpanded.totalAssembledOnHandWithRev || 0) >= totalNeededToPackage ? 0 : 1}</td>
                                                                    <td>{(childSubAssemblyRelation && !childSubAssemblyRelation.isImpliedPart ? Math.ceil(parentMinAssembledOnHand) * childSubAssemblyRelation.quantity : 0).toFixed(0)}</td>
                                                                    <td>{(Math.ceil(parentAvgAssembledOnHand) * childSubAssemblyRelation.quantity).toFixed(0)}</td>
                                                                    <td>{parentSubAssemblyExpanded.totalAssembledOnHand * childSubAssemblyRelation.quantity}</td>
                                                                    <td>{parentSubAssemblyExpanded.totalAssembledOnHandWithRev * childSubAssemblyRelation.quantity}</td>
                                                                    <td>{((parentSubAssemblyExpanded.totalAssembledOnHandWithRev * childSubAssemblyRelation.quantity) - (Math.ceil(parentAvgAssembledOnHand) * childSubAssemblyRelation.quantity)).toFixed(0)}</td>
                                                                    <td>{(parentSubAssemblyExpanded.totalSold * childSubAssemblyRelation.quantity).toFixed(0)}</td>
                                                                    <td>{((parentSubAssemblyExpanded.totalSoldWithRev ?? parentSubAssemblyExpanded.totalSold) * childSubAssemblyRelation.quantity).toFixed(0)}</td>
                                                                    <td>{(parentSubAssemblyExpanded.fourWeekSalesPerWeek * childSubAssemblyRelation.quantity).toFixed(2)}</td>
                                                                    <td>{(parentSubAssemblyExpanded.fourWeekSalesPerWeekWithRev * childSubAssemblyRelation.quantity).toFixed(2)}</td>
                                                                    <td>{(parentSubAssemblyExpanded.twentyFourWeekSalesPerWeek * childSubAssemblyRelation.quantity).toFixed(2)}</td>
                                                                    <td>{(parentSubAssemblyExpanded.twentyFourWeekSalesPerWeekWithRev * childSubAssemblyRelation.quantity).toFixed(2)}</td>
                                                                    <td>${calculateSubAssemblyCost(editingSubAssembly.subAssembly).toFixed(2)}</td>
                                                                    <td>${(calculateSubAssemblyCost(editingSubAssembly.subAssembly) * childSubAssemblyRelation.quantity).toFixed(2)}</td>
                                                                </tr>
                                                            );
                                                        });
                                                        
                                                        // Add spacer if we have parent rows
                                                        if (rows.length > 0) {
                                                            rows.push(
                                                                <tr key="spacer-after-parents">
                                                                    <td style={{height: 8}} colSpan={editingSubAssembly ? 42 : 38}></td>
                                                                </tr>
                                                            );
                                                        }
                                                    }
                                                    
                                                    // Parts to Assembly
                                                    if (editingAssembly && editingAssembly.assembly.partsToAssemblies && parentExpanded) {
                                                        editingAssembly.assembly.partsToAssemblies.forEach((pta) => {
                                                            const part = this.state.parts?.find(p => p.part.id === pta.partFk);
                                                            if (!part || !part.part.isActive) return;
                                                            if (pta.isImpliedPart && !showImpliedParts) return;
                                                            
                                                            // Get expanded part data for calculations
                                                            const expandedPart = this.state.parts?.find(p => p.part.id === pta.partFk);
                                                            
                                                            const totalNeededToPackage = Math.ceil(totalToPackage) * pta.quantity;
                                                            rows.push(
                                                                <tr key={`part-${pta.partFk}`}>
                                                                    <td>{pta.quantity || 0}</td>
                                                                    <td className="text-left">
                                                                        {this.state.permissions?.includes('EditAssembliesAndParts') ? (
                                                                            <a 
                                                                                href={`/admin/parts/${part.part.id}`}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                style={{cursor: 'pointer', color: "black", fontWeight: 'bold', textDecoration: 'none'}}
                                                                                onClick={(e) => {
                                                                                    if (!e.ctrlKey && !e.metaKey) {
                                                                                        e.preventDefault();
                                                                                        window.location.href = `/admin/parts/${part.part.id}`;
                                                                                    }
                                                                                }}
                                                                            >
                                                                                {part.part.partId}
                                                                            </a>
                                                                        ) : (
                                                                            <span>{part.part.partId}</span>
                                                                        )}
                                                                    </td>
                                                                    <td>{part.part.revision}</td>
                                                                    <td style={{textAlign: 'center'}}>{pta.isImpliedPart ? '✓' : ''}</td>
                                                                    <td className="description-ellipsis text-left">{part.part.description}</td>
                                                                    <td>{parentExpanded.totalAssembled.toFixed(0)}</td>
                                                                    <td>{parentExpanded.totalAssembledWithRev.toFixed(0)}</td>
                                                                    <td>{totalToPackage.toFixed(0)}</td>
                                                                    <td>{totalToPackageWithRev.toFixed(0)}</td>
                                                                    <td>{minAssembledOnHand.toFixed(0)}</td>
                                                                    <td>{avgAssembledOnHand.toFixed(0)}</td>
                                                                    <td>{parentExpanded.totalAssembledOnHand.toFixed(0)}</td>
                                                                    <td>{parentExpanded.totalAssembledOnHandWithRev.toFixed(0)}</td>
                                                                    <td>{parentExpanded.totalSold.toFixed(0)}</td>
                                                                    <td>{(parentExpanded.totalSoldWithRev ?? parentExpanded.totalSold).toFixed(0)}</td>
                                                                    <td>{parentExpanded.fourWeekSalesPerWeek.toFixed(2)}</td>
                                                                    <td>{parentExpanded.fourWeekSalesPerWeekWithRev.toFixed(2)}</td>
                                                                    <td>{parentExpanded.twentyFourWeekSalesPerWeek.toFixed(2)}</td>
                                                                    <td>{parentExpanded.twentyFourWeekSalesPerWeekWithRev.toFixed(2)}</td>
                                                                    <td>{parentExpanded.totalAssembled * pta.quantity}</td>
                                                                    <td>{parentExpanded.totalAssembledWithRev * pta.quantity}</td>
                                                                    <td>{expandedPart?.totalUnpackaged || 0}</td>
                                                                    <td>{expandedPart?.totalUnpackaged || 0}</td>
                                                                    <td>{totalNeededToPackage.toFixed(0)}</td>
                                                                    <td>{(expandedPart?.totalUnpackaged || 0) >= totalNeededToPackage ? 0 : 1}</td>
                                                                    <td>{(pta && !pta.isImpliedPart ? Math.ceil(minAssembledOnHand) * pta.quantity : 0).toFixed(0)}</td>
                                                                    <td>{(Math.ceil(avgAssembledOnHand) * pta.quantity).toFixed(0)}</td>
                                                                    <td>{parentExpanded.totalAssembledOnHand * pta.quantity}</td>
                                                                    <td>{parentExpanded.totalAssembledOnHandWithRev * pta.quantity}</td>
                                                                    <td>{((parentExpanded.totalAssembledOnHandWithRev * pta.quantity) - (Math.ceil(avgAssembledOnHand) * pta.quantity)).toFixed(0)}</td>
                                                                    <td>{(parentExpanded.totalSold * pta.quantity).toFixed(0)}</td>
                                                                    <td>{((parentExpanded.totalSoldWithRev ?? parentExpanded.totalSold) * pta.quantity).toFixed(0)}</td>
                                                                    <td>{(parentExpanded.fourWeekSalesPerWeek * pta.quantity).toFixed(2)}</td>
                                                                    <td>{(parentExpanded.fourWeekSalesPerWeekWithRev * pta.quantity).toFixed(2)}</td>
                                                                    <td>{(parentExpanded.twentyFourWeekSalesPerWeek * pta.quantity).toFixed(2)}</td>
                                                                    <td>{(parentExpanded.twentyFourWeekSalesPerWeekWithRev * pta.quantity).toFixed(2)}</td>
                                                                    <td>${expandedPart?.cost && expandedPart?.quantity ? (expandedPart.cost / expandedPart.quantity).toFixed(2) : "-"}</td>
                                                                    <td>${expandedPart?.cost && expandedPart?.quantity ? ((expandedPart.cost / expandedPart.quantity) * pta.quantity).toFixed(2) : "-"}</td>
                                                                </tr>
                                                            );
                                                        });
                                                    }
                                                    
                                                    // SubAssemblies to Assembly
                                                    if (editingAssembly && editingAssembly.assembly.subAssembliesToAssemblies && parentExpanded) {
                                                        editingAssembly.assembly.subAssembliesToAssemblies.forEach((staa) => {
                                                            const subAssemblyExpanded = subAssemblies?.find(sa => sa.subAssembly.id === staa.subAssemblyFk);
                                                            if (!subAssemblyExpanded || !subAssemblyExpanded.subAssembly.isActive) return;
                                                            if (staa.isImpliedPart && !showImpliedParts) return;
                                                            
                                                            const totalNeededToPackage = Math.ceil(totalToPackage) * staa.quantity;
                                                            const totalUnitCost = calculateSubAssemblyCost(subAssemblyExpanded.subAssembly);
                                                            
                                                            rows.push(
                                                                <tr key={`subassembly-${staa.subAssemblyFk}`}>
                                                                    <td>{staa.quantity || 0}</td>
                                                                    <td className="text-left">
                                                                        {this.state.permissions?.includes('EditAssembliesAndParts') ? (
                                                                            <a 
                                                                                href={`/admin/assemblies/${subAssemblyExpanded.subAssembly.id}`}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                style={{cursor: 'pointer', color: "black", fontWeight: 'bold', textDecoration: 'none'}}
                                                                                onClick={(e) => {
                                                                                    if (!e.ctrlKey && !e.metaKey) {
                                                                                        e.preventDefault();
                                                                                        window.location.href = `/admin/assemblies/${subAssemblyExpanded.subAssembly.id}`;
                                                                                    }
                                                                                }}
                                                                            >
                                                                                {subAssemblyExpanded.subAssembly.subAssemblyId}
                                                                            </a>
                                                                        ) : (
                                                                            <span>{subAssemblyExpanded.subAssembly.subAssemblyId}</span>
                                                                        )}
                                                                    </td>
                                                                    <td>{subAssemblyExpanded.subAssembly.revision}</td>
                                                                    <td style={{textAlign: 'center'}}>{staa.isImpliedPart ? '✓' : ''}</td>
                                                                    <td className="description-ellipsis text-left">{subAssemblyExpanded.subAssembly.description}</td>
                                                                    <td>{parentExpanded.totalAssembled.toFixed(0)}</td>
                                                                    <td>{parentExpanded.totalAssembledWithRev.toFixed(0)}</td>
                                                                    <td>{totalToPackage.toFixed(0)}</td>
                                                                    <td>{totalToPackageWithRev.toFixed(0)}</td>
                                                                    <td>{minAssembledOnHand.toFixed(0)}</td>
                                                                    <td>{avgAssembledOnHand.toFixed(0)}</td>
                                                                    <td>{parentExpanded.totalAssembledOnHand.toFixed(0)}</td>
                                                                    <td>{parentExpanded.totalAssembledOnHandWithRev.toFixed(0)}</td>
                                                                    <td>{parentExpanded.totalSold.toFixed(0)}</td>
                                                                    <td>{(parentExpanded.totalSoldWithRev ?? parentExpanded.totalSold).toFixed(0)}</td>
                                                                    <td>{parentExpanded.fourWeekSalesPerWeek.toFixed(2)}</td>
                                                                    <td>{parentExpanded.fourWeekSalesPerWeekWithRev.toFixed(2)}</td>
                                                                    <td>{parentExpanded.twentyFourWeekSalesPerWeek.toFixed(2)}</td>
                                                                    <td>{parentExpanded.twentyFourWeekSalesPerWeekWithRev.toFixed(2)}</td>
                                                                    <td>{parentExpanded.totalAssembled * staa.quantity}</td>
                                                                    <td>{parentExpanded.totalAssembledWithRev * staa.quantity}</td>
                                                                    <td>{subAssemblyExpanded.totalAssembledOnHand}</td>
                                                                    <td>{subAssemblyExpanded.totalAssembledOnHandWithRev}</td>
                                                                    <td>{totalNeededToPackage.toFixed(0)}</td>
                                                                    <td>{subAssemblyExpanded.totalAssembledOnHandWithRev >= totalNeededToPackage ? 0 : 1}</td>
                                                                    <td>{(staa && !staa.isImpliedPart ? Math.ceil(minAssembledOnHand) * staa.quantity : 0).toFixed(0)}</td>
                                                                    <td>{(Math.ceil(avgAssembledOnHand) * staa.quantity).toFixed(0)}</td>
                                                                    <td>{parentExpanded.totalAssembledOnHand * staa.quantity}</td>
                                                                    <td>{parentExpanded.totalAssembledOnHandWithRev * staa.quantity}</td>
                                                                    <td>{((parentExpanded.totalAssembledOnHandWithRev * staa.quantity) - (Math.ceil(avgAssembledOnHand) * staa.quantity)).toFixed(0)}</td>
                                                                    <td>{(parentExpanded.totalSold * staa.quantity).toFixed(0)}</td>
                                                                    <td>{((parentExpanded.totalSoldWithRev ?? parentExpanded.totalSold) * staa.quantity).toFixed(0)}</td>
                                                                    <td>{(parentExpanded.fourWeekSalesPerWeek * staa.quantity).toFixed(2)}</td>
                                                                    <td>{(parentExpanded.fourWeekSalesPerWeekWithRev * staa.quantity).toFixed(2)}</td>
                                                                    <td>{(parentExpanded.twentyFourWeekSalesPerWeek * staa.quantity).toFixed(2)}</td>
                                                                    <td>{(parentExpanded.twentyFourWeekSalesPerWeekWithRev * staa.quantity).toFixed(2)}</td>
                                                                    <td>${totalUnitCost.toFixed(2)}</td>
                                                                    <td>${(totalUnitCost * staa.quantity).toFixed(2)}</td>
                                                                </tr>
                                                            );
                                                        });
                                                    }
                                                    
                                                    // Parts to SubAssembly
                                                    if (editingSubAssembly && editingSubAssembly.subAssembly.partsToSubAssemblies && parentExpanded) {
                                                        editingSubAssembly.subAssembly.partsToSubAssemblies.forEach((ptsa) => {
                                                            const part = this.state.parts?.find(p => p.part.id === ptsa.partFk);
                                                            if (!part || !part.part.isActive) return;
                                                            if (ptsa.isImpliedPart && !showImpliedParts) return;
                                                            
                                                            // Get expanded part data for calculations
                                                            const expandedPart = this.state.parts?.find(p => p.part.id === ptsa.partFk);
                                                            const totalNeededToPackage = Math.ceil(totalToPackage) * ptsa.quantity;
                                                            rows.push(
                                                                <tr key={`part-${ptsa.partFk}`}>
                                                                    <td className="text-left">
                                                                        {this.state.permissions?.includes('EditAssembliesAndParts') ? (
                                                                            <a 
                                                                                href={`/admin/assemblies/${editingSubAssembly.subAssembly.id}`}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                style={{cursor: 'pointer', color: "black", fontWeight: 'bold', textDecoration: 'none'}}
                                                                                onClick={(e) => {
                                                                                    if (!e.ctrlKey && !e.metaKey) {
                                                                                        e.preventDefault();
                                                                                        window.location.href = `/admin/assemblies/${editingSubAssembly.subAssembly.id}`;
                                                                                    }
                                                                                }}
                                                                            >
                                                                                {editingSubAssembly.subAssembly.subAssemblyId}
                                                                            </a>
                                                                        ) : (
                                                                            <span>{editingSubAssembly.subAssembly.subAssemblyId}</span>
                                                                        )}
                                                                    </td>
                                                                    <td>{editingSubAssembly.subAssembly.revision}</td>
                                                                    <td></td>
                                                                    <td className="description-ellipsis text-left">{editingSubAssembly.subAssembly.description}</td>
                                                                    <td>{ptsa.quantity || 0}</td>
                                                                    <td className="text-left">
                                                                        {this.state.permissions?.includes('EditAssembliesAndParts') ? (
                                                                            <a 
                                                                                href={`/admin/parts/${part.part.id}`}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                style={{cursor: 'pointer', color: "black", fontWeight: 'bold', textDecoration: 'none'}}
                                                                                onClick={(e) => {
                                                                                    if (!e.ctrlKey && !e.metaKey) {
                                                                                        e.preventDefault();
                                                                                        window.location.href = `/admin/parts/${part.part.id}`;
                                                                                    }
                                                                                }}
                                                                            >
                                                                                {part.part.partId}
                                                                            </a>
                                                                        ) : (
                                                                            <span>{part.part.partId}</span>
                                                                        )}
                                                                    </td>
                                                                    <td>{part.part.revision}</td>
                                                                    <td style={{textAlign: 'center'}}>{ptsa.isImpliedPart ? '✓' : ''}</td>
                                                                    <td className="description-ellipsis text-left">{part.part.description}</td>
                                                                    <td>{parentExpanded.totalAssembled.toFixed(0)}</td>
                                                                    <td>{parentExpanded.totalAssembledWithRev.toFixed(0)}</td>
                                                                    <td>{totalToPackage.toFixed(0)}</td>
                                                                    <td>{totalToPackageWithRev.toFixed(0)}</td>
                                                                    <td>{minAssembledOnHand.toFixed(0)}</td>
                                                                    <td>{avgAssembledOnHand.toFixed(0)}</td>
                                                                    <td>{parentExpanded.totalAssembledOnHand.toFixed(0)}</td>
                                                                    <td>{parentExpanded.totalAssembledOnHandWithRev.toFixed(0)}</td>
                                                                    <td>{parentExpanded.totalSold.toFixed(0)}</td>
                                                                    <td>{(parentExpanded.totalSoldWithRev ?? parentExpanded.totalSold).toFixed(0)}</td>
                                                                    <td>{parentExpanded.fourWeekSalesPerWeek.toFixed(2)}</td>
                                                                    <td>{parentExpanded.fourWeekSalesPerWeekWithRev.toFixed(2)}</td>
                                                                    <td>{parentExpanded.twentyFourWeekSalesPerWeek.toFixed(2)}</td>
                                                                    <td>{parentExpanded.twentyFourWeekSalesPerWeekWithRev.toFixed(2)}</td>
                                                                    <td>{parentExpanded.totalAssembled * ptsa.quantity}</td>
                                                                    <td>{parentExpanded.totalAssembledWithRev * ptsa.quantity}</td>
                                                                    <td>{expandedPart?.totalUnpackaged || 0}</td>
                                                                    <td>{expandedPart?.totalUnpackaged || 0}</td>
                                                                    <td>{totalNeededToPackage.toFixed(0)}</td>
                                                                    <td>{(expandedPart?.totalUnpackaged || 0) >= totalNeededToPackage ? 0 : 1}</td>
                                                                    <td>{(ptsa && !ptsa.isImpliedPart ? Math.ceil(minAssembledOnHand) * ptsa.quantity : 0).toFixed(0)}</td>
                                                                    <td>{(Math.ceil(avgAssembledOnHand) * ptsa.quantity).toFixed(0)}</td>
                                                                    <td>{parentExpanded.totalAssembledOnHand * ptsa.quantity}</td>
                                                                    <td>{parentExpanded.totalAssembledOnHandWithRev * ptsa.quantity}</td>
                                                                    <td>{((parentExpanded.totalAssembledOnHandWithRev * ptsa.quantity) - (Math.ceil(avgAssembledOnHand) * ptsa.quantity)).toFixed(0)}</td>
                                                                    <td>{(parentExpanded.totalSold * ptsa.quantity).toFixed(0)}</td>
                                                                    <td>{((parentExpanded.totalSoldWithRev ?? parentExpanded.totalSold) * ptsa.quantity).toFixed(0)}</td>
                                                                    <td>{(parentExpanded.fourWeekSalesPerWeek * ptsa.quantity).toFixed(2)}</td>
                                                                    <td>{(parentExpanded.fourWeekSalesPerWeekWithRev * ptsa.quantity).toFixed(2)}</td>
                                                                    <td>{(parentExpanded.twentyFourWeekSalesPerWeek * ptsa.quantity).toFixed(2)}</td>
                                                                    <td>{(parentExpanded.twentyFourWeekSalesPerWeekWithRev * ptsa.quantity).toFixed(2)}</td>
                                                                    <td>${expandedPart?.cost && expandedPart?.quantity ? (expandedPart.cost / expandedPart.quantity).toFixed(2) : "-"}</td>
                                                                    <td>${expandedPart?.cost && expandedPart?.quantity ? ((expandedPart.cost / expandedPart.quantity) * ptsa.quantity).toFixed(2) : "-"}</td>
                                                                </tr>
                                                            );
                                                        });
                                                    }
                                                    
                                                    // SubAssemblies to SubAssembly
                                                    if (editingSubAssembly && editingSubAssembly.subAssembly.subAssembliesToSubAssemblies && parentExpanded) {
                                                        editingSubAssembly.subAssembly.subAssembliesToSubAssemblies.forEach((stasa) => {
                                                            const childSubAssemblyExpanded = subAssemblies?.find(sa => sa.subAssembly.id === stasa.subAssemblyFk);
                                                            if (!childSubAssemblyExpanded || !childSubAssemblyExpanded.subAssembly.isActive) return;
                                                            if (stasa.isImpliedPart && !showImpliedParts) return;
                                                            
                                                            const totalNeededToPackage = Math.ceil(totalToPackage) * stasa.quantity;
                                                            const totalUnitCost = calculateSubAssemblyCost(childSubAssemblyExpanded.subAssembly);
                                                            
                                                            rows.push(
                                                                <tr key={`subassembly-${stasa.subAssemblyFk}`}>
                                                                    <td className="text-left">
                                                                        {this.state.permissions?.includes('EditAssembliesAndParts') ? (
                                                                            <a 
                                                                                href={`/admin/assemblies/${editingSubAssembly.subAssembly.id}`}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                style={{cursor: 'pointer', color: "black", fontWeight: 'bold', textDecoration: 'none'}}
                                                                                onClick={(e) => {
                                                                                    if (!e.ctrlKey && !e.metaKey) {
                                                                                        e.preventDefault();
                                                                                        window.location.href = `/admin/assemblies/${editingSubAssembly.subAssembly.id}`;
                                                                                    }
                                                                                }}
                                                                            >
                                                                                {editingSubAssembly.subAssembly.subAssemblyId}
                                                                            </a>
                                                                        ) : (
                                                                            <span>{editingSubAssembly.subAssembly.subAssemblyId}</span>
                                                                        )}
                                                                    </td>
                                                                    <td>{editingSubAssembly.subAssembly.revision}</td>
                                                                    <td></td>
                                                                    <td className="description-ellipsis text-left">{editingSubAssembly.subAssembly.description}</td>
                                                                    <td>{stasa.quantity || 0}</td>
                                                                    <td className="text-left">
                                                                        {this.state.permissions?.includes('EditAssembliesAndParts') ? (
                                                                            <a 
                                                                                href={`/admin/assemblies/${childSubAssemblyExpanded.subAssembly.id}`}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                style={{cursor: 'pointer', color: "black", fontWeight: 'bold', textDecoration: 'none'}}
                                                                                onClick={(e) => {
                                                                                    if (!e.ctrlKey && !e.metaKey) {
                                                                                        e.preventDefault();
                                                                                        window.location.href = `/admin/assemblies/${childSubAssemblyExpanded.subAssembly.id}`;
                                                                                    }
                                                                                }}
                                                                            >
                                                                                {childSubAssemblyExpanded.subAssembly.subAssemblyId}
                                                                            </a>
                                                                        ) : (
                                                                            <span>{childSubAssemblyExpanded.subAssembly.subAssemblyId}</span>
                                                                        )}
                                                                    </td>
                                                                    <td>{childSubAssemblyExpanded.subAssembly.revision}</td>
                                                                    <td style={{textAlign: 'center'}}>{stasa.isImpliedPart ? '✓' : ''}</td>
                                                                    <td className="description-ellipsis text-left">{childSubAssemblyExpanded.subAssembly.description}</td>
                                                                    <td>{parentExpanded.totalAssembled.toFixed(0)}</td>
                                                                    <td>{parentExpanded.totalAssembledWithRev.toFixed(0)}</td>
                                                                    <td>{totalToPackage.toFixed(0)}</td>
                                                                    <td>{totalToPackageWithRev.toFixed(0)}</td>
                                                                    <td>{minAssembledOnHand.toFixed(0)}</td>
                                                                    <td>{avgAssembledOnHand.toFixed(0)}</td>
                                                                    <td>{parentExpanded.totalAssembledOnHand.toFixed(0)}</td>
                                                                    <td>{parentExpanded.totalAssembledOnHandWithRev.toFixed(0)}</td>
                                                                    <td>{parentExpanded.totalSold.toFixed(0)}</td>
                                                                    <td>{(parentExpanded.totalSoldWithRev ?? parentExpanded.totalSold).toFixed(0)}</td>
                                                                    <td>{parentExpanded.fourWeekSalesPerWeek.toFixed(2)}</td>
                                                                    <td>{parentExpanded.fourWeekSalesPerWeekWithRev.toFixed(2)}</td>
                                                                    <td>{parentExpanded.twentyFourWeekSalesPerWeek.toFixed(2)}</td>
                                                                    <td>{parentExpanded.twentyFourWeekSalesPerWeekWithRev.toFixed(2)}</td>
                                                                    <td>{parentExpanded.totalAssembled * stasa.quantity}</td>
                                                                    <td>{parentExpanded.totalAssembledWithRev * stasa.quantity}</td>
                                                                    <td>{childSubAssemblyExpanded.totalAssembledOnHand || 0}</td>
                                                                    <td>{childSubAssemblyExpanded.totalAssembledOnHandWithRev || 0}</td>
                                                                    <td>{totalNeededToPackage.toFixed(0)}</td>
                                                                    <td>{(childSubAssemblyExpanded.totalAssembledOnHandWithRev || 0) >= totalNeededToPackage ? 0 : 1}</td>
                                                                    <td>{(stasa && !stasa.isImpliedPart ? Math.ceil(minAssembledOnHand) * stasa.quantity : 0).toFixed(0)}</td>
                                                                    <td>{(Math.ceil(avgAssembledOnHand) * stasa.quantity).toFixed(0)}</td>
                                                                    <td>{parentExpanded.totalAssembledOnHand * stasa.quantity}</td>
                                                                    <td>{parentExpanded.totalAssembledOnHandWithRev * stasa.quantity}</td>
                                                                    <td>{((parentExpanded.totalAssembledOnHandWithRev * stasa.quantity) - (Math.ceil(avgAssembledOnHand) * stasa.quantity)).toFixed(0)}</td>
                                                                    <td>{(parentExpanded.totalSold * stasa.quantity).toFixed(0)}</td>
                                                                    <td>{((parentExpanded.totalSoldWithRev ?? parentExpanded.totalSold) * stasa.quantity).toFixed(0)}</td>
                                                                    <td>{(parentExpanded.fourWeekSalesPerWeek * stasa.quantity).toFixed(2)}</td>
                                                                    <td>{(parentExpanded.fourWeekSalesPerWeekWithRev * stasa.quantity).toFixed(2)}</td>
                                                                    <td>{(parentExpanded.twentyFourWeekSalesPerWeek * stasa.quantity).toFixed(2)}</td>
                                                                    <td>{(parentExpanded.twentyFourWeekSalesPerWeekWithRev * stasa.quantity).toFixed(2)}</td>
                                                                    <td>${totalUnitCost.toFixed(2)}</td>
                                                                    <td>${(totalUnitCost * stasa.quantity).toFixed(2)}</td>
                                                                </tr>
                                                            );
                                                        });
                                                    }
                                                    
                                                    if (rows.length === 0) {
                                                        rows.push(
                                                            <tr key="empty">
                                                                <td colSpan="24" style={{textAlign: 'center', padding: 20}}>
                                                                    <Typography variant="body2" color="textSecondary">
                                                                        This {editingAssembly ? 'assembly' : 'subassembly'} has no parts or subassemblies in its BOM
                                                                    </Typography>
                                                                </td>
                                                            </tr>
                                                        );
                                                    }
                                                    
                                                    return rows;
                                                })()}
                                            </tbody>
                                        </table>
                                    </div>
                                </Box>
                            )}
                        </div>
                    {this.state.newRevisionOpen ?
                        <DrawerMenuRight title={"New Revision"} open={this.state.newRevisionOpen} customWidth={500}
                                         onClose={() => this.setState({newRevisionOpen: false})}
                                         hasUnsavedChanges={
                                             this.state.newRevision !== undefined ||
                                             this.state.newRevisionNotes !== undefined
                                         }
                                         onSubmit={this.cloneNewRevision}
                                         submitConfirmMessage="Are you sure you want to create a new revision?"
                        >
                            <div style={{marginBottom: 20}}>Current
                                revision: {editingAssembly ? editingAssembly.assembly.revision : editingSubAssembly.subAssembly.revision}</div>
                            <TextField defaultValue={''}
                                       onChange={(event) => this.setState({newRevision: event.target.value})}
                                       label={"Revision"} variant="outlined"
                                       style={{width: '32%', marginBottom: 20}} inputProps={{maxLength: 5}}/>
                            <TextField defaultValue={''}
                                       onChange={(event) => this.setState({newRevisionNotes: event.target.value})}
                                       label={"Revision Notes"} variant="outlined"
                                       style={{width: '65%', marginBottom: 20, marginLeft: 10}}
                                       inputProps={{maxLength: 500}}/>
                            {(() => {
                                const currentItem = editingAssembly ? editingAssembly.assembly : editingSubAssembly.subAssembly;
                                const hasImages = currentItem.imageURLs && currentItem.imageURLs.length > 0 && currentItem.imageURLs.some(url => url && url.trim() !== '');
                                const hasFiles = currentItem.fileURLs && (
                                    (Array.isArray(currentItem.fileURLs) && currentItem.fileURLs.length > 0 && currentItem.fileURLs.some(url => url && url.trim() !== '')) ||
                                    (typeof currentItem.fileURLs === 'string' && currentItem.fileURLs.trim() !== '')
                                );
                                if (hasImages || hasFiles) {
                                    return (
                                        <div style={{marginBottom: 20}}>
                                            <Checkbox
                                                checked={this.state.cloneImagesAndFiles !== false}
                                                onChange={(e) => this.setState({cloneImagesAndFiles: e.target.checked})}
                                                color="primary"
                                            />
                                            <Typography variant="body1" component="span">
                                                Clone Images & Files
                                            </Typography>
                                        </div>
                                    );
                                }
                                return null;
                            })()}
                            {this.state.savingPart === true ?
                                <CircularProgress/>
                                :
                                <Button onClick={this.cloneNewRevision} className="success-button-lg">Clone To New Revision</Button>
                            }
                        </DrawerMenuRight>
                        : ""}
                    {this.state.showCloneDialog ?
                        <DrawerMenuRight title={this.state.isCloningAssembly ? `Clone ${editingAssembly.assembly.assemblyId} rev ${editingAssembly.assembly.revision}` : `Clone ${editingSubAssembly.subAssembly.subAssemblyId} rev ${editingSubAssembly.subAssembly.revision}`} open={this.state.showCloneDialog}
                                         onClose={this.cancelClone}
                                         hasUnsavedChanges={false}
                                         onSubmit={this.state.isCloningAssembly ? this.handleCloneAssemblySubmit : this.handleCloneSubAssemblySubmit}
                                         submitConfirmMessage={this.state.isCloningAssembly ? "Are you sure you want to clone this assembly?" : "Are you sure you want to clone this subassembly?"}
                                         >
                            {this.state.isCloningAssembly ? (
                                <>
                                    <TextField defaultValue={''}
                                               onChange={this.changeClonedAssemblyId} label={"Part Number"} variant="outlined"
                                               style={{width: '48%', marginBottom: 20}} inputProps={{maxLength: 50}}
                                               autoComplete="off"/>
                                    <TextField defaultValue={''}
                                               onChange={this.changeClonedAssemblyRevision} label={"Revision"} variant="outlined"
                                               style={{width: '48%', marginBottom: 20, marginLeft: 10}} inputProps={{maxLength: 5}}
                                               autoComplete="off"/>
                                    {(() => {
                                        const hasImages = editingAssembly.assembly.imageURLs && editingAssembly.assembly.imageURLs.length > 0 && editingAssembly.assembly.imageURLs.some(url => url && url.trim() !== '');
                                        const hasFiles = editingAssembly.assembly.fileURLs && (
                                            (Array.isArray(editingAssembly.assembly.fileURLs) && editingAssembly.assembly.fileURLs.length > 0 && editingAssembly.assembly.fileURLs.some(url => url && url.trim() !== '')) ||
                                            (typeof editingAssembly.assembly.fileURLs === 'string' && editingAssembly.assembly.fileURLs.trim() !== '')
                                        );
                                        if (hasImages || hasFiles) {
                                            return (
                                                <div style={{marginBottom: 20}}>
                                                    <Checkbox
                                                        checked={this.state.cloneImagesAndFiles !== false}
                                                        onChange={(e) => this.setState({cloneImagesAndFiles: e.target.checked})}
                                                        color="primary"
                                                    />
                                                    <Typography variant="body1" component="span">
                                                        Clone Images & Files
                                                    </Typography>
                                                </div>
                                            );
                                        }
                                        return null;
                                    })()}
                                </>
                            ) : (
                                <>
                                    <TextField defaultValue={''}
                                               onChange={this.changeClonedSubAssemblyId} label={"Part Number"} variant="outlined"
                                               style={{width: '48%', marginBottom: 20}} inputProps={{maxLength: 50}}
                                               autoComplete="off"/>
                                    <TextField defaultValue={''}
                                               onChange={this.changeClonedSubAssemblyRevision} label={"Revision"} variant="outlined"
                                               style={{width: '48%', marginBottom: 20, marginLeft: 10}} inputProps={{maxLength: 5}}
                                               autoComplete="off"/>
                                    {(() => {
                                        const hasImages = editingSubAssembly.subAssembly.imageURLs && editingSubAssembly.subAssembly.imageURLs.length > 0 && editingSubAssembly.subAssembly.imageURLs.some(url => url && url.trim() !== '');
                                        const hasFiles = editingSubAssembly.subAssembly.fileURLs && (
                                            (Array.isArray(editingSubAssembly.subAssembly.fileURLs) && editingSubAssembly.subAssembly.fileURLs.length > 0 && editingSubAssembly.subAssembly.fileURLs.some(url => url && url.trim() !== '')) ||
                                            (typeof editingSubAssembly.subAssembly.fileURLs === 'string' && editingSubAssembly.subAssembly.fileURLs.trim() !== '')
                                        );
                                        if (hasImages || hasFiles) {
                                            return (
                                                <div style={{marginBottom: 20}}>
                                                    <Checkbox
                                                        checked={this.state.cloneImagesAndFiles !== false}
                                                        onChange={(e) => this.setState({cloneImagesAndFiles: e.target.checked})}
                                                        color="primary"
                                                    />
                                                    <Typography variant="body1" component="span">
                                                        Clone Images & Files
                                                    </Typography>
                                                </div>
                                            );
                                        }
                                        return null;
                                    })()}
                                </>
                            )}
                            <Button onClick={this.state.isCloningAssembly ? this.handleCloneAssemblySubmit : this.handleCloneSubAssemblySubmit} className="success-button-lg">Clone {this.state.isCloningAssembly ? 'Assembly' : 'SubAssembly'}</Button>
                        </DrawerMenuRight>
                    : ""}
                </div>
                
                {/* Image Modal */}
                <Dialog
                    open={this.state.imageModalOpen}
                    onClose={this.closeImageModal}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogContent style={{padding: '20px', textAlign: 'center'}}>
                        {this.state.selectedImageUrl && (
                            <img 
                                src={this.state.selectedImageUrl} 
                                alt="Assembly/SubAssembly Image"
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '80vh',
                                    objectFit: 'contain'
                                }}
                            />
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.closeImageModal}>Close</Button>
                    </DialogActions>
                </Dialog>
            </DashboardTemplate>
        );
    }
}

let globalProps;

EditAssembly.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(EditAssembly);
